    const { useState, useEffect, useRef, useCallback, useMemo } = React;

    // ── App version + changelog ───────────────────────────────────
    // Bump APP_VERSION on every shipped change and prepend a changelog
    // entry. Surfaced as a hover-tooltip badge on the Profile page.
    const APP_VERSION = "v1.8.7";
    const NOOKS_CHANGELOG = [
      { version: "v1.8.7", date: "2026-08-29", notes: [
        "Added a new Photography category (filters, listing form, and chatbot search)"
      ] },
      { version: "v1.8.6", date: "2026-08-29", notes: [
        "Fixed maps showing a blank API-key-required tile instead of the real map — CARTO basemap tiles are now authenticated with an API key"
      ] },
      { version: "v1.8.5", date: "2026-08-01", notes: [
        "Add Business wizard: owners can self-declare YWCA Hopemaker programme affiliation (Step 3), editable later from Manage Business",
        "Admin Approvals now flags self-declared Hopemaker claims for cross-check with YWCA before applying the verified badge"
      ] },
      { version: "v1.8.4", date: "2026-07-29", notes: [
        "YWCA Hopemakers page is live at /hopemakers — footer link and Explore filter pill restored",
        "Hopemakers copy revised per YWCA's review (removed 'graduate' framing, balanced logo lockup)"
      ] },
      { version: "v1.8.3", date: "2026-07-26", notes: [
        "Fixed direct links to /wishes (was showing 'Business not found')",
        "Browser tab now shows the business name on listing pages",
        "Static pages (Terms, Privacy, FAQ, Regulations) now load the latest app version",
        "Version badge now reads from a single source across the app"
      ] },
      { version: "v1.8.2", date: "2026-07-19", notes: [
        "Version badge and changelog tooltip are now admin-only"
      ] },
      { version: "v1.8.0", date: "2026-07-19", notes: [
        "WhatsApp notifications: add your number in Edit Profile",
        "Choose alerts: bookings, messages, new shops nearby, news",
        "Added this version badge — hover to see what's new"
      ] },
      { version: "v1.7.1", date: "2026-07-15", notes: [
        "Draggable chat launcher; SSR speed-dial fixes"
      ] }
    ];
    // Single source for both the Profile and footer version badges' hover tooltip.
    const CHANGELOG_TOOLTIP = "nooks " + APP_VERSION + "\n\nLatest changes:\n" +
      NOOKS_CHANGELOG.map(function (c) {
        return c.version + (c.date ? " (" + c.date + ")" : "") + "\n" +
          c.notes.map(function (n) { return "• " + n; }).join("\n");
      }).join("\n\n");

    // ── Map tile provider (CARTO Basemaps) ──────────────────────────
    // CARTO now requires a free API key for their raster basemap tiles;
    // unauthenticated requests return an "API KEY REQUIRED" placeholder
    // tile instead of the map. Get a free key (no account needed, emailed
    // instantly, 5M tile requests/month free) at https://carto.com/basemaps/apikey
    // Restrict it to the nooks.sg domain when requesting.
    const CARTO_API_KEY = "cb1_2iom_1_d65f79f6fbd9dbed379597d7";
    const CARTO_TILE_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=" + CARTO_API_KEY;
    const CARTO_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> © <a href="https://carto.com/attributions">CARTO</a>';

    // ── Error Boundary ────────────────────────────────────────────
    // Catches render-time exceptions so the whole page doesn't go blank.
    // The error message is shown in the UI, making it easy to report.
    class ErrorBoundary extends React.Component {
      constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
      }
      static getDerivedStateFromError(error) {
        return { hasError: true, error };
      }
      componentDidCatch(error, info) {
        console.error('[nooks] Render error caught by ErrorBoundary:', error, info);
      }
      render() {
        if (this.state.hasError) {
          const msg = this.state.error ? String(this.state.error.message || this.state.error) : 'Unknown error';
          return React.createElement('div', { style: { padding: '2rem 1rem', maxWidth: '540px', margin: '2rem auto', textAlign: 'center' } },
            React.createElement('h2', { style: { fontFamily: 'var(--serif)', marginBottom: '0.75rem', color: '#C62828' } }, 'Something went wrong'),
            React.createElement('p', { style: { color: 'var(--muted)', marginBottom: '1rem', fontSize: '0.9rem' } }, 'We hit an unexpected error loading this page.'),
            React.createElement('pre', { style: { background: '#f5f5f5', borderRadius: '8px', padding: '0.75rem', fontSize: '0.72rem', textAlign: 'left', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: '1.25rem', color: '#333' } }, msg),
            React.createElement('p', { style: { fontSize: '0.78rem', color: 'var(--muted)', marginBottom: '1rem' } }, 'Screenshot this page and send it to the nooks team so we can fix it.'),
            React.createElement('button', {
              onClick: () => window.location.reload(),
              style: { padding: '0.55rem 1.5rem', background: 'var(--amber)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }
            }, 'Reload Page')
          );
        }
        return this.props.children;
      }
    }

    // Firebase Config
    const firebaseConfig = {
      apiKey: "AIzaSyC0rc49p_nxAfI6c2kXI_xITfbcIulZxh4",
      authDomain: "nooks-sg.firebaseapp.com",
      projectId: "nooks-sg",
      storageBucket: "nooks-sg.firebasestorage.app",
      messagingSenderId: "606833005680",
      appId: "1:606833005680:web:73cdc31ee2ed78783ed0b5",
      measurementId: "G-CBTW0456Z2"
    };

    // Initialize Firebase
    firebase.initializeApp(firebaseConfig);
    const auth = firebase.auth();
    const db = firebase.firestore();
    const storage = firebase.storage();
    const googleProvider = new firebase.auth.GoogleAuthProvider();

    // --- FCM Push Notifications -----------------------------------------------
    // VAPID key: Firebase Console ? Project Settings ? Cloud Messaging ?
    //   Web configuration ? Generate key pair ? copy the public key here.
    const FCM_VAPID_KEY = "BNT6TtQg4NBcr_MxcBHYUZDEvdNQKpxq5Db8GSnILq9-m9YQx14hlP-7LWf0Kc3f_nrJYHldK-9CIAbgokCEOZQ";

    let fcmMessaging = null;
    try { fcmMessaging = firebase.messaging(); } catch(e) { /* browser doesn't support SW */ }

    // Register service worker (required for background push notifications)
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/firebase-messaging-sw.js")
        .then(reg => console.log("[SW] Registered:", reg.scope))
        .catch(err => console.warn("[SW] Registration failed:", err.message));
    }

    // Call this after a user logs in to request permission and store their FCM token
    async function initFcmForUser(user) {
      if (!fcmMessaging || !user || !("Notification" in window)) return;
      if (FCM_VAPID_KEY === "YOUR_VAPID_PUBLIC_KEY_HERE") {
        console.warn("[FCM] VAPID key not set — push notifications disabled. See firebase.json comment.");
        return;
      }
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;
        const token = await fcmMessaging.getToken({ vapidKey: FCM_VAPID_KEY });
        if (!token) return;
        // Store token on the user doc so Cloud Functions can push to this device
        await db.collection("users").doc(user.uid).update({ fcmToken: token });
        console.log("[FCM] Token stored for", user.uid);
        // Refresh token if it rotates
        fcmMessaging.onTokenRefresh(async () => {
          const newToken = await fcmMessaging.getToken({ vapidKey: FCM_VAPID_KEY }).catch(() => null);
          if (newToken) await db.collection("users").doc(user.uid).update({ fcmToken: newToken });
        });
        // Foreground messages — show as in-app toast (push notifications don't fire when tab is active)
        fcmMessaging.onMessage((payload) => {
          const { title, body } = payload.notification || {};
          if (title) showToast(`${title}${body ? ": " + body : ""}`, "info", 6000);
        });
      } catch (err) {
        console.warn("[FCM] Init error:", err.message);
      }
    }
    // -------------------------------------------------------------------------

    // Global error handlers
    window.addEventListener("error", function(e) { console.error("[nooks error]", e.message, e.filename, e.lineno); });
    window.addEventListener("unhandledrejection", function(e) { console.error("[nooks promise]", e.reason); });

    // ---------------------------------------------------
    // LIGHTWEIGHT ANALYTICS TRACKER
    // ---------------------------------------------------
    const NooksAnalytics = (() => {
      const SESSION_KEY = "nooks_session";
      const VISITOR_KEY = "nooks_visitor_id";

      // Generate or retrieve a persistent visitor ID
      const getVisitorId = () => {
        let vid = localStorage.getItem(VISITOR_KEY);
        if (!vid) {
          vid = "v_" + Date.now() + "_" + Math.random().toString(36).slice(2, 10);
          localStorage.setItem(VISITOR_KEY, vid);
        }
        return vid;
      };

      // Session management
      const getOrCreateSession = () => {
        let session = null;
        try { session = JSON.parse(sessionStorage.getItem(SESSION_KEY)); } catch(e) {}
        if (!session) {
          session = {
            id: "s_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8),
            startedAt: Date.now(),
            pageCount: 0,
            isNew: !localStorage.getItem(VISITOR_KEY + "_returning")
          };
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
        }
        return session;
      };

      const updateSession = (session) => {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      };

      // Detect traffic source
      const getTrafficSource = () => {
        const ref = document.referrer;
        if (!ref) return "direct";
        try {
          const hostname = new URL(ref).hostname;
          if (hostname.includes("google")) return "organic_search";
          if (hostname.includes("bing") || hostname.includes("yahoo") || hostname.includes("duckduckgo")) return "organic_search";
          if (hostname.includes("facebook") || hostname.includes("instagram") || hostname.includes("tiktok") || hostname.includes("twitter") || hostname.includes("linkedin")) return "social";
          if (hostname.includes("nooks-sg")) return "internal";
          return "referral";
        } catch(e) { return "direct"; }
      };

      // Track a page view
      const trackPageView = (page) => {
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const visitorId = getVisitorId();
        const session = getOrCreateSession();
        session.pageCount++;
        updateSession(session);

        const source = session.pageCount === 1 ? getTrafficSource() : null; // Only track source on first page of session
        const isNewVisitor = session.isNew && session.pageCount === 1;

        // Mark as returning visitor for future sessions
        localStorage.setItem(VISITOR_KEY + "_returning", "1");

        // Write to Firestore analytics_daily
        const dayRef = db.collection("analytics_daily").doc(today);
        db.runTransaction(async (t) => {
          const doc = await t.get(dayRef);
          if (doc.exists) {
            const data = doc.data();
            const visitors = data.visitors || [];
            const sessions = data.sessions || [];
            const isUniqueVisitor = !visitors.includes(visitorId);
            const isNewSession = !sessions.includes(session.id);

            const updates = {
              pageViews: (data.pageViews || 0) + 1,
            };
            if (isUniqueVisitor) {
              updates.visitors = firebase.firestore.FieldValue.arrayUnion(visitorId);
              updates.uniqueVisitors = (data.uniqueVisitors || 0) + 1;
            }
            if (isNewSession) {
              updates.sessions = firebase.firestore.FieldValue.arrayUnion(session.id);
              updates.totalSessions = (data.totalSessions || 0) + 1;
              if (isNewVisitor) updates.newVisitors = (data.newVisitors || 0) + 1;
              else updates.returningVisitors = (data.returningVisitors || 0) + 1;
              if (source && source !== "internal") {
                updates[`sources.${source}`] = (data.sources?.[source] || 0) + 1;
              }
            }
            t.update(dayRef, updates);
          } else {
            t.set(dayRef, {
              date: today,
              pageViews: 1,
              uniqueVisitors: 1,
              visitors: [visitorId],
              totalSessions: 1,
              sessions: [session.id],
              newVisitors: isNewVisitor ? 1 : 0,
              returningVisitors: isNewVisitor ? 0 : 1,
              sources: source && source !== "internal" ? { [source]: 1 } : {},
              totalDuration: 0,
              bounces: 0,
              totalPagesPerSession: 0,
              engagedSessions: 0
            });
          }
        }).catch(err => console.warn("Analytics write failed:", err));
      };

      // Track session end (duration, bounce, engagement)
      const trackSessionEnd = () => {
        let session = null;
        try { session = JSON.parse(sessionStorage.getItem(SESSION_KEY)); } catch(e) {}
        if (!session) return;

        const duration = Math.round((Date.now() - session.startedAt) / 1000); // seconds
        const isBounce = session.pageCount <= 1;
        const isEngaged = duration > 10 || session.pageCount >= 2;
        const today = new Date(session.startedAt).toISOString().slice(0, 10);

        const dayRef = db.collection("analytics_daily").doc(today);
        dayRef.update({
          totalDuration: firebase.firestore.FieldValue.increment(duration),
          bounces: firebase.firestore.FieldValue.increment(isBounce ? 1 : 0),
          totalPagesPerSession: firebase.firestore.FieldValue.increment(session.pageCount),
          engagedSessions: firebase.firestore.FieldValue.increment(isEngaged ? 1 : 0)
        }).catch(() => {});
      };

      // Auto-track session end on page unload
      window.addEventListener("beforeunload", trackSessionEnd);
      // Also track with visibilitychange for mobile
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") trackSessionEnd();
      });

      return { trackPageView };
    })();

    // Theme
    const THEME = {
      bg: "#FDFAF7",
      card: "#FFFFFF",
      green: "#c87606",
      greenLight: "#FCEADE",
      text: "#1A1A1A",
      muted: "#6B6B6B",
      light: "#9E9E9E",
      border: "#EBEBEB",
      cream: "#F5F3EF",
      serif: "'Georgia','Times New Roman',serif",
      sans: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif"
    };

    // Simplified Stylized SVG Icons
    const svgIcon = (path, vb="0 0 24 24") => `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="${vb}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
    // Non-category icons only. Category icons live inline in CATEGORIES
    // below and are merged into this object after declaration — so adding
    // a new category is a SINGLE-LINE change in the CATEGORIES array.
    const ICONS = {
      home: svgIcon('<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>'),
      photo: svgIcon('<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>'),
      search: svgIcon('<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>'),
      heart: svgIcon('<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>'),
      heartFill: svgIcon('<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor"/>'),
      pin: svgIcon('<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>'),
      star: svgIcon('<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="currentColor"/>'),
      settings: svgIcon('<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>'),
      store: svgIcon('<path d="M3 9l1-4h16l1 4"/><path d="M3 9v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9"/><path d="M9 21V12h6v9"/>'),
      book: svgIcon('<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>'),
      logout: svgIcon('<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>'),
      phone: svgIcon('<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>'),
      chat: svgIcon('<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>'),
      share: svgIcon('<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>'),
      user: svgIcon('<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>'),
      arrowLeft: svgIcon('<line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>'),
      google: svgIcon('<path d="M21.35 11.1h-9.18v2.95h5.27c-.23 1.2-.92 2.22-1.96 2.9l3.16 2.45c1.84-1.7 2.9-4.2 2.9-7.17 0-.57-.05-1.12-.14-1.65z" fill="#4285F4"/><path d="M12.17 22c2.63 0 4.84-.87 6.45-2.37l-3.16-2.45c-.87.58-1.98.93-3.29.93-2.53 0-4.67-1.71-5.44-4.01l-3.26 2.52A9.98 9.98 0 0 0 12.17 22z" fill="#34A853"/><path d="M6.73 14.1a6.02 6.02 0 0 1 0-3.82l-3.26-2.52a9.98 9.98 0 0 0 0 8.86l3.26-2.52z" fill="#FBBC05"/><path d="M12.17 5.98c1.43 0 2.71.49 3.72 1.46l2.78-2.78C16.99 3 14.8 2 12.17 2 8.48 2 5.29 4.03 3.47 7.04l3.26 2.52c.77-2.3 2.91-3.58 5.44-3.58z" fill="#EA4335"/>'),
      facebook: svgIcon('<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" fill="currentColor"/>'),
      email: svgIcon('<rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,4 12,13 2,4"/>'),
      clock: svgIcon('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>'),
      calendar: svgIcon('<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>'),
      send: svgIcon('<line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>'),
      shield: svgIcon('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>'),
      check: svgIcon('<polyline points="20 6 9 17 4 12"/>'),
      alert: svgIcon('<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>'),
      helpCircle: svgIcon('<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>'),
      bag: svgIcon('<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>'),
      brush: svgIcon('<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>'),
      cart: svgIcon('<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>'),
      package: svgIcon('<line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>'),
      tool: svgIcon('<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>'),
      tag: svgIcon('<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>'),
    };

    // Icon component
    function Icon({ name, size = 16, color, style = {} }) {
      const iconSvg = ICONS[name] || "";
      return <span style={{ display: "inline-flex", alignItems: "center", color: color || "inherit", width: size, height: size, ...style }} dangerouslySetInnerHTML={{ __html: iconSvg.replace(/width="\d+"/, `width="${size}"`).replace(/height="\d+"/, `height="${size}"`) }} />;
    }

    // Categories — SINGLE SOURCE OF TRUTH is js/nooks-categories.js
    // (exposed as window.NOOKS_CATEGORIES). We map it into the shape
    // this React app expects. To add a new category, edit
    // js/nooks-categories.js only — nothing here needs to change.
    if (!window.NOOKS_CATEGORIES) {
      console.error('[index.html] window.NOOKS_CATEGORIES not loaded — js/nooks-categories.js must be included before the React bundle.');
    }
    const CATEGORIES = (window.NOOKS_CATEGORIES || []).map(c => ({
      id: c.id,
      label: c.label,
      color: c.color,
      svg: c.svg,
      iconName: c.id,
    }));
    // Merge category SVGs into ICONS (so <Icon name={cat.iconName} /> works).
    // "all" reuses the existing ICONS.home rendering for backward-compat.
    CATEGORIES.forEach(c => {
      if (c.id === "all") { c.iconName = "home"; return; }
      if (c.svg && !ICONS[c.id]) ICONS[c.id] = svgIcon(c.svg);
    });

    // Live businesses loaded from Firestore (see App component)

    // Utility Functions

    // Anonymous name rotation for wishes — randomly assigned per wish
    var WISH_ANON_NAMES = [
      "A neighbour",
      "A nookee",
      "A friend near you",
      "A girl next door",
      "A guy next door",
      "Someone around the corner",
      "Your fellow nookie",
      "A nearby homie",
      "A local whisperer",
      "A wishful thinker",
      "The one down the street",
      "A stone's throw away"
    ];
    function getRandomAnonName() {
      return WISH_ANON_NAMES[Math.floor(Math.random() * WISH_ANON_NAMES.length)];
    }
    // Deterministic name from wish id (so the same wish always shows the same name)
    function getAnonNameForWish(wishId) {
      if (!wishId) return getRandomAnonName();
      var hash = 0;
      for (var i = 0; i < wishId.length; i++) {
        hash = ((hash << 5) - hash) + wishId.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit int
      }
      return WISH_ANON_NAMES[Math.abs(hash) % WISH_ANON_NAMES.length];
    }

    // SVG comic speech bubble shapes — hand-drawn style inspired by comic art (no starburst)
    var WISH_BUBBLE_SVGS = [
      // 0: Oval with tail bottom-left
      '<svg class="wish-svg-bg" viewBox="0 0 220 85" preserveAspectRatio="none"><path d="M110,5 C155,3 200,8 208,25 C216,42 210,55 195,60 C175,66 140,67 110,66 C80,67 50,65 38,63 L22,82 L32,62 C18,60 6,52 4,38 C1,20 35,6 70,4 C85,3 95,4 110,5Z" fill="white" stroke="STROKECOLOR" stroke-width="3.5" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      // 1: Rounded rect with chunky tail bottom-left
      '<svg class="wish-svg-bg" viewBox="0 0 220 88" preserveAspectRatio="none"><path d="M18,6 L195,4 C208,4 214,12 214,22 L215,50 C215,60 208,66 196,66 L60,66 L22,86 L38,66 L22,66 C12,66 5,58 5,48 L6,22 C6,12 12,6 18,6Z" fill="white" stroke="STROKECOLOR" stroke-width="3.5" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      // 2: Cloud/thought bubble with dot trail
      '<svg class="wish-svg-bg" viewBox="0 0 220 92" preserveAspectRatio="none"><path d="M40,12 C45,4 58,2 68,5 C75,0 88,0 98,5 C108,1 120,1 130,5 C140,0 155,2 165,8 C178,4 192,10 198,22 C205,32 200,45 192,52 C198,60 192,66 180,66 C172,70 158,68 148,64 C138,70 122,70 112,66 C100,72 85,70 76,66 C62,70 48,68 40,62 C28,64 16,56 12,44 C6,32 14,18 28,14 C30,12 35,10 40,12Z" fill="white" stroke="STROKECOLOR" stroke-width="3" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/><circle cx="42" cy="74" r="6.5" fill="white" stroke="STROKECOLOR" stroke-width="2.8" vector-effect="non-scaling-stroke"/><circle cx="28" cy="85" r="4" fill="white" stroke="STROKECOLOR" stroke-width="2.5" vector-effect="non-scaling-stroke"/></svg>',
      // 3: Wide oval with tail bottom-right
      '<svg class="wish-svg-bg" viewBox="0 0 220 82" preserveAspectRatio="none"><path d="M110,6 C160,2 210,14 214,36 C218,55 200,65 180,66 L188,80 L172,65 C155,68 130,68 110,67 C70,68 20,60 8,40 C-2,22 40,4 80,4 C90,3 100,4 110,6Z" fill="white" stroke="STROKECOLOR" stroke-width="3.5" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      // 4: Rect with center tail
      '<svg class="wish-svg-bg" viewBox="0 0 220 88" preserveAspectRatio="none"><path d="M25,8 L198,4 C210,4 216,14 214,26 L210,50 C208,60 200,66 190,66 L125,67 L110,86 L100,67 L28,66 C16,66 8,58 8,46 L10,24 C10,14 16,8 25,8Z" fill="white" stroke="STROKECOLOR" stroke-width="3.5" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      // 5: Bean/organic with tail left
      '<svg class="wish-svg-bg" viewBox="0 0 220 85" preserveAspectRatio="none"><path d="M80,6 C120,0 180,4 200,18 C218,32 215,52 195,62 C178,70 145,68 120,66 C95,68 65,68 45,64 L18,82 L30,60 C12,52 2,38 8,22 C14,8 45,4 80,6Z" fill="white" stroke="STROKECOLOR" stroke-width="3.5" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      // 6: Wide pill with small tail
      '<svg class="wish-svg-bg" viewBox="0 0 220 82" preserveAspectRatio="none"><path d="M35,6 C55,2 170,2 190,6 C210,12 218,28 215,40 C212,54 200,64 185,66 C170,68 160,66 150,65 L145,78 L136,64 C120,66 80,68 55,66 C30,64 10,54 6,40 C2,26 12,12 35,6Z" fill="white" stroke="STROKECOLOR" stroke-width="3.5" vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    ];
    function getBubbleSvgForWish(wishId, strokeColor) {
      if (!wishId) return WISH_BUBBLE_SVGS[0].replace(/STROKECOLOR/g, strokeColor || '#3d3529');
      var hash = 0;
      for (var i = 0; i < wishId.length; i++) {
        hash = ((hash << 7) - hash) + wishId.charCodeAt(i);
        hash = hash & hash;
      }
      var idx = Math.abs(hash) % WISH_BUBBLE_SVGS.length;
      return WISH_BUBBLE_SVGS[idx].replace(/STROKECOLOR/g, strokeColor || '#3d3529');
    }

    // Escape user-generated text before injecting into raw HTML strings
    // (prevents XSS in Leaflet popup HTML which is built via string concatenation)
    function escapeHtml(str) {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    const getInitials = (name) => {
      return name
        .split(" ")
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    };

    const DAYS_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const isBusinessOpen = (hours) => {
      const now = new Date();
      const dayIndex = now.getDay();
      const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const dayName = dayNames[dayIndex];
      
      const todayHours = hours.find(h => h.day === dayName);
      if (!todayHours || todayHours.hours === "Closed") return false;
      
      const [start, end] = todayHours.hours.split(" - ");
      const startTime = new Date(`2000-01-01 ${start}`);
      const endTime = new Date(`2000-01-01 ${end}`);
      const currentTime = new Date(`2000-01-01 ${now.toLocaleTimeString('en-US', { hour12: true })}`);
      
      return currentTime >= startTime && currentTime <= endTime;
    };

    const getWeeksOn = (createdAt) => {
      const now = new Date();
      const created = new Date(createdAt);
      const weeks = Math.floor((now - created) / (1000 * 60 * 60 * 24 * 7));
      return weeks > 0 ? weeks : 0;
    };

    // LocalStorage Utilities
    const useLocalStorage = (key, initialValue) => {
      const [value, setValue] = useState(() => {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      });

      const setStoredValue = (v) => {
        const valueToStore = v instanceof Function ? v(value) : v;
        setValue(valueToStore);
        localStorage.setItem(key, JSON.stringify(valueToStore));
      };

      return [value, setStoredValue];
    };

    // DetailMap Component — interactive Leaflet map for business detail page
    function DetailMap({ lat, lng, name }) {
      const mapRef = useRef(null);
      const mapInstanceRef = useRef(null);

      useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        const map = L.map(mapRef.current, {
          scrollWheelZoom: false,
          zoomControl: true,
          dragging: true,
          attributionControl: false
        }).setView([lat, lng], 16);

        L.tileLayer(CARTO_TILE_URL, {
          attribution: CARTO_ATTRIBUTION,
          maxZoom: 19
        }).addTo(map);

        const icon = L.divIcon({
          className: "detail-map-marker",
          html: '<div style="background:#c87606;width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>',
          iconSize: [32, 32],
          iconAnchor: [16, 32]
        });

        L.marker([lat, lng], { icon }).addTo(map)
          .bindPopup('<strong>' + name + '</strong>', { offset: [0, -28] });

        // Locate Me — Leaflet custom control (bottomright, avoids all z-index/React DOM issues)
        var userMarker = null;
        var LocateCtrl = L.Control.extend({
          options: { position: 'bottomright' },
          onAdd: function(m) {
            var btn = L.DomUtil.create('div', 'nk-locate-ctrl');
            btn.title = 'Show my location';
            btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="#c87606" stroke="white" stroke-width="1.2" style="display:block"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>';
            btn.style.cssText = 'cursor:pointer;background:white;border-radius:8px;padding:4px;box-shadow:0 1px 5px rgba(0,0,0,0.22);line-height:0;transition:opacity 0.2s';
            L.DomEvent.disableClickPropagation(btn);
            L.DomEvent.on(btn, 'click', function() {
              if (!navigator.geolocation) { btn.title = 'Geolocation not supported'; return; }
              btn.style.opacity = '0.5';
              navigator.geolocation.getCurrentPosition(
                function(pos) {
                  btn.style.opacity = '1';
                  var uLat = pos.coords.latitude, uLng = pos.coords.longitude;
                  if (userMarker) { userMarker.remove(); userMarker = null; }
                  var uIcon = L.divIcon({
                    className: '',
                    html: '<div style="width:14px;height:14px;background:#2563eb;border-radius:50%;border:2.5px solid white;box-shadow:0 0 0 5px rgba(37,99,235,0.22)"></div>',
                    iconSize: [14, 14],
                    iconAnchor: [7, 7]
                  });
                  userMarker = L.marker([uLat, uLng], { icon: uIcon })
                    .addTo(m).bindPopup('You are here');
                  m.fitBounds(L.latLngBounds([[lat, lng], [uLat, uLng]]), { padding: [50, 50], maxZoom: 17 });
                },
                function() {
                  btn.style.opacity = '1';
                  btn.title = 'Could not get your location — please allow access';
                },
                { enableHighAccuracy: true, timeout: 10000 }
              );
            });
            return btn;
          }
        });
        new LocateCtrl().addTo(map);

        mapInstanceRef.current = map;

        // Fix map rendering in hidden/scrollable containers
        setTimeout(() => map.invalidateSize(), 200);

        return () => {
          map.remove();
          mapInstanceRef.current = null;
        };
      }, [lat, lng, name]);

      return React.createElement('div', {
        ref: mapRef,
        style: {
          width: "100%",
          height: "200px",
          borderRadius: "12px",
          overflow: "hidden",
          marginBottom: "1.5rem",
          border: "1px solid var(--border)"
        }
      });
    }

    // NeighbourhoodHeroMap — LANE 1 hero. Real CartoDB Voyager tiles, frozen (no interactions),
    // auto-centered on user geolocation at zoom 14, with a 500m walking-radius circle and
    // up to 5 nearest business cover-photo pins. Falls back to Singapore centre when no geo.
    function NeighbourhoodHeroMap({ userLocation, liveBusinesses, navigate }) {
      const mapRef = React.useRef(null);
      const mapInstanceRef = React.useRef(null);
      const layersRef = React.useRef({ you: null, circle: null, clusterGroup: null });

      const hasLoc = !!(userLocation && typeof userLocation.lat === "number" && typeof userLocation.lng === "number");

      // Compute nearest 5 for overlay count + tooltip display
      const nearby = React.useMemo(function(){
        if (!hasLoc || !Array.isArray(liveBusinesses)) return [];
        return liveBusinesses
          .filter(function(b){ return b && typeof b.lat === "number" && typeof b.lng === "number"; })
          .map(function(b){
            return Object.assign({}, b, { _dist: haversineDistance(userLocation.lat, userLocation.lng, b.lat, b.lng) });
          })
          .sort(function(a,b){ return a._dist - b._dist; })
          .slice(0, 5);
      }, [hasLoc, userLocation && userLocation.lat, userLocation && userLocation.lng, liveBusinesses]);

      // Initialize Leaflet once on mount
      React.useEffect(function(){
        if (!mapRef.current || mapInstanceRef.current) return;
        if (typeof L === "undefined") return;

        var center = hasLoc ? [userLocation.lat, userLocation.lng] : [1.3521, 103.8198];
        var zoom = hasLoc ? 14 : 11;

        var map = L.map(mapRef.current, {
          center: center,
          zoom: zoom,
          dragging: false,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          boxZoom: false,
          keyboard: false,
          zoomControl: false,
          touchZoom: false,
          tap: false,
          attributionControl: true,
          zoomSnap: 0.25
        });

        L.tileLayer(CARTO_TILE_URL, {
          subdomains: "abcd",
          maxZoom: 19,
          attribution: CARTO_ATTRIBUTION
        }).addTo(map);

        mapInstanceRef.current = map;

        return function cleanup(){
          if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
            layersRef.current = { you: null, circle: null, clusterGroup: null };
          }
        };
      // eslint-disable-next-line
      }, []);

      // Sync layers when location or businesses change
      React.useEffect(function(){
        var map = mapInstanceRef.current;
        if (!map) return;

        // Remove previous dynamic layers
        if (layersRef.current.you) { map.removeLayer(layersRef.current.you); layersRef.current.you = null; }
        if (layersRef.current.circle) { map.removeLayer(layersRef.current.circle); layersRef.current.circle = null; }
        if (layersRef.current.clusterGroup) { map.removeLayer(layersRef.current.clusterGroup); layersRef.current.clusterGroup = null; }

        if (!hasLoc) {
          map.setView([1.3521, 103.8198], 11);
          return;
        }

        map.setView([userLocation.lat, userLocation.lng], 14);

        // Walking radius circle (500m) — matches mission: "right around the corner"
        layersRef.current.circle = L.circle([userLocation.lat, userLocation.lng], {
          radius: 500,
          color: "#c87606",
          weight: 1.5,
          dashArray: "5,5",
          fillColor: "#c87606",
          fillOpacity: 0.08,
          interactive: false
        }).addTo(map);

        // "You" marker
        var youIcon = L.divIcon({
          className: "nhm-you-icon",
          html: '<div class="nhm-you-dot"></div><div class="nhm-you-pill">You</div>',
          iconSize: [14, 14],
          iconAnchor: [7, 7]
        });
        layersRef.current.you = L.marker([userLocation.lat, userLocation.lng], {
          icon: youIcon,
          interactive: false,
          keyboard: false,
          zIndexOffset: 1000
        }).addTo(map);

        // Business pins wrapped in a MarkerClusterGroup so pins that share a block/building
        // collapse into a branded orange cluster bubble. Click the bubble → pins spiderfy
        // outward so users can tap individuals. zoomToBoundsOnClick is disabled because
        // the hero map is intentionally frozen.
        var clusterGroup = L.markerClusterGroup({
          maxClusterRadius: 35, // ~280m at zoom 14 — only clusters pins in the same immediate area
          spiderfyDistanceMultiplier: 1.5,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: false, // map is frozen; we handle click ourselves
          spiderfyOnMaxZoom: false,   // not reliable when tile maxZoom (19) > map zoom (14); use explicit handler
          animate: true,
          animateAddingMarkers: false,
          iconCreateFunction: function(cluster){
            var count = cluster.getChildCount();
            return L.divIcon({
              className: "nhm-cluster-wrap",
              html: '<div class="nhm-cluster-chip"><span>' + count + '</span></div>',
              iconSize: [42, 42],
              iconAnchor: [21, 21]
            });
          }
        });

        // Frozen hero → always spiderfy on cluster click regardless of zoom state.
        // This is the single source of truth for "what happens when you click a cluster."
        clusterGroup.on("clusterclick", function(a){
          a.layer.spiderfy();
        });

        nearby.forEach(function(b){
          var imgSrc = b.coverPhotoUrl || b.cover_image || "";
          var html = '<div class="nhm-pin-chip">' +
            (imgSrc ? '<img src="' + imgSrc + '" alt="" />' : '') +
            '</div>';
          var icon = L.divIcon({
            className: "nhm-pin-wrap",
            html: html,
            iconSize: [40, 40],
            iconAnchor: [20, 20]
          });
          var marker = L.marker([b.lat, b.lng], { icon: icon, riseOnHover: true, keyboard: true });
          marker.on("click", function(){
            if (typeof navigate === "function") navigate("detail/" + (b.slug || b.id));
          });
          marker.bindTooltip((b.name || "Home business") + " · " + b._dist.toFixed(1) + " km", {
            direction: "top",
            offset: [0, -18],
            className: "nhm-pin-tip",
            opacity: 1
          });
          clusterGroup.addLayer(marker);
        });

        clusterGroup.addTo(map);
        layersRef.current.clusterGroup = clusterGroup;
      }, [hasLoc, userLocation && userLocation.lat, userLocation && userLocation.lng, nearby]);

      return (
        <div className="nooks-hero-map" aria-label="Neighbourhood map showing nearby home businesses">
          <style>{`
            .nooks-hero-map{position:relative;max-width:720px;margin:0 auto 10px;aspect-ratio:18/7;min-height:220px;border-radius:20px;border:1px solid #E4D3BF;overflow:hidden;box-shadow:0 10px 32px rgba(192,97,58,.10);background:#F3DFCC}
            .nooks-hero-map .nhm-leaflet{position:absolute;inset:0;z-index:1}
            .nooks-hero-map .leaflet-container{background:#F3DFCC;font-family:inherit}
            .nooks-hero-map .leaflet-control-attribution{background:rgba(255,255,255,.85);font-size:9px;padding:1px 6px;color:#6B6B6B}
            .nooks-hero-map .leaflet-control-attribution a{color:#c87606}
            .nhm-you-icon{background:transparent!important;border:none!important}
            .nhm-you-dot{width:14px;height:14px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 4px rgba(37,99,235,.25);position:absolute;left:0;top:0}
            .nhm-you-pill{position:absolute;top:18px;left:50%;transform:translateX(-50%);background:#2563eb;color:#fff;font-size:10px;padding:3px 8px;border-radius:4px;font-weight:600;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.2)}
            .nhm-pin-wrap{background:transparent!important;border:none!important}
            .nhm-pin-chip{width:40px;height:40px;border-radius:50%;background:#f0ece7;border:3px solid #c87606;overflow:hidden;box-shadow:inset 0 0 0 1px rgba(0,0,0,.12),0 4px 12px rgba(192,97,58,.35);cursor:pointer;transition:transform .2s;position:relative;animation:nhmPulse 2.4s infinite}
            .nhm-pin-chip:hover{transform:scale(1.15);z-index:9999}
            .nhm-pin-chip img{width:100%;height:100%;object-fit:cover;display:block;position:relative;z-index:1}
            @keyframes nhmPulse{0%,100%{box-shadow:inset 0 0 0 1px rgba(0,0,0,.12),0 4px 12px rgba(192,97,58,.35),0 0 0 0 rgba(192,97,58,.45)}50%{box-shadow:inset 0 0 0 1px rgba(0,0,0,.12),0 4px 12px rgba(192,97,58,.35),0 0 0 10px rgba(192,97,58,0)}}
            .leaflet-tooltip.nhm-pin-tip{background:#1A1A1A!important;color:#fff!important;border:none!important;font-size:11px;padding:5px 9px;border-radius:5px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,.25);white-space:nowrap}
            .leaflet-tooltip.nhm-pin-tip::before{border-top-color:#1A1A1A!important}
            .nhm-cluster-wrap{background:transparent!important;border:none!important}
            .nhm-cluster-chip{width:42px;height:42px;border-radius:50%;background:linear-gradient(135deg,#c87606 0%,#a85c04 100%);border:3px solid #fff;box-shadow:0 4px 14px rgba(192,97,58,.5),inset 0 0 0 1px rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform .2s;animation:nhmPulse 2.4s infinite}
            .nhm-cluster-chip:hover{transform:scale(1.12)}
            .nhm-cluster-chip span{color:#fff;font-size:15px;font-weight:700;font-family:inherit;text-shadow:0 1px 2px rgba(0,0,0,.3);line-height:1}
            .nooks-hero-map .leaflet-cluster-anim .leaflet-marker-icon,.nooks-hero-map .leaflet-cluster-anim .leaflet-marker-shadow{transition:transform .3s ease-out,opacity .3s ease-in}
            .nooks-hero-map .leaflet-cluster-spider-leg{stroke:#c87606!important;stroke-opacity:.6!important;stroke-width:1.5!important;stroke-dasharray:3,3!important}
            .nhm-overlay{position:absolute;top:10px;left:50%;transform:translateX(-50%);background:rgba(255,255,255,.96);backdrop-filter:blur(6px);border:1px solid #E4D3BF;border-radius:999px;padding:5px 13px;font-size:11px;color:#6B6B6B;box-shadow:0 3px 10px rgba(0,0,0,.08);z-index:500;max-width:90%;text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;pointer-events:none}
            .nhm-overlay strong{color:#c87606}
            @media (max-width:640px){
              .nooks-hero-map{aspect-ratio:4/3;min-height:220px;max-width:92%;border-radius:16px;margin-bottom:14px}
              .nhm-pin-chip{width:36px;height:36px}
            }
            @media (prefers-reduced-motion: reduce){.nhm-pin-chip{animation:none}}
          `}</style>
          <div ref={mapRef} className="nhm-leaflet"></div>
          {!hasLoc && (<div className="nhm-overlay">📍 <strong>Enable location</strong> to see what's near you</div>)}
          {hasLoc && nearby.length === 0 && (<div className="nhm-overlay">Loading nearby businesses…</div>)}
          {hasLoc && nearby.length > 0 && (<div className="nhm-overlay"><strong>{nearby.length}</strong> home {nearby.length === 1 ? "business" : "businesses"} within walking distance</div>)}
        </div>
      );
    }

    // BookingModal Component
    function BookingModal({ isOpen, onClose, item, business, currentUser, onConfirm, db }) {
      const [selectedDate, setSelectedDate] = useState(null);
      const [selectedTime, setSelectedTime] = useState(null);
      const [notes, setNotes] = useState("");
      const [customerName, setCustomerName] = useState(currentUser?.displayName || "");
      const [customerPhone, setCustomerPhone] = useState(currentUser?.phoneNumber || "");
      const [submitting, setSubmitting] = useState(false);

      const generateDateSlots = () => {
        const today = new Date();
        const slots = [];
        for (let i = 0; i < 14; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() + i);
          slots.push(d);
        }
        return slots;
      };

      const generateTimeSlots = (date) => {
        if (!business?.hours) return [];
        const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const dayName = dayNames[date.getDay()];
        const dayHours = business.hours[dayName];

        if (!dayHours || !dayHours.enabled) return [];

        const startStr = dayHours.open || "09:00";
        const endStr = dayHours.close || "18:00";
        const [startH, startM] = startStr.split(":").map(Number);
        const [endH, endM] = endStr.split(":").map(Number);

        const slots = [];
        for (let h = startH; h < endH; h++) {
          slots.push(new Date(2000, 0, 1, h, 0));
        }
        return slots;
      };

      const isTimeInPast = (date, time) => {
        const now = new Date();
        // Normalize both dates to midnight for date-only comparison
        const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const todayAtMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        // Future dates are never in the past
        if (dateAtMidnight.getTime() > todayAtMidnight.getTime()) return false;
        // Past dates are always in the past
        if (dateAtMidnight.getTime() < todayAtMidnight.getTime()) return true;
        // Same day: compare the time slot against current time
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes()) <= now;
      };

      const handleConfirm = async () => {
        if (!selectedDate || !selectedTime || !customerName || !customerPhone) {
          showToast("Please fill in all required fields.", "error");
          return;
        }
        // Prevent booking past date/time
        if (isTimeInPast(selectedDate, selectedTime)) {
          showToast("This time slot has already passed. Please select a future time.", "error");
          setSelectedTime(null);
          return;
        }
        setSubmitting(true);
        try {
          const timeStr = selectedTime.toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit" });
          const dateStr = selectedDate.toLocaleDateString("en-SG");

          await db.collection("bookings").add({
            businessId: business.id,
            businessName: business.name,
            operatorUid: business.ownerId,
            serviceItem: {
              name: item.name,
              price: item.price,
              imageUrl: item.imageUrl || ""
            },
            customerUid: currentUser.uid,
            customerName,
            customerPhone,
            date: dateStr,
            timeSlot: timeStr,
            notes,
            status: "pending",
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
          });

          showToast("Booking confirmed! The business owner will review your request.", "success", 5000);
          onConfirm();
          onClose();
        } catch (err) {
          console.error("Booking error:", err);
          showToast("Error creating booking. Please try again.", "error");
        }
        setSubmitting(false);
      };

      if (!isOpen) return null;

      const dateSlots = generateDateSlots();
      const timeSlots = selectedDate ? generateTimeSlots(selectedDate) : [];

      return (
        <div className="booking-modal-overlay" onClick={onClose}>
          <div className="booking-modal" onClick={e => e.stopPropagation()}>
            <button className="booking-modal-close" onClick={onClose}>×</button>

            <h2 style={{ fontFamily: "var(--serif)", marginBottom: "0.5rem" }}>{item.name}</h2>
            <p style={{ color: "var(--amber)", fontWeight: 600, marginBottom: "1.5rem" }}>S${parseFloat(item.price).toFixed(2)}</p>

            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: "0.5rem" }}>Select Date</label>
              <div className="date-picker-row">
                {dateSlots.map((date, i) => {
                  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                  const dayInfo = business.hours?.[dayNames[date.getDay()]];
                  const isClosed = !dayInfo || !dayInfo.enabled;
                  return (
                    <button
                      key={i}
                      className={`date-item ${selectedDate?.toDateString() === date.toDateString() ? 'selected' : ''} ${new Date().toDateString() === date.toDateString() ? 'today' : ''} ${isClosed ? 'disabled' : ''}`}
                      onClick={() => { if (!isClosed) { setSelectedDate(date); setSelectedTime(null); } }}
                      disabled={isClosed}
                    >
                      <div>{date.toLocaleDateString("en-SG", { weekday: "short" })}</div>
                      <div style={{ fontSize: "0.75rem", marginTop: "0.2rem" }}>{date.getDate()}</div>
                      {isClosed && <div style={{ fontSize: "0.55rem", color: "#999" }}>Closed</div>}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedDate && (
              <div style={{ marginBottom: "1.5rem" }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: "0.75rem" }}>Select Time</label>
                {timeSlots.length > 0 ? (
                  <div className="time-slots">
                    {timeSlots.map((time, i) => {
                      const timeStr = time.toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit" });
                      const isPast = isTimeInPast(selectedDate, time);
                      return (
                        <button
                          key={i}
                          className={`time-slot ${isPast ? 'disabled' : ''} ${selectedTime?.getTime() === time.getTime() ? 'selected' : ''}`}
                          onClick={() => !isPast && setSelectedTime(time)}
                          disabled={isPast}
                        >
                          {timeStr}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: "var(--muted)" }}>No available slots for this date</p>
                )}
              </div>
            )}

            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: "0.5rem" }}>Your Name</label>
              <input
                type="text"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                className="form-input"
                placeholder="Enter your name"
              />
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: "0.5rem" }}>Your Phone</label>
              <input
                type="tel"
                value={customerPhone}
                onChange={e => setCustomerPhone(e.target.value)}
                className="form-input"
                placeholder="Enter your phone number"
              />
            </div>

            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: "0.5rem" }}>Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="form-textarea"
                placeholder="Add any special requests or notes"
                style={{ minHeight: "80px" }}
              />
            </div>

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button onClick={onClose} className="modal-cancel-btn" style={{ flex: 1 }}>Cancel</button>
              <button onClick={handleConfirm} className="modal-submit-btn" style={{ flex: 1 }} disabled={submitting}>
                {submitting ? "Confirming..." : "Confirm Booking"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Unified BookingsPage Component — follows MessagesPage tab pattern
    // Merges operator bookings + customer bookings into one page
    function BookingsPage({ currentUser, userBusinesses, db, navigate }) {
      const [operatorBookings, setOperatorBookings] = useState([]);
      const [customerBookings, setCustomerBookings] = useState([]);
      const [loading, setLoading] = useState(true);
      const [activeTab, setActiveTab] = useState("all");
      const [cancellingId, setCancellingId] = useState(null);

      // Build set of business IDs user owns (same pattern as MessagesPage)
      var bizArr = userBusinesses || [];
      var myBizIds = new Set(bizArr.map(function(b) { return b.id; }));
      var hasBusinesses = bizArr.length > 0;

      // Listener 1: Operator bookings (bookings received by user's businesses)
      useEffect(() => {
        if (!currentUser || !hasBusinesses) return;
        var bizIds = bizArr.map(function(b) { return b.id; });
        var chunks = [];
        for (var i = 0; i < bizIds.length; i += 30) {
          chunks.push(bizIds.slice(i, i + 30));
        }
        var unsubscribes = [];
        var allResults = {};
        chunks.forEach(function(chunk) {
          var unsub = db.collection("bookings")
            .where("businessId", "in", chunk)
            .onSnapshot(async function(snap) {
              snap.docs.forEach(function(d) {
                allResults[d.id] = { id: d.id, ...d.data(), _role: "operator" };
              });
              var allBookings = Object.values(allResults);
              // Check review status for completed bookings
              var completedBizIds = [...new Set(allBookings.filter(function(b) { return b.status === "completed"; }).map(function(b) { return b.businessId; }))];
              if (completedBizIds.length > 0) {
                try {
                  for (var bi = 0; bi < completedBizIds.length; bi++) {
                    var revSnap = await db.collection("reviews").where("bizId", "==", completedBizIds[bi]).get();
                    var reviewedBookingIds = new Set(revSnap.docs.map(function(d) { return d.data().bookingId; }).filter(Boolean));
                    allBookings.forEach(function(b) {
                      if (b.status === "completed" && b.businessId === completedBizIds[bi]) {
                        b.hasReview = reviewedBookingIds.has(b.id);
                      }
                    });
                  }
                } catch(e) { console.warn("Could not check reviews:", e); }
              }
              setOperatorBookings(allBookings);
              setLoading(false);
            }, function(err) { console.error("Operator bookings listener error:", err); setLoading(false); });
          unsubscribes.push(unsub);
        });
        return function() { unsubscribes.forEach(function(u) { u(); }); };
      }, [currentUser, userBusinesses, db]);

      // Listener 2: Customer bookings (bookings the user made at other businesses)
      useEffect(() => {
        if (!currentUser) { setLoading(false); return; }
        var unsub = db.collection("bookings")
          .where("customerUid", "==", currentUser.uid)
          .onSnapshot(async function(snap) {
            var results = snap.docs.map(function(d) { return { id: d.id, ...d.data(), _role: "customer" }; });
            results.sort(function(a, b) {
              var aTime = a.createdAt ? a.createdAt.toMillis() : 0;
              var bTime = b.createdAt ? b.createdAt.toMillis() : 0;
              return bTime - aTime;
            });
            // Check review status
            if (results.some(function(b) { return b.status === "completed"; })) {
              try {
                var reviewSnap = await db.collection("reviews").where("customerUid", "==", currentUser.uid).get();
                var reviewedBookingIds = new Set(reviewSnap.docs.map(function(d) { return d.data().bookingId; }).filter(Boolean));
                results.forEach(function(b) {
                  if (b.status === "completed") b.hasReview = reviewedBookingIds.has(b.id);
                });
              } catch(e) { console.warn("Could not check reviews:", e); }
            }
            setCustomerBookings(results);
            setLoading(false);
          }, function(err) { console.error("Customer bookings listener error:", err); setLoading(false); });
        return function() { unsub(); };
      }, [currentUser, db]);

      if (loading) return <div style={{ padding: "2rem", textAlign: "center" }}>Loading bookings...</div>;

      // Merge & deduplicate: operator bookings that are also customer bookings (user booked at own business)
      // Prefer operator role for those, but show customer-only bookings with customer role
      var allBookings = [];
      var seenIds = new Set();
      operatorBookings.forEach(function(b) { seenIds.add(b.id); allBookings.push(b); });
      customerBookings.forEach(function(b) {
        if (!seenIds.has(b.id)) { allBookings.push(b); seenIds.add(b.id); }
      });
      // Sort newest first
      allBookings.sort(function(a, b) {
        var aTime = a.createdAt ? (a.createdAt.seconds || 0) : 0;
        var bTime = b.createdAt ? (b.createdAt.seconds || 0) : 0;
        return bTime - aTime;
      });

      // Determine role per booking: operator if businessId is in myBizIds, else customer
      allBookings.forEach(function(b) {
        b._role = myBizIds.has(b.businessId) ? "operator" : "customer";
      });

      // Build tabs — same pattern as MessagesPage
      var customerOnlyBookings = allBookings.filter(function(b) { return b._role === "customer"; });
      var bizWithBookings = [];
      if (hasBusinesses) {
        var seen = {};
        allBookings.forEach(function(b) {
          if (myBizIds.has(b.businessId)) seen[b.businessId] = true;
        });
        bizWithBookings = bizArr.filter(function(b) { return seen[b.id]; });
      }

      // Count per tab (pending bookings for operator tabs as "action needed")
      var countPerTab = { all: allBookings.length, personal: customerOnlyBookings.length };
      var pendingPerTab = { all: 0, personal: 0 };
      allBookings.forEach(function(b) {
        if (b.status === "pending") {
          pendingPerTab.all++;
          if (myBizIds.has(b.businessId)) {
            pendingPerTab[b.businessId] = (pendingPerTab[b.businessId] || 0) + 1;
          }
        }
        if (myBizIds.has(b.businessId)) {
          countPerTab[b.businessId] = (countPerTab[b.businessId] || 0) + 1;
        }
      });

      var showTabs = hasBusinesses && bizWithBookings.length > 0;

      // Filter bookings by active tab
      var filteredBookings = allBookings;
      if (activeTab === "personal") {
        filteredBookings = customerOnlyBookings;
      } else if (activeTab !== "all") {
        filteredBookings = allBookings.filter(function(b) { return b.businessId === activeTab; });
      }

      // Cancel handler (customer)
      var handleCancel = async function(bookingId) {
        if (!confirm("Cancel this booking? The store owner will be notified.")) return;
        setCancellingId(bookingId);
        try {
          await db.collection("bookings").doc(bookingId).update({ status: "cancelled", updatedAt: firebase.firestore.FieldValue.serverTimestamp() });
        } catch (e) {
          showToast("Could not cancel booking. Please try again.", "error");
        }
        setCancellingId(null);
      };

      // Message handler (operator)
      var handleMessageCustomer = async function(booking, e) {
        e.target.disabled = true;
        try {
          var customerUid = booking.customerUid;
          var bizId = booking.businessId;
          var exists = await db.collection("conversations")
            .where("participants", "array-contains", currentUser.uid)
            .where("businessId", "==", bizId)
            .get();
          var targetConv = null;
          exists.docs.forEach(function(doc) {
            if (doc.data().participants.includes(customerUid)) targetConv = doc;
          });
          if (targetConv) {
            navigate("chat/" + targetConv.id);
          } else {
            var bookingOpener = "Hi" + (booking.customerName ? " " + booking.customerName.split(" ")[0] : "") + ", following up on your booking" + (booking.serviceName ? " for " + booking.serviceName : "") + (booking.date ? " on " + booking.date : "") + ".";
            var newConv = await db.collection("conversations").add({
              participants: [currentUser.uid, customerUid],
              businessId: bizId,
              businessName: booking.businessName,
              otherParticipantName: booking.customerName,
              shopperName: booking.customerName,
              shopperUid: customerUid,
              lastMessage: bookingOpener,
              lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
              unreadBy: { [currentUser.uid]: 0, [customerUid]: 1 }
            });
            await db.collection("conversations").doc(newConv.id).collection("messages").add({
              text: bookingOpener,
              senderUid: currentUser.uid,
              senderName: currentUser.displayName || booking.businessName || "Store",
              createdAt: firebase.firestore.FieldValue.serverTimestamp()
            });
            navigate("chat/" + newConv.id);
          }
        } catch (err) {
          console.error("Message customer error:", err);
          showToast("Could not open conversation.", "error");
        }
        e.target.disabled = false;
      };

      // Render a single booking card — adapts based on _role
      function renderBookingCard(booking) {
        var isOperator = booking._role === "operator";
        return (
          <div key={booking.id} className="booking-card">
            {/* Role indicator pill */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
              <span style={{
                display: "inline-block",
                padding: "2px 10px",
                borderRadius: "99px",
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
                background: isOperator ? "var(--amber-light)" : "#EDE7F6",
                color: isOperator ? "var(--amber-dark)" : "#5E35B1"
              }}>
                {isOperator ? "RECEIVED" : "MY BOOKING"}
              </span>
            </div>

            <div className="booking-card-header">
              <div>
                {/* Operator sees customer name; customer sees business name */}
                <div style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "0.15rem" }}>
                  {isOperator ? (booking.customerName || "Customer") : (
                    <span
                      onClick={(e) => { e.stopPropagation(); navigate("detail/" + (booking.businessSlug || booking.bizId || booking.businessId)); }}
                      style={{ color: "var(--amber)", cursor: "pointer" }}
                      title={"View " + (booking.businessName || "shop")}
                    >{booking.businessName || "Business"}</span>
                  )}
                </div>
                {!isOperator && (
                  <div style={{ marginBottom: "3px" }}>
                    <span
                      onClick={async (e) => {
                        e.stopPropagation();
                        var node = e.currentTarget;
                        if (!currentUser || (node && node.dataset.busy)) return;
                        if (node) node.dataset.busy = "1";
                        try {
                          var bizId = booking.bizId || booking.businessId;
                          var exists = await db.collection("conversations")
                            .where("participants", "array-contains", currentUser.uid)
                            .where("businessId", "==", bizId)
                            .get();
                          if (exists.docs.length > 0) { navigate("chat/" + exists.docs[0].id); return; }
                          if (!booking.operatorUid) { navigate("detail/" + (booking.businessSlug || bizId)); return; }
                          var opener = "Hi! This is about my booking" + (booking.serviceItem && booking.serviceItem.name ? " for " + booking.serviceItem.name : "") + (booking.date ? " on " + booking.date : "") + ".";
                          var newConv = await db.collection("conversations").add({
                            participants: [currentUser.uid, booking.operatorUid],
                            businessId: bizId,
                            businessName: booking.businessName || "",
                            otherParticipantName: booking.businessName || "",
                            shopperName: currentUser.displayName || "Customer",
                            shopperUid: currentUser.uid,
                            lastMessage: opener,
                            lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
                            unreadBy: { [currentUser.uid]: 0, [booking.operatorUid]: 1 }
                          });
                          await db.collection("conversations").doc(newConv.id).collection("messages").add({
                            text: opener, senderUid: currentUser.uid, senderName: currentUser.displayName || "Customer", createdAt: firebase.firestore.FieldValue.serverTimestamp()
                          });
                          navigate("chat/" + newConv.id);
                        } catch (err) { console.error("Open shop chat error:", err); showToast("Could not open conversation.", "error"); }
                        finally { if (node) node.dataset.busy = ""; }
                      }}
                      style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--amber)", cursor: "pointer" }}
                    >💬 Message shop</span>
                  </div>
                )}
                {/* If on "All" tab and operator, show which business received it */}
                {isOperator && (activeTab === "all" || activeTab === "personal") && (
                  <div style={{ fontSize: "0.75rem", color: "var(--mid)", marginBottom: "2px" }}>
                    for {booking.businessName || "your business"}
                  </div>
                )}
                <div className="booking-service-name">{booking.serviceItem?.name}</div>
              </div>
              <span className={"booking-status-badge booking-status-" + booking.status}>
                {booking.status.toUpperCase()}
              </span>
            </div>

            <div className="booking-details">
              <span>{booking.date}</span>
              <span>{booking.timeSlot}</span>
              <span>S${parseFloat(booking.serviceItem?.price || 0).toFixed(2)}</span>
            </div>

            {booking.notes && <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: "0.75rem" }}>Notes: {booking.notes}</p>}

            {/* --- OPERATOR ACTIONS --- */}
            {isOperator && (
              <>
                {/* Message Customer */}
                <button
                  style={{ display: "flex", alignItems: "center", gap: "0.4rem", background: "none", border: "1px solid var(--amber)", color: "var(--amber)", padding: "0.4rem 0.85rem", borderRadius: "6px", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600, marginBottom: "0.75rem" }}
                  onClick={(e) => handleMessageCustomer(booking, e)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  Message Customer
                </button>

                {booking.status === "pending" && (
                  <div className="booking-actions">
                    <button className="booking-btn booking-btn-accept" onClick={async () => { await db.collection("bookings").doc(booking.id).update({ status: "confirmed", updatedAt: firebase.firestore.FieldValue.serverTimestamp() }); }}>
                      Accept
                    </button>
                    <button className="booking-btn booking-btn-decline" onClick={async () => { await db.collection("bookings").doc(booking.id).update({ status: "declined", updatedAt: firebase.firestore.FieldValue.serverTimestamp() }); }}>
                      Decline
                    </button>
                  </div>
                )}

                {booking.status === "confirmed" && (
                  <button className="booking-btn booking-btn-complete" onClick={async () => { await db.collection("bookings").doc(booking.id).update({ status: "completed", updatedAt: firebase.firestore.FieldValue.serverTimestamp() }); }}>
                    Mark Complete
                  </button>
                )}

                {/* Operator review status */}
                {booking.status === "completed" && booking.hasReview && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "var(--amber-light)", borderRadius: "10px", fontSize: "0.78rem", color: "var(--amber-dark)", fontWeight: 500 }}>
                    ✅ Customer has reviewed this booking
                  </div>
                )}
                {booking.status === "completed" && !booking.hasReview && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "var(--yellow-light)", borderRadius: "10px", border: "1px solid var(--yellow)", fontSize: "0.78rem", color: "var(--yellow-dark)", fontWeight: 500 }}>
                    ⏳ Awaiting customer review
                  </div>
                )}
              </>
            )}

            {/* --- CUSTOMER ACTIONS --- */}
            {!isOperator && (
              <>
                {/* Status messaging */}
                {booking.status === "pending" && <p style={{ fontSize: "0.8rem", color: "#E65100", marginBottom: "0.5rem" }}>Awaiting store owner confirmation...</p>}
                {booking.status === "confirmed" && <p style={{ fontSize: "0.8rem", color: "#2E7D32", marginBottom: "0.5rem" }}>Your booking is confirmed!</p>}
                {booking.status === "declined" && <p style={{ fontSize: "0.8rem", color: "#C62828", marginBottom: "0.5rem" }}>This booking was declined by the store owner.</p>}
                {booking.status === "cancelled" && <p style={{ fontSize: "0.8rem", color: "#999", marginBottom: "0.5rem" }}>You cancelled this booking.</p>}
                {booking.status === "completed" && <p style={{ fontSize: "0.8rem", color: "var(--amber)", marginBottom: "0.5rem" }}>Completed — thank you!</p>}

                {/* Review prompt */}
                {booking.status === "completed" && !booking.hasReview && (
                  <div
                    onClick={(e) => { e.stopPropagation(); navigate("detail/" + (booking.bizId || booking.businessId) + "?review=true"); }}
                    style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", padding: "14px 16px", background: "var(--yellow-light)", borderRadius: "12px", border: "1px solid var(--yellow)", marginBottom: "0.5rem", cursor: "pointer" }}
                  >
                    <span style={{ fontSize: "1.3rem" }}>⭐</span>
                    <div style={{ flex: "1 1 150px" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--ink)" }}>How was your experience?</span>
                      <p style={{ fontSize: "0.75rem", color: "var(--mid)", margin: "2px 0 0" }}>Leave a review for {booking.businessName || "this business"}</p>
                    </div>
                    <span style={{ background: "var(--coral)", color: "#fff", borderRadius: "99px", padding: "7px 16px", fontWeight: 600, fontSize: "0.78rem", fontFamily: "var(--display)", whiteSpace: "nowrap" }}>
                      Write Review →
                    </span>
                  </div>
                )}

                {/* Already reviewed */}
                {booking.status === "completed" && booking.hasReview && (
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "var(--amber-light)", borderRadius: "10px", marginBottom: "0.5rem", fontSize: "0.78rem", color: "var(--amber-dark)", fontWeight: 500 }}>
                    ✅ You've reviewed this booking
                  </div>
                )}

                {/* Cancel button */}
                {(booking.status === "pending" || booking.status === "confirmed") && (
                  <button className="booking-btn booking-btn-decline" style={{ marginTop: "0.5rem" }} disabled={cancellingId === booking.id} onClick={() => handleCancel(booking.id)}>
                    {cancellingId === booking.id ? "Cancelling..." : "Cancel Booking"}
                  </button>
                )}
              </>
            )}
          </div>
        );
      }

      return (
        <div className="messages-container" style={{ padding: "0 1.5rem 2rem" }}>
          <button onClick={() => navigate("explore")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.9rem", padding: "0.5rem 0", marginBottom: "0.25rem" }}>
            <Icon name="arrowLeft" size={16} /> Back
          </button>
          <h1 style={{ fontFamily: "var(--serif)", marginBottom: "1rem" }}>Bookings</h1>

          {/* Tab bar — Messages-style: All | My Bookings | [Business1] | [Business2] */}
          {(showTabs || customerOnlyBookings.length > 0) && (
            <div className="message-filter-tabs">
              <button className={"message-filter-tab" + (activeTab === "all" ? " active" : "")} onClick={() => setActiveTab("all")}>
                All ({allBookings.length}){pendingPerTab.all > 0 && <span className="tab-unread">{pendingPerTab.all}</span>}
              </button>

              {customerOnlyBookings.length > 0 && (
                <button className={"message-filter-tab" + (activeTab === "personal" ? " active" : "")} onClick={() => setActiveTab("personal")}>
                  My Bookings ({customerOnlyBookings.length})
                </button>
              )}

              {bizWithBookings.length > 0 && <span className="tab-separator">|</span>}

              {bizWithBookings.map(function(biz) {
                return (
                  <button key={biz.id} className={"message-filter-tab" + (activeTab === biz.id ? " active" : "")} onClick={() => setActiveTab(biz.id)}>
                    {biz.name} ({countPerTab[biz.id] || 0}){(pendingPerTab[biz.id] || 0) > 0 && <span className="tab-unread">{pendingPerTab[biz.id]}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {filteredBookings.length > 0 ? (
            filteredBookings.map(function(booking) { return renderBookingCard(booking); })
          ) : (
            <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>
              {activeTab === "personal"
                ? "No bookings yet. Browse shops to book a service!"
                : activeTab === "all"
                  ? "No bookings yet."
                  : "No bookings for this business yet."}
            </div>
          )}
        </div>
      );
    }

    // MessagesPage Component
    function MessagesPage({ currentUser, userBusinesses, db, navigate }) {
      const [conversations, setConversations] = useState([]);
      const [loading, setLoading] = useState(true);
      const [activeTab, setActiveTab] = useState("all");

      // Build a Set of business IDs the current user owns (plain vars — no useMemo needed for small lists)
      var bizArr = userBusinesses || [];
      var myBizIds = new Set(bizArr.map(function(b) { return b.id; }));
      var hasBusinesses = bizArr.length > 0;

      useEffect(() => {
        if (!currentUser) {
          setLoading(false);
          return;
        }

        const unsubscribe = db.collection("conversations")
          .where("participants", "array-contains", currentUser.uid)
          .onSnapshot(snap => {
            const convs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Sort client-side: newest message first (no composite index needed)
            convs.sort((a, b) => (b.lastMessageAt?.seconds || 0) - (a.lastMessageAt?.seconds || 0));
            setConversations(convs);
            setLoading(false);
          }, function(err) {
            console.error("Conversations listener error:", err);
            showToast("Could not load messages. Please try again.", "error");
            setLoading(false);
          });

        return () => unsubscribe();
      }, [currentUser, db]);

      if (loading) return <div style={{ padding: "2rem", textAlign: "center" }}>Loading conversations...</div>;

      // Helper: relative timestamp
      function relativeTime(lastMessageAt) {
        if (!lastMessageAt?.seconds) return "";
        const msgDate = new Date(lastMessageAt.seconds * 1000);
        const now = new Date();
        const diffMs = now - msgDate;
        const diffMin = Math.floor(diffMs / 60000);
        const diffHr = Math.floor(diffMs / 3600000);
        const diffDay = Math.floor(diffMs / 86400000);
        if (diffMin < 1) return "Just now";
        if (diffMin < 60) return diffMin + "m ago";
        if (diffHr < 24) return diffHr + "h ago";
        if (diffDay === 1) return "Yesterday";
        if (diffDay < 7) return diffDay + "d ago";
        return msgDate.toLocaleDateString("en-SG");
      }

      // Build tabs: figure out which of the user's businesses actually have conversations
      var bizWithConvs = [];
      if (hasBusinesses) {
        var seen = {};
        conversations.forEach(function(conv) {
          if (myBizIds.has(conv.businessId)) seen[conv.businessId] = true;
        });
        bizWithConvs = bizArr.filter(function(b) { return seen[b.id]; });
      }

      // Count unread per tab
      var unreadPerTab = {};
      (function() {
        unreadPerTab = { all: 0, personal: 0 };
        conversations.forEach(function(conv) {
          var unread = conv.unreadBy && currentUser ? (conv.unreadBy[currentUser.uid] || 0) : 0;
          unreadPerTab.all += unread;
          if (myBizIds.has(conv.businessId)) {
            unreadPerTab[conv.businessId] = (unreadPerTab[conv.businessId] || 0) + unread;
          } else {
            unreadPerTab.personal += unread;
          }
        });
      })();

      // Filter conversations by active tab
      var filteredConversations = conversations;
      if (activeTab === "personal") {
        filteredConversations = conversations.filter(function(c) { return !myBizIds.has(c.businessId); });
      } else if (activeTab !== "all") {
        filteredConversations = conversations.filter(function(c) { return c.businessId === activeTab; });
      }

      // Show tabs only if user owns businesses AND has conversations for them
      var personalConvs = conversations.filter(function(c) { return !myBizIds.has(c.businessId); });
      var showTabs = hasBusinesses && (bizWithConvs.length > 0);

      return (
        <div className="messages-container" style={{ padding: "0 1.5rem 2rem" }}>
          <button onClick={() => navigate("explore")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.9rem", padding: "0.5rem 0", marginBottom: "0.25rem" }}>
            <Icon name="arrowLeft" size={16} /> Back
          </button>
          <h1 style={{ fontFamily: "var(--serif)", marginBottom: "1rem" }}>Messages</h1>

          {showTabs && (
            <div className="message-filter-tabs">
              <button className={"message-filter-tab" + (activeTab === "all" ? " active" : "")} onClick={() => setActiveTab("all")}>
                All{unreadPerTab.all > 0 && <span className="tab-unread">{unreadPerTab.all}</span>}
              </button>
              {personalConvs.length > 0 && (
                <button className={"message-filter-tab" + (activeTab === "personal" ? " active" : "")} onClick={() => setActiveTab("personal")}>
                  Personal{unreadPerTab.personal > 0 && <span className="tab-unread">{unreadPerTab.personal}</span>}
                </button>
              )}
              {bizWithConvs.length > 0 && <span className="tab-separator">|</span>}
              {bizWithConvs.map(biz => (
                <button key={biz.id} className={"message-filter-tab" + (activeTab === biz.id ? " active" : "")} onClick={() => setActiveTab(biz.id)}>
                  {biz.name}{(unreadPerTab[biz.id] || 0) > 0 && <span className="tab-unread">{unreadPerTab[biz.id]}</span>}
                </button>
              ))}
            </div>
          )}

          {filteredConversations.length > 0 ? (
            <div className="conversation-list">
              {filteredConversations.map(conv => {
                const unreadCount = conv.unreadBy?.[currentUser.uid] || 0;
                const isUnread = unreadCount > 0;
                const timeLabel = relativeTime(conv.lastMessageAt);

                // Role detection: am I the business owner for this conversation?
                const iAmOwner = myBizIds.has(conv.businessId);

                // Context-aware display
                let primaryLabel, secondaryLabel;
                if (iAmOwner) {
                  primaryLabel = conv.shopperName || conv.otherParticipantName || "Customer";
                  // If already filtered to a specific business tab, skip the "re:" line
                  secondaryLabel = (activeTab !== "all" && activeTab !== "personal") ? "" : "re: " + (conv.businessName || "your business");
                } else {
                  primaryLabel = conv.businessName || "Business";
                  secondaryLabel = "with " + (conv.otherParticipantName || "Owner");
                }

                return (
                  <div key={conv.id} className={"conversation-item" + (isUnread ? " unread" : "")} onClick={() => navigate(`chat/${conv.id}`)}>
                    <div className="conversation-header">
                      <span className="conversation-name">
                        {isUnread && <span className="unread-dot" />}
                        {primaryLabel}
                      </span>
                      <span className="conversation-time">{timeLabel}</span>
                    </div>
                    {secondaryLabel && <div className="conversation-business" style={{ fontSize: "0.85rem", color: "var(--muted)" }}>{secondaryLabel} {conv.businessId && <span onClick={(e) => { e.stopPropagation(); navigate("detail/" + conv.businessId); }} style={{ color: "var(--amber)", cursor: "pointer", fontWeight: 600, fontSize: "0.75rem" }}>View Store →</span>}</div>}
                    <div className="conversation-preview">
                      <span>{conv.lastMessage || "No messages yet"}</span>
                      {isUnread && <span className="badge-count">{unreadCount}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>
              {activeTab === "all" ? "No conversations yet" : "No messages in this category"}
            </div>
          )}
        </div>
      );
    }

    // ChatPage Component
    function ChatPage({ conversationId, currentUser, userBusinesses, db, navigate }) {
      const [conversation, setConversation] = useState(null);
      const [messages, setMessages] = useState([]);
      const [newMessage, setNewMessage] = useState("");
      const [loading, setLoading] = useState(true);
      const messagesEndRef = React.useRef(null);

      useEffect(() => {
        if (!conversationId || !currentUser) {
          setLoading(false);
          return;
        }

        const unsub = db.collection("conversations").doc(conversationId).onSnapshot(snap => {
          if (snap.exists) {
            setConversation({ id: snap.id, ...snap.data() });
            if ((snap.data().unreadBy?.[currentUser.uid] || 0) > 0) {
              snap.ref.update({ [`unreadBy.${currentUser.uid}`]: 0 });
            }
          }
        }, function(err) { console.error("Conversation listener error:", err); showToast("Could not load conversation.", "error"); setLoading(false); });

        const messageUnsub = db.collection("conversations").doc(conversationId).collection("messages")
          .orderBy("createdAt", "asc")
          .onSnapshot(snap => {
            setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
          }, function(err) { console.error("Messages listener error:", err); showToast("Could not load messages.", "error"); setLoading(false); });

        return () => {
          unsub();
          messageUnsub();
        };
      }, [conversationId, currentUser, db]);

      useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, [messages]);

      const handleSendMessage = async () => {
        if (!newMessage.trim() || !conversation) return;

        try {
          await db.collection("conversations").doc(conversationId).collection("messages").add({
            senderUid: currentUser.uid,
            senderName: currentUser.displayName,
            text: newMessage,
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
          });

          const otherUid = conversation.participants.find(uid => uid !== currentUser.uid);
          await db.collection("conversations").doc(conversationId).update({
            lastMessage: newMessage,
            lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
            [`unreadBy.${otherUid}`]: (conversation.unreadBy?.[otherUid] || 0) + 1
          });

          setNewMessage("");
        } catch (err) {
          console.error("Send message error:", err);
        }
      };

      if (loading) return <div style={{ padding: "2rem", textAlign: "center" }}>Loading chat...</div>;

      if (!conversation) return <div style={{ padding: "2rem", textAlign: "center", color: "var(--muted)" }}>Conversation not found</div>;

      return (
        <div className="chat-container" style={{ height: "100%", maxHeight: "100%" }}>
          <div className="chat-header">
            <button onClick={() => navigate("messages")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", marginRight: "0.75rem" }}>
              <Icon name="arrowLeft" size={20} />
            </button>
            <div className="chat-header-info">
              {(() => {
                const myBizIds = new Set((userBusinesses || []).map(b => b.id));
                const iAmOwner = myBizIds.has(conversation.businessId);
                if (iAmOwner) {
                  return React.createElement(React.Fragment, null,
                    React.createElement('div', { className: "chat-header-name" }, conversation.shopperName || conversation.otherParticipantName || "Customer"),
                    React.createElement('div', {
                      style: { fontSize: "0.8rem", color: "var(--amber)", cursor: "pointer", fontWeight: 500 },
                      onClick: function() { navigate("detail/" + conversation.businessId); }
                    }, conversation.businessName || "Your business", " ↗")
                  );
                } else {
                  return React.createElement(React.Fragment, null,
                    React.createElement('div', {
                      className: "chat-header-name",
                      style: { cursor: "pointer" },
                      onClick: function() { navigate("detail/" + conversation.businessId); }
                    }, conversation.businessName || "Business"),
                    React.createElement('div', { style: { fontSize: "0.8rem", color: "var(--muted)" } }, conversation.otherParticipantName || "Owner")
                  );
                }
              })()}
            </div>
          </div>

          <div className="chat-messages">
            {messages.length === 0 ? (
              <div style={{ textAlign: "center", color: "var(--muted)", margin: "auto" }}>No messages yet. Start a conversation!</div>
            ) : (
              messages.map(msg => (
                <div key={msg.id} style={{ display: "flex", flexDirection: (msg.senderUid || msg.senderId) === currentUser.uid ? "row-reverse" : "row" }}>
                  <div className={`chat-bubble ${(msg.senderUid || msg.senderId) === currentUser.uid ? "mine" : "theirs"}`}>
                    <div>{msg.text}</div>
                    <div className="chat-bubble-time">
                      {new Date(msg.createdAt?.seconds * 1000 || 0).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-row">
            <input
              type="text"
              className="chat-input"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleSendMessage()}
              placeholder="Type a message..."
            />
            <button className="chat-send-btn" onClick={handleSendMessage} disabled={!newMessage.trim()}>
              <Icon name="send" size={18} color="white" />
            </button>
          </div>
        </div>
      );
    }

    // Main App Component
    function AcknowledgementModal({ onAccept, onViewFull }) {
      const [checked, setChecked] = useState(false);
      const [submitting, setSubmitting] = useState(false);

      var handleAccept = async function() {
        setSubmitting(true);
        await onAccept();
        setSubmitting(false);
      };

      return (
        <div className="ack-overlay">
          <div className="ack-modal">
            <h2><Icon name="shield" size={22} color="var(--amber)" /> Home Business Regulations</h2>
            <p>Before listing your business on <span className="nooks-brand">nooks</span>, please review and acknowledge Singapore's home-based business regulations.</p>

            <div className="ack-section-title">Permitted Activities</div>
            <ul>
              <li>Baking, food production, sewing, hairdressing, beauty services (no massage), private tuition (max 3 students), freelance creative work</li>
            </ul>

            <div className="ack-section-title">Key Rules</div>
            <ul>
              <li>Business must be run by the owner, owner's family, or registered tenants only</li>
              <li>No hiring of non-resident employees or helpers</li>
              <li>Must not cause noise, smoke, heavy traffic, or any nuisance to neighbours</li>
              <li>No commercial signs or banners on the property</li>
              <li>No large-scale storage of goods</li>
            </ul>

            <div className="ack-section-title">Food Business Requirements</div>
            <ul>
              <li>Must follow Singapore Food Agency (SFA) guidelines</li>
              <li>Must complete Food Safety Course Level 1 (WSQ)</li>
            </ul>

            <div className="ack-section-title">Registration</div>
            <ul>
              <li>ACRA registration is not needed for small-scale hobbyist projects (e.g., casual baking, crafting). If running the business on an ongoing basis for profit, registration with ACRA is recommended</li>
              <li>Check eligibility under HDB/URA Home-Based Business Scheme or Home Office Scheme</li>
            </ul>

            <div className="legal-callout" style={{ fontSize: "0.82rem", margin: "1rem 0" }}>
              <strong>Note:</strong> If neighbours complain about noise, smell, or heavy traffic, HDB may require you to cease operations. Business owners are fully responsible for compliance.
            </div>

            <div className="ack-checkbox-row">
              <input type="checkbox" id="ack-check" checked={checked} onChange={function(e) { setChecked(e.target.checked); }} />
              <label htmlFor="ack-check">I have read and understood the home-based business regulations in Singapore. I acknowledge that I am solely responsible for ensuring my business activities comply with all applicable HDB, URA, and SFA guidelines.</label>
            </div>

            <button className="ack-btn" disabled={!checked || submitting} onClick={handleAccept}>
              {submitting ? "Saving..." : "I Acknowledge & Accept"}
            </button>

            <p style={{ textAlign: "center", marginTop: "0.75rem", marginBottom: 0 }}>
              <a href="#" onClick={function(e) { e.preventDefault(); onViewFull(); }} style={{ color: "var(--amber)", fontSize: "0.85rem" }}>View full regulations page →</a>
            </p>
          </div>
        </div>
      );
    }

    // ── Image processing helpers ──────────────────────────────────────────
    //
    // Design notes (2026-04-25 rewrite, replacing the old Image()+toBlob path):
    //
    //   • createImageBitmap(file) is Promise-native: it resolves with a fully-
    //     decoded ImageBitmap or rejects with a clear DOMException. The old
    //     `new Image() + URL.createObjectURL` path could silently stall in
    //     Safari (neither onload nor onerror fired) for certain HEIC / very
    //     large photos, which is why a 15s timeout had been duct-taped on.
    //
    //   • If canvas.toBlob ever returns null (Safari OOM, tainted canvas,
    //     unsupported codec), we now THROW instead of silently uploading the
    //     original file. The old behaviour caused HEIC files to be stored
    //     under an `image/jpeg` content-type — visually broken on every
    //     non-Safari browser.
    //
    //   • HEIC: every modern macOS/iOS Safari can decode HEIC via
    //     createImageBitmap, but Chrome/Firefox cannot. We surface a clear,
    //     user-facing error if decode fails so the caller's toast says
    //     something actionable instead of silently failing later.
    //
    // Return contract: Blob (image/jpeg). Throws Error on any failure;
    // callers must be wrapped in try/catch and surface a toast to the user.

    var _IMAGE_DECODE_TIMEOUT_MS = 15000;

    function _isHeicFile(file) {
      var t = (file && file.type) || "";
      var n = (file && file.name) || "";
      return /^image\/hei[cf]/i.test(t) || /\.(heic|heif)$/i.test(n);
    }

    // Decode any image-like File/Blob into an ImageBitmap, with a hard timeout
    // so a stuck WebKit decoder cannot hang the whole submit flow.
    async function _decodeImage(file) {
      if (!file || !(file.type || "").toString().startsWith("image/")) {
        throw new Error("File is not an image (type: " + (file && file.type) + ")");
      }
      if (typeof createImageBitmap !== "function") {
        throw new Error("This browser is too old to process images here. Please update Safari/Chrome/Firefox.");
      }
      var timeoutHandle;
      var timeoutPromise = new Promise(function(_, reject) {
        timeoutHandle = setTimeout(function() {
          reject(new Error("Image decode timed out after " + (_IMAGE_DECODE_TIMEOUT_MS / 1000) + "s"));
        }, _IMAGE_DECODE_TIMEOUT_MS);
      });
      try {
        return await Promise.race([createImageBitmap(file), timeoutPromise]);
      } catch (e) {
        if (_isHeicFile(file)) {
          throw new Error("HEIC photos can't be processed here. Please convert to JPEG and try again.");
        }
        throw new Error("Could not decode image: " + ((e && e.message) || e));
      } finally {
        clearTimeout(timeoutHandle);
      }
    }

    // Encode a canvas to a JPEG Blob. Throws on null (Safari OOM / unsupported
    // codec). Promise-wrapped so callers can `await` cleanly.
    function _canvasToJpeg(canvas, quality) {
      return new Promise(function(resolve, reject) {
        canvas.toBlob(function(blob) {
          if (blob) resolve(blob);
          else reject(new Error("Browser failed to encode image (canvas.toBlob returned null) — image may be too large or in an unsupported format"));
        }, "image/jpeg", quality);
      });
    }

    // Compress an arbitrary image down to maxWidth (longer dimension) at the
    // given JPEG quality. Returns a Blob of type image/jpeg. Throws on failure.
    async function compressImage(file, maxWidth, quality) {
      var bitmap = await _decodeImage(file);
      try {
        var scale = Math.min(1, maxWidth / bitmap.width);
        var canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        return await _canvasToJpeg(canvas, quality);
      } finally {
        if (bitmap && typeof bitmap.close === "function") bitmap.close();
      }
    }

    // Crop source image to a square and resize to outputSize px.
    // posX/posY are 0–100 (object-position equivalents), zoom is ≥1.
    // Honours the same zoom+reposition values set by the wizard preview UI.
    // Throws on failure.
    async function cropSquareImage(file, outputSize, posX, posY, zoom) {
      var bitmap = await _decodeImage(file);
      try {
        var iw = bitmap.width, ih = bitmap.height;
        var z = Math.max(1, zoom || 1);
        var px = Math.max(0, Math.min(100, posX !== undefined ? posX : 50)) / 100;
        var py = Math.max(0, Math.min(100, posY !== undefined ? posY : 50)) / 100;
        // Square crop size in source pixels (shrinks as zoom increases)
        var cropSize = Math.min(iw, ih) / z;
        // Top-left of crop region in source space
        var sx = px * (iw - cropSize);
        var sy = py * (ih - cropSize);
        // Clamp so crop never exceeds image bounds
        sx = Math.max(0, Math.min(iw - cropSize, sx));
        sy = Math.max(0, Math.min(iw - cropSize, sy));
        var outSize = Math.min(outputSize, Math.round(cropSize));
        var canvas = document.createElement("canvas");
        canvas.width = outSize;
        canvas.height = outSize;
        canvas.getContext("2d").drawImage(bitmap, sx, sy, cropSize, cropSize, 0, 0, outSize, outSize);
        return await _canvasToJpeg(canvas, 0.88);
      } finally {
        if (bitmap && typeof bitmap.close === "function") bitmap.close();
      }
    }

    // -- Singapore Postal Sector ? Town/Estate lookup --------------------------
    // _SG_POSTAL_SECTORS removed — OneMap SG is the single source of truth for all address resolution.
    // Extract a human-readable street name from a Google-formatted SG address
    function _extractStreetFromAddress(formattedAddress) {
      if (!formattedAddress) return '';
      return formattedAddress
        .replace(/,?\s*Singapore\s*\d{0,6}\s*$/i, '') // strip trailing "Singapore XXXXXX"
        .replace(/,?\s*#[\dA-Za-z-]+/g, '')            // strip unit numbers
        .replace(/^Blk\s+\d+[A-Za-z]?\s+/i, '')       // strip "Blk 287 "
        .replace(/^\d+[A-Za-z]?\s+/, '')               // strip leading house numbers
        .replace(/,.*$/, '')                            // strip everything after first comma
        .trim();
    }
    // Convert an ALL-CAPS OneMap string to Title Case (single source of truth for ROAD_NAME formatting).
    function _toTitleCase(str) {
      if (!str) return '';
      return str.toLowerCase().replace(/\b([a-z])/g, function(c) { return c.toUpperCase(); });
    }

    // Single source of truth: derive the best human-readable location for a business card.
    // Cascade: street (from OneMap) ? extract from address ? district ? location ? fallback
    function getDisplayLocation(biz) {
      if (!biz) return 'Address not set';
      // Returns true if a string is NOT a meaningful human-readable location
      const isMeaningless = (s) => !s || /^singapore\s*\d*$/i.test(s.trim()) || /^\d{5,6}$/.test(s.trim());
      // 1. Dedicated street field (stored from OneMap ROAD_NAME at save time — single source of truth)
      // _toTitleCase normalises any incorrectly-cased legacy values already in Firestore
      if (biz.street && !isMeaningless(biz.street)) return _toTitleCase(biz.street.trim());
      // 2. Extract street name from full formatted address
      if (biz.address && !isMeaningless(biz.address)) {
        const extracted = _extractStreetFromAddress(biz.address);
        if (extracted && !isMeaningless(extracted)) return extracted;
      }
      // 3. District field (e.g. neighbourhood from address autocomplete)
      if (biz.district && !isMeaningless(biz.district)) return biz.district.trim();
      // 4. Meaningful location field
      if (biz.location && !isMeaningless(biz.location)) return biz.location.trim();
      // 7. Last resort: return full address or generic fallback
      return biz.address || 'Singapore';
    }

    // Haversine formula: returns distance in km between two lat/lng points
    function haversineDistance(lat1, lng1, lat2, lng2) {
      const R = 6371; // Earth's radius in km
      const toRad = (deg) => deg * Math.PI / 180;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                Math.sin(dLng / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    }

    // -- Name placeholder for cards without a cover photo --
    const CARD_PALETTES = [
      { bg: "linear-gradient(135deg, #2d6a4f, #40916c)", color: "#b7e4c7" },
      { bg: "linear-gradient(135deg, #3a0ca3, #7209b7)", color: "#d4b8e0" },
      { bg: "linear-gradient(135deg, #d4403a, #e76f51)", color: "#fde8e4" },
      { bg: "linear-gradient(135deg, #0077b6, #00b4d8)", color: "#caf0f8" },
      { bg: "linear-gradient(135deg, #b5651d, #e09f3e)", color: "#fff3e0" },
      { bg: "linear-gradient(135deg, #264653, #2a9d8f)", color: "#a8dadc" },
      { bg: "linear-gradient(135deg, #6b2737, #d4403a)", color: "#f8d7da" },
      { bg: "linear-gradient(135deg, #1d3557, #457b9d)", color: "#a8dadc" },
    ];
    function getCardPalette(name) {
      const hash = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
      return CARD_PALETTES[hash % CARD_PALETTES.length];
    }
    function CardNamePlaceholder({ name, fontSize, padding }) {
      const p = getCardPalette(name);
      const fs = fontSize || ((name || "").length > 16 ? "1.3rem" : "1.6rem");
      return (
        <div style={{ width: "100%", height: "100%", background: p.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: padding || "1.2rem" }}>
          <span style={{ fontFamily: "var(--font-heading)", fontSize: fs, fontWeight: 400, color: p.color, textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>{name}</span>
        </div>
      );
    }

    // -- Single Source of Truth: Admin Configuration ---------------
    const ADMIN_EMAILS = ["zentan@gmail.com", "iamahboy@gmail.com"];

    // -- Global Toast Notification System --------------------------
    var _toastSetState = null;
    function showToast(message, type, duration) {
      if (!type) type = "info";
      if (!duration) duration = 3500;
      if (_toastSetState) {
        var id = Math.random().toString(36).substr(2, 9);
        _toastSetState(function(prev) { return prev.concat([{ id: id, message: message, type: type }]); });
        setTimeout(function() {
          _toastSetState(function(prev) { return prev.filter(function(t) { return t.id !== id; }); });
        }, duration);
      }
    }
    window.showToast = showToast;

    function ToastContainer() {
      var _state = useState([]);
      var toasts = _state[0];
      var setToasts = _state[1];
      React.useEffect(function() { _toastSetState = setToasts; return function() { _toastSetState = null; }; }, []);
      return React.createElement("div", { className: "toast-container", role: "alert", style: toasts.length === 0 ? { display: "none" } : undefined },
        toasts.map(function(t) {
          return React.createElement("div", { key: t.id, className: "toast toast-" + t.type },
            React.createElement("span", null, t.message),
            React.createElement("button", {
              className: "toast-dismiss",
              onClick: function() { setToasts(function(prev) { return prev.filter(function(x) { return x.id !== t.id; }); }); }
            }, "\u00d7")
          );
        })
      );
    }

    // -- Postal Code Nudge — shown once to signed-in users who haven't set homePostalCode --
    function PostalNudge({ onSetNow, onDismiss }) {
      return (
        <div className="postal-nudge" role="status" aria-live="polite">
          <span className="postal-nudge-icon">📍</span>
          <span className="postal-nudge-text">
            <strong>Be the first to know.</strong> Set your home area and we'll notify you the moment a new business opens near you — before anyone else finds out.
          </span>
          <button className="postal-nudge-cta" onClick={onSetNow}>Set mine →</button>
          <button className="postal-nudge-dismiss" onClick={onDismiss} aria-label="Dismiss">&times;</button>
        </div>
      );
    }

    // -- FCM Push Nudge — shown once to signed-in users who haven't been asked for notification permission yet --
    // Intentionally NOT auto-triggered on login: the native browser permission prompt is contextless
    // and gets auto-suppressed by Chrome/iOS Safari when fired without a preceding explanation + user
    // gesture. This banner provides both, then "Enable" calls initFcmForUser() directly from the click.
    function FcmNudge({ onEnable, onDismiss }) {
      return (
        <div className="postal-nudge" role="status" aria-live="polite">
          <span className="postal-nudge-icon">🔔</span>
          <span className="postal-nudge-text">
            <strong>Never miss a buyer.</strong> Turn on notifications to get alerted the instant someone messages or books you.
          </span>
          <button className="postal-nudge-cta" onClick={onEnable}>Enable →</button>
          <button className="postal-nudge-dismiss" onClick={onDismiss} aria-label="Dismiss">&times;</button>
        </div>
      );
    }

    // -- Auto-geocode: fix businesses stored with the SG-center fallback coords --
    // When lat—1.3521 & lng—103.8198, coordinates were never properly set.
    // Geocodes from postalCode via OneMap SG (single source of truth), updates React state immediately,
    // and writes back to Firestore when an authorised user is signed in.
    var _pendingCoordFixes = {};
    function _autoGeocodePlaceholders(businesses, setStateFn) {
      var SG_LAT = 1.3521, SG_LNG = 103.8198, THRESHOLD = 0.003;
      businesses.forEach(function(biz) {
        if (!biz.postalCode || _pendingCoordFixes['_done_' + biz.id]) return;
        var lat = parseFloat(biz.lat) || 0, lng = parseFloat(biz.lng) || 0;
        var isPlaceholder = (lat === 0 && lng === 0) ||
          (Math.abs(lat - SG_LAT) < THRESHOLD && Math.abs(lng - SG_LNG) < THRESHOLD);
        var missingStreet = !biz.street || biz.street.trim() === '';
        if (!isPlaceholder && !missingStreet) return; // only geocode if coords are wrong OR street missing
        _pendingCoordFixes['_done_' + biz.id] = true; // prevent re-queuing
        // OneMap SG (Singapore Land Authority) — single source of truth for SG addresses
        fetch('https://www.onemap.gov.sg/api/common/elastic/search?searchVal=' +
          encodeURIComponent(biz.postalCode) + '&returnGeom=Y&getAddrDetails=Y&pageNum=1')
          .then(function(r) { return r.json(); })
          .then(function(data) {
            if (!data || !data.results || !data.results.length) return;
            var r = data.results[0];
            var newLat = parseFloat(r.LATITUDE), newLng = parseFloat(r.LONGITUDE);
            if (!newLat || !newLng) return;
            // OneMap returns authoritative ROAD_NAME for every Singapore postal code
            var newStreet = r.ROAD_NAME ? _toTitleCase(r.ROAD_NAME) : '';
            _pendingCoordFixes[biz.id] = { lat: newLat, lng: newLng, street: newStreet, name: biz.name, ownerId: biz.ownerId };
            // Fix display immediately (lat, lng AND street)
            if (setStateFn) {
              setStateFn(function(prev) {
                return prev.map(function(b) {
                  return b.id === biz.id ? Object.assign({}, b, { lat: newLat, lng: newLng, street: newStreet || b.street }) : b;
                });
              });
            }
            // Write to Firestore if authorised user already signed in
            var user = firebase.auth().currentUser;
            if (user) {
              var isAdm = ['zentan@gmail.com', 'iamahboy@gmail.com'].includes(user.email);
              if (isAdm || user.uid === biz.ownerId) {
                firebase.firestore().collection('businesses').doc(biz.id).update({
                  lat: newLat, lng: newLng,
                  street: newStreet || biz.street || '',
                  _coordsFixedAt: firebase.firestore.FieldValue.serverTimestamp()
                }).then(function() {
                  delete _pendingCoordFixes[biz.id];
                  console.log('[Nooks] Coords auto-fixed:', biz.name, '->', newLat, newLng);
                }).catch(function(e) { console.warn('[Nooks] Coord fix failed:', e.message); });
              }
            }
          })
          .catch(function(e) { console.warn('[Nooks] Geocode error for', biz.postalCode, e.message); });
      });
    }
    // Apply any pending coord fixes when admin/owner signs in
    function _applyPendingCoordFixes(user) {
      var isAdm = ['zentan@gmail.com', 'iamahboy@gmail.com'].includes(user.email);
      Object.keys(_pendingCoordFixes).forEach(function(bizId) {
        if (bizId.startsWith('_done_')) return;
        var fix = _pendingCoordFixes[bizId];
        if (!isAdm && user.uid !== fix.ownerId) return;
        var updatePayload = {
          lat: fix.lat, lng: fix.lng,
          _coordsFixedAt: firebase.firestore.FieldValue.serverTimestamp()
        };
        if (fix.street) updatePayload.street = fix.street;
        firebase.firestore().collection('businesses').doc(bizId).update(updatePayload).then(function() {
          delete _pendingCoordFixes[bizId];
          console.log('[Nooks] Pending coord+street fix applied:', fix.name, fix.street || '');
        }).catch(function(e) { console.warn('[Nooks] Pending fix failed:', bizId, e.message); });
      });
    }

    // ─── Login notification summary ──────────────────────────────────────
    // One-time snapshot taken right after sign-in: "what's new since you were
    // last here" — new pending bookings (owner side), booking status changes
    // (customer side), and unread messages. Fully independent of the live
    // pendingBookingsCount/unreadMessagesCount onSnapshot listeners (those stay
    // live for nav badges; this is just a single summary toast at login).
    // notifLastSeenAt is a client-owned field on users/{uid} — deliberately NOT
    // reusing lastSignInAt, which is owned by the onSignInRecorded Cloud Function
    // and would race against this read.
    async function _checkLoginNotifications(uid) {
      try {
        var userRef = db.collection("users").doc(uid);
        var userSnap = await userRef.get();
        var cutoff = userSnap.exists ? userSnap.data().notifLastSeenAt : null;

        // First-ever login (or any pre-rollout existing user): establish a
        // baseline now rather than toasting a backlog of historical data.
        if (!cutoff) {
          userRef.update({ notifLastSeenAt: firebase.firestore.FieldValue.serverTimestamp() }).catch(function(){});
          return;
        }

        var parts = [];

        // Owner side: new pending bookings across all owned businesses.
        try {
          var bizSnap = await db.collection("businesses").where("ownerId", "==", uid).get();
          var bizIds = bizSnap.docs.map(function(d) { return d.id; });
          if (bizIds.length > 0) {
            var newPending = 0;
            var chunks = [];
            for (var i = 0; i < bizIds.length; i += 30) chunks.push(bizIds.slice(i, i + 30));
            for (var c = 0; c < chunks.length; c++) {
              var snap = await db.collection("bookings")
                .where("businessId", "in", chunks[c])
                .where("status", "==", "pending")
                .where("createdAt", ">", cutoff)
                .get();
              newPending += snap.docs.length;
            }
            if (newPending > 0) parts.push(newPending + " new pending booking" + (newPending > 1 ? "s" : ""));
          }
        } catch (e) { console.error("Login notif check (owner bookings) failed:", e); }

        // Customer side: bookings whose status changed since last login.
        try {
          var custSnap = await db.collection("bookings")
            .where("customerUid", "==", uid)
            .where("status", "in", ["confirmed", "declined", "completed"])
            .where("updatedAt", ">", cutoff)
            .get();
          if (custSnap.docs.length > 0) parts.push(custSnap.docs.length + " booking update" + (custSnap.docs.length > 1 ? "s" : ""));
        } catch (e) { console.error("Login notif check (customer bookings) failed:", e); }

        // Messages: same unreadBy map the live nav badge uses, one-time read here.
        try {
          var convSnap = await db.collection("conversations").where("participants", "array-contains", uid).get();
          var unread = 0;
          convSnap.docs.forEach(function(doc) { unread += (doc.data().unreadBy && doc.data().unreadBy[uid]) || 0; });
          if (unread > 0) parts.push(unread + " unread message" + (unread > 1 ? "s" : ""));
        } catch (e) { console.error("Login notif check (messages) failed:", e); }

        if (parts.length > 0 && window.showToast) {
          window.showToast("Welcome back — " + parts.join(" · "), "info", 6000);
        }

        userRef.update({ notifLastSeenAt: firebase.firestore.FieldValue.serverTimestamp() }).catch(function(){});
      } catch (e) {
        console.error("Login notification check failed:", e);
      }
    }

    // Nooks Concierge — site-wide floating chat widget (discovery + FAQ only;
    // operator-assist is out of scope until a server-side tool exists for it).
    // Renders assistant chat text: converts **bold** to <strong> and \n to <br>.
    // HTML-escapes first so the model can never inject markup accidentally.
    function renderChatText(text) {
      var html = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
      return React.createElement("span", { dangerouslySetInnerHTML: { __html: html } });
    }

    function ChatWidget({ currentUser, navigate }) {
      const [isOpen, setIsOpen] = React.useState(false);
      const [showNudge, setShowNudge] = React.useState(false);
      const [messages, setMessages] = React.useState([]);
      const [input, setInput] = React.useState("");
      const [isSending, setIsSending] = React.useState(false);
      const [sessionId, setSessionId] = React.useState(null);
      const messagesEndRef = React.useRef(null);
      // Draggable launcher: free placement, remembered per device. `launcherPos`
      // null = default CSS corner; otherwise an explicit {left, top} in px.
      const launcherRef = React.useRef(null);
      const [launcherPos, setLauncherPos] = React.useState(null);
      const dragRef = React.useRef({ active: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });

      // One-time session id (ties to server-side per-visitor rate limit) +
      // one-time nudge bubble, both localStorage-backed.
      React.useEffect(function () {
        var sid = null;
        try {
          sid = localStorage.getItem("nooks_chat_session");
          if (!sid) {
            sid = (window.crypto && window.crypto.randomUUID)
              ? window.crypto.randomUUID()
              : "sid-" + Date.now() + "-" + Math.random().toString(36).slice(2);
            localStorage.setItem("nooks_chat_session", sid);
          }
        } catch (e) {}
        setSessionId(sid);

        try {
          if (!localStorage.getItem("nooks_chat_nudge_seen")) {
            var t = setTimeout(function () { setShowNudge(true); }, 2500);
            return function () { clearTimeout(t); };
          }
        } catch (e) {}
      }, []);

      React.useEffect(function () {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, [messages, isSending]);

      function dismissNudge() {
        setShowNudge(false);
        try { localStorage.setItem("nooks_chat_nudge_seen", "1"); } catch (e) {}
      }

      // Keep the launcher inside the viewport (8px margin), using its real size.
      function clampLauncher(left, top) {
        var el = launcherRef.current;
        var w = el ? el.offsetWidth : 64;
        var h = el ? el.offsetHeight : 64;
        var maxLeft = Math.max(8, window.innerWidth - w - 8);
        var maxTop = Math.max(8, window.innerHeight - h - 8);
        return { left: Math.min(Math.max(8, left), maxLeft), top: Math.min(Math.max(8, top), maxTop) };
      }

      // Restore saved position on mount; re-clamp on viewport resize.
      React.useEffect(function () {
        try {
          var raw = localStorage.getItem("nooks_chat_pos");
          if (raw) {
            var p = JSON.parse(raw);
            if (p && typeof p.left === "number" && typeof p.top === "number") setLauncherPos(clampLauncher(p.left, p.top));
          }
        } catch (e) {}
        function onResize() { setLauncherPos(function (prev) { return prev ? clampLauncher(prev.left, prev.top) : prev; }); }
        window.addEventListener("resize", onResize);
        return function () { window.removeEventListener("resize", onResize); };
      }, []);

      // Pointer-based drag (mouse + touch). A <5px move counts as a click, so
      // tapping still opens the chat; anything more drags it and is persisted.
      function onLauncherDown(e) {
        if (e.target && e.target.closest && e.target.closest(".chat-widget-nudge-close")) return;
        var el = launcherRef.current; if (!el) return;
        var r = el.getBoundingClientRect();
        dragRef.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top };
        try { el.setPointerCapture(e.pointerId); } catch (_) {}
      }
      function onLauncherMove(e) {
        var d = dragRef.current; if (!d.active) return;
        var dx = e.clientX - d.sx, dy = e.clientY - d.sy;
        if (!d.moved && (dx * dx + dy * dy) < 25) return;
        d.moved = true;
        setLauncherPos(clampLauncher(d.ox + dx, d.oy + dy));
      }
      function onLauncherUp(e) {
        var d = dragRef.current; if (!d.active) return;
        d.active = false;
        var el = launcherRef.current;
        try { el && el.releasePointerCapture(e.pointerId); } catch (_) {}
        if (d.moved && el) {
          try {
            var r = el.getBoundingClientRect();
            localStorage.setItem("nooks_chat_pos", JSON.stringify({ left: Math.round(r.left), top: Math.round(r.top) }));
          } catch (_) {}
        }
      }

      function openWidget() {
        if (dragRef.current.moved) return; // ignore the click that ends a drag
        dismissNudge();
        setIsOpen(true);
        setMessages(function (prev) {
          if (prev.length > 0) return prev;
          return [{
            role: "assistant",
            text: "Hey there! I'm the Nooks Concierge. I can help you find home businesses nearby, or answer questions about how Nooks works. What can I help with?",
            cards: [],
          }];
        });
      }

      function closeWidget() {
        setIsOpen(false);
      }

      function sendMessage(textOverride) {
        var text = (textOverride !== undefined ? textOverride : input).trim();
        if (!text || isSending) return;

        var userMsg = { role: "user", text: text, cards: [] };
        var nextMessages = messages.concat([userMsg]);
        setMessages(nextMessages);
        setInput("");
        setIsSending(true);

        function withLocationAndSend(lat, lng) {
          // History must NOT include the message we're sending now — the
          // server appends it separately as the final turn. Including it
          // here too produces two consecutive "user" turns, which the
          // Anthropic API rejects (it requires strict user/assistant
          // alternation), causing every message to fail with the generic
          // fallback reply. `messages` (pre-concat state) is the correct
          // source here, not `nextMessages`.
          var historyPayload = messages.slice(-10).map(function (m) {
            return { role: m.role, text: m.text };
          });

          fetch("https://asia-southeast1-nooks-sg.cloudfunctions.net/chatWithNooks", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: text,
              sessionId: sessionId,
              history: historyPayload,
              userLat: lat,
              userLng: lng,
            }),
          })
            .then(function (res) {
              if (res.status === 429) {
                return res.json().then(function (data) {
                  throw { rateLimited: true, message: (data && data.error) || "Rate limit reached" };
                });
              }
              if (!res.ok) throw new Error("Request failed: " + res.status);
              return res.json();
            })
            .then(function (data) {
              setMessages(function (prev) {
                return prev.concat([{
                  role: "assistant",
                  text: data.reply || "Sorry, I didn't catch that — could you rephrase?",
                  cards: data.cards || [],
                  actions: data.actions || [],
                }]);
              });
            })
            .catch(function (err) {
              var msg = (err && err.rateLimited)
                ? "I've hit my chat limit for now — please try again in a bit, or browse nooks.sg directly."
                : "Something went wrong reaching the Concierge. Please try again shortly.";
              setMessages(function (prev) {
                return prev.concat([{ role: "assistant", text: msg, cards: [], isError: true }]);
              });
              console.error("chatWithNooks error:", err);
            })
            .finally(function () {
              setIsSending(false);
            });
        }

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            function (pos) { withLocationAndSend(pos.coords.latitude, pos.coords.longitude); },
            function () { withLocationAndSend(null, null); },
            { enableHighAccuracy: false, timeout: 4000, maximumAge: 300000 }
          );
        } else {
          withLocationAndSend(null, null);
        }
      }

      function handleInputKeyDown(e) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          sendMessage();
        }
      }

      var quickReplies = [
        "Find something near me",
        "How does Nooks work?",
        "How do I list my business on Nooks?",
      ];

      return (
        <React.Fragment>
          {!isOpen && (
            <div
              className="chat-widget-launcher-wrap"
              ref={launcherRef}
              onPointerDown={onLauncherDown}
              onPointerMove={onLauncherMove}
              onPointerUp={onLauncherUp}
              onPointerCancel={onLauncherUp}
              title="Drag to move · tap to chat"
              style={Object.assign(
                { touchAction: "none", cursor: "grab" },
                launcherPos ? { left: launcherPos.left + "px", top: launcherPos.top + "px", right: "auto", bottom: "auto" } : {}
              )}
            >
              {showNudge && (
                <div className="chat-widget-nudge" onClick={openWidget}>
                  Looking for something nearby? Ask me 👋
                  <button
                    className="chat-widget-nudge-close"
                    onClick={function (e) { e.stopPropagation(); dismissNudge(); }}
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
              <button className="chat-widget-bubble" onClick={openWidget} aria-label="Open Nooks Concierge chat">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="chat-widget-cta-label" onClick={openWidget}>Talk to me!</div>
            </div>
          )}

          {isOpen && (
            <div className="chat-widget-panel">
              <div className="chat-widget-header">
                <div className="chat-widget-header-text">
                  <div className="chat-widget-title">Nooks Concierge</div>
                  <div className="chat-widget-subtitle">Find what's around the corner</div>
                </div>
                <button className="chat-widget-close" onClick={closeWidget} aria-label="Close chat">×</button>
              </div>

              <div className="chat-widget-messages">
                {messages.map(function (m, i) {
                  return (
                    <div key={i} className={"chat-widget-msg-row " + (m.role === "user" ? "chat-widget-msg-row-user" : "chat-widget-msg-row-assistant")}>
                      <div className={"chat-widget-msg " + (m.role === "user" ? "chat-widget-msg-user" : "chat-widget-msg-assistant") + (m.isError ? " chat-widget-msg-error" : "")}>
                        {m.role === "assistant" ? renderChatText(m.text) : m.text}
                      </div>
                      {m.cards && m.cards.length > 0 && (
                        <div className="chat-widget-cards">
                          {m.cards.map(function (c, ci) {
                            return (
                              <div
                                key={ci}
                                className="chat-widget-card"
                                onClick={function () { closeWidget(); navigate(c.url || ("detail/" + (c.slug || c.id))); }}
                              >
                                {c.image && <img src={c.image} alt={c.name} className="chat-widget-card-img" />}
                                <div className="chat-widget-card-body">
                                  <div className="chat-widget-card-name">{c.name}</div>
                                  {c.category && <div className="chat-widget-card-meta">{CATEGORIES.find(cat => cat.id === c.category)?.label || c.category}</div>}
                                  {c.distance != null && <div className="chat-widget-card-meta">{c.distance}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {m.actions && m.actions.length > 0 && (
                        <div className="chat-widget-actions">
                          {m.actions.map(function (a, ai) {
                            return (
                              <button
                                key={ai}
                                className="chat-widget-action-btn"
                                onClick={function () { closeWidget(); navigate(a.url); }}
                              >
                                {a.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {isSending && (
                  <div className="chat-widget-msg-row chat-widget-msg-row-assistant">
                    <div className="chat-widget-msg chat-widget-msg-assistant chat-widget-typing">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}

                {messages.length === 1 && !isSending && (
                  <div className="chat-widget-chips">
                    {quickReplies.map(function (q, qi) {
                      return (
                        <button key={qi} className="chat-widget-chip" onClick={function () { sendMessage(q); }}>
                          {q}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="chat-widget-input-row">
                <input
                  type="text"
                  className="chat-widget-input"
                  placeholder="Ask about a business or how Nooks works..."
                  value={input}
                  onChange={function (e) { setInput(e.target.value); }}
                  onKeyDown={handleInputKeyDown}
                  disabled={isSending}
                />
                <button
                  className="chat-widget-send"
                  onClick={function () { sendMessage(); }}
                  disabled={isSending || !input.trim()}
                  aria-label="Send"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 2 11 13" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 2 15 22l-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
        </React.Fragment>
      );
    }

    // ═══════════════════════════════════════════════════════════════
    // This Week — weekly what's-on (events collection).
    // Design: _plans/events-collection-design.md
    // ═══════════════════════════════════════════════════════════════
    // Singapore's 5 broad regions as approximate centroids. Events (which carry
    // only lat/lng) are bucketed to the nearest region centre for the This Week
    // area filter and the per-region event-count bubbles.
    const TW_REGIONS = {
      North:   [1.4300, 103.8200],
      South:   [1.2750, 103.8200],
      East:    [1.3500, 103.9400],
      West:    [1.3400, 103.7100],
      Central: [1.3450, 103.8400]
    };
    const TW_TYPES = {
      popup:{l:"Pop-up",icon:"bag"}, class:{l:"Class",icon:"brush"},
      market:{l:"Market",icon:"cart"}, drop:{l:"Drop",icon:"package"},
      workshop:{l:"Workshop",icon:"tool"}, sale:{l:"Sale",icon:"tag"}
    };
    // Per-type photographic fallbacks (licensed Adobe Stock, generic per-type
    // photos — NOT event-specific, so they never misrepresent a real event).
    // Three variants per type so same-type cards don't all look identical. Used
    // only when an event has no real source cover photo. Served from hosting root
    // (img/thisweek/*.jpg). The ?v= suffix lets a future swap bust the CDN cache.
    const TW_TYPE_IMG = {
      popup:   ["/img/thisweek/popup.jpg",   "/img/thisweek/popup-2.jpg",   "/img/thisweek/popup-3.jpg"],
      class:   ["/img/thisweek/class.jpg",   "/img/thisweek/class-2.jpg",   "/img/thisweek/class-3.jpg"],
      market:  ["/img/thisweek/market.jpg",  "/img/thisweek/market-2.jpg",  "/img/thisweek/market-3.jpg"],
      drop:    ["/img/thisweek/drop.jpg",    "/img/thisweek/drop-2.jpg",    "/img/thisweek/drop-3.jpg"],
      workshop:["/img/thisweek/workshop.jpg","/img/thisweek/workshop-2.jpg","/img/thisweek/workshop-3.jpg"],
      sale:    ["/img/thisweek/sale.jpg",    "/img/thisweek/sale-2.jpg",    "/img/thisweek/sale-3.jpg"]
    };
    // Stable string hash (djb2-ish) → non-negative int. Same input always maps to
    // the same variant, so a given event keeps its photo across renders/reloads.
    function twHash(s) {
      var h = 0, str = String(s || "");
      for (var i = 0; i < str.length; i++) { h = ((h << 5) - h + str.charCodeAt(i)) | 0; }
      return Math.abs(h);
    }
    // Resolve the best thumbnail image for an event: real cover first, else a
    // per-type stock photo chosen deterministically by event id (so same-type
    // cards vary, but each event is stable). Returns "" only for an unknown
    // type (→ icon shows).
    function twThumbImg(e) {
      var c = (e && e.coverPhotoUrl) ? String(e.coverPhotoUrl) : "";
      if (c.length > 4) return c;
      var arr = TW_TYPE_IMG[(e && e.type) || ""];
      if (!arr || !arr.length) return "";
      var idx = twHash((e && (e.id || e.title)) || "") % arr.length;
      return arr[idx] + "?v=1";
    }
    // Render a type's line-icon via the site Icon system — replaces the old
    // emoji so This Week matches the rest of the site's visual language.
    function twIco(ty, size) { return <Icon name={(ty && ty.icon) || "pin"} size={size || 14} />; }
    function twKm(a, b, c, d) {
      var R = 6371, r = function(x){ return x * Math.PI / 180; };
      var dLa = r(c - a), dLo = r(d - b);
      var s = Math.sin(dLa/2)*Math.sin(dLa/2) + Math.cos(r(a))*Math.cos(r(c))*Math.sin(dLo/2)*Math.sin(dLo/2);
      return 2 * R * Math.asin(Math.sqrt(s));
    }
    function twWalk(km) { return Math.max(1, Math.round((km || 0) * 12)); }
    // Bucket a coordinate to its nearest region (events carry lat/lng only).
    function twNearestRegion(la, ln) {
      if (typeof la !== "number" || typeof ln !== "number") return null;
      var best = null, bd = Infinity;
      Object.keys(TW_REGIONS).forEach(function(t) {
        var c = TW_REGIONS[t], d = twKm(la, ln, c[0], c[1]);
        if (d < bd) { bd = d; best = t; }
      });
      return best;
    }
    // Prose label for a region: "the East" / "Central" (caller handles "You").
    function twAreaLabel(r) { return r === "Central" ? "Central" : "the " + r; }
    function twWeekBounds() {
      var now = new Date();
      var day = now.getDay();                 // 0 Sun .. 6 Sat
      var diff = (day === 0 ? -6 : 1 - day);  // back to Monday
      var mon = new Date(now); mon.setDate(now.getDate() + diff); mon.setHours(0,0,0,0);
      var sun = new Date(mon); sun.setDate(mon.getDate() + 6); sun.setHours(23,59,59,999);
      return { start: mon, end: sun };
    }
    function twFmtWhen(ev) {
      try {
        var s = ev && ev.startAt && ev.startAt.toDate ? ev.startAt.toDate() : (ev && ev.startAt ? new Date(ev.startAt) : null);
        if (!s || isNaN(s.getTime())) return "";
        var days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"], mons = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        var t = s.toLocaleTimeString("en-SG", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase().replace(/\s/g, "");
        return days[s.getDay()] + " " + s.getDate() + " " + mons[s.getMonth()] + " · " + t;
      } catch (e) { return ""; }
    }
    const TW_CSS = `
.tw-wrap{max-width:1080px;margin:0 auto;padding:8px 20px 60px;font-family:var(--sans);color:var(--text)}
.tw-mast{padding:18px 0 10px}
.tw-eyebrow{font-family:var(--mono);font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:var(--amber)}
.tw-title{font-family:var(--serif);font-weight:600;font-size:clamp(26px,4vw,42px);line-height:1.05;margin:8px 0 6px}
.tw-title span{color:var(--amber)}
.tw-sub{color:var(--muted);font-size:15px;max-width:560px}
.tw-map-hero{margin:14px 0 6px}
.tw-pills,.tw-filters{display:flex;flex-wrap:wrap;gap:8px;margin:14px 0}
.tw-pill{display:inline-flex;align-items:center;gap:6px;white-space:nowrap;font-size:12.5px;font-weight:500;padding:7px 14px;border-radius:9999px;border:1px solid var(--border);background:var(--card);color:var(--muted);cursor:pointer;transition:.15s}
.tw-pill:hover{border-color:var(--amber);color:var(--text)}
.tw-pill.on{background:var(--amber);border-color:var(--amber);color:#fff}
.tw-pill-count{display:inline-block;margin-left:6px;min-width:16px;height:16px;line-height:16px;padding:0 5px;text-align:center;font-size:10.5px;font-weight:700;border-radius:9999px;background:var(--coral);color:#fff;vertical-align:middle}
.tw-pill.on .tw-pill-count{background:#fff;color:var(--amber)}
.tw-feed{display:flex;flex-direction:column;gap:12px;margin-top:6px}
.tw-row{display:grid;grid-template-columns:62px minmax(0,1fr) auto;gap:16px;align-items:center;background:var(--card);border:1px solid var(--border);border-radius:16px;padding:14px 18px;transition:.15s;cursor:pointer;color:inherit;text-decoration:none}
.tw-row:hover{border-color:var(--amber);box-shadow:0 6px 18px rgba(0,0,0,.06)}
.tw-thumb{width:62px;height:62px;border-radius:14px;background-size:cover;background-position:center;background-color:var(--cream);display:grid;place-items:center;font-size:24px;border:1px solid var(--border)}
.tw-mid{min-width:0}
.tw-kick{font-family:var(--mono);font-size:10px;letter-spacing:.08em;text-transform:uppercase;color:var(--amber);margin-bottom:3px;display:flex;gap:7px;align-items:center}
.tw-pick{background:var(--coral);color:#fff;border-radius:9999px;padding:1px 7px;font-size:8.5px;letter-spacing:.04em}
.tw-sponsored{background:#8a5104;color:#fff;border-radius:9999px;padding:1px 7px;font-size:8.5px;letter-spacing:.05em;text-transform:uppercase;font-weight:700}
.tw-mid h4{font-family:var(--serif);font-weight:600;font-size:17.5px;line-height:1.2;margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tw-desc{font-size:12.8px;color:var(--muted);margin-top:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tw-when{font-size:12px;color:var(--light);margin-top:3px}
.tw-right{display:flex;flex-direction:column;gap:8px;align-items:center;justify-content:center;min-width:78px;padding-left:14px;border-left:1px solid var(--border)}
.tw-dist{font-family:var(--serif);font-weight:700;font-size:20px;color:#a85f04;line-height:1;text-align:center}
.tw-dist small{font-size:10.5px;font-weight:400;color:var(--light);font-family:var(--sans);display:block;margin-top:3px}
.tw-view{font-size:11px;font-weight:500;color:var(--amber);border:1px solid var(--amber-light);background:var(--amber-light);border-radius:9999px;padding:4px 12px;white-space:nowrap}
.tw-empty-note{background:var(--amber-light);border:1px solid #F3D9C4;border-radius:14px;padding:14px 16px;font-size:14px;color:var(--text);margin:12px 0}
.tw-cta{margin:26px 0 10px;background:var(--cream);border:1px solid var(--border);border-radius:18px;padding:22px 24px;display:flex;justify-content:space-between;align-items:center;gap:20px;flex-wrap:wrap}
.tw-cta h3{font-family:var(--serif);font-size:20px;font-weight:600}
.tw-cta p{font-size:13.5px;color:var(--muted);margin-top:4px;max-width:460px}
.tw-btn{background:var(--amber);color:#fff;border:none;border-radius:20px;padding:12px 22px;font-size:14px;font-weight:600;font-family:var(--sans);cursor:pointer}
.tw-btn:hover{opacity:.92}.tw-btn:disabled{opacity:.6;cursor:default}
.tw-admin-link{margin-top:14px;font-size:12.5px;color:var(--muted);cursor:pointer;text-align:center}
.tw-admin-link:hover{color:var(--amber)}
.tw-skel-wrap{display:flex;flex-direction:column;gap:12px;margin-top:8px}
.tw-skel{height:90px;border-radius:16px;background:linear-gradient(90deg,#f3ece3,#faf5ee,#f3ece3);background-size:200% 100%;animation:twsh 1.2s infinite}
@keyframes twsh{0%{background-position:200% 0}100%{background-position:-200% 0}}
.tw-gate{text-align:center;padding:60px 20px}
.tw-gate h2{font-family:var(--serif);font-size:24px;margin-bottom:8px}
.tw-gate p{color:var(--muted);margin-bottom:18px}
.tw-form-layout{display:grid;grid-template-columns:1.35fr 1fr;gap:28px;align-items:start;padding-top:10px}
.tw-form{background:var(--card);border:1px solid var(--border);border-radius:18px;padding:24px}
.tw-field{margin-bottom:18px}
.tw-field label{display:block;font-size:13px;font-weight:600;margin-bottom:6px}
.tw-hint{color:var(--light);font-weight:400;font-size:11.5px}
.tw-cnt{float:right;font-family:var(--mono);font-size:11px;color:var(--light)}
.tw-form input,.tw-form textarea,.tw-form select{width:100%;font-family:var(--sans);font-size:14px;color:var(--text);background:var(--bg);border:1px solid var(--border);border-radius:12px;padding:11px 13px;outline:none}
.tw-form input:focus,.tw-form textarea:focus,.tw-form select:focus{border-color:var(--amber)}
.tw-form textarea{resize:vertical;min-height:80px}
.tw-two{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.tw-three{display:grid;grid-template-columns:1.3fr 1fr 1fr;gap:12px}
.tw-chips{display:flex;flex-wrap:wrap;gap:8px}
.tw-submit-row{display:flex;align-items:center;gap:14px;padding-top:14px;border-top:1px solid var(--border)}
.tw-note{font-size:12px;color:var(--light)}
.tw-photo-drop{display:flex;align-items:center;gap:10px;justify-content:center;width:100%;padding:16px 14px;border:1.5px dashed var(--border);border-radius:12px;background:var(--bg);color:var(--muted);font-size:13px;font-weight:500;cursor:pointer;transition:.15s}
.tw-photo-drop:hover{border-color:var(--amber);color:var(--amber)}
.tw-photo-drop .icon,.tw-photo-drop span{pointer-events:none}
.tw-photo-picked{position:relative;border-radius:12px;overflow:hidden;border:1px solid var(--border)}
.tw-photo-picked img{display:block;width:100%;max-height:180px;object-fit:cover}
.tw-photo-clear{position:absolute;top:8px;right:8px;background:rgba(20,15,12,.72);color:#fff;border:none;border-radius:9999px;padding:5px 12px;font-size:12px;font-weight:600;font-family:var(--sans);cursor:pointer}
.tw-photo-clear:hover{background:rgba(20,15,12,.9)}
.tw-rail{position:sticky;top:80px}
.tw-rail-tag{font-family:var(--mono);font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--light);margin-bottom:10px}
.tw-rail-note{font-size:12px;color:var(--light);margin-top:12px;line-height:1.5}
.tw-prev{background:var(--card);border:1px solid var(--border);border-radius:16px;overflow:hidden;box-shadow:0 8px 24px rgba(0,0,0,.06)}
.tw-prev-img{height:130px;background:linear-gradient(135deg,#FCEADE,#c87606);display:grid;place-items:center;font-size:40px;background-size:cover;background-position:center;color:#fff}
.tw-prev-body{padding:15px 16px 17px}
.tw-prev-body h4{font-family:var(--serif);font-weight:600;font-size:18px;line-height:1.2;margin:6px 0}
.tw-prev-body .tw-desc{white-space:normal}
.tw-tabs{display:flex;gap:6px;border-bottom:1px solid var(--border);margin:10px 0 20px}
.tw-tab{font-size:13.5px;font-weight:500;color:var(--muted);padding:9px 14px;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;text-transform:capitalize}
.tw-tab.on{color:var(--amber);border-bottom-color:var(--amber)}
.tw-ev{background:var(--card);border:1px solid var(--border);border-radius:18px;padding:18px 20px;box-shadow:0 4px 16px rgba(0,0,0,.04);margin-bottom:16px}
.tw-ev-top{display:grid;grid-template-columns:64px minmax(0,1fr) auto;gap:16px;align-items:start}
.tw-ev-biz{font-size:12px;color:var(--light);margin-bottom:3px}
.tw-ev-top h3{font-family:var(--serif);font-weight:600;font-size:19px;line-height:1.2;margin:0}
.tw-ev-meta{display:flex;flex-wrap:wrap;gap:8px;margin-top:8px}
.tw-m{font-size:12px;color:var(--muted);background:var(--bg);border:1px solid var(--border);border-radius:9999px;padding:4px 10px}
.tw-m.type{background:var(--amber-light);border-color:#F3D9C4;color:#8a5104;font-weight:500}
.tw-src{font-family:var(--mono);font-size:10px;padding:4px 9px;border-radius:9999px;white-space:nowrap;align-self:start}
.src-sub{background:var(--cream);color:var(--muted)}.src-auto{background:#EBF3F9;color:#4d6f86}.src-ed{background:#F3E8FF;color:#7a5aa8}
.tw-blurb{margin-top:14px}
.tw-blurb label{font-size:11px;font-family:var(--mono);letter-spacing:.06em;text-transform:uppercase;color:var(--light);display:block;margin-bottom:5px}
.tw-blurb textarea{width:100%;font-family:var(--sans);font-size:13.5px;background:var(--bg);border:1px solid var(--border);border-radius:10px;padding:10px 12px;min-height:52px;outline:none;resize:vertical}
.tw-ev-actions{display:flex;align-items:center;justify-content:space-between;margin-top:16px;padding-top:14px;border-top:1px solid var(--border)}
.tw-pickbtn{display:flex;align-items:center;gap:7px;font-size:13px;color:var(--muted);cursor:pointer;user-select:none}
.tw-star{width:26px;height:26px;border-radius:8px;border:1px solid var(--border);display:grid;place-items:center;font-size:14px;background:var(--card)}
.tw-pickbtn.on{color:#8a5104;font-weight:600}.tw-pickbtn.on .tw-star{background:var(--amber-light);border-color:#F3D9C4}
.tw-toggles{display:flex;align-items:center;gap:18px;flex-wrap:wrap}
.tw-sponsorbtn{display:flex;align-items:center;gap:7px;font-size:13px;color:var(--muted);cursor:pointer;user-select:none}
.tw-sponsorbtn .tw-spico{width:26px;height:26px;border-radius:8px;border:1px solid var(--border);display:grid;place-items:center;font-size:13px;font-weight:700;background:var(--card)}
.tw-sponsorbtn.on{color:#8a5104;font-weight:600}.tw-sponsorbtn.on .tw-spico{background:#FBEFD9;border-color:#E9C782}
.tw-sponsorbtn.busy{opacity:.6;pointer-events:none}
.tw-badge-sp{background:#8a5104;color:#fff;border-color:#8a5104;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
.tw-btns{display:flex;gap:10px}
.tw-rej{background:var(--card);border:1px solid var(--border);color:var(--muted);border-radius:20px;padding:9px 18px;font-size:13px;font-weight:500;cursor:pointer;font-family:var(--sans)}
.tw-rej:hover{border-color:var(--coral);color:var(--coral)}
.tw-app{background:var(--amber);border:none;color:#fff;border-radius:20px;padding:9px 20px;font-size:13px;font-weight:600;cursor:pointer;font-family:var(--sans)}
.tw-status-lbl{font-size:12.5px;color:var(--muted);text-transform:capitalize}
.tw-admin-bar{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;margin:2px 0 16px}
.tw-src-filters{display:flex;gap:8px;flex-wrap:wrap}
.tw-admin-bar .tw-btn{padding:9px 18px;font-size:13px}
@media(max-width:820px){.tw-form-layout{grid-template-columns:1fr}.tw-rail{position:static}}
@media(max-width:560px){.tw-ev-top{grid-template-columns:1fr}.tw-src{justify-self:start}.tw-ev-actions{flex-direction:column;align-items:stretch;gap:12px}.tw-btns{justify-content:flex-end}}
/* per-type thumbnail / hero gradients (fallback when no cover photo). Inline
   background-image (a real cover) overrides these automatically. */
.tw-thumb{color:#8a5104}
.tw-thumb.t-popup,.tw-modal-hero.t-popup{background-image:linear-gradient(135deg,#FDE7DE,#F6B8A6)}
.tw-thumb.t-class,.tw-modal-hero.t-class{background-image:linear-gradient(135deg,#EFE3FB,#C9A9EC)}
.tw-thumb.t-market,.tw-modal-hero.t-market{background-image:linear-gradient(135deg,#FCEBCF,#F0C070)}
.tw-thumb.t-drop,.tw-modal-hero.t-drop{background-image:linear-gradient(135deg,#DDEBF4,#9FC3DC)}
.tw-thumb.t-workshop,.tw-modal-hero.t-workshop{background-image:linear-gradient(135deg,#F5E6D6,#D9B48A)}
.tw-thumb.t-sale,.tw-modal-hero.t-sale{background-image:linear-gradient(135deg,#FBE0E4,#EFA6B4)}
.tw-row:focus-visible{outline:2px solid var(--amber);outline-offset:2px}
/* event detail modal */
.tw-modal-overlay{position:fixed;inset:0;background:rgba(20,15,12,.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:18px;overflow-y:auto}
.tw-modal{background:var(--card);border-radius:20px;max-width:520px;width:100%;max-height:calc(100vh - 36px);overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.3);position:relative}
.tw-modal-x{position:absolute;top:12px;right:12px;z-index:3;width:34px;height:34px;border-radius:50%;border:none;background:rgba(255,255,255,.92);color:#333;font-size:22px;line-height:1;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,.2)}
.tw-modal-hero{height:200px;background-size:cover;background-position:center;background-color:var(--cream);display:flex;align-items:flex-end;gap:8px;padding:12px;position:relative;border-radius:20px 20px 0 0}
.tw-modal-hero-ico{position:absolute;inset:0;display:grid;place-items:center;color:#8a5104;opacity:.7}
.tw-modal-type{position:relative;display:inline-flex;align-items:center;gap:5px;font-family:var(--mono);font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;background:rgba(255,255,255,.92);color:#8a5104;padding:5px 10px;border-radius:9999px}
.tw-modal-pick{position:relative;background:var(--coral);color:#fff;border-radius:9999px;padding:5px 10px;font-size:10px;font-weight:600}
.tw-modal-sponsored{position:relative;background:#8a5104;color:#fff;border-radius:9999px;padding:5px 10px;font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
.tw-modal-body{padding:20px 22px 24px}
.tw-modal-title{font-family:var(--serif);font-weight:600;font-size:24px;line-height:1.15;margin:0 0 12px}
.tw-modal-meta{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px}
.tw-modal-meta .tw-m{display:inline-flex;align-items:center;gap:5px}
.tw-modal-desc{font-size:14.5px;line-height:1.6;color:var(--text);margin:0 0 16px}
.tw-modal-foot{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;padding-top:14px;border-top:1px solid var(--border)}
.tw-modal-src{font-family:var(--mono);font-size:11px;color:var(--light)}
.tw-modal-cta{background:var(--amber);color:#fff;text-decoration:none;border-radius:20px;padding:10px 18px;font-size:13.5px;font-weight:600}
.tw-modal-cta:hover{opacity:.92}
`;
    // Per-type thumbnail: cover photo if present, else an on-brand gradient
    // (defined in CSS by the t-<type> class) with the type's line-icon.
    function twThumb(e, ty) {
      var img = twThumbImg(e);
      return (
        <div className={"tw-thumb t-" + (e.type || "popup")}
             style={img ? { backgroundImage: "url('" + img.replace(/'/g, "%27") + "')" } : {}}>
          {img ? "" : twIco(ty, 24)}
        </div>
      );
    }

    // In-app event detail — photo/fallback, when/where, description, price, a
    // mini map (reuses DetailMap), and an outbound "view on source" link.
    // Facts only; no generated copy.
    function EventDetailModal({ e, userLocation, onClose }) {
      var ty = TW_TYPES[e.type] || { l: "Event", icon: "pin" };
      var heroImg = twThumbImg(e);
      var link = e.ctaUrl || e.sourceUrl || "";
      var km = (userLocation && typeof userLocation.lat === "number" && typeof e.lat === "number")
        ? twKm(userLocation.lat, userLocation.lng, e.lat, e.lng) : null;
      useEffect(function() {
        function onKey(ev) { if (ev.key === "Escape") onClose(); }
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return function() { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
      }, []);
      return (
        <div className="tw-modal-overlay" onClick={onClose}>
          <div className="tw-modal" onClick={function(ev) { ev.stopPropagation(); }} role="dialog" aria-modal="true">
            <button className="tw-modal-x" onClick={onClose} aria-label="Close">×</button>
            <div className={"tw-modal-hero t-" + (e.type || "popup")}
                 style={heroImg ? { backgroundImage: "url('" + heroImg.replace(/'/g, "%27") + "')" } : {}}>
              {heroImg ? "" : <span className="tw-modal-hero-ico">{twIco(ty, 56)}</span>}
              <span className="tw-modal-type">{twIco(ty, 12)} {ty.l}</span>
              {e.sponsored ? <span className="tw-modal-sponsored">Sponsored</span> : null}
              {e.curatedPick ? <span className="tw-modal-pick">★ pick</span> : null}
            </div>
            <div className="tw-modal-body">
              <h2 className="tw-modal-title">{e.title || "Untitled event"}</h2>
              <div className="tw-modal-meta">
                <span className="tw-m"><Icon name="calendar" size={13} /> {twFmtWhen(e)}</span>
                {e.locationText ? <span className="tw-m"><Icon name="pin" size={13} /> {e.locationText}</span> : null}
                {e.price ? <span className="tw-m">{e.price}</span> : null}
                {km != null ? <span className="tw-m">{twWalk(km)} min walk</span> : null}
              </div>
              {e.description ? <p className="tw-modal-desc">{e.description}</p> : null}
              {typeof e.lat === "number" ? <DetailMap lat={e.lat} lng={e.lng} name={e.title || "Event"} /> : null}
              <div className="tw-modal-foot">
                <span className="tw-modal-src">{e.sourceName ? "via " + e.sourceName : (e.source === "auto" ? "Auto-sourced" : "")}</span>
                {link ? <a className="tw-modal-cta" href={link} target="_blank" rel="noopener noreferrer">View on {e.sourceName || "source"} →</a> : null}
              </div>
            </div>
          </div>
        </div>
      );
    }

    function twEventRow(e, i, onOpen) {
      var ty = TW_TYPES[e.type] || { l: "Event", icon: "pin" };
      return (
        <div className="tw-row" key={e.id || i} role="button" tabIndex={0}
             onClick={function() { onOpen(e); }}
             onKeyDown={function(ev) { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onOpen(e); } }}>
          {twThumb(e, ty)}
          <div className="tw-mid">
            <div className="tw-kick">{twIco(ty, 12)} {ty.l}{e.sponsored ? <span className="tw-sponsored">Sponsored</span> : null}{e.curatedPick ? <span className="tw-pick">★ pick</span> : null}</div>
            <h4>{e.title || "Untitled event"}</h4>
            <div className="tw-desc">{e.businessName ? e.businessName + " · " : ""}{e.description || ""}</div>
            <div className="tw-when">{twFmtWhen(e)}{e.locationText ? " · " + e.locationText : ""}</div>
          </div>
          <div className="tw-right">
            <div className="tw-dist">{twWalk(e.km)}<small>min walk</small></div>
            <span className="tw-view">View →</span>
          </div>
        </div>
      );
    }
    function twBizRow(b, i, navigate) {
      var cover = b.coverPhotoUrl || "";
      var target = b.slug || b.id || "";
      return (
        <a className="tw-row" key={b.id || i} href={"/detail/" + target}
           onClick={(ev) => { ev.preventDefault(); if (target) navigate("detail/" + target); }}>
          <div className="tw-thumb" style={cover ? { backgroundImage: "url('" + cover.replace(/'/g, "%27") + "')" } : {}}>{cover ? "" : "🏠"}</div>
          <div className="tw-mid">
            <div className="tw-kick">Home business</div>
            <h4>{b.name || "Business"}</h4>
            <div className="tw-desc">{(b.description || "").slice(0, 90)}</div>
          </div>
          <div className="tw-right">
            <div className="tw-dist">{twWalk(b.km)}<small>min walk</small></div>
            <span className="tw-view">View →</span>
          </div>
        </a>
      );
    }

    function ThisWeekPage({ db, currentUser, userLocation, liveBusinesses, isAdmin, navigate }) {
      const [events, setEvents] = useState(null);   // null = loading
      const [area, setArea] = useState("near"); // "near" = my location / all areas; otherwise a TW_REGIONS name
      const [cat, setCat] = useState("all");
      const [selectedEvent, setSelectedEvent] = useState(null);

      useEffect(function() {
        var wb = twWeekBounds();
        // Two reads: (1) this week's approved events, and (2) sponsored approved
        // events regardless of date — sponsors surface in This Week even when the
        // event itself isn't happening this week. Both stay constrained to
        // status == "approved" so the public read rule is satisfied (no composite
        // index needed — two equality filters). Merged + de-duped by id below.
        var weekQ = db.collection("events")
          .where("status", "==", "approved")
          .where("startAt", ">=", firebase.firestore.Timestamp.fromDate(wb.start))
          .where("startAt", "<=", firebase.firestore.Timestamp.fromDate(wb.end))
          .orderBy("startAt")
          .get()
          .then(function(snap) { return snap.docs; })
          .catch(function(e) { console.error("[ThisWeek] week load failed:", e); return []; });
        var sponsoredQ = db.collection("events")
          .where("status", "==", "approved")
          .where("sponsored", "==", true)
          .get()
          .then(function(snap) { return snap.docs; })
          .catch(function(e) { console.error("[ThisWeek] sponsored load failed:", e); return []; });
        Promise.all([weekQ, sponsoredQ]).then(function(res) {
          var map = {};
          res[0].forEach(function(d) { map[d.id] = Object.assign({ id: d.id }, d.data()); });
          res[1].forEach(function(d) { map[d.id] = Object.assign({ id: d.id }, d.data()); });
          setEvents(Object.keys(map).map(function(k) { return map[k]; }));
        });
      }, []);

      var hasLoc = !!(userLocation && typeof userLocation.lat === "number");
      var usingNear = (area === "near");
      var selectedRegion = usingNear ? null : area;
      // Centre: your location when "near me"; otherwise the chosen region (Central is a neutral fallback).
      var center = (usingNear && hasLoc) ? [userLocation.lat, userLocation.lng]
                 : (selectedRegion ? TW_REGIONS[selectedRegion] : TW_REGIONS.Central);

      // Per-region event tallies for this week (ignores the category filter — it's
      // the area total) to power the count bubbles on each region pill.
      var regionCounts = {};
      Object.keys(TW_REGIONS).forEach(function(r) { regionCounts[r] = 0; });
      (events || []).forEach(function(e) {
        var r = twNearestRegion(typeof e.lat === "number" ? e.lat : null, typeof e.lng === "number" ? e.lng : null);
        if (r) regionCounts[r]++;
      });

      var evList = (events || []).map(function(e) {
        var la = typeof e.lat === "number" ? e.lat : center[0];
        var ln = typeof e.lng === "number" ? e.lng : center[1];
        return Object.assign({}, e, { km: twKm(center[0], center[1], la, ln), _region: twNearestRegion(la, ln) });
      });
      if (cat !== "all") evList = evList.filter(function(e) { return e.type === cat; });
      // Area filter: a picked region shows only events bucketed to it; "near me" shows all.
      if (selectedRegion) evList = evList.filter(function(e) { return e._region === selectedRegion; });
      // Sponsored events pin to the top; everything else stays nearest-first.
      evList.sort(function(a, b) {
        var sa = a.sponsored ? 1 : 0, sb = b.sponsored ? 1 : 0;
        if (sa !== sb) return sb - sa;
        return (a.km || 0) - (b.km || 0);
      });

      var loading = events === null;
      var noResults = !loading && evList.length === 0;
      var fb = [];
      if (noResults) {
        fb = (liveBusinesses || []).filter(function(b) { return typeof b.lat === "number"; })
          .map(function(b) { return Object.assign({}, b, { km: twKm(center[0], center[1], b.lat, b.lng) }); })
          .sort(function(a, b) { return a.km - b.km; }).slice(0, 12);
      }

      return (
        <div className="tw-wrap">
          <style dangerouslySetInnerHTML={{ __html: TW_CSS }} />
          <div className="tw-mast">
            <div className="tw-eyebrow">this week · around the corner</div>
            <h1 className="tw-title">This Week Around <span>{selectedRegion ? twAreaLabel(selectedRegion) : "You"}</span></h1>
            <p className="tw-sub">Pop-ups, classes and small joys near you — refreshed every Monday.</p>
          </div>

          <div className="tw-map-hero">
            <NeighbourhoodHeroMap userLocation={userLocation} liveBusinesses={liveBusinesses} navigate={navigate} />
          </div>

          <div className="tw-pills">
            <button className={"tw-pill" + (usingNear ? " on" : "")} onClick={function() { setArea("near"); }}>{hasLoc ? "📍 Near me" : "🗺️ All areas"}</button>
            {Object.keys(TW_REGIONS).map(function(t) {
              return <button key={t} className={"tw-pill" + (area === t ? " on" : "")} onClick={function() { setArea(t); }}>{t}{regionCounts[t] > 0 && <span className="tw-pill-count">{regionCounts[t]}</span>}</button>;
            })}
          </div>

          <div className="tw-filters">
            <span className={"tw-pill" + (cat === "all" ? " on" : "")} onClick={function() { setCat("all"); }}>All</span>
            {Object.keys(TW_TYPES).map(function(k) {
              return <span key={k} className={"tw-pill" + (cat === k ? " on" : "")} onClick={function() { setCat(k); }}>{twIco(TW_TYPES[k], 13)} {TW_TYPES[k].l}</span>;
            })}
          </div>

          {loading && <div className="tw-skel-wrap"><div className="tw-skel"></div><div className="tw-skel"></div><div className="tw-skel"></div></div>}

          {!loading && evList.length > 0 && (
            <div className="tw-feed">{evList.map(function(e, i) { return twEventRow(e, i, setSelectedEvent); })}</div>
          )}

          {noResults && (
            <div className="tw-empty-note">
              <b>Nothing scheduled this week{selectedRegion ? " in " + twAreaLabel(selectedRegion) : (hasLoc ? " near you" : "")} yet.</b> Here are home businesses {selectedRegion ? "in that area" : "near you"} in the meantime.
            </div>
          )}
          {noResults && fb.length > 0 && (
            <div className="tw-feed">{fb.map(function(b, i) { return twBizRow(b, i, navigate); })}</div>
          )}

          <div className="tw-cta">
            <div>
              <h3>Running something this week?</h3>
              <p>Home businesses can submit a pop-up, class or drop — free. Verified events go into Monday's issue.</p>
            </div>
            <button className="tw-btn" onClick={function() { navigate("submit-event"); }}>Submit an event →</button>
          </div>

          {selectedEvent && <EventDetailModal e={selectedEvent} userLocation={userLocation} onClose={function() { setSelectedEvent(null); }} />}
        </div>
      );
    }

    function SubmitEventPage({ db, currentUser, userBusinesses, navigate }) {
      const [bizId, setBizId] = useState("");
      const [title, setTitle] = useState("");
      const [type, setType] = useState("popup");
      const [date, setDate] = useState("");
      const [startT, setStartT] = useState("");
      const [endT, setEndT] = useState("");
      const [loc, setLoc] = useState("");
      const [desc, setDesc] = useState("");
      const [price, setPrice] = useState("");
      const [link, setLink] = useState("");
      const [photoFile, setPhotoFile] = useState(null);
      const [photoPreview, setPhotoPreview] = useState("");
      const [submitting, setSubmitting] = useState(false);

      useEffect(function() {
        if (userBusinesses && userBusinesses.length && !bizId) setBizId(userBusinesses[0].id);
      }, [userBusinesses]);

      // Revoke the object URL when the preview changes / unmounts to avoid leaks.
      useEffect(function() {
        return function() { if (photoPreview) URL.revokeObjectURL(photoPreview); };
      }, [photoPreview]);

      function onPickPhoto(e) {
        var f = e.target.files && e.target.files[0];
        if (!f) return;
        if (!/^image\//.test(f.type)) { showToast("Please choose an image file.", "error"); return; }
        if (f.size > 8 * 1024 * 1024) { showToast("That image is over 8MB — please pick a smaller one.", "error"); return; }
        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoFile(f);
        setPhotoPreview(URL.createObjectURL(f));
      }
      function clearPhoto() {
        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoFile(null);
        setPhotoPreview("");
      }

      if (!currentUser) {
        return <div className="tw-wrap"><style dangerouslySetInnerHTML={{ __html: TW_CSS }} /><div className="tw-gate"><h2>Sign in to submit an event</h2><p>You'll need an account and a listed business first.</p><button className="tw-btn" onClick={function() { navigate("welcome"); }}>Sign in</button></div></div>;
      }
      if (!userBusinesses || userBusinesses.length === 0) {
        return <div className="tw-wrap"><style dangerouslySetInnerHTML={{ __html: TW_CSS }} /><div className="tw-gate"><h2>List your business first</h2><p>Events belong to a home business. Add yours, then come back to post an event.</p><button className="tw-btn" onClick={function() { navigate("add-business"); }}>List my business</button></div></div>;
      }

      var biz = userBusinesses.find(function(b) { return b.id === bizId; }) || userBusinesses[0];
      var ty = TW_TYPES[type];

      async function submit() {
        if (!title.trim() || !date || !startT || !desc.trim()) { showToast("Please fill in the event title, date, start time and description.", "error"); return; }
        if (!biz || typeof biz.lat !== "number") { showToast("This business has no map location yet — set it in your listing first.", "error"); return; }
        setSubmitting(true);
        try {
          // Optional event photo: upload it and use as the cover. If none was
          // chosen, fall back to the hosting business's cover photo.
          var coverUrl = biz.coverPhotoUrl || "";
          if (photoFile) {
            var safeName = (photoFile.name || "photo").replace(/[^a-zA-Z0-9._-]/g, "_").slice(-40);
            var ref = storage.ref("events/" + currentUser.uid + "/" + Date.now() + "_" + safeName);
            var compressed = await compressImage(photoFile, 1200, 0.85);
            await ref.put(compressed, { contentType: "image/jpeg" });
            coverUrl = await ref.getDownloadURL();
          }
          var startDate = new Date(date + "T" + startT + ":00");
          var endDate = endT ? new Date(date + "T" + endT + ":00") : null;
          await db.collection("events").add({
            businessId: biz.id,
            businessName: biz.name || "",
            businessSlug: biz.slug || "",
            title: title.trim(),
            type: type,
            description: desc.trim(),
            startAt: firebase.firestore.Timestamp.fromDate(startDate),
            endAt: endDate ? firebase.firestore.Timestamp.fromDate(endDate) : null,
            lat: biz.lat, lng: biz.lng,
            locationText: loc.trim() || biz.address || biz.street || "",
            coverPhotoUrl: coverUrl,
            hasOwnPhoto: !!photoFile,
            price: price.trim() || "",
            ctaUrl: link.trim() || "",
            source: "submission",
            status: "pending",
            curatedPick: false,
            sponsored: false,
            ownerId: currentUser.uid,
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
          });
          setSubmitting(false);
          showToast("Event submitted for review — you'll hear back before Monday.", "success");
          navigate("this-week");
        } catch (e) {
          console.error("[SubmitEvent]", e); setSubmitting(false);
          showToast("Something went wrong submitting your event. Please try again.", "error");
        }
      }

      return (
        <div className="tw-wrap">
          <style dangerouslySetInnerHTML={{ __html: TW_CSS }} />
          <div className="tw-mast">
            <div className="tw-eyebrow">this week · for home businesses</div>
            <h1 className="tw-title">Add something to This Week</h1>
            <p className="tw-sub">Reviewed by the nooks team before it's published.</p>
          </div>
          <div className="tw-form-layout">
            <div className="tw-form">
              <div className="tw-field"><label>Which business is hosting?</label>
                <select value={bizId} onChange={function(e) { setBizId(e.target.value); }}>
                  {userBusinesses.map(function(b) { return <option key={b.id} value={b.id}>{b.name}</option>; })}
                </select>
              </div>
              <div className="tw-field"><label>Event title</label><input type="text" value={title} onChange={function(e) { setTitle(e.target.value); }} placeholder="e.g. Ah Ma's Kueh — one-day pop-up" /></div>
              <div className="tw-field"><label>What kind of event?</label>
                <div className="tw-chips">{Object.keys(TW_TYPES).map(function(k) { return <span key={k} className={"tw-pill" + (type === k ? " on" : "")} onClick={function() { setType(k); }}>{twIco(TW_TYPES[k], 13)} {TW_TYPES[k].l}</span>; })}</div>
              </div>
              <div className="tw-field"><label>When</label><div className="tw-three"><input type="date" value={date} onChange={function(e) { setDate(e.target.value); }} /><input type="time" value={startT} onChange={function(e) { setStartT(e.target.value); }} /><input type="time" value={endT} onChange={function(e) { setEndT(e.target.value); }} /></div></div>
              <div className="tw-field"><label>Where <span className="tw-hint">(defaults to your business address)</span></label><input type="text" value={loc} onChange={function(e) { setLoc(e.target.value); }} placeholder={biz.address || "e.g. Void deck, Blk 201"} /></div>
              <div className="tw-field"><label>Short description <span className="tw-cnt">{desc.length}/200</span></label><textarea maxLength={200} value={desc} onChange={function(e) { setDesc(e.target.value); }} placeholder="One or two lines a neighbour would want to read."></textarea></div>
              <div className="tw-two"><div className="tw-field"><label>Price <span className="tw-hint">(optional)</span></label><input type="text" value={price} onChange={function(e) { setPrice(e.target.value); }} placeholder="e.g. $6 a box" /></div><div className="tw-field"><label>Booking link <span className="tw-hint">(optional)</span></label><input type="text" value={link} onChange={function(e) { setLink(e.target.value); }} placeholder="https://…" /></div></div>
              <div className="tw-field">
                <label>Event photo <span className="tw-hint">(optional — defaults to your business cover)</span></label>
                {photoPreview ? (
                  <div className="tw-photo-picked">
                    <img src={photoPreview} alt="Event photo preview" />
                    <button type="button" className="tw-photo-clear" onClick={clearPhoto}>Remove photo</button>
                  </div>
                ) : (
                  <label className="tw-photo-drop">
                    <Icon name="photo" size={20} />
                    <span>Upload a photo for this event</span>
                    <input type="file" accept="image/*" onChange={onPickPhoto} style={{ display: "none" }} />
                  </label>
                )}
              </div>
              <div className="tw-submit-row"><button className="tw-btn" disabled={submitting} onClick={submit}>{submitting ? "Submitting…" : "Submit for review"}</button><span className="tw-note">You'll hear back before Monday.</span></div>
            </div>
            <aside className="tw-rail">
              <div className="tw-rail-tag">Live preview</div>
              <div className="tw-prev">
                {(function() {
                  var prevImg = photoPreview || biz.coverPhotoUrl || "";
                  return <div className="tw-prev-img" style={prevImg ? { backgroundImage: "url('" + prevImg.replace(/'/g, "%27") + "')" } : {}}>{prevImg ? "" : twIco(ty, 34)}</div>;
                })()}
                <div className="tw-prev-body">
                  <div className="tw-kick">{twIco(ty, 12)} {ty.l}</div>
                  <h4>{title || "Your event title"}</h4>
                  <div className="tw-desc">{desc || "Your short description will show here."}</div>
                  <div className="tw-when">{date ? twFmtWhen({ startAt: new Date(date + "T" + (startT || "00:00") + ":00") }) : "When"}</div>
                </div>
              </div>
              <p className="tw-rail-note">This is how your event will appear once approved. Editors may lightly tidy the wording.</p>
            </aside>
          </div>
        </div>
      );
    }

    function AdminEventCard({ ev, onDecide, onToggleSponsored, sponsorable, readOnly }) {
      const [blurb, setBlurb] = useState(ev.description || "");
      const [pick, setPick] = useState(!!ev.curatedPick);
      const [sponsored, setSponsored] = useState(!!ev.sponsored);
      const [spBusy, setSpBusy] = useState(false);
      var ty = TW_TYPES[ev.type] || { l: "Event", icon: "pin" };
      var srcCls = ev.source === "auto" ? "src-auto" : (ev.source === "editorial" ? "src-ed" : "src-sub");
      // Live toggle for already-approved events (sponsorship is usually applied
      // after approval). Writes immediately via the parent handler.
      function liveToggleSponsor() {
        if (spBusy || !onToggleSponsored) return;
        var next = !sponsored;
        setSpBusy(true);
        Promise.resolve(onToggleSponsored(ev, next)).then(function() {
          setSponsored(next);
        }).finally(function() { setSpBusy(false); });
      }
      return (
        <div className="tw-ev">
          <div className="tw-ev-top">
            <div className={"tw-thumb t-" + (ev.type || "popup")} style={twThumbImg(ev) ? { backgroundImage: "url('" + twThumbImg(ev).replace(/'/g, "%27") + "')" } : {}}>{twThumbImg(ev) ? "" : twIco(ty, 24)}</div>
            <div className="tw-mid">
              <div className="tw-ev-biz">{ev.businessName || "—"}{ev.locationText ? " · " + ev.locationText : ""}</div>
              <h3>{ev.title}</h3>
              <div className="tw-ev-meta"><span className="tw-m type">{twIco(ty, 12)} {ty.l}</span><span className="tw-m">{twFmtWhen(ev)}</span>{ev.price ? <span className="tw-m">{ev.price}</span> : null}{sponsored ? <span className="tw-m tw-badge-sp">Sponsored</span> : null}</div>
            </div>
            <span className={"tw-src " + srcCls}>{ev.source || "submission"}</span>
          </div>
          {!readOnly && <div className="tw-blurb"><label>Blurb (editable)</label><textarea value={blurb} onChange={function(e) { setBlurb(e.target.value); }}></textarea></div>}
          <div className="tw-ev-actions">
            {!readOnly ? (
              <React.Fragment>
                <div className="tw-toggles">
                  <div className={"tw-pickbtn" + (pick ? " on" : "")} onClick={function() { setPick(!pick); }}><span className="tw-star">★</span>Curated pick</div>
                  <div className={"tw-sponsorbtn" + (sponsored ? " on" : "")} onClick={function() { setSponsored(!sponsored); }}><span className="tw-spico">$</span>Sponsored</div>
                </div>
                <div className="tw-btns"><button className="tw-rej" onClick={function() { onDecide(ev, "rejected", blurb, pick, sponsored); }}>Reject</button><button className="tw-app" onClick={function() { onDecide(ev, "approved", blurb, pick, sponsored); }}>Approve</button></div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span className="tw-status-lbl">{ev.status}{ev.reviewedBy ? " · by " + ev.reviewedBy : ""}</span>
                {sponsorable && (
                  <div className={"tw-sponsorbtn" + (sponsored ? " on" : "") + (spBusy ? " busy" : "")} onClick={liveToggleSponsor} title={sponsored ? "Remove sponsorship" : "Mark as sponsored — pins to top of This Week"}>
                    <span className="tw-spico">$</span>{spBusy ? "Saving…" : (sponsored ? "Sponsored" : "Mark sponsored")}
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        </div>
      );
    }

    function AdminEventsPage({ db, currentUser, isAdmin, navigate }) {
      const [items, setItems] = useState(null);
      const [tab, setTab] = useState("pending");
      const [srcFilter, setSrcFilter] = useState("all");
      const [bulkBusy, setBulkBusy] = useState(false);

      useEffect(function() {
        if (!isAdmin) return;
        setItems(null);
        db.collection("events").where("status", "==", tab).get()
          .then(function(snap) {
            var a = snap.docs.map(function(d) { return Object.assign({ id: d.id }, d.data()); });
            a.sort(function(x, y) {
              var tx = x.createdAt && x.createdAt.toMillis ? x.createdAt.toMillis() : 0;
              var tyv = y.createdAt && y.createdAt.toMillis ? y.createdAt.toMillis() : 0;
              return tyv - tx;
            });
            setItems(a);
          })
          .catch(function(e) { console.error("[AdminEvents] load:", e); setItems([]); });
      }, [tab, isAdmin]);

      if (!isAdmin) {
        return <div className="tw-wrap"><style dangerouslySetInnerHTML={{ __html: TW_CSS }} /><div className="tw-gate"><h2>Access denied</h2><p>You don't have admin privileges.</p></div></div>;
      }

      function decide(ev, decision, blurb, pick, sponsored) {
        db.collection("events").doc(ev.id).update({
          status: decision,
          description: (blurb != null ? blurb : ev.description) || "",
          curatedPick: !!pick,
          sponsored: !!sponsored,
          reviewedBy: currentUser.email || currentUser.uid,
          reviewedAt: firebase.firestore.FieldValue.serverTimestamp()
        }).then(function() {
          showToast(decision === "approved" ? "Approved — live in this week's issue." : "Rejected.", decision === "approved" ? "success" : "info");
          setItems(function(prev) { return (prev || []).filter(function(x) { return x.id !== ev.id; }); });
        }).catch(function(e) { console.error("[AdminEvents] decide:", e); showToast("Update failed — check your admin access.", "error"); });
      }

      // Toggle sponsorship on an already-reviewed event (returns the promise so
      // the card can reflect success/failure). Sponsored events pin to the top
      // of This Week and surface regardless of their date.
      function toggleSponsored(ev, next) {
        return db.collection("events").doc(ev.id).update({
          sponsored: !!next,
          sponsoredBy: currentUser.email || currentUser.uid,
          sponsoredAt: firebase.firestore.FieldValue.serverTimestamp()
        }).then(function() {
          setItems(function(prev) { return (prev || []).map(function(x) { return x.id === ev.id ? Object.assign({}, x, { sponsored: !!next }) : x; }); });
          showToast(next ? "Marked as sponsored — pinned to the top of This Week." : "Sponsorship removed.", next ? "success" : "info");
        }).catch(function(e) { console.error("[AdminEvents] sponsor:", e); showToast("Update failed — check your admin access.", "error"); throw e; });
      }

      // Bulk-approve every currently-shown pending event in one batch.
      function bulkApprove(list) {
        if (!list.length || bulkBusy) return;
        if (!window.confirm("Approve all " + list.length + " event" + (list.length === 1 ? "" : "s") + "? They'll go live in This Week.")) return;
        setBulkBusy(true);
        var batch = db.batch();
        list.forEach(function(ev) {
          batch.update(db.collection("events").doc(ev.id), {
            status: "approved",
            curatedPick: !!ev.curatedPick,
            sponsored: !!ev.sponsored,
            reviewedBy: currentUser.email || currentUser.uid,
            reviewedAt: firebase.firestore.FieldValue.serverTimestamp()
          });
        });
        batch.commit().then(function() {
          var ids = {};
          list.forEach(function(ev) { ids[ev.id] = true; });
          setItems(function(prev) { return (prev || []).filter(function(x) { return !ids[x.id]; }); });
          setBulkBusy(false);
          showToast("Approved " + list.length + " event" + (list.length === 1 ? "" : "s") + " — live in this week's issue.", "success");
        }).catch(function(e) { console.error("[AdminEvents] bulkApprove:", e); setBulkBusy(false); showToast("Bulk approve failed — check your admin access.", "error"); });
      }

      var loading = items === null;
      var shown = (items || []).filter(function(ev) { return srcFilter === "all" || (ev.source || "submission") === srcFilter; });
      return (
        <div className="tw-wrap">
          <style dangerouslySetInnerHTML={{ __html: TW_CSS }} />
          <div className="tw-mast">
            <div className="tw-eyebrow">admin · events</div>
            <h1 className="tw-title">Events · review queue</h1>
            <p className="tw-sub">Approve what goes into next Monday's issues.</p>
          </div>
          <div className="tw-tabs">
            {["pending", "approved", "rejected"].map(function(t) { return <span key={t} className={"tw-tab" + (tab === t ? " on" : "")} onClick={function() { setTab(t); }}>{t}</span>; })}
          </div>
          {!loading && items.length > 0 && (
            <div className="tw-admin-bar">
              <div className="tw-src-filters">
                {["all", "auto", "submission"].map(function(s) {
                  var n = s === "all" ? items.length : items.filter(function(ev) { return (ev.source || "submission") === s; }).length;
                  return <span key={s} className={"tw-pill" + (srcFilter === s ? " on" : "")} onClick={function() { setSrcFilter(s); }}>{s === "all" ? "All" : (s === "auto" ? "Auto" : "Submitted")}{n ? <span className="tw-pill-count">{n}</span> : null}</span>;
                })}
              </div>
              {tab === "pending" && shown.length > 0 && (
                <button className="tw-btn" disabled={bulkBusy} onClick={function() { bulkApprove(shown); }}>{bulkBusy ? "Approving…" : "Approve all " + shown.length + " →"}</button>
              )}
            </div>
          )}
          {loading && <div className="tw-skel-wrap"><div className="tw-skel"></div><div className="tw-skel"></div></div>}
          {!loading && shown.length === 0 && <div className="tw-empty-note">Nothing {tab}{srcFilter !== "all" ? (srcFilter === "auto" ? " from auto-harvest" : " from submissions") : ""} right now.</div>}
          {!loading && shown.map(function(ev) { return <AdminEventCard key={ev.id} ev={ev} onDecide={decide} onToggleSponsored={toggleSponsored} sponsorable={tab === "approved"} readOnly={tab !== "pending"} />; })}
        </div>
      );
    }

    // Mobile speed-dial: one "+" button that expands to Store / Event / Wish.
    // Consolidates the three "create" entry points into a single FAB so the
    // corner stays clear of the chat launcher. Hidden on desktop via CSS.
    function MobileFabDial({ navigate }) {
      const [open, setOpen] = useState(false);
      const actions = [
        { key: "store", label: "List a store", icon: "store", go: "add-business" },
        { key: "event", label: "Post an event", icon: "calendar", go: "submit-event" },
        { key: "wish", label: "Make a wish", icon: "star", go: "map?wishes=1&make=1" }
      ];
      function pick(go) { setOpen(false); navigate(go); }
      return (
        <div className={"nooks-fab-dial" + (open ? " open" : "")}>
          {open && <div className="nooks-fab-scrim" onClick={function() { setOpen(false); }} />}
          <div className="nooks-fab-actions" role="menu" aria-hidden={!open}>
            {actions.map(function(a) {
              return (
                <button key={a.key} className="nooks-fab-action" role="menuitem" tabIndex={open ? 0 : -1}
                        onClick={function() { pick(a.go); }} title={a.label}>
                  <span className="nooks-fab-action-ico"><Icon name={a.icon} size={18} /></span>
                  <span className="nooks-fab-action-label">{a.label}</span>
                </button>
              );
            })}
          </div>
          <button className="nooks-fab-main" aria-label={open ? "Close create menu" : "Create"} aria-expanded={open}
                  onClick={function() { setOpen(function(v) { return !v; }); }}>
            <span className="nooks-fab-plus" aria-hidden="true">+</span>
          </button>
        </div>
      );
    }

    function App() {
    // GDPR Consent Banner Component
    function ConsentBanner() {
      const [showBanner, setShowBanner] = useState(function() {
        return !localStorage.getItem("nooks_consent");
      });

      const handleAccept = function() {
        localStorage.setItem("nooks_consent", JSON.stringify({ accepted: true, timestamp: Date.now() }));
        setShowBanner(false);
      };

      const handleDecline = function() {
        localStorage.setItem("nooks_consent", JSON.stringify({ accepted: false, timestamp: Date.now() }));
        window["ga-disable-G-CBTW0456Z2"] = true;
        setShowBanner(false);
      };

      if (!showBanner) return null;

      return (
        <div style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "var(--card)",
          borderTop: "1px solid var(--border)",
          padding: "1rem 1.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          zIndex: 999,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.1)"
        }}>
          <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--text)", flex: 1 }}>
            We use cookies to enhance your experience. By continuing to browse, you consent to our cookie policy.
          </p>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button
              onClick={handleDecline}
              style={{
                padding: "0.5rem 1rem",
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.9rem",
                color: "var(--text)",
                fontFamily: "var(--sans)"
              }}
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              style={{
                padding: "0.5rem 1rem",
                background: "var(--amber)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.9rem",
                fontFamily: "var(--sans)"
              }}
            >
              Accept
            </button>
          </div>
        </div>
      );
    }

      const [currentRoute, setCurrentRoute] = useState("welcome");
      const [currentUser, setCurrentUser] = useState(null);
      const [loading, setLoading] = useState(true);
      const [sidebarOpen, setSidebarOpen] = useState(false);
      const [modalOpen, setModalOpen] = useState(false);
      const [modalTab, setModalTab] = useState("signin");
      const [searchTerm, setSearchTerm] = useState("");
      const [selectedCategory, setSelectedCategory] = useState("all");
      const [sortBy, setSortBy] = useState("popular");
      const [likes, setLikes] = useState([]);
      const [userBusinesses, setUserBusinesses] = useState([]);
      const [userProfile, setUserProfile] = useState(null);
      const [editingProfile, setEditingProfile] = useState(false);
      const [profileForm, setProfileForm] = useState({ firstName: "", lastName: "", displayName: "", email: "", phone: "", birthday: "", homePostalCode: "", whatsappPhone: "", waOptIn_bookings: false, waOptIn_chat: false, waOptIn_discovery: false, waOptIn_features: false });
      const [postalNudgeDismissed, setPostalNudgeDismissed] = useState(function() {
        try { return !!sessionStorage.getItem("nooks_postal_nudge_session"); } catch(e) { return false; }
      });
      const [fcmNudgeDismissed, setFcmNudgeDismissed] = useState(function() {
        try { return !!localStorage.getItem("nooks_fcm_nudge_v1"); } catch(e) { return false; }
      });
      const [profilePicUrl, setProfilePicUrl] = useState(null);
      const [selectedUserBiz, setSelectedUserBiz] = useState(null);
      const [selectedBusiness, setSelectedBusiness] = useState(null);
      // Keep the browser tab title in sync with the open business on detail pages.
      // The route handler only knows static page titles; the business name loads
      // asynchronously, so this effect updates the title once it's available.
      useEffect(function () {
        if (currentRoute && currentRoute.indexOf("detail") === 0 && selectedBusiness && selectedBusiness.name) {
          document.title = selectedBusiness.name + " · nooks";
        }
      }, [currentRoute, selectedBusiness]);
      const [lightboxUrl, setLightboxUrl] = useState(null);
      // Detail modal for individual menuItems (added 2026-05). Holds the item object or null.
      const [publicItemDetail, setPublicItemDetail] = useState(null);
      const [detailNotFound, setDetailNotFound] = useState(false);
      const [detailTab, setDetailTab] = useState("overview");
      React.useEffect(function() { setDetailTab("overview"); }, [selectedBusiness]);
      // Scrollspy: highlight active tab as user scrolls through sections
      React.useEffect(function() {
        if (!currentRoute || !currentRoute.startsWith("detail/")) return;
        var secs = ["overview", "services", "hours", "reviews"];
        var observers = [];
        secs.forEach(function(sec) {
          var el = document.getElementById("detail-sec-" + sec);
          if (!el) return;
          var io = new IntersectionObserver(function(entries) {
            entries.forEach(function(e) { if (e.isIntersecting) setDetailTab(sec); });
          }, { threshold: 0.15, rootMargin: "0px 0px -65% 0px" });
          io.observe(el);
          observers.push(io);
        });
        return function() { observers.forEach(function(o) { o.disconnect(); }); };
      }, [selectedBusiness, currentRoute]);
      const [bizSuccessMsg, setBizSuccessMsg] = useState(null);
      const [isAdmin, setIsAdmin] = useState(false);
      const [liveBusinesses, setLiveBusinesses] = useState([]);
      const liveBusinessesRef = React.useRef([]);
      const [lastBusinessDoc, setLastBusinessDoc] = useState(null);
      const [hasMoreBusinesses, setHasMoreBusinesses] = useState(true);
      const [loadingMore, setLoadingMore] = useState(false);
      const [bookingModalOpen, setBookingModalOpen] = useState(false);
      const [selectedBookingItem, setSelectedBookingItem] = useState(null);
      const [pendingBookingsCount, setPendingBookingsCount] = useState(0);
      // Reviews state for SPA detail page
      const [detailReviews, setDetailReviews] = useState([]);
      const [detailReviewsLoaded, setDetailReviewsLoaded] = useState(false);
      const [reviewModalOpen, setReviewModalOpen] = useState(false);
      const [reviewStars, setReviewStars] = useState(0);
      const [reviewText, setReviewText] = useState("");
      const [reviewSubmitting, setReviewSubmitting] = useState(false);
      const [reviewEligibleBooking, setReviewEligibleBooking] = useState(null);
      const [reviewCheckDone, setReviewCheckDone] = useState(false);
      const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
      const [thisWeekCount, setThisWeekCount] = useState(0); // approved events in the current week — powers the nav bulletin badge
      // streetNames removed — addresses from Firestore are the single source of truth
      const [userLocation, setUserLocation] = useState(null); // {lat, lng} from geolocation
      const [showAckModal, setShowAckModal] = useState(false);
      const [hasAcknowledged, setHasAcknowledged] = useState(false);
      const [mapVisible, setMapVisible] = useState(true);
      const REGULATIONS_VERSION = "1.0";

      // This Week nav badge — count of approved events in the current week.
      // Public (runs for every visitor), one-time read; reuses the same
      // events(status + startAt) composite index ThisWeekPage already relies on.
      useEffect(function() {
        var wb = twWeekBounds();
        db.collection("events")
          .where("status", "==", "approved")
          .where("startAt", ">=", firebase.firestore.Timestamp.fromDate(wb.start))
          .where("startAt", "<=", firebase.firestore.Timestamp.fromDate(wb.end))
          .get()
          .then(function(snap) { setThisWeekCount(snap.size); })
          .catch(function(e) { console.warn("[nav] This Week count failed:", e); });
      }, []);

      // Load reviews when a business detail page is opened
      useEffect(function() {
        if (!selectedBusiness) { setDetailReviews([]); setDetailReviewsLoaded(false); setReviewEligibleBooking(null); setReviewCheckDone(false); return; }
        setDetailReviewsLoaded(false);
        db.collection("reviews").where("bizId", "==", selectedBusiness.id).orderBy("createdAt", "desc").get()
          .then(function(snap) {
            setDetailReviews(snap.docs.map(function(d) { return { id: d.id, ...d.data() }; }));
            setDetailReviewsLoaded(true);
          })
          .catch(function(e) { console.warn("Load detail reviews:", e); setDetailReviewsLoaded(true); });
        // Check if current user has an eligible booking to review
        if (currentUser) {
          db.collection("bookings")
            .where("businessId", "==", selectedBusiness.id)
            .where("customerUid", "==", currentUser.uid)
            .where("status", "==", "completed")
            .orderBy("createdAt", "desc")
            .get()
            .then(function(snap) {
              // Find first completed booking that hasn't been reviewed
              var eligible = null;
              var bookingIds = snap.docs.map(function(d) { return d.id; });
              if (bookingIds.length === 0) { setReviewEligibleBooking(null); setReviewCheckDone(true); return; }
              db.collection("reviews").where("customerUid", "==", currentUser.uid).where("bizId", "==", selectedBusiness.id).get()
                .then(function(revSnap) {
                  var reviewedBookingIds = new Set(revSnap.docs.map(function(d) { return d.data().bookingId; }).filter(Boolean));
                  for (var i = 0; i < snap.docs.length; i++) {
                    if (!reviewedBookingIds.has(snap.docs[i].id)) { eligible = { id: snap.docs[i].id, ...snap.docs[i].data() }; break; }
                  }
                  setReviewEligibleBooking(eligible);
                  setReviewCheckDone(true);
                }).catch(function() { setReviewCheckDone(true); });
            })
            .catch(function(e) { console.warn("Eligible booking check:", e); setReviewCheckDone(true); });
        } else {
          setReviewCheckDone(true);
        }
      }, [selectedBusiness ? selectedBusiness.id : null, currentUser ? currentUser.uid : null]);

      // Auto-open review modal if navigated with ?review=true
      useEffect(function() {
        if (selectedBusiness && reviewCheckDone && window.location.search.includes("review=true")) {
          setReviewModalOpen(true);
          // Clean up the URL param
          var url = new URL(window.location);
          url.searchParams.delete("review");
          window.history.replaceState({}, "", url.pathname + url.hash);
        }
      }, [selectedBusiness, reviewCheckDone]);

      // Sync likes from Firestore for logged-in users
      useEffect(function() {
        if (!currentUser) { setLikes([]); return; }
        var unsub = db.collection("user_likes").doc(currentUser.uid).onSnapshot(function(doc) {
          if (doc.exists) {
            setLikes(doc.data().businessIds || []);
          } else {
            setLikes([]);
          }
        }, function(err) { console.error("Likes listener error:", err); });
        return function() { unsub(); };
      }, [currentUser]);

      // Detect user geolocation once on mount (single source of truth)
      useEffect(function() {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
          function(pos) {
            setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          function() { /* denied/unavailable — userLocation stays null, sort falls back to SG center */ },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }, []);

      // Check if user has acknowledged regulations
      useEffect(() => {
        if (!currentUser) { setHasAcknowledged(false); return; }
        var checkAck = async function() {
          try {
            var doc = await db.collection("user_acknowledgements").doc(currentUser.uid).get();
            if (doc.exists && doc.data().version === REGULATIONS_VERSION) {
              setHasAcknowledged(true);
            } else {
              // Check if user has any business listings - if so, show ack modal
              var bizSnap = await db.collection("businesses").where("ownerId", "==", currentUser.uid).get();
              if (!bizSnap.empty) {
                setShowAckModal(true);
              }
            }
          } catch(e) {
            console.warn("Ack check failed:", e);
          }
        };
        checkAck();
      }, [currentUser]);

      // Save acknowledgement with full audit trail
      var saveAcknowledgement = async function() {
        if (!currentUser) return;
        try {
          // Get IP address for audit trail
          var ip = "unknown";
          try {
            var ipResp = await fetch("https://api.ipify.org?format=json");
            var ipData = await ipResp.json();
            ip = ipData.ip || "unknown";
          } catch(e) { /* silent */ }

          await db.collection("user_acknowledgements").doc(currentUser.uid).set({
            userId: currentUser.uid,
            email: currentUser.email || "",
            displayName: currentUser.displayName || "",
            version: REGULATIONS_VERSION,
            acknowledgedAt: firebase.firestore.FieldValue.serverTimestamp(),
            ipAddress: ip,
            userAgent: navigator.userAgent || "",
            platform: navigator.platform || "",
            language: navigator.language || "",
            screenSize: window.innerWidth + "x" + window.innerHeight
          });
          setHasAcknowledged(true);
          setShowAckModal(false);
        } catch(e) {
          console.error("Failed to save acknowledgement:", e);
          showToast("Failed to save. Please try again.", "error");
        }
      };

      // Address display: use the saved address from Firestore directly (single source of truth)
      // No reverse geocoding — the address the business owner entered is what we show

      // Fetch approved businesses with pagination (initial load: 50 businesses)
      useEffect(() => {
        const fetchLive = async () => {
          try {
            const snap = await db.collection("businesses").where("status", "==", "approved").limit(200).get();
            const docs = snap.docs;
            const loaded = docs.map(d => ({ id: d.id, ...d.data() }));
            setLiveBusinesses(loaded);
            setLastBusinessDoc(docs.length > 0 ? docs[docs.length - 1] : null);
            setHasMoreBusinesses(docs.length === 200);
            _autoGeocodePlaceholders(loaded, setLiveBusinesses);
          } catch (err) { console.error("Fetch live businesses:", err); }
        };
        fetchLive();
      }, []);

      // Load more businesses function (cursor-based pagination)
      const loadMoreBusinesses = async () => {
        if (!lastBusinessDoc || !hasMoreBusinesses || loadingMore) return;
        setLoadingMore(true);
        try {
          const snap = await db.collection("businesses").where("status", "==", "approved").startAfter(lastBusinessDoc).limit(50).get();
          const docs = snap.docs;
          setLiveBusinesses(prev => [...prev, ...docs.map(d => ({ id: d.id, ...d.data() }))]);
          setLastBusinessDoc(docs.length > 0 ? docs[docs.length - 1] : lastBusinessDoc);
          setHasMoreBusinesses(docs.length === 50);
        } catch (err) { console.error("Load more businesses:", err); }
        setLoadingMore(false);
      };

      // Keep liveBusinessesRef in sync
      useEffect(function() {
        liveBusinessesRef.current = liveBusinesses;
      }, [liveBusinesses]);

      // Firebase Auth Listener
      useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          if (user) {
            const signInProvider = user.providerData && user.providerData.length > 0 ? user.providerData[0].providerId : (user.phoneNumber ? "phone" : "password");
            setCurrentUser({
              uid: user.uid,
              email: user.email || "",
              phoneNumber: user.phoneNumber || "",
              displayName: user.displayName || (user.email ? user.email.split("@")[0] : user.phoneNumber || "User"),
              signInProvider: signInProvider
            });
            // NOTE: we deliberately do NOT call initFcmForUser() here.
            // Calling Notification.requestPermission() unconditionally on every login
            // fires a contextless native browser prompt, which Chrome/iOS Safari widely
            // auto-suppress or auto-deny — most users (incl. store owners) never end up
            // with a stored fcmToken. Instead we show an explanatory FcmNudge banner
            // (see below) and only call initFcmForUser() from its "Enable" button click,
            // so the permission request is always preceded by context and a user gesture.
            // Fetch user profile from Firestore
            db.collection("users").doc(user.uid).get().then(doc => {
              if (doc.exists) {
                const data = doc.data();
                // Block suspended users
                if (data.status === "suspended") {
                  auth.signOut();
                  setCurrentUser(null);
                  setUserProfile(null);
                  showToast("Your account has been suspended." + (data.suspendReason ? " Reason: " + data.suspendReason : "") + " Please contact support if you believe this is an error.", "error", 8000);
                  return;
                }
                setUserProfile(data);
                setProfilePicUrl(data.profilePicUrl || null);
                // Check admin role
                const adminEmails = ADMIN_EMAILS;
                const shouldBeAdmin = adminEmails.includes(user.email);
                setIsAdmin(data.role === "admin" || shouldBeAdmin);
                // Auto-set admin role if not already set
                if (shouldBeAdmin && data.role !== "admin") {
                  db.collection("users").doc(user.uid).update({ role: "admin" });
                }
                // Backfill missing name field from Google Auth
                if (!data.name && user.displayName) {
                  db.collection("users").doc(user.uid).update({ name: user.displayName });
                }
              } else {
                // Create initial profile
                const adminEmails = ADMIN_EMAILS;
                const initialProfile = {
                  name: user.displayName || "",
                  firstName: user.displayName ? user.displayName.split(" ")[0] : "",
                  lastName: user.displayName ? user.displayName.split(" ").slice(1).join(" ") : "",
                  displayName: user.displayName || "",
                  email: user.email || "",
                  phone: user.phoneNumber || "",
                  birthday: "",
                  profilePicUrl: user.photoURL || "",
                  role: adminEmails.includes(user.email) ? "admin" : "user",
                  joinedAt: firebase.firestore.FieldValue.serverTimestamp()
                };
                db.collection("users").doc(user.uid).set(initialProfile);
                setUserProfile(initialProfile);
                setIsAdmin(adminEmails.includes(user.email));
                if (user.photoURL) setProfilePicUrl(user.photoURL);
              }
            });
            // Apply any pending auto-geocode coord fixes now that user is signed in
            _applyPendingCoordFixes(user);
            // Fetch user businesses
            db.collection("businesses").where("ownerId", "==", user.uid).get().then(snap => {
              const bizList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
              setUserBusinesses(bizList);

              // Listen to pending bookings for this user's businesses
              if (bizList.length > 0) {
                var bizIds = bizList.map(function(b) { return b.id; });
                var chunks = [];
                for (var ci = 0; ci < bizIds.length; ci += 30) {
                  chunks.push(bizIds.slice(ci, ci + 30));
                }
                var pendingCounts = {};
                chunks.forEach(function(chunk, idx) {
                  db.collection("bookings")
                    .where("businessId", "in", chunk)
                    .where("status", "==", "pending")
                    .onSnapshot(function(snap) {
                      pendingCounts[idx] = snap.docs.length;
                      var total = 0;
                      Object.values(pendingCounts).forEach(function(c) { total += c; });
                      setPendingBookingsCount(total);
                    }, function(err) { console.error("Pending bookings listener error:", err); });
                });
              }
            });

            // Listen to unread messages across all conversations
            db.collection("conversations")
              .where("participants", "array-contains", user.uid)
              .onSnapshot(snap => {
                let totalUnread = 0;
                snap.docs.forEach(doc => {
                  const unreadCount = doc.data().unreadBy?.[user.uid] || 0;
                  totalUnread += unreadCount;
                });
                setUnreadMessagesCount(totalUnread);
              }, function(err) { console.error("Unread messages listener error:", err); });

            // One-time "what's new since last login" summary toast.
            _checkLoginNotifications(user.uid);
          } else {
            setCurrentUser(null);
            setUserProfile(null);
            setUserBusinesses([]);
            setPendingBookingsCount(0);
            setUnreadMessagesCount(0);
          }
          setLoading(false);
        });

        return () => unsubscribe();
      }, []);

      // Save profile to Firestore
      const saveProfile = async (updates) => {
        if (!currentUser) return;
        await db.collection("users").doc(currentUser.uid).update(updates);
        setUserProfile(prev => ({ ...prev, ...updates }));
      };

      // Upload profile picture. compressImage now throws on decode/encode
      // failure (HEIC, oversized, Safari OOM) so wrap in try/catch and
      // surface a toast — otherwise this would bubble as an uncaught
      // promise rejection and the user would see no feedback.
      const uploadProfilePic = async (file) => {
        if (!currentUser || !file) return;
        try {
          const ref = storage.ref(`profilePics/${currentUser.uid}`);
          await ref.put(await compressImage(file, 400, 0.8));
          const url = await ref.getDownloadURL();
          await saveProfile({ profilePicUrl: url });
          setProfilePicUrl(url);
        } catch (err) {
          console.error("[uploadProfilePic] failed:", err);
          showToast(err.message || "Could not upload profile picture — please try again.", "error");
        }
      };

      // Route Handler — clean URLs via History API (no hash)
      useEffect(() => {
        const handleRouteChange = () => {
          // Scroll to top on every route change
          window.scrollTo(0, 0);
          // Read path from URL, strip leading slash
          var path = window.location.pathname.slice(1) || "welcome";
          // Also support legacy hash URLs (redirect to clean URL)
          // ONLY when the pathname is root — avoids treating Instagram's #_ OAuth artifact as a route
          if (!path && window.location.hash && window.location.hash.length > 1) {
            var hashRoute = window.location.hash.slice(1);
            window.history.replaceState(null, "", "/" + hashRoute);
            path = hashRoute;
          }

          // Route aliases and redirects
          // NOTE: "about" → "home" until Phase 2 pre-renders /about as its own page. Until then, /about falls back to home to avoid a blank view.
          var routeAliases = { "list": "add-business", "signup": "welcome", "login": "welcome", "register": "welcome", "about": "home" };
          if (routeAliases[path]) {
            path = routeAliases[path];
            window.history.replaceState(null, "", "/" + path);
          }

          // "Wishes" has no standalone route — it's the Map in wishes mode (/map?wishes=1).
          // Without this, a direct visit or shared link to /wishes falls through to the
          // slug catch below and renders "Business not found".
          if (path === "wishes" || path === "wish") {
            window.history.replaceState(null, "", "/map?wishes=1");
            path = "map";
          }

          // Known routes — redirect unknown routes to home
          var knownRoutes = ["welcome", "home", "explore", "map", "profile", "likes", "settings", "regulations", "faq", "terms", "privacy", "about", "add-business", "admin", "bookings", "my-bookings", "messages", "detail", "my-business", "chat", "this-week", "submit-event", "admin-events"];
          var routeBase = path.split("/")[0];
          if (routeBase && !knownRoutes.includes(routeBase) && !path.endsWith(".html")) {
            // Root-level slug shortlink: /car-buddy ? /detail/car-buddy
            // Slugs are single-segment paths matching the format generated during onboarding
            var slugPattern = /^[a-z0-9][a-z0-9-]*$/;
            if (!path.includes("/") && slugPattern.test(routeBase)) {
              path = "detail/" + routeBase;
              window.history.replaceState(null, "", "/" + path);
            } else {
              path = "home";
              window.history.replaceState(null, "", "/home");
            }
          }

          // Handle multi-segment routes like my-business/abc123
          if (path.startsWith("my-business/") || path.startsWith("detail/") || path.startsWith("chat/")) {
            setCurrentRoute(path);
          } else {
            setCurrentRoute(path.split("/")[0]);
          }

          const parts = path.split("/");
          if (parts[0] === "detail" && parts[1]) {
            const identifier = parts[1];
            setDetailNotFound(false);
            setSelectedBusiness(null);
            // Use ref for current liveBusinesses (avoids stale closure)
            var currentLive = liveBusinessesRef.current || [];
            var foundById = currentLive.find(function(b) { return String(b.id) === String(identifier); });
            var foundBySlug = !foundById ? currentLive.find(function(b) { return b.slug === identifier; }) : null;
            var found = foundById || foundBySlug;
            // Track business detail view.
            // - Filters out known crawlers by User-Agent so counts reflect real humans.
            // - Counts anonymous visitors too (most casual traffic is not signed in).
            // - Skips if the owner is viewing their own listing.
            // - Writes to three fields that together power admin + my-business dashboards:
            //     viewCount              -> all-time cumulative
            //     viewsByDate.{YYYY-MM-DD} -> per-day map (weekly chart)
            //     viewsByHour.{YYYY-MM-DDTHH} -> per-hour map (rolling 24h, aggregator prunes >30h)
            var trackView = function(bizDocId, bizOwnerId) {
              try {
                // Crawler / bot filter — substring match on UA.
                var ua = (navigator.userAgent || "").toLowerCase();
                var BOT_PATTERNS = [
                  "bot","crawl","spider","slurp","bingpreview","mediapartners",
                  "googlebot","bingbot","ahrefsbot","semrushbot","yandex","baidu",
                  "duckduckbot","facebookexternalhit","facebot","twitterbot",
                  "linkedinbot","whatsapp","telegram","discord","slackbot",
                  "headlesschrome","phantomjs","puppeteer","playwright","lighthouse"
                ];
                for (var i = 0; i < BOT_PATTERNS.length; i++) {
                  if (ua.indexOf(BOT_PATTERNS[i]) !== -1) return;
                }
                var viewer = firebase.auth().currentUser;
                if (viewer && viewer.uid === bizOwnerId) return; // Don't inflate own stats
                var now = new Date();
                var today = now.toISOString().slice(0, 10);         // YYYY-MM-DD
                var thisHour = now.toISOString().slice(0, 13);      // YYYY-MM-DDTHH
                db.collection("businesses").doc(bizDocId).update({
                  viewCount: firebase.firestore.FieldValue.increment(1),
                  [`viewsByDate.${today}`]: firebase.firestore.FieldValue.increment(1),
                  [`viewsByHour.${thisHour}`]: firebase.firestore.FieldValue.increment(1),
                  lastViewedAt: firebase.firestore.FieldValue.serverTimestamp()
                }).catch(function(err) { console.warn("View tracking failed:", err.code); });
              } catch(e) {}
            };

            if (found) {
              setSelectedBusiness(found);
              trackView(found.id, found.ownerId);
            } else {
              // Firestore fallback: try slug query first (most common), then doc ID
              var slugQuery = db.collection("businesses")
                .where("slug", "==", identifier)
                .where("status", "==", "approved")
                .limit(1).get();

              slugQuery.then(function(snap) {
                if (!snap.empty) {
                  var d = snap.docs[0];
                  setSelectedBusiness({ id: d.id, ...d.data() });
                  trackView(d.id, d.data().ownerId);
                } else {
                  // Slug not found — try as doc ID
                  db.collection("businesses").doc(identifier).get().then(function(doc) {
                    if (doc.exists) {
                      setSelectedBusiness({ id: doc.id, ...doc.data() });
                      trackView(doc.id, doc.data().ownerId);
                    } else {
                      setDetailNotFound(true);
                    }
                  }).catch(function(e) {
                    console.error("Doc query error:", e);
                    setDetailNotFound(true);
                  });
                }
              }).catch(function(e) {
                console.error("Slug query error:", e);
                setDetailNotFound(true);
              });
            }
          } else {
            setDetailNotFound(false);
          }

          // Dynamic page title + meta description + canonical URL
          var pageTitles = {
            welcome: "nooks · Discover Local Home Businesses",
            home: "nooks · Home",
            explore: "nooks · Explore Local Businesses",
            map: "nooks · Map View — Shops & Neighbourhood Wishes",
            messages: "nooks · Messages",
            profile: "nooks · My Profile",
            likes: "nooks · Saved Businesses",
            bookings: "nooks · Bookings",
            "my-bookings": "nooks · Bookings",
            "add-business": "nooks · List Your Business",
            regulations: "nooks · Home Business Regulations",
            faq: "nooks · FAQ",
            terms: "nooks · Terms of Service",
            privacy: "nooks · Privacy Policy",
            admin: "nooks · Admin Panel",
            "this-week": "nooks · This Week Around You",
            "submit-event": "nooks · Submit an Event",
            "admin-events": "nooks · Events Review Queue",
            about: "nooks · About Us",
            blog: "nooks · Blog",
            careers: "nooks · Careers"
          };
          var pageDescriptions = {
            welcome: "Discover and support local home businesses in your Singapore neighbourhood. From home bakers to tutors, pet groomers to fitness coaches — find trusted services right around you.",
            explore: "Browse and discover home-based businesses near you in Singapore. Filter by category — food, beauty, tutoring, pet care, crafts, and more. Find your neighbour's hidden talent.",
            map: "See home businesses near you on an interactive map of Singapore. Discover what's within walking distance — or tell your neighbourhood what services you wish were nearby.",
            "this-week": "What's on in your Singapore neighbourhood this week — pop-ups, classes, markets and drops from home businesses, sorted by how close they are to you. Refreshed every Monday.",
            "add-business": "List your home-based business on nooks for free. Reach customers in your Singapore estate who are looking for exactly what you offer.",
            regulations: "Singapore home-based business rules and regulations. Understand HDB and URA guidelines before starting a home business in Singapore.",
            faq: "Frequently asked questions about nooks — how to list a home business, how to find local services, and how the platform works.",
            about: "We're on a mission to connect every Singapore neighbourhood. Learn about the team behind nooks and why we built it.",
            blog: "Tips, stories, and insights for Singapore home business owners and their neighbours."
          };
          var defaultDesc = "Discover and support local home businesses in your Singapore neighbourhood. From home bakers to tutors, pet groomers to fitness coaches — find trusted services right around you.";
          var routeKey = path.split("/")[0];
          document.title = pageTitles[routeKey] || "nooks · Discover Local Home Businesses";
          // Update meta description
          var metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) metaDesc.setAttribute("content", pageDescriptions[routeKey] || defaultDesc);
          var ogDesc = document.querySelector('meta[property="og:description"]');
          if (ogDesc) ogDesc.setAttribute("content", pageDescriptions[routeKey] || defaultDesc);
          var twDesc = document.querySelector('meta[name="twitter:description"]');
          if (twDesc) twDesc.setAttribute("content", pageDescriptions[routeKey] || defaultDesc);
          // Update canonical
          var canonical = document.querySelector('link[rel="canonical"]');
          if (canonical) canonical.href = "https://nooks.sg/" + path;

          // Track page view
          try { NooksAnalytics.trackPageView(path || "welcome"); } catch(e) {}
        };

        window.addEventListener("popstate", handleRouteChange);
        handleRouteChange();

        return () => window.removeEventListener("popstate", handleRouteChange);
      }, []);

      const navigate = (route) => {
        window.history.pushState(null, "", "/" + route);
        // Dispatch popstate to trigger the route handler
        window.dispatchEvent(new PopStateEvent("popstate"));
        // Scroll to top — target the actual scrollable container (.main-content has overflow-y: auto)
        const mainEl = document.querySelector(".main-content");
        if (mainEl) mainEl.scrollTop = 0;
        window.scrollTo(0, 0);
        setTimeout(function() {
          const el = document.querySelector(".main-content");
          if (el) el.scrollTop = 0;
          window.scrollTo(0, 0);
        }, 50);
      };

      const [authView, setAuthView] = useState("methods"); // methods | email-signin | email-signup | phone | forgot-password | postal-step
      const [authError, setAuthError] = useState("");
      const [authInfo, setAuthInfo] = useState(""); // non-error guidance messages (e.g. wrong provider)
      const [authLoading, setAuthLoading] = useState(false);
      const [phoneStep, setPhoneStep] = useState("input"); // input | otp
      const [confirmationResult, setConfirmationResult] = useState(null);
      const recaptchaRef = React.useRef(null);
      const [postalStepInput, setPostalStepInput] = useState("");
      const [postalStepError, setPostalStepError] = useState("");
      const [postalStepLoading, setPostalStepLoading] = useState(false);

      const resetAuthModal = () => {
        setAuthView("methods");
        setAuthError("");
        setAuthInfo("");
        setAuthLoading(false);
        setPhoneStep("input");
        setConfirmationResult(null);
        setPostalStepInput("");
        setPostalStepError("");
        setPostalStepLoading(false);
      };

      const openAuthModal = () => {
        resetAuthModal();
        setModalOpen(true);
      };

      const closeAuthModal = () => {
        setModalOpen(false);
        // Clean up reCAPTCHA
        if (recaptchaRef.current) {
          try { recaptchaRef.current.clear(); } catch(e) {}
          recaptchaRef.current = null;
        }
        setTimeout(resetAuthModal, 300);
      };

      // Record every successful sign-in as an event doc. A Cloud Function
      // trigger (onSignInRecorded) increments signInCount + lastSignInAt on
      // the user doc — keeping user write rules locked while still giving the
      // admin a reliable sign-in tally.
      const recordSignInEvent = (method) => {
        try {
          const u = auth.currentUser;
          if (!u) return;
          db.collection("users").doc(u.uid).collection("signIns").add({
            method: method || "unknown",
            at: firebase.firestore.FieldValue.serverTimestamp(),
            userAgent: (navigator.userAgent || "").slice(0, 200)
          }).catch((err) => console.warn("signIn log failed:", err.code));
        } catch(e) {}
      };

      const handleGoogleSignIn = async () => {
        setAuthError("");
        setAuthLoading(true);
        try {
          const result = await auth.signInWithPopup(googleProvider);
          recordSignInEvent("google");
          if (result.additionalUserInfo && result.additionalUserInfo.isNewUser) {
            setAuthView("postal-step");
          } else {
            closeAuthModal();
          }
        } catch (error) {
          if (error.code !== "auth/popup-closed-by-user") {
            setAuthError(error.message);
          }
        }
        setAuthLoading(false);
      };

      const handleSignIn = async (email, password) => {
        setAuthError("");
        setAuthLoading(true);
        try {
          await auth.signInWithEmailAndPassword(email, password);
          recordSignInEvent("password");
          closeAuthModal();
        } catch (error) {
          setAuthError(error.message.replace("Firebase: ", ""));
        }
        setAuthLoading(false);
      };

      const handleForgotPassword = async (email) => {
        setAuthError("");
        setAuthInfo("");
        setAuthLoading(true);
        try {
          const methods = await auth.fetchSignInMethodsForEmail(email);

          if (methods.length === 0) {
            setAuthError("No account found with that email address.");
            setAuthLoading(false);
            return;
          }

          const hasPassword = methods.includes("password");
          const hasGoogle = methods.includes("google.com");
          const hasPhone = methods.includes("phone");

          if (!hasPassword) {
            // Account exists but was never created with email/password
            if (hasGoogle) {
              setAuthInfo("This email is linked to a Google account. Please sign in using the Google button instead.");
            } else if (hasPhone) {
              setAuthInfo("This account uses phone sign-in. Please use the phone option instead.");
            } else {
              setAuthInfo("This account uses a different sign-in method. Please go back and choose another option.");
            }
            setAuthLoading(false);
            return;
          }

          // Account has a password credential — send the reset email
          await auth.sendPasswordResetEmail(email);
          setAuthView("forgot-password-sent");
        } catch (error) {
          const code = error.code;
          if (code === "auth/invalid-email") {
            setAuthError("Please enter a valid email address.");
          } else {
            setAuthError(error.message.replace("Firebase: ", ""));
          }
        }
        setAuthLoading(false);
      };

      const handleSignUp = async (name, email, password) => {
        setAuthError("");
        setAuthLoading(true);
        try {
          await auth.createUserWithEmailAndPassword(email, password);
          await auth.currentUser.updateProfile({ displayName: name });
          recordSignInEvent("signup");
          setAuthView("postal-step");
        } catch (error) {
          setAuthError(error.message.replace("Firebase: ", ""));
        }
        setAuthLoading(false);
      };

      const handlePhoneSendOTP = async (phoneNumber) => {
        setAuthError("");
        setAuthLoading(true);
        try {
          // Clean up any previous reCAPTCHA
          if (recaptchaRef.current) {
            try { recaptchaRef.current.clear(); } catch(e) {}
            recaptchaRef.current = null;
          }
          // Create invisible reCAPTCHA on the send button
          recaptchaRef.current = new firebase.auth.RecaptchaVerifier("phone-send-btn", {
            size: "invisible",
            callback: () => {}
          });
          await recaptchaRef.current.render();
          const result = await auth.signInWithPhoneNumber(phoneNumber, recaptchaRef.current);
          // Keep reCAPTCHA alive — some Firebase versions need it for the confirm() step
          // It gets cleaned up in closeAuthModal after successful verification
          setConfirmationResult(result);
          setPhoneStep("otp");
        } catch (error) {
          console.error("Phone auth error:", error);
          let msg = error.message.replace("Firebase: ", "");
          if (error.code === "auth/internal-error") {
            msg = "Phone auth failed. Please check your phone number and try again.";
          } else if (error.code === "auth/too-many-requests") {
            msg = "Too many attempts. Please try again later.";
          } else if (error.code === "auth/invalid-phone-number") {
            msg = "Invalid phone number format. Use country code + number (e.g. +6591234567).";
          }
          setAuthError(msg);
          if (recaptchaRef.current) {
            try { recaptchaRef.current.clear(); } catch(e) {}
            recaptchaRef.current = null;
          }
        }
        setAuthLoading(false);
      };

      const handlePhoneVerifyOTP = async (code) => {
        console.log("Verify OTP called with code:", code, "length:", code.length);
        console.log("confirmationResult exists:", !!confirmationResult);
        setAuthError("");
        if (!confirmationResult) {
          setAuthError("Session expired. Please go back and resend the code.");
          return;
        }
        setAuthLoading(true);
        try {
          // Add timeout to prevent infinite "Verifying..." state
          var timeoutPromise = new Promise(function(_, reject) {
            setTimeout(function() { reject(new Error("Verification timed out. Please go back and try again.")); }, 30000);
          });
          const result = await Promise.race([confirmationResult.confirm(code), timeoutPromise]);
          console.log("Phone auth success:", result.user.uid);
          recordSignInEvent("phone");
          showToast("Phone verified! Welcome to nooks.", "success");
          if (result.additionalUserInfo && result.additionalUserInfo.isNewUser) {
            setAuthView("postal-step");
          } else {
            closeAuthModal();
          }

        } catch (error) {
          console.error("OTP verify error:", error);
          if (error.code === "auth/invalid-verification-code") {
            setAuthError("Invalid code. Please check and try again.");
          } else if (error.code === "auth/code-expired") {
            setAuthError("Code expired. Please go back and resend.");
          } else if (error.message && error.message.indexOf("timed out") !== -1) {
            setAuthError("Verification timed out. Please go back and resend the code.");
          } else {
            setAuthError("Verification failed: " + (error.message || "Unknown error").replace("Firebase: ", ""));
          }
        }
        setAuthLoading(false);
      };

      const handlePostalStepSave = async () => {
        const postal = postalStepInput.trim();
        if (!/^\d{6}$/.test(postal)) {
          setPostalStepError("Please enter a valid 6-digit Singapore postal code.");
          return;
        }
        setPostalStepLoading(true);
        setPostalStepError("");
        try {
          const res = await fetch(
            "https://www.onemap.gov.sg/api/common/elastic/search?searchVal=" +
            encodeURIComponent(postal) + "&returnGeom=Y&getAddrDetails=Y&pageNum=1"
          );
          const data = await res.json();
          if (!data.results || data.results.length === 0) {
            setPostalStepError("Postal code not found. Please check and try again.");
            setPostalStepLoading(false);
            return;
          }
          const u = auth.currentUser;
          if (u) {
            await db.collection("users").doc(u.uid).update({ homePostalCode: postal });
            setUserProfile(function(prev) { return Object.assign({}, prev, { homePostalCode: postal }); });
          }
          closeAuthModal();
        } catch(e) {
          setPostalStepError("Could not verify postal code. Please try again.");
        }
        setPostalStepLoading(false);
      };

      const handleSignOut = async () => {
        try {
          await auth.signOut();
          setSidebarOpen(false);
          navigate("welcome");
        } catch (error) {
          showToast("Sign out failed: " + error.message, "error");
        }
      };

      const toggleLike = function(businessId) {
        if (!currentUser) {
          showToast("Sign in to save your favourite shops!", "info");
          openAuthModal();
          return;
        }
        var isLiked = likes.includes(businessId);
        var newLikes;
        if (isLiked) {
          newLikes = likes.filter(function(id) { return id !== businessId; });
        } else {
          newLikes = likes.concat([businessId]);
        }
        setLikes(newLikes);
        // Update user's likes list
        db.collection("user_likes").doc(currentUser.uid).set({ businessIds: newLikes }, { merge: true }).catch(function(e) {
          console.error("Failed to save like:", e);
        });
        // SSoT: Sync likesCount on the business document
        db.collection("businesses").doc(businessId).update({
          likesCount: firebase.firestore.FieldValue.increment(isLiked ? -1 : 1)
        }).catch(function(e) {
          console.warn("Failed to update likesCount:", e.code);
        });
        // Also update local state so UI reflects immediately
        setLiveBusinesses(function(prev) {
          return prev.map(function(b) {
            if (b.id === businessId) return Object.assign({}, b, { likesCount: (b.likesCount || 0) + (isLiked ? -1 : 1) });
            return b;
          });
        });
      };


      const categoryCounts = useMemo(() => {
        const counts = {};
        liveBusinesses.forEach(b => {
          if (b.category) {
            counts[b.category] = (counts[b.category] || 0) + 1;
          }
        });
        counts["all"] = liveBusinesses.length;
        // YWCA Hopemakers is a verification flag, not a business category —
        // count verified stores so the pill can show a live tally.
        counts["ywca-hopemakers"] = liveBusinesses.filter(function(b){ return b && b.ywcaHopemaker && b.ywcaHopemaker.verified; }).length;
        return counts;
      }, [liveBusinesses]);

      const sortedCategories = useMemo(() => {
        var allCat = CATEGORIES.filter(function(c) { return c.id === "all"; });
        var rest = CATEGORIES.filter(function(c) { return c.id !== "all"; });
        rest.sort(function(a, b) {
          return (categoryCounts[b.id] || 0) - (categoryCounts[a.id] || 0);
        });
        // Pin the YWCA Hopemakers pill right after "All" (always shown).
        var hopemaker = { id: "ywca-hopemakers", label: "YWCA Hopemakers", iconName: "shield" };
        return allCat.concat([hopemaker], rest);
      }, [categoryCounts]);

      const filteredBusinesses = liveBusinesses.filter((business) => {
        const matchesCategory = selectedCategory === "all" ? true
          : selectedCategory === "ywca-hopemakers" ? !!(business.ywcaHopemaker && business.ywcaHopemaker.verified)
          : business.category === selectedCategory;
        const search = searchTerm.toLowerCase();
        const matchesSearch = !search ||
          (business.name || "").toLowerCase().includes(search) ||
          (business.description || "").toLowerCase().includes(search) ||
          (business.address || "").toLowerCase().includes(search);
        return matchesCategory && matchesSearch;
      }).sort((a, b) => {
        if (sortBy === "newest") {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        }
        if (sortBy === "nearest") {
          const uLat = userLocation ? userLocation.lat : 1.3521;
          const uLng = userLocation ? userLocation.lng : 103.8198;
          const distA = haversineDistance(uLat, uLng, a.lat || 0, a.lng || 0);
          const distB = haversineDistance(uLat, uLng, b.lat || 0, b.lng || 0);
          return distA - distB;
        }
        // Default: popular — weighted score combining rating, review count, and likes
        // Uses Bayesian average to avoid 1-review-5-star businesses dominating
        function popularityScore(biz) {
          var avg = biz.rating || 0;
          var reviews = biz.reviewCount || 0;
          var likes = biz.likesCount || 0;
          // Bayesian-dampened rating: pulls toward 3.0 with 2 phantom reviews
          var bayesianRating = (avg * reviews + 3.0 * 2) / (reviews + 2);
          // Normalize review volume (caps at 20 for full credit)
          var reviewVolume = Math.min(reviews, 20) / 20 * 5;
          // Normalize likes (caps at 30 for full credit)
          var likesVolume = Math.min(likes, 30) / 30 * 5;
          // Weighted blend: 50% rating quality, 30% review volume, 20% likes
          return (bayesianRating * 0.5) + (reviewVolume * 0.3) + (likesVolume * 0.2);
        }
        return popularityScore(b) - popularityScore(a);
      });

      const likedBusinesses = liveBusinesses.filter(b => likes.includes(b.id));

      return (
        <div className="app-container">
          {/* Lightbox */}
          {lightboxUrl && (
            <div onClick={() => setLightboxUrl(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}>
              <img src={lightboxUrl} alt="" style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: "8px", boxShadow: "0 4px 32px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()} />
              <button onClick={() => setLightboxUrl(null)} style={{ position: "absolute", top: "1.25rem", right: "1.25rem", background: "rgba(0,0,0,0.5)", border: "none", color: "#fff", fontSize: "1.5rem", lineHeight: 1, width: "2rem", height: "2rem", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>
          )}
          {/* Per-item detail modal (added 2026-05) — opened from public business detail page */}
          {publicItemDetail && (
            <ItemDetailModal
              open={true}
              item={publicItemDetail}
              business={selectedBusiness}
              currentUser={currentUser}
              db={db}
              navigate={navigate}
              onClose={() => setPublicItemDetail(null)}
              onBook={(it) => { setSelectedBookingItem(it); setBookingModalOpen(true); }}
            />
          )}
          {/* Header */}
          <header className={`header${currentRoute === "explore" ? " header--explore" : ""}`}>
            <button className="header-logo" onClick={() => navigate("explore")}><img src="/nooksLOGO.svg" alt="nooks" style={{height:"40px",display:"block"}} onError={(e)=>{e.currentTarget.style.display='none';const s=document.createElement('span');s.textContent='nooks';s.style.cssText='font-family:Georgia,serif;font-weight:700;font-size:1.4rem;background:linear-gradient(135deg,#c87606,#e8950a);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;display:inline-block';e.currentTarget.parentNode.insertBefore(s,e.currentTarget);}} /></button>
            {currentRoute === "explore" && (
              <div className="header-search">
                <span className="header-search-icon"><Icon name="search" size={14} /></span>
                <input
                  type="text"
                  className="header-search-input"
                  placeholder="Search businesses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            )}
            {/* Desktop Nav + Actions — right side */}
            <div className="header-right">
              <nav className="desktop-nav">
                <button className={currentRoute === "explore" ? "active" : ""} onClick={() => navigate("explore")}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill={currentRoute === "explore" ? "var(--amber)" : "none"} stroke={currentRoute === "explore" ? "var(--amber)" : "currentColor"}/><polyline points="9 22 9 12 15 12 15 22" stroke={currentRoute === "explore" ? "white" : "currentColor"} fill="none"/></svg>
                  Explore
                </button>
                <button className={currentRoute === "map" && new URLSearchParams(window.location.search).get("wishes") !== "1" ? "active" : ""} onClick={() => navigate("map")}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon fill="none" points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
                  Map
                </button>
                <button className={currentRoute === "map" && new URLSearchParams(window.location.search).get("wishes") === "1" ? "active" : ""} onClick={() => navigate("map?wishes=1")}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" fill={currentRoute === "map" && new URLSearchParams(window.location.search).get("wishes") === "1" ? "var(--amber)" : "none"}/></svg>
                  Wishes
                </button>
                <button className={`${currentRoute === "this-week" ? "active " : ""}nav-thisweek`} onClick={() => navigate("this-week")}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" fill={currentRoute === "this-week" ? "var(--amber)" : "none"} stroke={currentRoute === "this-week" ? "var(--amber)" : "currentColor"}/><line x1="16" y1="2" x2="16" y2="6" stroke={currentRoute === "this-week" ? "white" : "currentColor"}/><line x1="8" y1="2" x2="8" y2="6" stroke={currentRoute === "this-week" ? "white" : "currentColor"}/><line x1="3" y1="10" x2="21" y2="10" stroke={currentRoute === "this-week" ? "white" : "currentColor"}/></svg>
                  This Week{thisWeekCount > 0 && <span className="tw-count">{thisWeekCount}</span>}
                </button>
                {currentUser && <span className="nav-divider" aria-hidden="true" />}
                {currentUser && <button className={currentRoute === "messages" ? "active" : ""} onClick={() => navigate("messages")}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" fill={currentRoute === "messages" ? "var(--amber)" : "none"} stroke={currentRoute === "messages" ? "var(--amber)" : "currentColor"}/></svg>
                  Messages{unreadMessagesCount > 0 && <span className="nav-badge">{unreadMessagesCount}</span>}
                </button>}
                {currentUser && <button className={(currentRoute === "bookings" || currentRoute === "my-bookings") ? "active" : ""} onClick={() => navigate("bookings")}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect fill="none" x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Bookings{pendingBookingsCount > 0 && <span className="nav-badge">{pendingBookingsCount}</span>}
                </button>}
                <button className="nav-cta" onClick={() => navigate("add-business")}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  List Business
                </button>
              </nav>
              <div className="header-actions">
                {currentUser ? (
                  <div
                    className="user-avatar"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    title="Open menu"
                  >
                    {getInitials(currentUser.displayName)}
                  </div>
                ) : (
                  <button className="signin-pill" onClick={openAuthModal}>
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </header>

          {/* Postal Code Nudge — shown once to signed-in users without homePostalCode */}
          {currentUser && userProfile && !userProfile.homePostalCode && !postalNudgeDismissed && (
            <PostalNudge
              onSetNow={() => {
                setPostalNudgeDismissed(true);
                try { sessionStorage.setItem("nooks_postal_nudge_session", "1"); } catch(e) {}
                navigate("profile");
                setTimeout(function() { setEditingProfile(true); }, 120);
              }}
              onDismiss={() => {
                setPostalNudgeDismissed(true);
                try { sessionStorage.setItem("nooks_postal_nudge_session", "1"); } catch(e) {}
              }}
            />
          )}

          {/* FCM Push Nudge — shown once to signed-in users we haven't asked for notification permission yet.
              Only shown when Notification.permission is still "default" (never asked / not granted / not denied)
              so we don't re-nag users who already answered the native prompt some other way. */}
          {currentUser && !fcmNudgeDismissed &&
            typeof Notification !== "undefined" && Notification.permission === "default" && (
            <FcmNudge
              onEnable={() => {
                setFcmNudgeDismissed(true);
                try { localStorage.setItem("nooks_fcm_nudge_v1", "1"); } catch(e) {}
                initFcmForUser(currentUser);
              }}
              onDismiss={() => {
                setFcmNudgeDismissed(true);
                try { localStorage.setItem("nooks_fcm_nudge_v1", "1"); } catch(e) {}
              }}
            />
          )}

          {/* Main Content */}
          <main className="main-content">
            {/* Welcome Page */}
            {currentRoute === "welcome" && (
              <div className="page active">
                <div className="welcome-page">
                  <div className="welcome-logo hero-rise hero-rise-1"><img src="/nooksLOGO.svg" alt="nooks" style={{height:"130px",display:"block",margin:"0 auto"}} onError={(e)=>{e.currentTarget.style.display='none';const s=document.createElement('span');s.textContent='nooks';s.style.cssText='font-family:Georgia,serif;font-weight:700;font-size:4.2rem;background:linear-gradient(135deg,#c87606,#e8950a);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;display:block;text-align:center';e.currentTarget.parentNode.insertBefore(s,e.currentTarget);}} /></div>
                  <p className="welcome-tagline hero-rise hero-rise-2">
                    Find trusted home businesses right around the corner — search by what's nearest to you
                  </p>
                  {/* LANE 1: Neighbourhood map hero — real CartoDB Voyager tiles, frozen, geolocation-centered at zoom 14, 500m walking radius + real business cover-photo pins. */}
                  <NeighbourhoodHeroMap userLocation={userLocation} liveBusinesses={liveBusinesses} navigate={navigate} />
                  <div style={{ position: "relative", maxWidth: "360px", margin: "0 auto 1.25rem" }}>
                    <input type="text" placeholder="Search businesses, services..." onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { navigate("explore"); setTimeout(function() { var el = document.querySelector('.search-input'); if (el) { el.value = e.target.value; el.dispatchEvent(new Event('input', { bubbles: true })); } }, 300); } }}
                      style={{ width: "100%", padding: "0.75rem 1rem 0.75rem 2.5rem", border: "1px solid var(--border)", borderRadius: "24px", fontSize: "0.9rem", background: "white", outline: "none", boxSizing: "border-box" }} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  </div>
                  <button className="welcome-cta hero-rise hero-rise-3" onClick={() => navigate("explore")}>
                    Start Exploring
                  </button>
                  <button className="welcome-secondary" onClick={() => navigate("home")}>
                    Learn More
                  </button>
                  <button className="welcome-text-link" onClick={() => navigate("add-business")} style={{ marginTop: "0.25rem" }}>
                    List your business
                  </button>
                  <button className="welcome-text-link" onClick={() => navigate("map?wishes=1")} style={{ marginTop: "0.25rem" }}>
                    Make a Wish
                  </button>
                </div>
              </div>
            )}

            {/* Home Page */}
            {currentRoute === "home" && (
              <div className="page active">
                <div style={{ maxWidth: "720px", margin: "0 auto", padding: "1rem 1.5rem 0" }}>
                  <button onClick={() => navigate("welcome")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.9rem" }}>
                    <Icon name="arrowLeft" size={16} /> Back
                  </button>
                </div>
                <section className="hero">
                  <h1 className="hero-title hero-rise hero-rise-1">Discover Local Home Businesses</h1>
                  <div className="hero-subtitle hero-rise hero-rise-2">
                    <p style={{ fontFamily: "var(--serif)", fontSize: "1.15rem", fontWeight: 600, color: "var(--text)", marginBottom: "1rem" }}>Welcome to <span className="nooks-brand">nooks</span> — Your Neighborhood, Reimagined</p>
                    <p style={{ marginBottom: "0.75rem" }}><span className="nooks-brand">nooks</span> connects you to the hidden talents and businesses right around you — from home-based creators to everyday service providers.</p>
                    <p style={{ marginBottom: "0.75rem" }}><span className="nooks-brand">nooks</span> uses your location to surface the best home businesses within walking distance — so you always know what's available nearby.</p>
                    <p style={{ marginBottom: "0.75rem" }}>We help individuals turn their skills into income, while making it easy for you to discover trusted products and services right in your estate.</p>
                    <p style={{ fontStyle: "italic", fontWeight: 500 }}>Because the best finds are closer than you think.</p>
                  </div>
                  
                  <div className="tilted-cards">
                    {liveBusinesses.slice(0, 5).map((business) => (
                      <div key={business.id} className="tilted-card" style={{ cursor: "pointer" }}>
                        <a
                          className="card-seo-link"
                          href={`/detail/${business.slug || business.id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`detail/${business.slug || business.id}`); }}
                          aria-label={business.name}
                        >{business.name}</a>
                        {business.coverPhotoUrl ? <img src={business.coverPhotoUrl} alt={business.name} /> : <CardNamePlaceholder name={business.name} fontSize="1.1rem" padding="0.8rem" />}
                      </div>
                    ))}
                  </div>

                  <button className="cta-button hero-rise hero-rise-3" onClick={() => navigate("explore")}>
                    Explore <span className="nooks-brand" style={{ color: "inherit" }}>nooks</span>
                  </button>
                </section>

                <section className="category-scroll">
                  {sortedCategories.map((cat) => (
                    <button
                      key={cat.id}
                      className="category-pill inactive"
                      onClick={() => {
                        setSelectedCategory(cat.id);
                        navigate("explore");
                      }}
                    >
                      <Icon name={cat.iconName} size={14} /> {cat.label} {(categoryCounts[cat.id] || cat.id === "ywca-hopemakers") ? <span className="pill-count">{categoryCounts[cat.id] || 0}</span> : null}
                    </button>
                  ))}
                </section>

                <section className="featured-section">
                  <h2 className="section-title reveal">Featured <span className="nooks-brand">nooks</span></h2>
                  <div className="featured-cards">
                    {liveBusinesses.slice(0, 6).map((business, idx) => (
                      <div
                        key={business.id}
                        className="featured-card reveal"
                        data-delay={String((idx % 4) + 1)}
                      >
                        <a
                          className="card-seo-link"
                          href={`/detail/${business.slug || business.id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`detail/${business.slug || business.id}`); }}
                          aria-label={business.name}
                        >{business.name}</a>
                        <button className="card-like-btn" onClick={(e) => { e.stopPropagation(); toggleLike(business.id); }} title={likes.includes(business.id) ? "Unlike" : "Like"}>
                          {likes.includes(business.id) ? <Icon name="heartFill" size={15} color="#e74c3c" /> : <Icon name="heart" size={15} color="#999" />}
                        </button>
                        <div className="featured-card-image">
                          {business.coverPhotoUrl ? <img src={business.coverPhotoUrl} alt={business.name} /> : <CardNamePlaceholder name={business.name} />}
                        </div>
                        <div className="featured-card-body">
                          <h3 className="featured-card-title">{business.name}</h3>
                          <div className="featured-card-meta">
                            <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{CATEGORIES.find(c => c.id === business.category)?.label || business.category}</span>
                          </div>
                          <p style={{ fontSize: "0.85rem", color: "#6B6B6B" }}>
                            {getDisplayLocation(business)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Value Proposition Section */}
                <section className="value-prop-section">
                  <h2 className="section-title reveal">Why <span className="nooks-brand">nooks</span>?</h2>
                  <div className="value-prop-grid">
                    <div className="value-prop-card reveal" data-delay="1">
                      <div className="value-prop-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      </div>
                      <div className="value-prop-title">Discover Nearby</div>
                      <div className="value-prop-desc">Find trusted home businesses within walking distance of where you live.</div>
                    </div>
                    <div className="value-prop-card reveal" data-delay="2">
                      <div className="value-prop-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      </div>
                      <div className="value-prop-title">Support Local</div>
                      <div className="value-prop-desc">Help home-based creators turn their skills into income — right in your estate.</div>
                    </div>
                    <div className="value-prop-card reveal" data-delay="3">
                      <div className="value-prop-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </div>
                      <div className="value-prop-title">Book Directly</div>
                      <div className="value-prop-desc">Message or book services directly — no middleman, no commission fees.</div>
                    </div>
                  </div>
                </section>

                <div style={{ height: "1rem" }} />
              </div>
            )}

            {/* Explore Page */}
            {currentRoute === "explore" && (
              <div className="page active">
                {/* Map Preview with sort overlay */}
                <div className="map-collapsible">
                <div style={{ position: "relative" }}>
                  <MapPreview businesses={filteredBusinesses} navigate={navigate} userLocation={userLocation} setUserLocation={setUserLocation} />
                  <div className="map-sort-overlay">
                    {[
                      { label: "Newest", icon: "clock" },
                      { label: "Popular", icon: "heart" },
                      { label: "Nearest", icon: "pin" }
                    ].map((item, i) => (
                      <button
                        key={i}
                        className={`sort-tab ${sortBy === item.label.toLowerCase() ? "active" : ""}`}
                        onClick={() => setSortBy(item.label.toLowerCase())}
                      >
                        <Icon name={item.icon} size={13} /> {item.label}
                      </button>
                    ))}
                  </div>
                </div>
                </div>{/* end map-collapsible */}

                {/* Category Scroll */}
                <div className="category-scroll">
                  {sortedCategories.map((cat) => (
                    <button
                      key={cat.id}
                      className={`category-pill ${selectedCategory === cat.id ? "active" : "inactive"}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <Icon name={cat.iconName} size={14} /> {cat.label} {(categoryCounts[cat.id] || cat.id === "ywca-hopemakers") ? <span className="pill-count">{categoryCounts[cat.id] || 0}</span> : null}
                    </button>
                  ))}
                </div>

                {/* Listings */}
                <div className="listings-container">
                  {filteredBusinesses.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-state-icon"><Icon name="search" size={48} color="#9E9E9E" /></div>
                      <h3 className="empty-state-title">No businesses found</h3>
                      <p className="empty-state-text">Try adjusting your search or category filters</p>
                    </div>
                  ) : (
                    filteredBusinesses.map((business, idx) => (
                      <div
                        key={business.id}
                        className="business-card reveal"
                        data-delay={String((idx % 4) + 1)}
                      >
                        <a
                          className="card-seo-link"
                          href={`/detail/${business.slug || business.id}`}
                          onClick={(e) => { e.preventDefault(); navigate(`detail/${business.slug || business.id}`); }}
                          aria-label={business.name}
                        >{business.name}</a>
                        <button className="card-like-btn" onClick={(e) => { e.stopPropagation(); toggleLike(business.id); }} title={likes.includes(business.id) ? "Unlike" : "Like"}>
                          {likes.includes(business.id) ? <Icon name="heartFill" size={15} color="#e74c3c" /> : <Icon name="heart" size={15} color="#999" />}
                        </button>
                        <div className="business-card-image" style={{ position: "relative" }}>
                          {business.coverPhotoUrl ? (
                            <img src={business.coverPhotoUrl} alt={business.name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                          ) : <CardNamePlaceholder name={business.name} />}
                          {business.ywcaHopemaker && business.ywcaHopemaker.verified && (
                            <span style={{ position: "absolute", top: "8px", left: "8px", zIndex: 2, display: "inline-flex", alignItems: "center", gap: "5px", background: "rgba(255,255,255,0.96)", backdropFilter: "blur(2px)", border: "1px solid #1F2A6F", color: "#1F2A6F", fontWeight: 700, fontSize: "0.66rem", letterSpacing: "0.2px", padding: "3px 8px 3px 5px", borderRadius: "999px", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }} title="YWCA Hopemaker — Verified by YWCA of Singapore">
                              <span style={{ display: "inline-block", width: "11px", height: "11px", position: "relative", flexShrink: 0 }}>
                                <span style={{ position: "absolute", left: "-1px", top: 0, width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "7px solid #5DA8D6" }} />
                                <span style={{ position: "absolute", left: "1.5px", top: 0, width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "7px solid #1F2A6F" }} />
                                <span style={{ position: "absolute", left: "4px", top: 0, width: 0, height: 0, borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "7px solid #D6336C" }} />
                              </span>
                              YWCA Hopemaker · Verified
                            </span>
                          )}
                        </div>
                        <div className="business-card-body">
                          <div>
                            <h3 className="business-card-title">{business.name}</h3>
                            <p className="business-card-description">{business.description}</p>
                            <div className="business-card-category">
                              {CATEGORIES.find(c => c.id === business.category)?.label}
                            </div>
                            <p className="business-card-location">
                              <Icon name="pin" size={14} /> {getDisplayLocation(business)}
                              {userLocation && business.lat && business.lng && (
                                <span style={{ marginLeft: "0.4rem", color: "var(--muted)", fontSize: "0.75rem" }}>• {haversineDistance(userLocation.lat, userLocation.lng, business.lat, business.lng).toFixed(1)}km</span>
                              )}
                            </p>
                            {/* Star rating row */}
                            {(business.rating > 0 || business.reviewCount > 0) && (
                              <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginTop: "0.35rem" }}>
                                <span style={{ color: "var(--yellow)", fontSize: "0.8rem", letterSpacing: "-1px" }}>
                                  {"★".repeat(Math.round(business.rating || 0))}{"☆".repeat(5 - Math.round(business.rating || 0))}
                                </span>
                                <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "var(--ink)" }}>{(business.rating || 0).toFixed(1)}</span>
                                <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>({business.reviewCount || 0})</span>
                              </div>
                            )}
                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.3rem", flexWrap: "wrap" }}>
                              {/* Price range from menuItems */}
                              {business.menuItems && business.menuItems.length > 0 && (() => {
                                var prices = business.menuItems.map(function(m) { return parseFloat(m.price) || 0; }).filter(function(p) { return p > 0; });
                                if (prices.length === 0) return null;
                                var min = Math.min.apply(null, prices);
                                var max = Math.max.apply(null, prices);
                                return <span style={{ fontSize: "0.75rem", color: "var(--amber)", fontWeight: 600 }}>{min === max ? "S$" + min.toFixed(0) : "S$" + min.toFixed(0) + " — S$" + max.toFixed(0)}</span>;
                              })()}
                              {/* Open now indicator */}
                              {business.hours && (() => {
                                var dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                                var now = new Date();
                                var dayInfo = business.hours[dayNames[now.getDay()]];
                                if (!dayInfo || !dayInfo.enabled) return <span style={{ fontSize: "0.7rem", color: "#C62828", fontWeight: 600 }}>Closed today</span>;
                                var nowMins = now.getHours() * 60 + now.getMinutes();
                                var openParts = dayInfo.open.split(":"); var closeParts = dayInfo.close.split(":");
                                var openMins = parseInt(openParts[0]) * 60 + parseInt(openParts[1]);
                                var closeMins = parseInt(closeParts[0]) * 60 + parseInt(closeParts[1]);
                                var isOpen = nowMins >= openMins && nowMins < closeMins;
                                return <span style={{ fontSize: "0.7rem", color: isOpen ? "#2E7D32" : "#F57F17", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                  {isOpen && <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#2E7D32", display: "inline-block", flexShrink: 0 }} />}
                                  {isOpen ? "Open now" : "Opens " + dayInfo.open}
                                </span>;
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  {hasMoreBusinesses && (
                    <div style={{ padding: "1rem", textAlign: "center" }}>
                      <button
                        onClick={loadMoreBusinesses}
                        disabled={loadingMore}
                        style={{
                          padding: "0.75rem 1.5rem",
                          background: "var(--amber)",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          cursor: loadingMore ? "default" : "pointer",
                          fontSize: "0.95rem",
                          fontFamily: "var(--sans)",
                          opacity: loadingMore ? 0.7 : 1
                        }}
                      >
                        {loadingMore ? "Loading..." : "Load more"}
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ height: "2rem" }} />
              </div>
            )}

            {/* Map Page */}
            {currentRoute === "map" && (
              <MapPage liveBusinesses={liveBusinesses} navigate={navigate} likes={likes} toggleLike={toggleLike} currentUser={currentUser} userProfile={userProfile} openAuthModal={openAuthModal} />
            )}

            {/* Detail Page — not found fallback */}
            {currentRoute.startsWith("detail/") && !selectedBusiness && detailNotFound && (
              <div className="page active">
                <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
                  <div style={{ fontSize: "3rem", marginBottom: "1rem", opacity: 0.3 }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  </div>
                  <h2 style={{ fontFamily: "var(--serif)", marginBottom: "0.5rem" }}>Business not found</h2>
                  <p style={{ color: "var(--muted)", marginBottom: "1.5rem" }}>This listing may have been removed or the link is incorrect.</p>
                  <button onClick={() => navigate("explore")} style={{ background: "var(--amber)", color: "white", border: "none", padding: "0.7rem 2rem", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.9rem" }}>Browse Businesses</button>
                </div>
              </div>
            )}

            {/* Detail Page */}
            {currentRoute.startsWith("detail/") && selectedBusiness && (
              <div className="page active">

                {/* Top bar — back + actions (no banner) */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px 0" }}>
                  <button className="detail-back-btn" onClick={() => navigate("explore")} title="Go back" style={{ position: "relative", top: 0, left: 0 }}>
                    <Icon name="arrowLeft" size={20} />
                  </button>
                  <div className="detail-actions" style={{ position: "relative", top: 0, right: 0 }}>
                    <button className="detail-action-btn" onClick={() => toggleLike(selectedBusiness.id)} title={likes.includes(selectedBusiness.id) ? "Unlike" : "Like"}>
                      {likes.includes(selectedBusiness.id) ? <Icon name="heartFill" size={18} color="#e74c3c" /> : <Icon name="heart" size={18} />}
                    </button>
                    <button className="detail-action-btn" title="Share" onClick={() => {
                      var url = "https://nooks.sg/detail/" + (selectedBusiness.slug || selectedBusiness.id);
                      var text = selectedBusiness.name + " on nooks — " + (selectedBusiness.description || "").substring(0, 80);
                      if (navigator.share) {
                        navigator.share({ title: selectedBusiness.name, text: text, url: url }).catch(function() {});
                      } else {
                        navigator.clipboard.writeText(url).then(function() {
                          showToast("Link copied to clipboard!", "success");
                        }).catch(function() {
                          showToast("Copy this link: " + url, "info", 6000);
                        });
                      }
                    }}><Icon name="share" size={18} /></button>
                  </div>
                </div>

                {/* Constrained content area */}
                <div className="detail-container">

                  {/* Circular avatar — centered, no overlap */}
                  <div className="detail-profile-row" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", marginTop: "16px", padding: "0 0 12px 0" }}>
                    {(() => {
                      const hasPhoto = selectedBusiness.logoUrl || selectedBusiness.coverPhotoUrl;
                      const p = !hasPhoto ? getCardPalette(selectedBusiness.name) : null;
                      return (
                        <div style={{ position: "relative", display: "inline-block" }}>
                          <div className="detail-avatar" style={{ width: "252px", height: "252px", borderRadius: "50%", border: "5px solid white", background: hasPhoto ? "var(--amber-light)" : p.bg, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative", zIndex: 1 }}>
                            {hasPhoto
                              ? <img src={selectedBusiness.logoUrl || selectedBusiness.coverPhotoUrl} alt={selectedBusiness.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <span style={{ fontFamily: "var(--font-heading)", fontSize: (selectedBusiness.name || "").length > 12 ? "2rem" : "2.8rem", fontWeight: 400, color: p.color, textAlign: "center", lineHeight: 1.2, padding: "1.5rem", wordBreak: "break-word" }}>{selectedBusiness.name}</span>
                            }
                          </div>
                          {selectedBusiness.ywcaHopemaker && selectedBusiness.ywcaHopemaker.verified && (
                            <span title={"YWCA Hopemaker · Verified" + (selectedBusiness.ywcaHopemaker.cohort ? " · " + selectedBusiness.ywcaHopemaker.cohort : "")} style={{ position: "absolute", top: "10px", right: "10px", zIndex: 2, display: "inline-flex", alignItems: "center", gap: "5px", background: "#fff", border: "2px solid #1F2A6F", color: "#1F2A6F", fontWeight: 700, fontSize: "0.72rem", letterSpacing: "0.2px", padding: "4px 10px 4px 7px", borderRadius: "999px", boxShadow: "0 3px 10px rgba(31,42,111,0.25)" }}>
                              <span style={{ display: "inline-block", width: "12px", height: "12px", position: "relative", flexShrink: 0 }}>
                                <span style={{ position: "absolute", left: "-1px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid #5DA8D6" }} />
                                <span style={{ position: "absolute", left: "1.5px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid #1F2A6F" }} />
                                <span style={{ position: "absolute", left: "4.5px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid #D6336C" }} />
                              </span>
                              YWCA · Verified
                            </span>
                          )}
                        </div>
                      );
                    })()}

                  </div>

                  <div className="detail-body">
                    <h1 className="detail-title hero-rise hero-rise-1">{selectedBusiness.name}</h1>
                    <div className="detail-meta hero-rise hero-rise-2">
                      <span className="business-card-category" style={{ display: "inline-block" }}>
                        {CATEGORIES.find(c => c.id === selectedBusiness.category)?.label || selectedBusiness.category}
                      </span>
                    </div>
                    {selectedBusiness.slug && (
                      <p style={{ fontSize: "0.8rem", color: "var(--muted)", marginBottom: "0.75rem" }}>nooks.sg/{selectedBusiness.slug}</p>
                    )}

                    {/* ── YWCA Hopemaker trust strip — rendered when verified ── */}
                    {selectedBusiness.ywcaHopemaker && selectedBusiness.ywcaHopemaker.verified && (
                      <a href="/hopemakers" style={{ display: "flex", gap: "14px", alignItems: "center", padding: "14px 16px", borderRadius: "12px", background: "linear-gradient(95deg, #fff 0%, #EEF1FA 60%, #F7EAF0 100%)", border: "1px solid #D9DFF2", textDecoration: "none", color: "inherit", marginBottom: "1rem", transition: "all 0.2s" }}
                        onMouseEnter={function(e) { e.currentTarget.style.boxShadow = "0 4px 12px rgba(31,42,111,0.10)"; }}
                        onMouseLeave={function(e) { e.currentTarget.style.boxShadow = "none"; }}>
                        <img src="/hopemakers/ywca-singapore-vertical.png" alt="YWCA Singapore" style={{ width: "44px", height: "auto", flexShrink: 0 }} />
                        <div style={{ flex: 1, lineHeight: 1.4 }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #1F2A6F", color: "#1F2A6F", fontWeight: 700, fontSize: "0.72rem", letterSpacing: "0.2px", padding: "3px 10px 3px 7px", borderRadius: "999px", marginBottom: "4px" }}>
                            <span style={{ display: "inline-block", width: "12px", height: "12px", position: "relative", flexShrink: 0 }}>
                              <span style={{ position: "absolute", left: "-1px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid #5DA8D6" }} />
                              <span style={{ position: "absolute", left: "1.5px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid #1F2A6F" }} />
                              <span style={{ position: "absolute", left: "4.5px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid #D6336C" }} />
                            </span>
                            YWCA Hopemaker · Verified
                          </div>
                          <div style={{ fontSize: "0.82rem", color: "#1F2A6F", fontWeight: 600 }}>Trained at YWCA of Singapore's Hopemaker: Women's Business Accelerator</div>
                          <div style={{ fontSize: "0.74rem", color: "#555", marginTop: "2px" }}>
                            {selectedBusiness.ywcaHopemaker.cohort ? ("Cohort: " + selectedBusiness.ywcaHopemaker.cohort + " · ") : ""}
                            <span style={{ color: "#1F2A6F", fontWeight: 600 }}>About the partnership →</span>
                          </div>
                        </div>
                      </a>
                    )}

                    {/* Stats bar */}
                    <div className="detail-stats hero-rise hero-rise-3">
                      {selectedBusiness.rating ? (
                        <div className="detail-stat">
                          <div className="detail-stat-icon" style={{ color: "#f5c842" }}>★</div>
                          <div className="detail-stat-value">{selectedBusiness.rating}</div>
                          <div className="detail-stat-label">Rating</div>
                        </div>
                      ) : null}
                      {selectedBusiness.reviewCount ? (
                        <div className="detail-stat">
                          <div className="detail-stat-icon">💬</div>
                          <div className="detail-stat-value">{selectedBusiness.reviewCount}</div>
                          <div className="detail-stat-label">Reviews</div>
                        </div>
                      ) : null}
                      <div className="detail-stat">
                        <div className="detail-stat-icon">📍</div>
                        <div className="detail-stat-value" style={{ fontSize: "0.82rem", lineHeight: "1.3" }}>{getDisplayLocation(selectedBusiness)}</div>
                        <div className="detail-stat-label">Location</div>
                      </div>
                      {(() => {
                        var minP = selectedBusiness.priceFrom || (selectedBusiness.menuItems && selectedBusiness.menuItems.length > 0
                          ? Math.min.apply(null, selectedBusiness.menuItems.map(function(i) { return parseFloat(i.price) || 9999; }).filter(function(p) { return p < 9999; }))
                          : null);
                        return minP ? (
                          <div className="detail-stat">
                            <div className="detail-stat-icon">💰</div>
                            <div className="detail-stat-value">${minP}</div>
                            <div className="detail-stat-label">From</div>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    {/* ── Tab nav — sticky scroll-to-section ─── */}
                    <div style={{ position: "sticky", top: 0, background: "var(--bg)", borderBottom: "1px solid var(--border)", marginBottom: "1.5rem", marginLeft: "-1.5rem", marginRight: "-1.5rem", paddingLeft: "1.5rem", paddingRight: "1.5rem", overflowX: "auto", zIndex: 10 }}>
                      <button onClick={function() { setDetailTab("overview"); var el = document.getElementById("detail-sec-overview"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ background: "none", border: "none", paddingBottom: "0.8rem", paddingRight: "1.25rem", cursor: "pointer", fontWeight: 600, fontSize: "0.875rem", color: detailTab === "overview" ? "var(--amber)" : "var(--muted)", borderBottom: detailTab === "overview" ? "2px solid var(--amber)" : "2px solid transparent", marginBottom: "-1px", whiteSpace: "nowrap", fontFamily: "inherit" }}>Overview</button>
                      {selectedBusiness.menuItems && selectedBusiness.menuItems.length > 0 && (
                        <button onClick={function() { setDetailTab("services"); var el = document.getElementById("detail-sec-services"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ background: "none", border: "none", paddingBottom: "0.8rem", paddingRight: "1.25rem", cursor: "pointer", fontWeight: 600, fontSize: "0.875rem", color: detailTab === "services" ? "var(--amber)" : "var(--muted)", borderBottom: detailTab === "services" ? "2px solid var(--amber)" : "2px solid transparent", marginBottom: "-1px", whiteSpace: "nowrap", fontFamily: "inherit" }}>Services</button>
                      )}
                      {selectedBusiness.hours && (
                        <button onClick={function() { setDetailTab("hours"); var el = document.getElementById("detail-sec-hours"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ background: "none", border: "none", paddingBottom: "0.8rem", paddingRight: "1.25rem", cursor: "pointer", fontWeight: 600, fontSize: "0.875rem", color: detailTab === "hours" ? "var(--amber)" : "var(--muted)", borderBottom: detailTab === "hours" ? "2px solid var(--amber)" : "2px solid transparent", marginBottom: "-1px", whiteSpace: "nowrap", fontFamily: "inherit" }}>Hours</button>
                      )}
                      <button onClick={function() { setDetailTab("reviews"); var el = document.getElementById("detail-sec-reviews"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }} style={{ background: "none", border: "none", paddingBottom: "0.8rem", paddingRight: "1.25rem", cursor: "pointer", fontWeight: 600, fontSize: "0.875rem", color: detailTab === "reviews" ? "var(--amber)" : "var(--muted)", borderBottom: detailTab === "reviews" ? "2px solid var(--amber)" : "2px solid transparent", marginBottom: "-1px", whiteSpace: "nowrap", fontFamily: "inherit" }}>Reviews</button>
                    </div>

                    {/* ── Overview ─────────────────────── */}
                    <div id="detail-sec-overview">
                        {selectedBusiness.lat && selectedBusiness.lng && (
                          <DetailMap lat={selectedBusiness.lat} lng={selectedBusiness.lng} name={selectedBusiness.name} />
                        )}
                        <p style={{ lineHeight: 1.7, marginBottom: "1.5rem" }}>{selectedBusiness.description}</p>
                        {selectedBusiness.galleryUrls && selectedBusiness.galleryUrls.length > 0 && (
                          <div style={{ marginBottom: "1.5rem" }}>
                            <div className="gallery">
                              {selectedBusiness.galleryUrls.map((url, i) => (
                                <div key={i} className="gallery-item">
                                  <img src={url} alt={`Gallery ${i}`} onClick={() => setLightboxUrl(url)} style={{ cursor: "zoom-in" }} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {selectedBusiness.socialMedia && Object.entries(selectedBusiness.socialMedia).filter(([k,v]) => v).length > 0 && (
                          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
                            {Object.entries(selectedBusiness.socialMedia).filter(([k,v]) => v).map(([key, val]) => {
                              var href = "";
                              if (key === "whatsapp") href = "https://wa.me/" + val.replace(/[^0-9]/g, "");
                              else if (key === "instagram") href = val.startsWith("http") ? val : "https://instagram.com/" + val.replace(/^@/, "");
                              else if (key === "tiktok") href = val.startsWith("http") ? val : "https://tiktok.com/@" + val.replace(/^@/, "");
                              else if (key === "facebook") href = val.startsWith("http") ? val : "https://facebook.com/" + val;
                              else if (key === "link") href = val.startsWith("http") ? val : "https://" + val;
                              else href = val.startsWith("http") ? val : "#";
                              var label = key === "whatsapp" ? "WhatsApp" : key === "link" ? "Website" : key.charAt(0).toUpperCase() + key.slice(1);
                              return (
                                <a key={key} href={href} target="_blank" rel="noopener noreferrer" style={{ padding: "0.4rem 0.85rem", border: "1px solid var(--border)", borderRadius: "20px", fontSize: "0.8rem", textDecoration: "none", color: "var(--text)", transition: "all 0.2s" }}
                                  onMouseEnter={function(e) { e.currentTarget.style.borderColor = "var(--amber)"; e.currentTarget.style.color = "var(--amber)"; }}
                                  onMouseLeave={function(e) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text)"; }}>
                                  {label}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        {currentUser && selectedBusiness && currentUser.uid !== selectedBusiness.ownerId && (!selectedBusiness.menuItems || selectedBusiness.menuItems.length === 0) && (
                          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
                            <button onClick={() => { setSelectedBookingItem({ name: "General Booking", price: "0", imageUrl: "" }); setBookingModalOpen(true); }} style={{ flex: 1, padding: "0.6rem 1rem", background: "var(--amber)", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}>Book Appointment</button>
                            <button onClick={async (e) => {
                              if (!currentUser) return;
                              if (e.target.disabled) return;
                              e.target.disabled = true;
                              try {
                              const exists = await db.collection("conversations")
                                .where("participants", "array-contains", currentUser.uid)
                                .where("businessId", "==", selectedBusiness.id)
                                .get();
                              if (exists.docs.length > 0) {
                                navigate(`chat/${exists.docs[0].id}`);
                              } else {
                                const opener = `Hi! I'm interested in ${selectedBusiness.name}. Can you tell me more?`;
                                const newConv = await db.collection("conversations").add({
                                  participants: [currentUser.uid, selectedBusiness.ownerId],
                                  businessId: selectedBusiness.id,
                                  businessName: selectedBusiness.name,
                                  otherParticipantName: selectedBusiness.ownerName,
                                  shopperName: currentUser.displayName || "Customer",
                                  shopperUid: currentUser.uid,
                                  lastMessage: opener,
                                  lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
                                  unreadBy: { [currentUser.uid]: 0, [selectedBusiness.ownerId]: 1 }
                                });
                                await db.collection("conversations").doc(newConv.id).collection("messages").add({
                                  text: opener,
                                  senderUid: currentUser.uid,
                                  senderName: currentUser.displayName || "Customer",
                                  createdAt: firebase.firestore.FieldValue.serverTimestamp()
                                });
                                navigate(`chat/${newConv.id}`);
                              }
                              } finally { e.target.disabled = false; }
                            }} style={{ flex: 1, padding: "0.6rem 1rem", background: "white", color: "var(--amber)", border: "1px solid var(--amber)", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}>Message</button>
                          </div>
                        )}
                    </div>

                    {/* ── Services ─────────────────────── */}
                    {selectedBusiness.menuItems && selectedBusiness.menuItems.length > 0 && (
                      <div id="detail-sec-services" style={{ marginTop: "1.5rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border)" }}>
                        <h3 style={{ fontFamily: "var(--serif)", marginBottom: "0.75rem" }}>Products & Services</h3>
                        {selectedBusiness.menuItems.map((item, i) => {
                          const _photos = getItemPhotos(item);
                          const _photoCount = _photos.length;
                          const _hasExtras = _photoCount > 1
                            || (item.longDescription && item.longDescription.length)
                            || (Array.isArray(item.variants) && item.variants.length > 0)
                            || (Array.isArray(item.faqs) && item.faqs.length > 0);
                          return (
                          <div key={i} style={{ display: "flex", flexDirection: "column", gap: "0.75rem", padding: "0.75rem 0", borderBottom: "1px solid var(--border)" }}>
                            <div
                              onClick={() => setPublicItemDetail(item)}
                              style={{ display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer", borderRadius: "6px", padding: "0.1rem", margin: "-0.1rem", transition: "background 0.15s" }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#FFF8F2"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                              title="Tap to see full details"
                            >
                              {_photoCount > 0 ? (
                                <div style={{ position: "relative", flexShrink: 0 }}>
                                  <img src={_photos[0]} alt={item.name} style={{ width: "52px", height: "52px", objectFit: "cover", borderRadius: "8px", display: "block" }} />
                                  {_photoCount > 1 && (
                                    <div style={{ position: "absolute", bottom: "-4px", right: "-4px", background: "rgba(0,0,0,0.7)", color: "#fff", fontSize: "0.6rem", padding: "1px 5px", borderRadius: "99px", fontWeight: 700 }}>+{_photoCount - 1}</div>
                                  )}
                                </div>
                              ) : (
                                <div style={{ width: "52px", height: "52px", background: "var(--cream)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <Icon name="photo" size={20} color="var(--muted)" />
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600 }}>{item.name}</div>
                                {item.description && <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{item.description}</div>}
                                {_hasExtras && <div style={{ fontSize: "0.7rem", color: "var(--amber)", marginTop: "0.15rem", fontWeight: 600 }}>See details ›</div>}
                              </div>
                              <div style={{ fontWeight: 600, color: "var(--amber)", flexShrink: 0 }}>{getItemPriceLabel(item)}</div>
                            </div>
                            {currentUser && selectedBusiness && (
                              <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button
                                  onClick={() => { setSelectedBookingItem(item); setBookingModalOpen(true); }}
                                  style={{ flex: 1, padding: "0.5rem 1rem", background: "var(--amber)", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}
                                >
                                  Book
                                </button>
                                <button
                                  onClick={async (e) => {
                                    if (!currentUser) return;
                                    if (e.target.disabled) return;
                                    e.target.disabled = true;
                                    try {
                                    var itemMsg = "Hi! I'm interested in: " + item.name + (item.price ? " (S$" + parseFloat(item.price).toFixed(2) + ")" : "") + (item.description ? "\n" + item.description : "");
                                    const exists = await db.collection("conversations")
                                      .where("participants", "array-contains", currentUser.uid)
                                      .where("businessId", "==", selectedBusiness.id)
                                      .get();
                                    if (exists.docs.length > 0) {
                                      var convId = exists.docs[0].id;
                                      await db.collection("conversations").doc(convId).collection("messages").add({
                                        text: itemMsg,
                                        senderUid: currentUser.uid,
                                        senderName: currentUser.displayName,
                                        createdAt: firebase.firestore.FieldValue.serverTimestamp()
                                      });
                                      var otherUid = selectedBusiness.ownerId;
                                      await db.collection("conversations").doc(convId).update({
                                        lastMessage: itemMsg,
                                        lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
                                        [`unreadBy.${otherUid}`]: (exists.docs[0].data().unreadBy?.[otherUid] || 0) + 1
                                      });
                                      navigate(`chat/${convId}`);
                                    } else {
                                      const newConv = await db.collection("conversations").add({
                                        participants: [currentUser.uid, selectedBusiness.ownerId],
                                        businessId: selectedBusiness.id,
                                        businessName: selectedBusiness.name,
                                        otherParticipantName: selectedBusiness.ownerName,
                                        shopperName: currentUser.displayName || "Customer",
                                        shopperUid: currentUser.uid,
                                        lastMessage: itemMsg,
                                        lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
                                        unreadBy: { [currentUser.uid]: 0, [selectedBusiness.ownerId]: 1 }
                                      });
                                      await db.collection("conversations").doc(newConv.id).collection("messages").add({
                                        text: itemMsg,
                                        senderUid: currentUser.uid,
                                        senderName: currentUser.displayName,
                                        createdAt: firebase.firestore.FieldValue.serverTimestamp()
                                      });
                                      navigate(`chat/${newConv.id}`);
                                    }
                                    } finally { e.target.disabled = false; }
                                  }}
                                  style={{ flex: 1, padding: "0.5rem 1rem", background: "white", color: "var(--amber)", border: "1px solid var(--amber)", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}
                                >
                                  Message
                                </button>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Hours ───────────────────────── */}
                    {selectedBusiness.hours && (
                      <div id="detail-sec-hours" style={{ marginTop: "1.5rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border)" }}>
                        <h3 style={{ fontFamily: "var(--serif)", marginBottom: "0.75rem" }}>Operating Hours</h3>
                        {DAYS_ORDER.filter(day => selectedBusiness.hours[day]).map(day => {
                          const info = selectedBusiness.hours[day];
                          return (
                            <div key={day} style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0", fontSize: "0.9rem", borderBottom: "1px solid var(--border)" }}>
                              <span style={{ color: info.enabled ? "var(--text)" : "var(--muted)", fontWeight: info.enabled ? 600 : 400 }}>{day}</span>
                              <span style={{ color: info.enabled ? "var(--amber)" : "var(--muted)" }}>{info.enabled ? `${info.open} - ${info.close}` : "Closed"}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ── Reviews ─────────────────────── */}
                    <div id="detail-sec-reviews" style={{ marginTop: "1.5rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                          <h3 style={{ fontFamily: "var(--serif)", margin: 0 }}>Customer Reviews</h3>
                          {currentUser && currentUser.uid !== selectedBusiness.ownerId && reviewCheckDone && reviewEligibleBooking && (
                            <button onClick={() => { setReviewStars(0); setReviewText(""); setReviewModalOpen(true); }}
                              style={{ background: "var(--amber)", color: "#fff", border: "none", borderRadius: "8px", padding: "0.5rem 1rem", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer", fontFamily: "var(--display)" }}>
                              ⭐ Write a Review
                            </button>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.25rem", padding: "1rem", background: "var(--cream)", borderRadius: "12px" }}>
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--ink)" }}>{(selectedBusiness.rating || 0).toFixed(1)}</div>
                            <div style={{ color: "var(--yellow)", fontSize: "1rem", letterSpacing: "-1px" }}>
                              {"★".repeat(Math.round(selectedBusiness.rating || 0))}{"☆".repeat(5 - Math.round(selectedBusiness.rating || 0))}
                            </div>
                            <div style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{selectedBusiness.reviewCount || 0} reviews</div>
                          </div>
                          {detailReviews.length > 0 && (
                            <div style={{ flex: 1 }}>
                              {[5,4,3,2,1].map(function(star) {
                                var count = detailReviews.filter(function(r) { return r.rating === star; }).length;
                                var pct = detailReviews.length > 0 ? (count / detailReviews.length * 100) : 0;
                                return React.createElement('div', { key: star, style: { display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" } },
                                  React.createElement('span', { style: { fontSize: "0.72rem", width: "12px", textAlign: "right", color: "var(--muted)" } }, star),
                                  React.createElement('span', { style: { fontSize: "0.72rem", color: "var(--yellow)" } }, "★"),
                                  React.createElement('div', { style: { flex: 1, height: "6px", background: "#eee", borderRadius: "3px", overflow: "hidden" } },
                                    React.createElement('div', { style: { width: pct + "%", height: "100%", background: "var(--yellow)", borderRadius: "3px" } })
                                  ),
                                  React.createElement('span', { style: { fontSize: "0.7rem", color: "var(--muted)", width: "20px" } }, count)
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {!detailReviewsLoaded && <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--muted)", fontSize: "0.85rem" }}>Loading reviews...</div>}
                        {detailReviewsLoaded && detailReviews.length === 0 && <div style={{ textAlign: "center", padding: "1.5rem", color: "var(--muted)", fontSize: "0.85rem" }}>No reviews yet. {currentUser && reviewEligibleBooking ? "Be the first!" : ""}</div>}
                        {detailReviews.map(function(review) {
                          return React.createElement('div', { key: review.id, style: { padding: "1rem", borderBottom: "1px solid var(--border)" } },
                            React.createElement('div', { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" } },
                              React.createElement('div', { style: { display: "flex", alignItems: "center", gap: "0.5rem" } },
                                React.createElement('span', { style: { fontWeight: 600, fontSize: "0.9rem" } }, review.customerName || "Customer"),
                                review.bookingId && React.createElement('span', { style: { background: "#E8F5E9", color: "#2E7D32", fontSize: "0.65rem", padding: "2px 6px", borderRadius: "4px", fontWeight: 600 } }, "✓ Verified")
                              ),
                              React.createElement('span', { style: { fontSize: "0.75rem", color: "var(--muted)" } }, review.reviewDate || "")
                            ),
                            React.createElement('div', { style: { color: "var(--yellow)", fontSize: "0.85rem", marginBottom: "0.4rem", letterSpacing: "-1px" } }, "★".repeat(review.rating || 0) + "☆".repeat(5 - (review.rating || 0))),
                            review.serviceName && React.createElement('div', { style: { fontSize: "0.75rem", color: "var(--muted)", marginBottom: "0.3rem" } }, "Service: " + review.serviceName),
                            React.createElement('p', { style: { fontSize: "0.88rem", lineHeight: 1.5, margin: "0.3rem 0" } }, review.text),
                            review.reply && React.createElement('div', { style: { marginTop: "0.75rem", padding: "0.75rem 1rem", background: "var(--cream)", borderRadius: "8px", borderLeft: "3px solid var(--amber)" } },
                              React.createElement('div', { style: { fontWeight: 600, fontSize: "0.78rem", color: "var(--amber)", marginBottom: "0.3rem" } }, "Owner's Response"),
                              React.createElement('p', { style: { fontSize: "0.82rem", lineHeight: 1.5, margin: 0 } }, review.reply)
                            )
                          );
                        })}
                    </div>

                    {/* --- Write Review Modal --- */}
                    {reviewModalOpen && React.createElement('div', {
                      onClick: () => setReviewModalOpen(false),
                      style: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }
                    },
                      React.createElement('div', {
                        onClick: (e) => e.stopPropagation(),
                        style: { background: "#fff", borderRadius: "16px", padding: "1.5rem", maxWidth: "450px", width: "100%", maxHeight: "80vh", overflowY: "auto" }
                      },
                        // Modal header
                        React.createElement('div', { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" } },
                          React.createElement('h3', { style: { fontFamily: "var(--serif)", margin: 0 } }, "Write a Review"),
                          React.createElement('button', { onClick: () => setReviewModalOpen(false), style: { background: "none", border: "none", fontSize: "1.3rem", cursor: "pointer", color: "var(--muted)" } }, "?")
                        ),
                        React.createElement('p', { style: { fontSize: "0.85rem", color: "var(--muted)", marginBottom: "1rem" } }, "Share your experience with ", selectedBusiness.name),

                        // Verified badge
                        reviewEligibleBooking && React.createElement('div', { style: { display: "flex", alignItems: "center", gap: "6px", padding: "8px 12px", background: "#E8F5E9", borderRadius: "8px", marginBottom: "1rem", fontSize: "0.78rem", color: "#2E7D32" } },
                          "? Verified booking: ", reviewEligibleBooking.serviceItem?.name || "Service"
                        ),

                        // No eligible booking
                        !reviewEligibleBooking && reviewCheckDone && React.createElement('div', { style: { padding: "1rem", background: "#FFF3E0", borderRadius: "8px", fontSize: "0.85rem", color: "#E65100", textAlign: "center" } },
                          "You need a completed booking with this business to leave a review."
                        ),

                        // Star rating
                        reviewEligibleBooking && React.createElement('div', { style: { textAlign: "center", marginBottom: "1rem" } },
                          React.createElement('p', { style: { fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem" } }, "How would you rate your experience?"),
                          React.createElement('div', { style: { display: "flex", justifyContent: "center", gap: "8px" } },
                            [1,2,3,4,5].map(function(star) {
                              return React.createElement('span', {
                                key: star,
                                onClick: () => setReviewStars(star),
                                style: { fontSize: "2rem", cursor: "pointer", color: star <= reviewStars ? "var(--yellow)" : "#ddd", transition: "transform 0.15s", transform: star <= reviewStars ? "scale(1.15)" : "scale(1)" }
                              }, "?");
                            })
                          ),
                          reviewStars > 0 && React.createElement('p', { style: { fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.3rem" } },
                            ["", "Poor", "Fair", "Good", "Very Good", "Excellent"][reviewStars]
                          )
                        ),

                        // Text input
                        reviewEligibleBooking && React.createElement('textarea', {
                          value: reviewText,
                          onChange: (e) => setReviewText(e.target.value),
                          placeholder: "Tell others about your experience...",
                          maxLength: 1000,
                          style: { width: "100%", minHeight: "100px", padding: "0.75rem", border: "1px solid var(--border)", borderRadius: "10px", fontSize: "0.88rem", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }
                        }),

                        reviewEligibleBooking && React.createElement('div', { style: { textAlign: "right", fontSize: "0.72rem", color: "var(--muted)", marginTop: "0.3rem", marginBottom: "1rem" } }, reviewText.length + "/1000"),

                        // Submit button
                        reviewEligibleBooking && React.createElement('button', {
                          disabled: reviewStars === 0 || reviewSubmitting,
                          onClick: async function() {
                            if (reviewStars === 0 || !reviewEligibleBooking || !currentUser) return;
                            setReviewSubmitting(true);
                            try {
                              var now = new Date();
                              var reviewDate = now.toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" });
                              // Save review
                              await db.collection("reviews").add({
                                bizId: selectedBusiness.id,
                                customerUid: currentUser.uid,
                                customerName: currentUser.displayName || "Customer",
                                rating: reviewStars,
                                text: reviewText.trim(),
                                bookingId: reviewEligibleBooking.id,
                                serviceName: reviewEligibleBooking.serviceItem?.name || "",
                                reviewDate: reviewDate,
                                createdAt: firebase.firestore.FieldValue.serverTimestamp()
                              });
                              // Mark booking as reviewed
                              await db.collection("bookings").doc(reviewEligibleBooking.id).update({ hasReview: true });
                              // Recalculate rating client-side
                              var allReviews = await db.collection("reviews").where("bizId", "==", selectedBusiness.id).get();
                              var total = 0; var count = 0;
                              allReviews.forEach(function(d) { total += d.data().rating || 0; count++; });
                              var avg = count > 0 ? total / count : 0;
                              await db.collection("businesses").doc(selectedBusiness.id).update({ rating: avg, reviewCount: count });
                              // Update local state
                              setSelectedBusiness(function(prev) { return prev ? { ...prev, rating: avg, reviewCount: count } : prev; });
                              setDetailReviews(allReviews.docs.map(function(d) { return { id: d.id, ...d.data() }; }).sort(function(a,b) { return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0); }));
                              setReviewEligibleBooking(null);
                              setReviewModalOpen(false);
                              setReviewStars(0);
                              setReviewText("");
                              showToast("Review submitted! Thank you 🎉", "success");
                            } catch(err) {
                              console.error("Review submit error:", err);
                              showToast(err.message || "Failed to submit review", "error");
                            } finally {
                              setReviewSubmitting(false);
                            }
                          },
                          style: { width: "100%", padding: "0.75rem", background: reviewStars === 0 ? "#ccc" : "var(--amber)", color: "#fff", border: "none", borderRadius: "10px", fontWeight: 600, fontSize: "0.9rem", cursor: reviewStars === 0 ? "not-allowed" : "pointer", fontFamily: "var(--display)" }
                        }, reviewSubmitting ? "Submitting..." : "Submit Review")
                      )
                    )}

                  </div>
                </div>
                <div style={{ height: "2rem" }} />
              </div>
            )}

            {/* Profile Page */}
            {/* Profile Page */}
            {currentRoute === "profile" && !editingProfile && (
              <div className="page active">
                <div className="profile-container" style={{ maxWidth: "600px", margin: "0 auto" }}>
                  {/* Success message after business submission */}
                  {bizSuccessMsg && (
                    <div style={{ background: "#FFF8E1", border: "1px solid #FFD54F", borderRadius: "12px", padding: "1rem 1.25rem", marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <span style={{ fontSize: "1.4rem" }}>⏳</span>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: "0.95rem", color: "#5D4037" }}>"{bizSuccessMsg}" is Under Review</p>
                        <p style={{ fontSize: "0.8rem", color: "#8D6E63", marginTop: "0.2rem" }}>We'll notify you once it's approved. This usually takes 1–2 business days.</p>
                      </div>
                      <button onClick={() => setBizSuccessMsg(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", color: "#8D6E63", marginLeft: "auto" }}>×</button>
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", marginBottom: "2rem" }}>
                    <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "var(--amber-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                      {profilePicUrl ? (
                        <img src={profilePicUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Icon name="user" size={40} color="var(--amber)" />
                      )}
                    </div>
                    <div>
                      <h1 style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", marginBottom: "0.25rem" }}>{currentUser?.displayName}</h1>
                      <p style={{ color: "var(--muted)", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
                        {userProfile?.joinedAt ? `Member since ${new Date(userProfile.joinedAt?.seconds * 1000).toLocaleDateString("en-SG", { month: "short", year: "numeric" })}` : "New member"}
                      </p>
                      <button
                        onClick={() => {
                          setProfileForm({
                            firstName: userProfile?.firstName || "",
                            lastName: userProfile?.lastName || "",
                            displayName: userProfile?.displayName || currentUser?.displayName || "",
                            email: userProfile?.email || currentUser?.email || "",
                            phone: userProfile?.phone || currentUser?.phoneNumber || "",
                            birthday: userProfile?.birthday || "",
                            homePostalCode: userProfile?.homePostalCode || "",
                            whatsappPhone: userProfile?.whatsappPhone || "",
                            waOptIn_bookings: !!userProfile?.waOptIn_bookings,
                            waOptIn_chat: !!userProfile?.waOptIn_chat,
                            waOptIn_discovery: !!userProfile?.waOptIn_discovery,
                            waOptIn_features: !!userProfile?.waOptIn_features
                          });
                          setEditingProfile(true);
                        }}
                        style={{ padding: "0.4rem 1rem", border: "1px solid var(--border)", borderRadius: "20px", background: "white", cursor: "pointer", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.4rem" }}
                      >
                        <Icon name="settings" size={14} /> Edit Profile
                      </button>
                    </div>
                  </div>

                  <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
                    <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", marginBottom: "1rem" }}>Likes</h2>
                    {likedBusinesses.length > 0 ? (
                      <div className="liked-grid">
                        {likedBusinesses.map(biz => (
                          <div key={biz.id} className="liked-card">
                            <a
                              className="card-seo-link"
                              href={`/detail/${biz.slug || biz.id}`}
                              onClick={(e) => { e.preventDefault(); navigate(`detail/${biz.slug || biz.id}`); }}
                              aria-label={biz.name}
                            >{biz.name}</a>
                            {biz.coverPhotoUrl ? <img src={biz.coverPhotoUrl} alt={biz.name} /> : <CardNamePlaceholder name={biz.name} fontSize="0.9rem" padding="0.6rem" />}
                            <div className="liked-card-name">{biz.name}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: "var(--muted)", textAlign: "center", padding: "2rem 0" }}>No likes yet</p>
                    )}
                  </div>

                  {/* My Businesses Section */}
                  <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                      <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem" }}>My Businesses</h2>
                      <button onClick={() => navigate("add-business")} style={{ background: "none", border: "none", color: "var(--amber)", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}>+ Add New</button>
                    </div>
                    {userBusinesses.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        {userBusinesses.map(biz => {
                          const statusConfig = {
                            approved: { label: "Live", color: "#2E7D32", bg: "#E8F5E9" },
                            pending: { label: "Pending Approval", color: "#E65100", bg: "#FFF3E0" },
                            suspended: { label: "Suspended", color: "#C62828", bg: "#FFEBEE" },
                            unlisted: { label: "Unlisted", color: "#757575", bg: "#F5F5F5" },
                            inactive: { label: "Inactive", color: "#C62828", bg: "#FFEBEE" },
                            draft: { label: "In Progress", color: "#6B6B6B", bg: "#F5F5F5" }
                          };
                          const st = statusConfig[biz.status] || statusConfig.draft;
                          return (
                            <div
                              key={biz.id}
                              onClick={() => { setSelectedUserBiz(biz); navigate(`my-business/${biz.id}`); }}
                              style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.85rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", cursor: "pointer", transition: "box-shadow 0.2s" }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
                              onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                            >
                              <div style={{ width: "48px", height: "48px", borderRadius: "10px", overflow: "hidden", flexShrink: 0, background: "var(--amber-light)" }}>
                                {biz.coverPhotoUrl ? (
                                  <img src={biz.coverPhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Icon name="store" size={22} color="var(--amber)" />
                                  </div>
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontWeight: 600, fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{biz.name}</p>
                                <p style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                                  {biz.category}
                                  {(biz.viewCount24h || biz.viewCount7d || biz.viewCount) ? (
                                    <span style={{ marginLeft: "0.4rem", color: "#E65100", fontWeight: 600 }}>
                                      &middot; {biz.viewCount24h || 0} today &middot; {(biz.viewCount || 0).toLocaleString()} all-time
                                    </span>
                                  ) : null}
                                </p>
                              </div>
                              <span style={{ fontSize: "0.7rem", fontWeight: 600, color: st.color, background: st.bg, padding: "0.25rem 0.65rem", borderRadius: "20px", whiteSpace: "nowrap" }}>
                                {st.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", padding: "2rem 0" }}>
                        <p style={{ color: "var(--muted)", marginBottom: "1rem" }}>You haven't created any businesses yet</p>
                        <button onClick={() => navigate("add-business")} style={{ padding: "0.6rem 1.5rem", background: "var(--amber)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem" }}>
                          + Add a Business
                        </button>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

            {/* Edit Profile Page */}
            {currentRoute === "profile" && editingProfile && (
              <div className="page active">
                <div className="profile-container" style={{ maxWidth: "600px", margin: "0 auto" }}>
                  <button onClick={() => setEditingProfile(false)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.35rem", color: "var(--muted)", marginBottom: "1rem", fontSize: "0.9rem" }}>
                    <Icon name="arrowLeft" size={14} /> Back to Profile
                  </button>

                  <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", marginBottom: "1.5rem" }}>Profile Picture</h2>
                  <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "2rem", textAlign: "center" }}>
                    <div style={{ width: "100px", height: "100px", borderRadius: "50%", margin: "0 auto 1rem", background: "var(--amber-light)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                      {profilePicUrl ? (
                        <img src={profilePicUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Icon name="user" size={50} color="var(--amber)" />
                      )}
                    </div>
                    <label style={{ cursor: "pointer", padding: "0.4rem 1rem", border: "1px dashed var(--border)", borderRadius: "20px", fontSize: "0.85rem", color: "var(--muted)" }}>
                      + Upload new photo
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                        if (e.target.files[0]) uploadProfilePic(e.target.files[0]);
                      }} />
                    </label>
                  </div>

                  <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", marginBottom: "1rem" }}>Personal Information</h2>
                  <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "2rem" }}>
                    <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
                      <div style={{ flex: 1 }}>
                        <label className="form-label">First Name</label>
                        <input className="form-input" value={profileForm.firstName} onChange={(e) => setProfileForm(p => ({ ...p, firstName: e.target.value }))} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="form-label">Last Name</label>
                        <input className="form-input" value={profileForm.lastName} onChange={(e) => setProfileForm(p => ({ ...p, lastName: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <label className="form-label">Display Name</label>
                      <input className="form-input" value={profileForm.displayName} onChange={(e) => setProfileForm(p => ({ ...p, displayName: e.target.value }))} />
                    </div>
                  </div>

                  <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", marginBottom: "1rem" }}>Account</h2>
                  <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "2rem" }}>
                    {(() => {
                      const isPhoneUser = currentUser?.signInProvider === "phone";
                      const isEmailUser = currentUser?.signInProvider === "password";
                      const isGoogleUser = currentUser?.signInProvider === "google.com";
                      return (
                        <>
                          <div style={{ marginBottom: "1rem" }}>
                            <label className="form-label">Email</label>
                            {isPhoneUser ? (
                              <input className="form-input" type="email" placeholder="Add your email address" value={profileForm.email} onChange={(e) => setProfileForm(p => ({ ...p, email: e.target.value }))} />
                            ) : (
                              <div style={{ fontSize: "0.95rem", padding: "0.6rem 0", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                {currentUser?.email || "Not set"}
                                {(isEmailUser || isGoogleUser) && <span style={{ fontSize: "0.7rem", background: "var(--amber-light)", color: "var(--amber)", padding: "0.15rem 0.5rem", borderRadius: "8px" }}>Primary</span>}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="form-label">Phone Number</label>
                            {isEmailUser || isGoogleUser ? (
                              <input className="form-input" type="tel" placeholder="Add your phone number" value={profileForm.phone} onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))} />
                            ) : (
                              <div style={{ fontSize: "0.95rem", padding: "0.6rem 0", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                {currentUser?.phoneNumber || profileForm.phone || "Not set"}
                                {isPhoneUser && <span style={{ fontSize: "0.7rem", background: "var(--amber-light)", color: "var(--amber)", padding: "0.15rem 0.5rem", borderRadius: "8px" }}>Primary</span>}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", marginBottom: "1rem" }}>Birthday</h2>
                  <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "2rem" }}>
                    <input type="date" className="form-input" value={profileForm.birthday} onChange={(e) => setProfileForm(p => ({ ...p, birthday: e.target.value }))} />
                  </div>

                  <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", marginBottom: "1rem" }}>Your Neighbourhood</h2>
                  <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "2rem" }}>
                    <label className="form-label">Home Postal Code</label>
                    <input
                      className="form-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="e.g. 520123"
                      value={profileForm.homePostalCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setProfileForm(p => ({ ...p, homePostalCode: val }));
                      }}
                    />
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.5rem", lineHeight: 1.5 }}>
                      We use this to notify you when new businesses open near you. Your exact address is never stored or shared.
                    </p>
                  </div>

                  <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", marginBottom: "1rem" }}>WhatsApp Notifications</h2>
                  <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "2rem" }}>
                    <label className="form-label">WhatsApp Number</label>
                    <input
                      className="form-input"
                      type="tel"
                      inputMode="tel"
                      placeholder="+65 8123 4567"
                      value={profileForm.whatsappPhone}
                      onChange={(e) => setProfileForm(p => ({ ...p, whatsappPhone: e.target.value.replace(/[^0-9+ ]/g, "") }))}
                    />
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.5rem", lineHeight: 1.5 }}>
                      Get nooks alerts on WhatsApp instead of checking the app. Include your country code (e.g. +65). You can turn any of these off anytime.
                    </p>

                    {(() => {
                      const hasNumber = (profileForm.whatsappPhone || "").replace(/[^0-9]/g, "").length >= 8;
                      const rows = [
                        { key: "waOptIn_bookings",  label: "Booking updates",     hint: "Confirmations, changes and cancellations" },
                        { key: "waOptIn_chat",      label: "New messages",        hint: "When someone messages you on nooks" },
                        { key: "waOptIn_discovery", label: "New shops near you",  hint: "When a home business opens in your area" },
                        { key: "waOptIn_features",  label: "News & new features", hint: "Occasional nooks updates and tips" }
                      ];
                      return (
                        <div style={{ marginTop: "0.75rem", opacity: hasNumber ? 1 : 0.5, pointerEvents: hasNumber ? "auto" : "none" }}>
                          {rows.map(r => (
                            <label
                              key={r.key}
                              style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", padding: "0.6rem 0", borderTop: "1px solid var(--border)", cursor: hasNumber ? "pointer" : "default" }}
                            >
                              <input
                                type="checkbox"
                                checked={!!profileForm[r.key]}
                                disabled={!hasNumber}
                                onChange={(e) => setProfileForm(p => ({ ...p, [r.key]: e.target.checked }))}
                                style={{ marginTop: "0.2rem", width: "1.05rem", height: "1.05rem", accentColor: "var(--amber)", flexShrink: 0 }}
                              />
                              <span>
                                <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>{r.label}</span>
                                <span style={{ display: "block", fontSize: "0.78rem", color: "var(--muted)" }}>{r.hint}</span>
                              </span>
                            </label>
                          ))}
                          {!hasNumber && (
                            <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.6rem" }}>
                              Add a WhatsApp number above to choose your alerts.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <button
                    onClick={async () => {
                      const _waAnyOptIn = profileForm.waOptIn_bookings || profileForm.waOptIn_chat || profileForm.waOptIn_discovery || profileForm.waOptIn_features;
                      const _waHasNumber = (profileForm.whatsappPhone || "").replace(/[^0-9]/g, "").length >= 8;
                      await saveProfile({
                        name: profileForm.displayName,
                        firstName: profileForm.firstName,
                        lastName: profileForm.lastName,
                        displayName: profileForm.displayName,
                        email: profileForm.email,
                        phone: profileForm.phone,
                        birthday: profileForm.birthday,
                        homePostalCode: profileForm.homePostalCode,
                        whatsappPhone: profileForm.whatsappPhone,
                        waOptIn_bookings: _waHasNumber && profileForm.waOptIn_bookings,
                        waOptIn_chat: _waHasNumber && profileForm.waOptIn_chat,
                        waOptIn_discovery: _waHasNumber && profileForm.waOptIn_discovery,
                        waOptIn_features: _waHasNumber && profileForm.waOptIn_features,
                        // Server-authoritative consent audit trail — stamped only when consent is actually given
                        ...(_waAnyOptIn && _waHasNumber ? { waOptInAt: firebase.firestore.FieldValue.serverTimestamp(), waOptInSource: "profile" } : {})
                      });
                      // Update Firebase Auth display name
                      if (auth.currentUser && profileForm.displayName) {
                        await auth.currentUser.updateProfile({ displayName: profileForm.displayName });
                        setCurrentUser(prev => ({ ...prev, displayName: profileForm.displayName }));
                      }
                      // SSoT: Sync ownerName in all user's businesses + businessName in conversations
                      if (userBusinesses.length > 0 && profileForm.displayName) {
                        try {
                          var batch = db.batch();
                          userBusinesses.forEach(function(biz) {
                            batch.update(db.collection("businesses").doc(biz.id), { ownerName: profileForm.displayName });
                          });
                          await batch.commit();
                          setUserBusinesses(prev => prev.map(b => ({ ...b, ownerName: profileForm.displayName })));
                        } catch (syncErr) { console.error("ownerName sync error:", syncErr); }
                      }
                      setEditingProfile(false);
                    }}
                    style={{ width: "100%", padding: "0.875rem", background: "var(--amber)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "1rem", marginBottom: "1rem" }}
                  >
                    Save Changes
                  </button>

                  <button
                    onClick={handleSignOut}
                    style={{ width: "100%", padding: "0.875rem", background: "#F5A6A0", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "1rem" }}
                  >
                    Sign Out
                  </button>

                  <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                    <span
                      title={CHANGELOG_TOOLTIP}
                      style={{ fontSize: "0.72rem", color: "var(--muted)", cursor: "help", borderBottom: "1px dotted var(--muted)" }}
                    >
                      nooks {APP_VERSION} &#9432;
                    </span>
                  </div>

                  <div style={{ height: "2rem" }} />
                </div>
              </div>
            )}

            {/* Likes Page */}
            {currentRoute === "likes" && (
              <div className="page active">
                <div className="profile-container" style={{ maxWidth: "600px", margin: "0 auto" }}>
                  <h1 className="hero-rise hero-rise-1" style={{ fontFamily: "var(--serif)", marginBottom: "1.5rem" }}>My Likes</h1>
                  {likedBusinesses.length > 0 ? (
                    <div className="liked-grid">
                      {likedBusinesses.map((biz, idx) => (
                        <div key={biz.id} className="liked-card reveal" data-delay={String((idx % 4) + 1)}>
                          <a
                            className="card-seo-link"
                            href={`/detail/${biz.slug || biz.id}`}
                            onClick={(e) => { e.preventDefault(); navigate(`detail/${biz.slug || biz.id}`); }}
                            aria-label={biz.name}
                          >{biz.name}</a>
                          {biz.coverPhotoUrl ? <img src={biz.coverPhotoUrl} alt={biz.name} /> : <CardNamePlaceholder name={biz.name} fontSize="0.9rem" padding="0.6rem" />}
                          <div className="liked-card-name">{biz.name}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-state-icon"><Icon name="heart" size={48} color="#9E9E9E" /></div>
                      <h3 className="empty-state-title">No likes yet</h3>
                      <p className="empty-state-text">Start exploring to like your favorite nooks</p>
                      <button className="cta-button" onClick={() => navigate("explore")}>Look Around</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Settings Page */}
            {currentRoute === "settings" && (
              <div className="page active">
                <div className="profile-container" style={{ maxWidth: "600px", margin: "0 auto" }}>
                  <h1 style={{ fontFamily: "var(--serif)", marginBottom: "1.5rem" }}>Settings</h1>
                  {currentUser && (
                    <>
                      <div className="settings-section">
                        <h2 className="settings-section-title">Preferences</h2>
                        <div className="setting-row">
                          <span className="setting-label">Notifications</span>
                          <div className="toggle-switch"><div className="toggle-switch-dot" /></div>
                        </div>
                      </div>
                      <div className="settings-section">
                        <h2 className="settings-section-title">Account</h2>
                        <button className="danger-btn" onClick={handleSignOut} style={{ marginBottom: "1rem" }}>Sign Out</button>
                        <button className="danger-btn" onClick={() => { if (window.confirm("Are you sure?")) alert("Account deletion would be processed here"); }}>Delete Account</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Regulations Page */}
            {currentRoute === "regulations" && (
              <div className="page active">
                <div className="legal-page">
                  <button onClick={() => window.history.back()} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.9rem" }}>
                    <Icon name="arrowLeft" size={16} /> Back
                  </button>
                  <h1 className="hero-rise hero-rise-1"><Icon name="shield" size={28} color="var(--amber)" style={{ marginRight: "0.5rem" }} /> Home-Based Business Regulations</h1>
                  <p className="legal-updated hero-rise hero-rise-2">Singapore HDB/URA Guidelines — Last updated March 2026</p>

                  <div className="legal-callout reveal">
                    Home-based businesses in Singapore are permitted under HDB/URA guidelines, allowing residents to run small-scale, non-disruptive activities like baking, hairdressing, or tutoring without formal approval. Operations must be managed solely by residents, with no non-resident employees, noise, or heavy equipment.
                  </div>

                  <h2>Key Guidelines & Requirements</h2>
                  <ul>
                    <li><strong>Permitted Activities:</strong> Baking/food production, sewing, hairdressing, beauty services (no massage), private tuition (max 3 students), and freelance creative work.</li>
                    <li><strong>Operator Eligibility:</strong> The business must be run by the owner, owner's family, or registered tenants.</li>
                    <li><strong>No Outsourcing:</strong> You cannot hire non-resident employees (staff/helpers).</li>
                    <li><strong>No Nuisance:</strong> Activities must not cause noise, smoke, or heavy traffic.</li>
                    <li><strong>Advertising:</strong> No commercial signs or banners on the property.</li>
                    <li><strong>Food Safety:</strong> Home bakers and cooks must follow Singapore Food Agency (SFA) guidelines and complete the mandatory Food Safety Course Level 1 (WSQ).</li>
                  </ul>

                  <h2>Schemes Offered</h2>
                  <ul>
                    <li><strong>Home-Based Business Scheme (HDB/URA):</strong> Suitable for production/service businesses (e.g., baking, beauty).</li>
                    <li><strong>Home Office Scheme (HDB/URA):</strong> Ideal for administrative, service-oriented businesses (e.g., website design, accounting).</li>
                  </ul>

                  <h2>Starting Your Business</h2>
                  <ul>
                    <li><strong>Check Eligibility:</strong> Ensure your activity qualifies under HDB/URA guidelines.</li>
                    <li><strong>Register Your Business:</strong> ACRA registration is not required for small-scale, personal hobbyist projects (e.g., casual baking, crafting) operated solely by residents. However, if you are running the business on an ongoing basis for profit, you should register with ACRA (Accounting and Corporate Regulatory Authority). Specific approvals may be required for certain activities.</li>
                    <li><strong>Food Hygiene:</strong> If cooking, register your business with SFA and complete the required training.</li>
                  </ul>

                  <h2>Important Considerations</h2>
                  <ul>
                    <li><strong>Neighbours:</strong> If neighbors complain to HDB about noise, smell, or heavy traffic, you may be required to stop or move.</li>
                    <li><strong>Insurance:</strong> Consider getting insurance to cover business assets and liability.</li>
                    <li><strong>Storage:</strong> Large-scale storage of goods is not allowed.</li>
                  </ul>

                  <div className="legal-callout">
                    <strong>Disclaimer:</strong> The information on this page is provided for general guidance only and does not constitute legal, regulatory, or professional advice. <span className="nooks-brand">nooks</span> is a neutral directory platform and does not verify, endorse, or guarantee the compliance of any listed business. Business owners are solely responsible for ensuring their activities comply with all applicable Singapore laws and regulations. <span className="nooks-brand">nooks</span> accepts no liability for any loss, damage, or regulatory action arising from a business owner's non-compliance. For official and most up-to-date guidelines, please refer to <a href="https://www.ura.gov.sg" target="_blank" rel="noopener" style={{ color: "var(--amber)" }}>URA</a>, <a href="https://www.hdb.gov.sg" target="_blank" rel="noopener" style={{ color: "var(--amber)" }}>HDB</a>, <a href="https://www.sfa.gov.sg" target="_blank" rel="noopener" style={{ color: "var(--amber)" }}>SFA</a>, and <a href="https://www.gobusiness.gov.sg" target="_blank" rel="noopener" style={{ color: "var(--amber)" }}>GoBusiness</a> websites.
                  </div>

                  <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginTop: "1.5rem" }}>By using nooks, you agree to our <a href="/terms" onClick={function(e) { e.preventDefault(); navigate("terms"); }} style={{ color: "var(--amber)" }}>Terms of Service</a> and <a href="/privacy" onClick={function(e) { e.preventDefault(); navigate("privacy"); }} style={{ color: "var(--amber)" }}>Privacy Policy</a>.</p>
                </div>
              </div>
            )}

            {/* FAQ Page */}
            {currentRoute === "faq" && (
              <div className="page active">
                <div className="legal-page">
                  <button onClick={() => window.history.back()} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.9rem" }}>
                    <Icon name="arrowLeft" size={16} /> Back
                  </button>
                  <h1 className="hero-rise hero-rise-1">Frequently Asked Questions</h1>
                  <p className="faq-intro hero-rise hero-rise-2">"A close neighbour is better than a distant relative." That's the spirit behind <span className="nooks-brand">nooks</span> — a community-first, location-based directory connecting you with the talented home-based businesses right in your neighbourhood.</p>

                  <div className="faq-item reveal">
                    <div className="faq-question">What is <span className="nooks-brand">nooks</span>?</div>
                    <div className="faq-answer"><span className="nooks-brand">nooks</span> is a free community directory for home-based businesses in Singapore. Think of it as your neighbourhood's digital notice board — where the auntie who bakes amazing kueh, the uncle who does leather crafting, or the young couple running a pet grooming service from their HDB flat can all be discovered by people living nearby. We believe every neighbourhood has hidden gems, and <span className="nooks-brand">nooks</span> helps bring them to light.</div>
                  </div>
                  <hr className="faq-divider" />

                  <div className="faq-item reveal">
                    <div className="faq-question">Why did we start <span className="nooks-brand">nooks</span>?</div>
                    <div className="faq-answer"><span className="nooks-brand">nooks</span> came out of personal frustration. Last-minute cleaning help. A quick print job. Tailoring one or two pieces. A trusted pet-sitter when every boarding place was full. These felt like small problems — until they weren't. Nothing out there quite solved them, so we bootstrapped this ourselves. If it helps a neighbour find what they need, or make some income from what they do, that's more than enough for us.</div>
                  </div>
                  <hr className="faq-divider" />

                  <div className="faq-item reveal">
                    <div className="faq-question">How does it work?</div>
                    <div className="faq-answer">Everything on <span className="nooks-brand">nooks</span> is location-first. We sort businesses by distance from you, show them on an interactive map, and help you discover hidden gems right in your HDB block or estate. If you're a home-based business owner, just sign in with Google, acknowledge Singapore's home-based business regulations, and list your business — add your description, photos, category, location, and social links. Your listing goes live after a quick review. If you're looking to discover, just browse by category (Food &amp; Beverage, Beauty, Pet Care, Crafts, and more), sort by newest, most popular, or nearest to you, and connect directly with the business owner via WhatsApp or in-app messaging.</div>
                  </div>
                  <hr className="faq-divider" />

                  <div className="faq-item reveal">
                    <div className="faq-question">Is <span className="nooks-brand">nooks</span> free?</div>
                    <div className="faq-answer">Yes, 100% free — for both business owners and users. No listing fees, no commissions, no hidden charges. <span className="nooks-brand">nooks</span> is a passion project built to support the local community. We may explore optional features like featured listings down the road, but the core directory will always remain free.</div>
                  </div>
                  <hr className="faq-divider" />

                  <div className="faq-item reveal">
                    <div className="faq-question">Is my home business allowed to list on <span className="nooks-brand">nooks</span>?</div>
                    <div className="faq-answer">As long as your business complies with Singapore's HDB/URA home-based business guidelines, you're welcome to list on <span className="nooks-brand">nooks</span>. During sign-up, we'll walk you through a summary of the key regulations — things like permitted activities, noise and nuisance rules, and food safety requirements. You'll need to acknowledge these before your listing goes live. If you're doing it as a small hobby (like casual baking or crafting), no ACRA registration is needed. But if you're running it as an ongoing business for profit, we'd recommend registering with ACRA. Check out our <a href="/regulations" onClick={function(e) { e.preventDefault(); navigate("regulations"); }} style={{ color: "var(--amber)" }}>Regulations page</a> for the full details.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms of Service Page */}
            {currentRoute === "terms" && (
              <div className="page active">
                <div className="legal-page">
                  <button onClick={() => window.history.back()} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.9rem" }}>
                    <Icon name="arrowLeft" size={16} /> Back
                  </button>
                  <h1 className="hero-rise hero-rise-1">Terms of Service</h1>
                  <p className="legal-updated hero-rise hero-rise-2">Effective date: 18 March 2026 — Last updated: 18 March 2026</p>

                  <p className="hero-rise hero-rise-3">Welcome to <span className="nooks-brand">nooks</span> (accessible at nooks.sg), a community directory platform that connects users with home-based businesses in Singapore. By accessing or using <span className="nooks-brand">nooks</span>, you agree to be bound by these Terms of Service. If you do not agree, please do not use the platform.</p>

                  <h2>1. About <span className="nooks-brand">nooks</span></h2>
                  <p><span className="nooks-brand">nooks</span> is a free, community-driven directory that allows home-based business owners in Singapore to list their products and services, and allows users to discover and connect with these businesses. <strong><span className="nooks-brand">nooks</span> does not sell products or services, does not process payments, and does not act as a party to any transaction between users and listed businesses.</strong></p>

                  <h2>2. Platform Role & Disclaimer</h2>
                  <p><span className="nooks-brand">nooks</span> is a neutral directory and information platform only. We do not:</p>
                  <ul>
                    <li>Endorse, verify, certify, or guarantee any business, product, service, or claim listed on the platform</li>
                    <li>Act as an agent, broker, or intermediary for any transaction</li>
                    <li>Guarantee the accuracy, completeness, or reliability of any listing, review, or user-generated content</li>
                    <li>Assume any responsibility for the quality, safety, legality, or suitability of products or services offered by listed businesses</li>
                  </ul>
                  <div className="legal-callout">
                    Any interactions, transactions, or disputes between users and businesses listed on <span className="nooks-brand">nooks</span> are solely between those parties. <span className="nooks-brand">nooks</span> bears no liability for any loss, damage, injury, or dispute arising from such interactions.
                  </div>

                  <h2>3. User Accounts</h2>
                  <p>You may create an account using Google Sign-In or other authentication methods we provide. You are responsible for maintaining the security of your account and for all activities under your account. You must provide accurate information and keep it up to date.</p>

                  <h2>4. Business Listings</h2>
                  <p>By listing a business on <span className="nooks-brand">nooks</span>, you represent and warrant that:</p>
                  <ul>
                    <li>You are the owner or authorized representative of the business</li>
                    <li>All information provided is accurate, truthful, and not misleading</li>
                    <li>Your business activities comply with all applicable Singapore laws and regulations, including HDB/URA home-based business guidelines, SFA food safety requirements, and ACRA registration requirements where applicable</li>
                    <li>You hold all necessary licences, permits, and certifications required for your business activities</li>
                    <li>You will not list any illegal, prohibited, or restricted products or services</li>
                  </ul>
                  <p><span className="nooks-brand">nooks</span> reserves the right to remove, suspend, or reject any listing at our sole discretion, without prior notice, for any reason including but not limited to suspected non-compliance, inaccuracy, or user complaints.</p>

                  <h2>5. User Conduct</h2>
                  <p>You agree not to:</p>
                  <ul>
                    <li>Use the platform for any unlawful purpose</li>
                    <li>Post false, misleading, defamatory, or infringing content</li>
                    <li>Impersonate any person or business</li>
                    <li>Scrape, harvest, or collect data from the platform without permission</li>
                    <li>Interfere with or disrupt the platform's functionality</li>
                    <li>Use the platform to spam, harass, or solicit users</li>
                  </ul>

                  <h2>6. User-Generated Content</h2>
                  <p>Business owners and users may submit content including listings, photos, descriptions, and reviews ("User Content"). You retain ownership of your User Content, but by posting it on <span className="nooks-brand">nooks</span> you grant us a non-exclusive, royalty-free, worldwide licence to display, distribute, and promote it in connection with the platform. You are solely responsible for your User Content and any consequences of posting it.</p>

                  <h2>7. Intellectual Property</h2>
                  <p>The <span className="nooks-brand">nooks</span> name, logo, design, and platform code are owned by <span className="nooks-brand">nooks</span>. You may not copy, modify, or redistribute any part of the platform without our written consent. All business listing content remains the intellectual property of the respective business owners.</p>

                  <h2>8. Limitation of Liability</h2>
                  <p>To the fullest extent permitted by Singapore law:</p>
                  <ul>
                    <li><strong><span className="nooks-brand">nooks</span> is provided "as is" and "as available"</strong> without warranties of any kind, express or implied, including fitness for a particular purpose, merchantability, or non-infringement</li>
                    <li><span className="nooks-brand">nooks</span> shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the platform</li>
                    <li><span className="nooks-brand">nooks</span> shall not be liable for any actions, products, services, or content of third parties or listed businesses</li>
                    <li>In no event shall <span className="nooks-brand">nooks</span>' total liability exceed SGD $100</li>
                  </ul>

                  <h2>9. Indemnification</h2>
                  <p>You agree to indemnify and hold harmless <span className="nooks-brand">nooks</span>, its operators, and affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the platform, your User Content, your business activities, or your violation of these Terms.</p>

                  <h2>10. Dispute Resolution</h2>
                  <p>Any disputes arising from or relating to these Terms or the use of <span className="nooks-brand">nooks</span> shall be governed by the laws of the Republic of Singapore. Parties agree to first attempt resolution through good-faith negotiation before pursuing any formal legal proceedings.</p>

                  <h2>11. Changes to These Terms</h2>
                  <p>We may update these Terms from time to time. Continued use of the platform after changes are posted constitutes acceptance of the revised Terms. Material changes will be communicated via the platform.</p>

                  <h2>12. Termination</h2>
                  <p>We reserve the right to suspend or terminate your account and access to the platform at any time, for any reason, without prior notice. Upon termination, your right to use the platform ceases immediately.</p>

                  <h2>13. Contact</h2>
                  <p>For questions about these Terms, contact us at <a href="mailto:admin@nooks.sg" style={{ color: "var(--amber)" }}>admin@nooks.sg</a>.</p>
                </div>
              </div>
            )}

            {/* Privacy Policy Page */}
            {currentRoute === "privacy" && (
              <div className="page active">
                <div className="legal-page">
                  <button onClick={() => window.history.back()} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.9rem" }}>
                    <Icon name="arrowLeft" size={16} /> Back
                  </button>
                  <h1 className="hero-rise hero-rise-1">Privacy Policy</h1>
                  <p className="legal-updated hero-rise hero-rise-2">Effective date: 18 March 2026 — Last updated: 18 March 2026</p>

                  <p className="hero-rise hero-rise-3">This Privacy Policy explains how <span className="nooks-brand">nooks</span> ("we", "us", "our") collects, uses, and protects your personal data in accordance with the Singapore Personal Data Protection Act 2012 (PDPA).</p>

                  <h2>1. Data We Collect</h2>
                  <p>We collect the following categories of personal data:</p>

                  <p><strong>Account Data</strong> — When you sign in via Google or other authentication methods, we receive your name, email address, and profile photo. We do not store your password.</p>
                  <p><strong>Business Listing Data</strong> — If you list a business, we collect your business name, description, category, address/postal code, location coordinates, photos, operating hours, social media links, and contact details.</p>
                  <p><strong>Acknowledgement Data</strong> — When you acknowledge the home-based business regulations, we record your user ID, email, display name, timestamp, IP address, browser/device information, and the regulation version acknowledged. This is retained as an audit trail.</p>
                  <p><strong>Usage Data</strong> — We collect anonymised analytics including page views, session duration, and general browsing patterns via Google Analytics and our built-in analytics. No personally identifiable information is used in analytics.</p>
                  <p><strong>Communication Data</strong> — Messages exchanged through the in-app messaging system between users and business owners.</p>

                  <h2>2. How We Use Your Data</h2>
                  <ul>
                    <li>To provide and operate the <span className="nooks-brand">nooks</span> platform and its features</li>
                    <li>To display your business listing to other users</li>
                    <li>To enable communication between users and business owners</li>
                    <li>To maintain an audit trail of regulatory acknowledgements for compliance purposes</li>
                    <li>To improve the platform through anonymised usage analytics</li>
                    <li>To enforce our Terms of Service and ensure platform integrity</li>
                    <li>To respond to your enquiries or support requests</li>
                  </ul>

                  <h2>3. Data Sharing</h2>
                  <p>We do not sell your personal data to third parties. We may share data in the following circumstances:</p>
                  <ul>
                    <li><strong>Public Listings:</strong> Business information you submit (name, description, photos, location, category, contact/social links) is displayed publicly on the platform. Do not include sensitive personal information in your listing.</li>
                    <li><strong>Service Providers:</strong> We use Firebase (Google Cloud) for authentication, database, and hosting. Your data is processed in accordance with Google's data processing terms.</li>
                    <li><strong>Analytics:</strong> Google Analytics collects anonymised usage data. No personally identifiable information is shared with Google for analytics purposes.</li>
                    <li><strong>Legal Requirements:</strong> We may disclose data if required by law, regulation, legal process, or government request.</li>
                  </ul>

                  <h2>4. Data Retention</h2>
                  <p>We retain your personal data for as long as your account is active or as needed to provide services. Business listing data is retained while the listing is active or pending review. Regulatory acknowledgement records are retained indefinitely as part of our compliance audit trail. If you delete your account, we will delete your personal data within 30 days, except where retention is required by law or for legitimate compliance purposes.</p>

                  <h2>5. Data Security</h2>
                  <p>We implement reasonable security measures to protect your data, including encrypted data transmission (HTTPS), Firebase security rules to control data access, and role-based access controls. However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>

                  <h2>6. Your Rights Under PDPA</h2>
                  <p>Under Singapore's PDPA, you have the right to:</p>
                  <ul>
                    <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                    <li><strong>Correction:</strong> Request correction of any inaccurate or incomplete data</li>
                    <li><strong>Withdrawal of Consent:</strong> Withdraw your consent for us to collect, use, or disclose your data (note: this may affect your ability to use the platform)</li>
                    <li><strong>Account Deletion:</strong> Request deletion of your account and associated personal data</li>
                  </ul>
                  <p>To exercise any of these rights, contact us at <a href="mailto:admin@nooks.sg" style={{ color: "var(--amber)" }}>admin@nooks.sg</a>. We will respond within 30 business days.</p>

                  <h2>7. Cookies & Local Storage</h2>
                  <p><span className="nooks-brand">nooks</span> uses browser local storage to save your preferences (e.g., liked businesses). We use Google Analytics which may set cookies for anonymised usage tracking. You can disable cookies in your browser settings, though this may affect platform functionality.</p>

                  <h2>8. Children's Privacy</h2>
                  <p><span className="nooks-brand">nooks</span> is not intended for use by individuals under 13 years of age. We do not knowingly collect data from children. If you believe a child has provided us with personal data, contact us and we will delete it.</p>

                  <h2>9. Changes to This Policy</h2>
                  <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Continued use of the platform after changes constitutes acceptance of the revised policy.</p>

                  <h2>10. Contact & Data Protection Officer</h2>
                  <p>For privacy-related questions, data access requests, or complaints, contact:</p>
                  <div className="legal-callout">
                    <strong><span className="nooks-brand">nooks</span> Data Protection Contact</strong><br />
                    Email: <a href="mailto:admin@nooks.sg" style={{ color: "var(--amber)" }}>admin@nooks.sg</a><br />
                    Website: nooks.sg
                  </div>
                </div>
              </div>
            )}

            {/* My Business Detail Page (under review) */}
            {currentRoute.startsWith("my-business/") && (
              <ErrorBoundary>
                <MyBusinessPage
                  bizId={currentRoute.split("/")[1]}
                  userBusinesses={userBusinesses}
                  setUserBusinesses={setUserBusinesses}
                  currentUser={currentUser}
                  userProfile={userProfile}
                  profilePicUrl={profilePicUrl}
                  db={db}
                  storage={storage}
                />
              </ErrorBoundary>
            )}

            {/* Add Business Page */}
            {currentRoute === "add-business" && !currentUser && (
              <div className="page active" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "2rem", textAlign: "center" }}>
                <img src="/nooksLOGO.svg" alt="nooks" style={{ height: "64px", marginBottom: "1rem" }} />
                <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.5rem", marginBottom: "0.5rem" }}>List your business on nooks</h2>
                <p style={{ color: "var(--muted)", fontSize: "0.95rem", maxWidth: "320px", marginBottom: "1.75rem", lineHeight: 1.5 }}>
                  You need an account to list and manage your business. It only takes a minute.
                </p>
                <button
                  onClick={openAuthModal}
                  style={{ background: "var(--amber)", color: "white", border: "none", padding: "0.8rem 2rem", borderRadius: "10px", fontSize: "1rem", fontWeight: 600, cursor: "pointer" }}
                >
                  Sign in to continue ?
                </button>
              </div>
            )}
            {currentRoute === "add-business" && currentUser && (
              <div className="page active">
                <AddBusinessWizard onComplete={(newBiz) => {
                  if (newBiz) {
                    setUserBusinesses(prev => [...prev, newBiz]);
                    setBizSuccessMsg(newBiz.name);
                    setTimeout(() => setBizSuccessMsg(null), 8000);
                    // Meta Pixel: Track business listing as Lead conversion
                    try { if (typeof fbq === 'function') fbq('track', 'Lead', { content_name: newBiz.name, content_category: newBiz.category }); } catch(e) {}
                    // Google Analytics: Track as conversion event
                    try { if (typeof gtag === 'function') gtag('event', 'generate_lead', { event_category: 'business_listing', event_label: newBiz.name }); } catch(e) {}
                  }
                  navigate("profile");
                }} currentUser={currentUser} hasAcknowledged={hasAcknowledged} saveAcknowledgement={saveAcknowledgement} />
              </div>
            )}

            {/* Admin Panel */}
            {currentRoute === "admin" && isAdmin && (
              <div className="page active">
                <AdminPanel db={db} storage={storage} currentUser={currentUser} navigate={navigate} />
              </div>
            )}
            {currentRoute === "admin" && !isAdmin && (
              <div className="page active">
                <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
                  <h2 style={{ fontFamily: "var(--serif)", marginBottom: "1rem" }}>Access Denied</h2>
                  <p style={{ color: "var(--muted)" }}>You don't have admin privileges.</p>
                </div>
              </div>
            )}

            {/* This Week — weekly what's-on */}
            {currentRoute === "this-week" && (
              <div className="page active">
                <ThisWeekPage db={db} currentUser={currentUser} userLocation={userLocation} liveBusinesses={liveBusinesses} isAdmin={isAdmin} navigate={navigate} />
              </div>
            )}
            {currentRoute === "submit-event" && (
              <div className="page active">
                <SubmitEventPage db={db} currentUser={currentUser} userBusinesses={userBusinesses} navigate={navigate} />
              </div>
            )}
            {currentRoute === "admin-events" && (
              <div className="page active">
                <AdminEventsPage db={db} currentUser={currentUser} isAdmin={isAdmin} navigate={navigate} />
              </div>
            )}

            {/* Unified Bookings Page — both operator & customer */}
            {(currentRoute === "bookings" || currentRoute === "my-bookings") && currentUser && (
              <div className="page active">
                <BookingsPage currentUser={currentUser} userBusinesses={userBusinesses} db={db} navigate={navigate} />
              </div>
            )}

            {/* Messages Page */}
            {currentRoute === "messages" && currentUser && (
              <div className="page active">
                <MessagesPage currentUser={currentUser} userBusinesses={userBusinesses} db={db} navigate={navigate} />
              </div>
            )}

            {/* Chat Page */}
            {currentRoute.startsWith("chat/") && currentUser && (
              <div className="page active" style={{ minHeight: 0, height: "calc(100vh - 70px)", overflow: "hidden" }}>
                <ChatPage conversationId={currentRoute.split("/")[1]} currentUser={currentUser} userBusinesses={userBusinesses} db={db} navigate={navigate} />
              </div>
            )}
          </main>

          {/* -- Mobile "+" speed-dial (Store / Event / Wish) — mobile only, browsing routes.
                 Replaces the old stacked +Store/+Wish FABs so a single button sits clear
                 of the chat launcher instead of being covered by it. -- */}
          {(currentRoute === "explore" || currentRoute === "home") && (
            <MobileFabDial navigate={navigate} />
          )}

          {/* Booking Modal */}
          {currentUser && selectedBusiness && selectedBookingItem && (
            <BookingModal
              isOpen={bookingModalOpen}
              onClose={() => setBookingModalOpen(false)}
              item={selectedBookingItem}
              business={selectedBusiness}
              currentUser={currentUser}
              onConfirm={() => {}}
              db={db}
            />
          )}

          {/* Sidebar */}
          <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />
          <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
            {currentUser && (
              <>
                <button className="sidebar-close-btn" onClick={() => setSidebarOpen(false)}>×</button>

                <div className="sidebar-header">
                  <div className="sidebar-avatar-large">
                    {profilePicUrl ? (
                      <img src={profilePicUrl} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                    ) : (
                      <Icon name="user" size={48} color="var(--amber)" />
                    )}
                  </div>
                  <div className="sidebar-user-name">{currentUser.displayName}</div>
                </div>

                <nav className="sidebar-menu">
                  <button className="sidebar-menu-item" onClick={() => { navigate("profile"); setSidebarOpen(false); }}>
                    <Icon name="user" size={18} /> My Profile
                  </button>
                  <button className="sidebar-menu-item" onClick={() => { navigate("likes"); setSidebarOpen(false); }}>
                    <Icon name="heart" size={18} /> My Likes
                  </button>

                  <button className="sidebar-menu-item" onClick={() => { navigate("bookings"); setSidebarOpen(false); }} style={{ position: "relative" }}>
                    <Icon name="calendar" size={18} /> Bookings
                    {pendingBookingsCount > 0 && <span className="badge-count">{pendingBookingsCount}</span>}
                  </button>

                  <button className="sidebar-menu-item" onClick={() => { navigate("messages"); setSidebarOpen(false); }} style={{ position: "relative" }}>
                    <Icon name="chat" size={18} /> Messages
                    {unreadMessagesCount > 0 && <span className="badge-count">{unreadMessagesCount}</span>}
                  </button>

                  <div className="sidebar-divider" />
                  <button className="sidebar-menu-item" onClick={() => { navigate("regulations"); setSidebarOpen(false); }}>
                    <Icon name="shield" size={18} /> Regulations
                  </button>
                  <button className="sidebar-menu-item" onClick={() => { navigate("faq"); setSidebarOpen(false); }}>
                    <Icon name="helpCircle" size={18} /> FAQ
                  </button>
                  <button className="sidebar-menu-item" onClick={() => { handleSignOut(); setSidebarOpen(false); }} style={{ color: "#C62828" }}>
                    <Icon name="logout" size={18} /> Log Out
                  </button>
                  {isAdmin && (
                    <button className="sidebar-menu-item" onClick={() => { navigate("admin"); setSidebarOpen(false); }} style={{ color: "#B71C1C" }}>
                      <Icon name="settings" size={18} /> Admin Panel
                    </button>
                  )}
                </nav>

                <div className="sidebar-divider" />

                <div className="sidebar-biz-section">
                  <div className="sidebar-biz-title">My Home Businesses</div>

                  {userBusinesses.filter(b => b.status === "pending").length > 0 && (
                    <>
                      <div className="sidebar-biz-status">Awaiting approval</div>
                      {userBusinesses.filter(b => b.status === "pending").map(biz => (
                        <div key={biz.id} className="sidebar-biz-item" onClick={() => { navigate(`my-business/${biz.id}`); setSidebarOpen(false); }}>
                          <span>{biz.name}</span>
                          <span style={{ color: "var(--muted)" }}>⏳</span>
                        </div>
                      ))}
                    </>
                  )}

                  {userBusinesses.filter(b => b.status === "draft").length > 0 && (
                    <>
                      <div className="sidebar-biz-status" style={{ marginTop: "0.75rem" }}>In progress</div>
                      {userBusinesses.filter(b => b.status === "draft").map(biz => (
                        <div key={biz.id} className="sidebar-biz-item" onClick={() => { navigate(`my-business/${biz.id}`); setSidebarOpen(false); }}>
                          <span>{biz.name || "Untitled"}</span>
                          <span style={{ color: "var(--muted)" }}>✏️</span>
                        </div>
                      ))}
                    </>
                  )}

                  {userBusinesses.filter(b => b.status === "approved").length > 0 && (
                    <>
                      <div className="sidebar-biz-status" style={{ marginTop: "0.75rem" }}>Live</div>
                      {userBusinesses.filter(b => b.status === "approved").map(biz => (
                        <div key={biz.id} className="sidebar-biz-item" onClick={() => { navigate(`my-business/${biz.id}`); setSidebarOpen(false); }}>
                          <span>{biz.name}</span>
                          <span style={{ color: "var(--muted)" }}>✅</span>
                        </div>
                      ))}
                    </>
                  )}

                  {userBusinesses.filter(b => b.status === "changes_requested").length > 0 && (
                    <>
                      <div className="sidebar-biz-status" style={{ marginTop: "0.75rem", color: "#6A1B9A" }}>Changes Requested</div>
                      {userBusinesses.filter(b => b.status === "changes_requested").map(biz => (
                        <div key={biz.id} className="sidebar-biz-item" onClick={() => { navigate(`my-business/${biz.id}`); setSidebarOpen(false); }}>
                          <span>{biz.name}</span>
                          <span>✏️</span>
                        </div>
                      ))}
                    </>
                  )}

                  {userBusinesses.filter(b => b.status === "suspended" || b.status === "rejected").length > 0 && (
                    <>
                      <div className="sidebar-biz-status" style={{ marginTop: "0.75rem", color: "#C62828" }}>Suspended / Rejected</div>
                      {userBusinesses.filter(b => b.status === "suspended" || b.status === "rejected").map(biz => (
                        <div key={biz.id} className="sidebar-biz-item" onClick={() => { navigate(`my-business/${biz.id}`); setSidebarOpen(false); }}>
                          <span>{biz.name}</span>
                          <span style={{ color: "var(--muted)" }}>⛔</span>
                        </div>
                      ))}
                    </>
                  )}

                  <button
                    className="sidebar-menu-item"
                    style={{ padding: "0.75rem 0", color: "var(--amber)", fontWeight: 600 }}
                    onClick={() => { navigate("add-business"); setSidebarOpen(false); }}
                  >
                    <Icon name="store" size={18} /> + Add a Business
                  </button>
                </div>
              </>
            )}
          </aside>

          {/* Regulations Acknowledgement Modal */}
          {showAckModal && (
            <AcknowledgementModal
              onAccept={saveAcknowledgement}
              onViewFull={() => { setShowAckModal(false); navigate("regulations"); }}
            />
          )}

          {/* Auth Modal */}
          <div className={`modal-overlay ${modalOpen ? "open" : ""}`} onClick={closeAuthModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>

              {/* Method Selection View */}
              {authView === "methods" && (
                <>
                  <h2 className="modal-title" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}>Welcome to <img src="/nooksLOGO.svg" alt="nooks" style={{height:"52px",display:"block"}} onError={(e)=>{e.currentTarget.style.display='none';const s=document.createElement('span');s.textContent='nooks';s.style.cssText='font-family:Georgia,serif;font-weight:700;font-size:1.7rem;background:linear-gradient(135deg,#c87606,#e8950a);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;display:inline-block';e.currentTarget.parentNode.insertBefore(s,e.currentTarget);}} /></h2>
                  <p style={{ color: "var(--muted)", fontSize: "0.9rem", marginBottom: "1.5rem", textAlign: "center" }}>
                    Sign in to save your favourites
                  </p>

                  {authError && <div className="auth-error-msg">{authError}</div>}

                  <button className="auth-method-btn google-btn" onClick={handleGoogleSignIn} disabled={authLoading}>
                    <span className="auth-icon"><Icon name="google" size={22} /></span>
                    Continue with Google
                  </button>

                  <button className="auth-method-btn facebook-btn" disabled title="Coming soon — Facebook login">
                    <span className="auth-icon"><Icon name="facebook" size={22} color="#1877F2" /></span>
                    Continue with Facebook
                    <span style={{ marginLeft: "auto", fontSize: "0.7rem", color: "var(--muted)" }}>Soon</span>
                  </button>

                  <div className="auth-divider">or</div>

                  <button className="auth-method-btn email-btn" onClick={() => { setAuthError(""); setAuthView("email-signin"); }}>
                    <span className="auth-icon"><Icon name="email" size={20} /></span>
                    Continue with Email
                  </button>

                  <button className="auth-method-btn phone-btn" onClick={() => { setAuthError(""); setAuthView("phone"); setPhoneStep("input"); }}>
                    <span className="auth-icon"><Icon name="phone" size={20} /></span>
                    Continue with Phone
                  </button>

                  <div style={{ marginTop: "1.25rem", textAlign: "center" }}>
                    <button className="modal-cancel-btn" style={{ padding: "0.6rem 1.5rem", borderRadius: "8px", border: "none", cursor: "pointer" }} onClick={closeAuthModal}>
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {/* Email Sign In View */}
              {authView === "email-signin" && (
                <>
                  <button className="auth-back-btn" onClick={() => { setAuthError(""); setAuthView("methods"); }}>
                    <Icon name="arrowLeft" size={14} /> Back
                  </button>
                  <h2 className="modal-title">Sign in with Email</h2>
                  {authError && <div className="auth-error-msg">{authError}</div>}
                  <SignInForm onSignIn={handleSignIn} loading={authLoading} />
                  <p style={{ textAlign: "center", fontSize: "0.85rem", color: "var(--muted)", marginTop: "0.75rem" }}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setAuthError(""); setAuthView("forgot-password"); }} style={{ color: "var(--muted)", fontWeight: 500 }}>Forgot password?</a>
                  </p>
                  <p style={{ textAlign: "center", fontSize: "0.85rem", color: "var(--muted)", marginTop: "0.5rem" }}>
                    Don't have an account?{" "}
                    <a href="#" onClick={(e) => { e.preventDefault(); setAuthError(""); setAuthView("email-signup"); }} style={{ color: "var(--amber)", fontWeight: 600 }}>Sign Up</a>
                  </p>
                </>
              )}

              {/* Email Sign Up View */}
              {authView === "email-signup" && (
                <>
                  <button className="auth-back-btn" onClick={() => { setAuthError(""); setAuthView("methods"); }}>
                    <Icon name="arrowLeft" size={14} /> Back
                  </button>
                  <h2 className="modal-title">Create Account</h2>
                  {authError && <div className="auth-error-msg">{authError}</div>}
                  <SignUpForm onSignUp={handleSignUp} loading={authLoading} />
                  <p style={{ textAlign: "center", fontSize: "0.85rem", color: "var(--muted)", marginTop: "1rem" }}>
                    Already have an account?{" "}
                    <a href="#" onClick={(e) => { e.preventDefault(); setAuthError(""); setAuthView("email-signin"); }} style={{ color: "var(--amber)", fontWeight: 600 }}>Sign In</a>
                  </p>
                </>
              )}

              {/* Phone Sign In View */}
              {authView === "phone" && (
                <>
                  <button className="auth-back-btn" onClick={() => { setAuthError(""); setAuthView("methods"); setPhoneStep("input"); }}>
                    <Icon name="arrowLeft" size={14} /> Back
                  </button>
                  <h2 className="modal-title">{phoneStep === "input" ? "Sign in with Phone" : "Enter Verification Code"}</h2>
                  {authError && <div className="auth-error-msg">{authError}</div>}
                  <PhoneAuthForm
                    step={phoneStep}
                    loading={authLoading}
                    onSendOTP={handlePhoneSendOTP}
                    onVerifyOTP={handlePhoneVerifyOTP}
                  />
                </>
              )}

              {/* Forgot Password View */}
              {authView === "forgot-password" && (
                <>
                  <button className="auth-back-btn" onClick={() => { setAuthError(""); setAuthInfo(""); setAuthView("email-signin"); }}>
                    <Icon name="arrowLeft" size={14} /> Back
                  </button>
                  <h2 className="modal-title">Reset Password</h2>
                  <p style={{ fontSize: "0.9rem", color: "var(--muted)", marginBottom: "1.25rem", lineHeight: 1.5 }}>
                    Enter your email and we'll send you a reset link.
                  </p>
                  {authError && <div className="auth-error-msg">{authError}</div>}
                  {authInfo && (
                    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.875rem 1rem", fontSize: "0.875rem", color: "var(--ink)", lineHeight: 1.5, marginBottom: "1rem" }}>
                      {authInfo}
                      {authInfo.includes("Google") && (
                        <button
                          className="auth-method-btn google-btn"
                          style={{ marginTop: "0.75rem", width: "100%" }}
                          onClick={() => { setAuthInfo(""); setAuthError(""); setAuthView("methods"); handleGoogleSignIn(); }}
                        >
                          <span className="auth-icon">
                            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/></svg>
                          </span>
                          Sign in with Google
                        </button>
                      )}
                      {authInfo.includes("phone") && (
                        <button
                          className="auth-method-btn phone-btn"
                          style={{ marginTop: "0.75rem", width: "100%" }}
                          onClick={() => { setAuthInfo(""); setAuthError(""); setAuthView("phone"); setPhoneStep("input"); }}
                        >
                          <span className="auth-icon"><Icon name="phone" size={18} /></span>
                          Sign in with Phone
                        </button>
                      )}
                    </div>
                  )}
                  {!authInfo && <ForgotPasswordForm onSubmit={handleForgotPassword} loading={authLoading} />}
                </>
              )}

              {/* Forgot Password Sent View */}
              {authView === "forgot-password-sent" && (
                <>
                  <div style={{ textAlign: "center", padding: "1rem 0" }}>
                    <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>📬</div>
                    <h2 className="modal-title">Check your inbox</h2>
                    <p style={{ fontSize: "0.9rem", color: "var(--muted)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
                      We've sent a password reset link to your email. It may take a minute to arrive.
                    </p>
                    <button
                      className="modal-submit-btn"
                      style={{ width: "100%", padding: "0.875rem", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}
                      onClick={() => { setAuthError(""); setAuthView("email-signin"); }}
                    >
                      Back to Sign In
                    </button>
                  </div>
                </>
              )}

              {/* Postal Code Step — shown to new users immediately after signup */}
              {authView === "postal-step" && (
                <>
                  <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                    <div style={{ fontSize: "2.25rem", marginBottom: "0.75rem" }}>📍</div>
                    <h2 className="modal-title" style={{ marginBottom: "0.5rem" }}>Where's your neighbourhood?</h2>
                    <p style={{ fontSize: "0.875rem", color: "var(--muted)", lineHeight: 1.55 }}>
                      nooks is 100% location-based. Your postal code lets us show you businesses and wishes right around you.
                    </p>
                  </div>
                  {postalStepError && (
                    <div className="auth-error-msg" style={{ marginBottom: "0.75rem" }}>{postalStepError}</div>
                  )}
                  <input
                    className="form-input"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="6-digit Singapore postal code"
                    value={postalStepInput}
                    onChange={function(e) { setPostalStepInput(e.target.value.replace(/\D/g, "").slice(0, 6)); setPostalStepError(""); }}
                    onKeyDown={function(e) { if (e.key === "Enter") handlePostalStepSave(); }}
                    style={{ width: "100%", marginBottom: "1rem", textAlign: "center", letterSpacing: "0.15em", fontSize: "1.1rem" }}
                    autoFocus
                  />
                  <button
                    className="modal-submit-btn"
                    style={{ width: "100%", padding: "0.875rem", border: "none", borderRadius: "8px", cursor: postalStepLoading ? "not-allowed" : "pointer", fontWeight: 600, opacity: postalStepLoading ? 0.7 : 1 }}
                    onClick={handlePostalStepSave}
                    disabled={postalStepLoading}
                  >
                    {postalStepLoading ? "Verifying…" : "Save & continue"}
                  </button>
                  <div style={{ textAlign: "center", marginTop: "0.875rem" }}>
                    <button
                      style={{ background: "none", border: "none", color: "var(--muted)", fontSize: "0.85rem", cursor: "pointer", textDecoration: "underline" }}
                      onClick={closeAuthModal}
                    >
                      Skip for now
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>

          {/* Mobile bottom tab bar - hidden on desktop */}
          <nav className="mobile-nav" style={(currentRoute.startsWith("chat/") || currentRoute === "map") ? { display: "none" } : {}}>
            <button className={`mobile-nav-item ${currentRoute === "explore" ? "active" : ""}`} onClick={() => navigate("explore")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              Explore
            </button>
            <button className={`mobile-nav-item ${currentRoute === "map" ? "active" : ""}`} onClick={() => navigate("map")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon fill="none" points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
              Map
            </button>
            <button className="mobile-nav-item" onClick={() => navigate("map?wishes=1")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon fill="none" points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>
              Wishes
            </button>
            <button className={`mobile-nav-item nav-thisweek ${currentRoute === "this-week" ? "active" : ""}`} onClick={() => navigate("this-week")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect fill="none" x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              This Week{thisWeekCount > 0 && <span className="tw-count">{thisWeekCount}</span>}
            </button>
            {currentUser && <button className={`mobile-nav-item ${currentRoute === "messages" ? "active" : ""}`} onClick={() => navigate("messages")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Messages{unreadMessagesCount > 0 && <span className="nav-badge">{unreadMessagesCount}</span>}
            </button>}
            {currentUser && <button className={`mobile-nav-item ${(currentRoute === "bookings" || currentRoute === "my-bookings") ? "active" : ""}`} onClick={() => navigate("bookings")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect fill="none" x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Bookings{pendingBookingsCount > 0 && <span className="nav-badge">{pendingBookingsCount}</span>}
            </button>}
            <button className={`mobile-nav-item ${currentRoute === "add-business" ? "active" : ""}`} onClick={() => navigate("add-business")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline fill="none" points="9 22 9 12 15 12 15 22"/></svg>
              List Store
            </button>
          </nav>

          {/* Footer - hidden on chat pages */}
          <footer className="footer" style={(currentRoute.startsWith("chat/") || currentRoute === "map") ? { display: "none" } : {}}>
            <div className="footer-links">
              <a href="/home" onClick={(e) => { e.preventDefault(); navigate("home"); }}>About</a>
              <a href="/faq" onClick={(e) => { e.preventDefault(); navigate("faq"); }}>FAQ</a>
              <a href="/map" onClick={(e) => { e.preventDefault(); navigate("map"); }}>Map</a>
              <a href="/map?wishes=1" onClick={(e) => { e.preventDefault(); navigate("map?wishes=1"); }}>Wish</a>
              <a href="/hopemakers">Hopemakers</a>
              <a href="/terms" onClick={(e) => { e.preventDefault(); navigate("terms"); }}>Terms</a>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate("privacy"); }}>Privacy</a>
              <a href="/regulations" onClick={(e) => { e.preventDefault(); navigate("regulations"); }}>Regulations</a>
            </div>
            © 2026 <span className="nooks-brand">nooks</span> — Discover local home businesses in Singapore
            {isAdmin && <span
              title={CHANGELOG_TOOLTIP}
              style={{ display: "inline-block", marginLeft: "0.5rem", fontSize: "0.68rem", fontWeight: 600, color: "var(--amber)", background: "var(--amber-light)", padding: "0.1rem 0.45rem", borderRadius: "20px", cursor: "help", whiteSpace: "nowrap", verticalAlign: "middle" }}
            >{APP_VERSION} &#9432;</span>}
          </footer>
          <ConsentBanner />
          <ChatWidget currentUser={currentUser} navigate={navigate} />
        </div>
      );
    }

    // Sign In Form Component
    function SignInForm({ onSignIn, loading }) {
      const [email, setEmail] = useState("");
      const [password, setPassword] = useState("");

      const handleSubmit = (e) => {
        e.preventDefault();
        onSignIn(email, password);
      };

      return (
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            className="modal-input"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <input
            type="password"
            className="modal-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="submit"
            className="modal-submit-btn"
            style={{ width: "100%", padding: "0.875rem", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, marginTop: "0.5rem" }}
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      );
    }

    // Forgot Password Form Component
    function ForgotPasswordForm({ onSubmit, loading }) {
      const [email, setEmail] = useState("");

      const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(email);
      };

      return (
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            className="modal-input"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <button
            type="submit"
            className="modal-submit-btn"
            style={{ width: "100%", padding: "0.875rem", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, marginTop: "0.5rem" }}
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
      );
    }

    // Sign Up Form Component
    function SignUpForm({ onSignUp, loading }) {
      const [name, setName] = useState("");
      const [email, setEmail] = useState("");
      const [password, setPassword] = useState("");
      const [confirm, setConfirm] = useState("");
      const [formError, setFormError] = useState("");

      const handleSubmit = (e) => {
        e.preventDefault();
        setFormError("");
        if (password.length < 6) {
          setFormError("Password must be at least 6 characters");
          return;
        }
        if (password !== confirm) {
          setFormError("Passwords do not match");
          return;
        }
        onSignUp(name, email, password);
      };

      return (
        <form onSubmit={handleSubmit}>
          {formError && <div className="auth-error-msg">{formError}</div>}
          <input
            type="text"
            className="modal-input"
            placeholder="Display Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <input
            type="email"
            className="modal-input"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            className="modal-input"
            placeholder="Password (min 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <input
            type="password"
            className="modal-input"
            placeholder="Confirm Password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          <button
            type="submit"
            className="modal-submit-btn"
            style={{ width: "100%", padding: "0.875rem", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, marginTop: "0.5rem" }}
            disabled={loading}
          >
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </form>
      );
    }

    // Phone Auth Form Component
    function PhoneAuthForm({ step, loading, onSendOTP, onVerifyOTP }) {
      const [phonePrefix, setPhonePrefix] = useState("+65");
      const [phoneNumber, setPhoneNumber] = useState("");
      const [otpCode, setOtpCode] = useState("");

      const handleSend = (e) => {
        e.preventDefault();
        const fullNumber = phonePrefix + phoneNumber.replace(/\s/g, "");
        onSendOTP(fullNumber);
      };

      const handleVerify = (e) => {
        e.preventDefault();
        console.log("handleVerify called, otpCode:", otpCode);
        onVerifyOTP(otpCode.trim());
      };

      if (step === "input") {
        return (
          <form onSubmit={handleSend}>
            <p className="auth-status-msg">We'll send a verification code via SMS to your phone number.</p>
            <div className="phone-input-row">
              <input
                type="text"
                className="phone-prefix"
                value={phonePrefix}
                onChange={(e) => setPhonePrefix(e.target.value)}
                maxLength={4}
              />
              <input
                type="tel"
                className="modal-input"
                placeholder="8123 4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                autoFocus
              />
            </div>
            <button
              id="phone-send-btn"
              type="submit"
              className="modal-submit-btn"
              style={{ width: "100%", padding: "0.875rem", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}
              disabled={loading}
            >
              {loading ? "Sending code..." : "Send Verification Code"}
            </button>
          </form>
        );
      }

      return (
        <div>
          <p className="auth-status-msg">Enter the 6-digit code sent to your phone.</p>
          <div className="otp-input-row">
            <input
              type="text"
              className="modal-input"
              placeholder="——————"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              autoFocus
              style={{ textAlign: "center", fontSize: "1.5rem", fontWeight: 700, letterSpacing: "0.3em", marginBottom: 0 }}
              onKeyDown={(e) => { if (e.key === "Enter" && otpCode.length === 6) handleVerify(e); }}
            />
          </div>
          <button
            type="button"
            className="modal-submit-btn"
            style={{ width: "100%", padding: "0.875rem", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}
            disabled={loading || otpCode.length < 6}
            onClick={handleVerify}
          >
            {loading ? "Verifying..." : "Verify Code"}
          </button>
        </div>
      );
    }

    // Map Preview Component (for Explore page)
    function MapPreview({ businesses, navigate, userLocation, setUserLocation }) {
      const mapRef = useRef(null);
      const mapInstanceRef = useRef(null);
      const markersGroupRef = useRef(null);
      const userMarkerRef = useRef(null);
      const [locating, setLocating] = useState(false);

      const locateUser = useCallback(() => {
        if (!mapInstanceRef.current) return;
        if (!navigator.geolocation) { showToast("Geolocation is not supported by your browser.", "error"); return; }

        setLocating(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            mapInstanceRef.current.setView([latitude, longitude], 17);

            // Update shared App-level location state
            if (setUserLocation) setUserLocation({ lat: latitude, lng: longitude });

            if (userMarkerRef.current) {
              mapInstanceRef.current.removeLayer(userMarkerRef.current);
            }
            userMarkerRef.current = L.marker([latitude, longitude], {
              icon: L.divIcon({
                html: '<div style="width:16px;height:16px;background:#4285F4;border:3px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>',
                iconSize: [16, 16],
                className: "user-location-marker"
              })
            }).addTo(mapInstanceRef.current).bindPopup("You are here");

            setLocating(false);
          },
          (err) => {
            console.warn("Geolocation error:", err.message);
            setLocating(false);
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }, [setUserLocation]);

      useEffect(() => {
        if (!mapRef.current) return;

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = L.map(mapRef.current).setView([1.3521, 103.8198], 15);

          L.tileLayer(
            CARTO_TILE_URL,
            {
              attribution: CARTO_ATTRIBUTION,
              maxZoom: 19
            }
          ).addTo(mapInstanceRef.current);

          // Track user location for nearest-cluster spiderfy — initialised from prop,
          // then updated once the map's own geolocation call resolves.
          var userLocRef = { current: userLocation || null };

          // Auto-spiderfy the cluster closest to the user's location.
          // Falls back to the first available cluster if location is unavailable.
          // Retries up to 20–50ms because two races can stall an immediate call:
          //   (a) allSame cluster not yet in _featureGroup — MCG rebuilds clusters after zoomend
          //   (b) _inZoomAnimation > 0 — MCG's cluster-split animation is still active
          mapInstanceRef.current.on("zoomend", function() {
            if (!markersGroupRef.current) return;
            var cg = markersGroupRef.current;
            var z = mapInstanceRef.current.getZoom();
            if (z < 10) return;
            var attempts = 0;
            function trySpiderfy() {
              if (attempts++ >= 20) return;
              var found = false;
              if (cg._featureGroup && cg._inZoomAnimation === 0) {
                var nearest = null;
                var nearestDist = Infinity;
                var uLoc = userLocRef.current;
                cg._featureGroup.getLayers().forEach(function(layer) {
                  if (!layer._childCount) return;
                  found = true;
                  if (uLoc) {
                    var ll = layer.getLatLng();
                    var d = (ll.lat - uLoc.lat) * (ll.lat - uLoc.lat) + (ll.lng - uLoc.lng) * (ll.lng - uLoc.lng);
                    if (d < nearestDist) { nearestDist = d; nearest = layer; }
                  } else {
                    if (!nearest) nearest = layer;
                  }
                });
                if (nearest && cg._spiderfied !== nearest) nearest.spiderfy();
              }
              if (!found) setTimeout(trySpiderfy, 50);
            }
            setTimeout(trySpiderfy, 50);
          });

          // Auto-locate on first load
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                const { latitude, longitude } = pos.coords;
                mapInstanceRef.current.setView([latitude, longitude], 16);
                // Update shared App-level location state and local nearest-cluster ref
                if (setUserLocation) setUserLocation({ lat: latitude, lng: longitude });
                userLocRef.current = { lat: latitude, lng: longitude };
                userMarkerRef.current = L.marker([latitude, longitude], {
                  icon: L.divIcon({
                    html: '<div style="width:16px;height:16px;background:#4285F4;border:3px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>',
                    iconSize: [16, 16],
                    className: "user-location-marker"
                  })
                }).addTo(mapInstanceRef.current).bindPopup("You are here");
              },
              () => { /* silently fall back to default SG center */ },
              { enableHighAccuracy: true, timeout: 8000 }
            );
          }
        }

        if (markersGroupRef.current) {
          mapInstanceRef.current.removeLayer(markersGroupRef.current);
        }

        if (markersGroupRef.current) {
          mapInstanceRef.current.removeLayer(markersGroupRef.current);
        }

        // Explore hero map: render every business as its own mini icon (no
        // clustering) so the map reads lively/busy. The full Map page still clusters.
        const clusterGroup = L.layerGroup();

        var validBiz = businesses.filter(function(b) { return b.lat && b.lng; });
        validBiz.forEach(function(business) {
          var imgSrc = business.coverPhotoUrl || business.cover_image || "";
          var html = '<div style="position:relative;border:3px solid #c87606;border-radius:50%;width:40px;height:40px;overflow:hidden;background:#f0ece7;">' +
            (imgSrc ? '<img src="' + imgSrc + '" style="width:100%;height:100%;object-fit:cover;" />' : '') +
            '</div>';
          var marker = L.marker([business.lat, business.lng], {
            icon: L.divIcon({ html: html, iconSize: [40, 40], iconAnchor: [20, 20], className: "custom-marker" }),
            _imgSrc: imgSrc
          }).bindPopup(
            '<a class="nooks-popup-card" href="/detail/' + (business.slug || business.id) + '" data-slug="' + (business.slug || business.id) + '" style="display:block;text-align:center;min-width:140px;padding:4px 0;cursor:pointer;color:inherit;text-decoration:none;">' +
              (imgSrc ? '<img src="' + imgSrc + '" style="width:100%;height:100px;object-fit:cover;border-radius:8px;margin-bottom:6px;" />' : '') +
              '<div style="font-weight:600;font-size:13px;margin-bottom:4px;">' + business.name + '</div>' +
              '<div style="display:flex;align-items:center;justify-content:center;gap:4px;font-size:12px;color:#999;">' +
                '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
                '<span>' + (business.likesCount || 0) + '</span>' +
              '</div>' +
            '</a>',
            { maxWidth: 180, className: "nooks-map-popup" }
          );
          marker.on("popupopen", function() {
            setTimeout(function() {
              var card = document.querySelector('.nooks-popup-card[data-slug="' + (business.slug || business.id) + '"]');
              if (card) card.addEventListener("click", function(e) { e.preventDefault(); navigate("detail/" + (business.slug || business.id)); });
            }, 50);
          });
          clusterGroup.addLayer(marker);
        });

        mapInstanceRef.current.addLayer(clusterGroup);
        markersGroupRef.current = clusterGroup;

        clusterGroup.on('clusterclick', function(a) {
          var z = mapInstanceRef.current.getZoom();
          if (z >= 10) {
            a.layer.spiderfy();
          } else {
            mapInstanceRef.current.flyTo(a.layer.getLatLng(), z + 2, { animate: true, duration: 0.4 });
          }
        });

        if (validBiz.length > 0) {
          var bounds = L.latLngBounds(validBiz.map(function(b) { return [b.lat, b.lng]; }));
          mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 13 });
        }

        return () => {
          if (mapInstanceRef.current) {
            if (markersGroupRef.current) mapInstanceRef.current.removeLayer(markersGroupRef.current);
          }
        };
      }, [businesses, navigate]);

      const locateSvg = ICONS.pin.replace(/width="\d+"/, 'width="20"').replace(/height="\d+"/, 'height="20"').replace('stroke="currentColor"', 'stroke="#c87606"');

      return (
        <div className="map-container" style={{ position: "relative" }}>
          <div ref={mapRef} style={{ height: "345px", width: "100%" }} />
          <button
            className={"locate-me-btn" + (locating ? " locating" : "")}
            onClick={locateUser}
            title="Locate me"
          >
            <span dangerouslySetInnerHTML={{ __html: locateSvg }} />
          </button>
          <button
            className="view-all-map-btn"
            onClick={() => navigate("map")}
          >
            View all on map <span style={{ marginLeft: "4px" }}>→</span>
          </button>
        </div>
      );
    }

    // Map Page — wraps FullMapComponent + sidebar, tracks visible businesses
    // ── Wish Modal Component ─────────────────────────────────────────
    function WishModal({ open, onClose, currentUser, userProfile, onWishCreated, openAuthModal }) {
      var [wishCategory, setWishCategory] = useState("");
      var [wishText, setWishText] = useState("");
      var [submitting, setSubmitting] = useState(false);
      var [error, setError] = useState("");
      var wishCategories = CATEGORIES.filter(function(c) { return c.id !== "all"; });

      if (!open) return null;

      // Gate: must be logged in
      if (!currentUser) {
        return (
          <div className="wish-modal-overlay" onClick={onClose}>
            <div className="wish-modal" onClick={function(e) { e.stopPropagation(); }}>
              <button className="wish-modal-close" onClick={onClose}>&times;</button>
              <div className="wish-modal-icon">&#128588;</div>
              <h3 className="wish-modal-title">Sign in to make a wish</h3>
              <p style={{ color: "var(--muted)", textAlign: "center" }}>You need to be logged in so your neighbours know the wish is from a real person.</p>
              <button
                className="btn btn-primary"
                style={{ marginTop: "1rem", width: "100%", padding: "0.75rem", borderRadius: "12px", fontWeight: 600, fontSize: "0.95rem" }}
                onClick={function() { onClose(); if (openAuthModal) openAuthModal(); }}
              >Sign In / Sign Up</button>
            </div>
          </div>
        );
      }

      // Gate: must have postal code
      if (!userProfile || !userProfile.homePostalCode) {
        return (
          <div className="wish-modal-overlay" onClick={onClose}>
            <div className="wish-modal" onClick={function(e) { e.stopPropagation(); }}>
              <button className="wish-modal-close" onClick={onClose}>&times;</button>
              <div className="wish-modal-icon">&#128205;</div>
              <h3 className="wish-modal-title">Add your postal code first</h3>
              <p style={{ color: "var(--muted)", textAlign: "center" }}>We need your postal code to place your wish on the map. Head to your profile settings to add it.</p>
            </div>
          </div>
        );
      }

      async function handleSubmit(e) {
        e.preventDefault();
        if (!wishCategory) { setError("Pick a category for your wish."); return; }
        if (!wishText.trim()) { setError("Tell your neighbours what you wish for!"); return; }
        if (wishText.trim().length > 140) { setError("Keep it short — 140 characters max."); return; }
        setError("");
        setSubmitting(true);
        try {
          // Geocode postal code to lat/lng via OneMap
          var lat = null, lng = null;
          try {
            var geoRes = await fetch("https://www.onemap.gov.sg/api/common/elastic/search?searchVal=" + encodeURIComponent(userProfile.homePostalCode) + "&returnGeom=Y&getAddrDetails=Y&pageNum=1");
            if (!geoRes.ok) throw new Error("OneMap API error: " + geoRes.status);
            var geoData = await geoRes.json();
            if (geoData.results && geoData.results.length > 0) {
              lat = parseFloat(geoData.results[0].LATITUDE);
              lng = parseFloat(geoData.results[0].LONGITUDE);
            }
          } catch(geoErr) { console.warn("[Wish] Geocode failed:", geoErr); }

          // Validate coords are real numbers within Singapore bounds
          if (!lat || !lng || isNaN(lat) || isNaN(lng) || lat < 1.15 || lat > 1.48 || lng < 103.6 || lng > 104.1) {
            setError("Could not locate your postal code on the map. Please check your postal code in profile settings.");
            setSubmitting(false);
            return;
          }

          await db.collection("wishes").add({
            uid: currentUser.uid,
            userName: getRandomAnonName(),
            category: wishCategory,
            text: wishText.trim(),
            lat: lat,
            lng: lng,
            postalCode: userProfile.homePostalCode,
            upvotes: 0,
            upvotedBy: [],
            status: "pending",
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
          });
          setWishCategory("");
          setWishText("");
          setSubmitting(false);
          if (onWishCreated) onWishCreated();
          onClose();
          showToast("Your wish has been submitted and is pending approval!", "success");
        } catch(err) {
          console.error("[Wish] Submit error:", err);
          setError("Something went wrong. Please try again.");
          setSubmitting(false);
        }
      }

      return (
        <div className="wish-modal-overlay" onClick={onClose}>
          <div className="wish-modal" onClick={function(e) { e.stopPropagation(); }}>
            <button className="wish-modal-close" onClick={onClose}>&times;</button>
            <div className="wish-modal-icon">&#10024;</div>
            <h3 className="wish-modal-title">Make a Wish</h3>
            <p style={{ color: "var(--muted)", textAlign: "center", marginBottom: "1rem", fontSize: "0.9rem" }}>
              Tell your neighbourhood what service you wish was nearby.
            </p>
            <form onSubmit={handleSubmit}>
              <label style={{ display: "block", fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.4rem" }}>Category</label>
              <div className="wish-category-grid">
                {wishCategories.map(function(cat) {
                  return (
                    <button
                      type="button"
                      key={cat.id}
                      className={"wish-cat-btn" + (wishCategory === cat.id ? " active" : "")}
                      style={{ "--cat-color": cat.color }}
                      onClick={function() { setWishCategory(cat.id); }}
                    >
                      <Icon name={cat.iconName} size={16} /> {cat.label}
                    </button>
                  );
                })}
              </div>
              <label style={{ display: "block", fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.4rem", marginTop: "1rem" }}>Your wish <span style={{ fontWeight: 400, color: "var(--muted)" }}>({140 - wishText.length} left)</span></label>
              <textarea
                className="wish-textarea"
                maxLength={140}
                rows={3}
                placeholder='e.g. "I wish there was a home baker nearby who makes sourdough!"'
                value={wishText}
                onChange={function(e) { setWishText(e.target.value); }}
              />
              {error && <p style={{ color: "#e74c3c", fontSize: "0.85rem", marginTop: "0.5rem" }}>{error}</p>}
              <button type="submit" className="wish-submit-btn" disabled={submitting}>
                {submitting ? "Placing wish..." : "Place my wish ✨"}
              </button>
            </form>
          </div>
        </div>
      );
    }

    function MapPage({ liveBusinesses, navigate, likes, toggleLike, currentUser, userProfile, openAuthModal }) {
      var visibleRef = useRef(liveBusinesses);
      var [visibleBusinesses, setVisibleBusinesses] = useState(liveBusinesses);
      var [selectedCategories, setSelectedCategories] = useState([]);
      // ── Layer toggle: "shops" or "wishes" ──
      // Initialise to "wishes" if the page was reached via ?wishes=1 (e.g. "Make a Wish" on homepage)
      var [mapMode, setMapMode] = useState(function() {
        return new URLSearchParams(window.location.search).get("wishes") === "1" ? "wishes" : "shops";
      });
      // Keep mapMode in sync when nav buttons switch between Map and Wishes
      // while the component stays mounted (navigate() dispatches popstate)
      useEffect(function() {
        function syncMapModeFromUrl() {
          var isWishes = new URLSearchParams(window.location.search).get("wishes") === "1";
          setMapMode(isWishes ? "wishes" : "shops");
        }
        window.addEventListener("popstate", syncMapModeFromUrl);
        return function() { window.removeEventListener("popstate", syncMapModeFromUrl); };
      }, []);
      // ── Wishes state ──
      var [wishes, setWishes] = useState([]);
      var [wishesLoading, setWishesLoading] = useState(false);
      var [wishModalOpen, setWishModalOpen] = useState(function() {
        return new URLSearchParams(window.location.search).get("make") === "1";
      });

      // Fetch wishes from Firestore when switching to wishes mode
      useEffect(function() {
        if (mapMode !== "wishes") return;
        setWishesLoading(true);
        var unsub = db.collection("wishes")
          .where("status", "==", "approved")
          .orderBy("createdAt", "desc")
          .onSnapshot(function(snap) {
            var arr = [];
            snap.forEach(function(doc) { arr.push(Object.assign({ id: doc.id }, doc.data())); });
            console.log("[Wishes] Loaded " + arr.length + " approved wishes");
            setWishes(arr);
            setWishesLoading(false);
          }, function(err) {
            console.error("[Wishes] Fetch error:", err);
            // Fallback: try without orderBy (no composite index needed)
            console.log("[Wishes] Retrying without orderBy...");
            db.collection("wishes")
              .where("status", "==", "approved")
              .get()
              .then(function(snap) {
                var arr = [];
                snap.forEach(function(doc) { arr.push(Object.assign({ id: doc.id }, doc.data())); });
                console.log("[Wishes] Fallback loaded " + arr.length + " approved wishes");
                setWishes(arr);
                setWishesLoading(false);
              })
              .catch(function(err2) {
                console.error("[Wishes] Fallback also failed:", err2);
                showToast("Could not load wishes: " + (err2.message || err2), "error");
                setWishesLoading(false);
              });
          });
        return function() { unsub(); };
      }, [mapMode]);

      // Upvote handler
      var handleUpvote = useCallback(function(wishId) {
        if (!currentUser) { showToast("Sign in to upvote a wish.", "info"); return; }
        var wishRef = db.collection("wishes").doc(wishId);
        db.runTransaction(function(tx) {
          return tx.get(wishRef).then(function(doc) {
            if (!doc.exists) return;
            var data = doc.data();
            var upvotedBy = data.upvotedBy || [];
            if (upvotedBy.indexOf(currentUser.uid) !== -1) {
              // Already upvoted — remove (toggle off)
              tx.update(wishRef, {
                upvotes: Math.max(0, (data.upvotes || 0) - 1),
                upvotedBy: upvotedBy.filter(function(uid) { return uid !== currentUser.uid; })
              });
            } else {
              tx.update(wishRef, {
                upvotes: (data.upvotes || 0) + 1,
                upvotedBy: upvotedBy.concat([currentUser.uid])
              });
            }
          });
        }).catch(function(err) { console.error("[Wish] Upvote error:", err); });
      }, [currentUser]);

      // Measure the REAL visible viewport height
      var [mapAreaH, setMapAreaH] = useState(600);
      useEffect(function() {
        function measure() {
          var viewportH = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
          var header = document.querySelector(".header");
          var mobileNav = document.querySelector(".mobile-nav");
          var headerH = header ? header.getBoundingClientRect().height : 60;
          var mobileNavH = 0;
          if (mobileNav) {
            var mnStyle = window.getComputedStyle(mobileNav);
            if (mnStyle.display !== "none") {
              mobileNavH = mobileNav.getBoundingClientRect().height;
            }
          }
          setMapAreaH(Math.max(300, viewportH - headerH - mobileNavH));
        }
        measure();
        var t1 = setTimeout(measure, 100);
        var t2 = setTimeout(measure, 400);
        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);
        if (window.visualViewport) {
          window.visualViewport.addEventListener("resize", measure);
        }
        return function() {
          clearTimeout(t1);
          clearTimeout(t2);
          window.removeEventListener("resize", measure);
          window.removeEventListener("orientationchange", measure);
          if (window.visualViewport) {
            window.visualViewport.removeEventListener("resize", measure);
          }
        };
      }, []);

      var handleVisibleChange = useCallback(function(visible) {
        visibleRef.current = visible;
        setVisibleBusinesses(visible);
      }, []);

      var categoryCounts = useMemo(function() {
        var counts = {};
        var source = mapMode === "wishes" ? wishes : visibleBusinesses;
        source.forEach(function(b) {
          if (b && b.category) counts[b.category] = (counts[b.category] || 0) + 1;
        });
        return counts;
      }, [visibleBusinesses, wishes, mapMode]);

      var sortedCategories = useMemo(function() {
        return CATEGORIES
          .filter(function(c) { return c.id !== "all"; })
          .slice()
          .sort(function(a, b) {
            return (categoryCounts[b.id] || 0) - (categoryCounts[a.id] || 0);
          });
      }, [categoryCounts]);

      // Filtered sidebar list for shops mode
      var filteredVisible = useMemo(function() {
        if (selectedCategories.length === 0) return visibleBusinesses;
        return visibleBusinesses.filter(function(b) {
          return b && b.category && selectedCategories.indexOf(b.category) !== -1;
        });
      }, [visibleBusinesses, selectedCategories]);

      // Filtered wish list
      var filteredWishes = useMemo(function() {
        if (selectedCategories.length === 0) return wishes;
        return wishes.filter(function(w) {
          return w && w.category && selectedCategories.indexOf(w.category) !== -1;
        });
      }, [wishes, selectedCategories]);

      var toggleCategory = useCallback(function(catId) {
        setSelectedCategories(function(prev) {
          if (prev.indexOf(catId) !== -1) {
            return prev.filter(function(c) { return c !== catId; });
          }
          return prev.concat([catId]);
        });
      }, []);

      var clearCategories = useCallback(function() {
        setSelectedCategories([]);
      }, []);

      var totalInView = mapMode === "wishes" ? wishes.length : visibleBusinesses.length;
      var shownCount = mapMode === "wishes" ? filteredWishes.length : filteredVisible.length;
      var filtersActive = selectedCategories.length > 0;

      // Refresh wishes after creating one
      var handleWishCreated = useCallback(function() {
        // onSnapshot will auto-update, nothing extra needed
      }, []);

      return (
        <div className="page active map-page-wrapper" style={{ overflow: "hidden", padding: 0, height: mapAreaH + "px" }}>
          <div className="full-map-container" style={{ height: "100%" }}>
            <button className="map-back-btn" onClick={() => navigate("explore")} aria-label="Back to Explore">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>

            <FullMapComponent
              businesses={liveBusinesses}
              wishes={filteredWishes}
              mapMode={mapMode}
              setMapMode={setMapMode}
              selectedCategories={selectedCategories}
              navigate={navigate}
              onVisibleChange={handleVisibleChange}
              onUpvote={handleUpvote}
              currentUser={currentUser}
              onMakeWish={function() { setWishModalOpen(true); }}
              filterBar={(
                <div className="map-filter-bar" role="toolbar" aria-label={"Filter " + (mapMode === "wishes" ? "wishes" : "nooks") + " by category"}>
                  <button
                    className={"category-pill " + (filtersActive ? "inactive" : "active")}
                    onClick={clearCategories}
                    aria-pressed={!filtersActive}
                  >
                    <Icon name="home" size={14} /> All
                  </button>
                  {sortedCategories.map(function(cat) {
                    var isActive = selectedCategories.indexOf(cat.id) !== -1;
                    var n = categoryCounts[cat.id] || 0;
                    return (
                      <button
                        key={cat.id}
                        className={"category-pill " + (filtersActive && !isActive ? "inactive" : (isActive ? "active" : "inactive"))}
                        onClick={function() { toggleCategory(cat.id); }}
                        aria-pressed={isActive}
                      >
                        <Icon name={cat.iconName} size={14} /> {cat.label}
                        {n > 0 ? <span className="pill-count">{n}</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            />

            {/* ── Sidebar ── */}
            <div className="map-sidebar">
              {mapMode === "shops" ? (
                <>
                  <h3 className="map-sidebar-title">Nearby <span className="nooks-brand">nooks</span></h3>
                  <div style={{ fontSize: "0.95rem", color: "var(--muted)", marginBottom: "1rem" }}>
                    {filtersActive
                      ? (<span>{shownCount} of {totalInView} <span className="nooks-brand">{totalInView === 1 ? "nook" : "nooks"}</span> in view</span>)
                      : (<span>{totalInView} <span className="nooks-brand">{totalInView === 1 ? "nook" : "nooks"}</span> in view</span>)}
                  </div>
                  {filteredVisible.map(function(business) {
                    return (
                      <div key={business.id} className="business-card" style={{ marginBottom: "1rem", height: "auto" }}>
                        <a className="card-seo-link" href={"/detail/" + (business.slug || business.id)} onClick={function(e) { e.preventDefault(); navigate("detail/" + (business.slug || business.id)); }} aria-label={business.name}>{business.name}</a>
                        <button className="card-like-btn" style={{ top: "0.5rem", right: "0.5rem", width: "28px", height: "28px" }} onClick={function(e) { e.stopPropagation(); toggleLike(business.id); }} title={likes.includes(business.id) ? "Unlike" : "Like"}>
                          {likes.includes(business.id) ? <Icon name="heartFill" size={13} color="#e74c3c" /> : <Icon name="heart" size={13} color="#999" />}
                        </button>
                        <div className="business-card-image" style={{ flex: "0 0 90px", width: "90px", minHeight: "90px", alignSelf: "stretch", position: "relative" }}>
                          {business.coverPhotoUrl ? (
                            <img src={business.coverPhotoUrl} alt={business.name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} />
                          ) : <CardNamePlaceholder name={business.name} fontSize="0.75rem" padding="0.5rem" />}
                          {business.ywcaHopemaker && business.ywcaHopemaker.verified && (
                            <span style={{ position: "absolute", top: "4px", left: "4px", zIndex: 2, display: "inline-flex", alignItems: "center", gap: "3px", background: "rgba(255,255,255,0.96)", backdropFilter: "blur(2px)", border: "1px solid #1F2A6F", color: "#1F2A6F", fontWeight: 700, fontSize: "0.55rem", letterSpacing: "0.2px", padding: "1px 5px 1px 4px", borderRadius: "999px", boxShadow: "0 1px 3px rgba(0,0,0,0.18)" }} title="YWCA Hopemaker — Verified by YWCA of Singapore">
                              <span style={{ display: "inline-block", width: "7px", height: "7px", position: "relative", flexShrink: 0 }}>
                                <span style={{ position: "absolute", left: "-1px", top: 0, width: 0, height: 0, borderLeft: "2.5px solid transparent", borderRight: "2.5px solid transparent", borderTop: "4.5px solid #5DA8D6" }} />
                                <span style={{ position: "absolute", left: "0.5px", top: 0, width: 0, height: 0, borderLeft: "2.5px solid transparent", borderRight: "2.5px solid transparent", borderTop: "4.5px solid #1F2A6F" }} />
                                <span style={{ position: "absolute", left: "2.5px", top: 0, width: 0, height: 0, borderLeft: "2.5px solid transparent", borderRight: "2.5px solid transparent", borderTop: "4.5px solid #D6336C" }} />
                              </span>
                              YWCA
                            </span>
                          )}
                        </div>
                        <div className="business-card-body">
                          <h3 className="business-card-title" style={{ fontSize: "0.95rem" }}>{business.name}</h3>
                          <div className="business-card-location" style={{ fontSize: "0.8rem" }}><Icon name="pin" size={14} /> {getDisplayLocation(business)}</div>
                          <div className="business-card-category">{CATEGORIES.find(c => c.id === business.category)?.label || business.category}</div>
                        </div>
                      </div>
                    );
                  })}
                  {shownCount === 0 && (
                    <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>
                      {filtersActive ? "No nooks match the selected categories in this area. Try zooming out or clearing filters." : "No nooks in this area. Try zooming out."}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3 className="map-sidebar-title">Neighbourhood <span className="nooks-brand">Wishes</span></h3>
                  <div style={{ fontSize: "0.95rem", color: "var(--muted)", marginBottom: "1rem" }}>
                    {filtersActive
                      ? (<span>{shownCount} of {totalInView} {totalInView === 1 ? "wish" : "wishes"}</span>)
                      : (<span>{totalInView} {totalInView === 1 ? "wish" : "wishes"}</span>)}
                  </div>
                  {wishesLoading && <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>Loading wishes...</div>}
                  {!wishesLoading && filteredWishes.map(function(wish) {
                    var catObj = CATEGORIES.find(function(c) { return c.id === wish.category; });
                    var hasUpvoted = currentUser && wish.upvotedBy && wish.upvotedBy.indexOf(currentUser.uid) !== -1;
                    var timeAgo = "";
                    if (wish.createdAt && wish.createdAt.seconds) {
                      var diff = Math.floor((Date.now() / 1000) - wish.createdAt.seconds);
                      if (diff < 3600) timeAgo = Math.max(1, Math.floor(diff / 60)) + "m ago";
                      else if (diff < 86400) timeAgo = Math.floor(diff / 3600) + "h ago";
                      else timeAgo = Math.floor(diff / 86400) + "d ago";
                    }
                    return (
                      <div key={wish.id} className="wish-sidebar-card">
                        <div className="wish-sidebar-header">
                          <span className="wish-sidebar-cat" style={{ background: catObj ? catObj.color + "22" : "#eee", color: catObj ? catObj.color : "#666" }}>
                            {catObj ? catObj.label : wish.category}
                          </span>
                          {timeAgo && <span className="wish-sidebar-time">{timeAgo}</span>}
                        </div>
                        <p className="wish-sidebar-text">"{wish.text}"</p>
                        <div className="wish-sidebar-footer">
                          <span className="wish-sidebar-author">— {wish.userName || getAnonNameForWish(wish.id)}</span>
                          <button
                            className={"wish-upvote-btn" + (hasUpvoted ? " upvoted" : "")}
                            onClick={function() { handleUpvote(wish.id); }}
                            title={hasUpvoted ? "Remove upvote" : "Upvote this wish"}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={hasUpvoted ? "#c87606" : "none"} stroke={hasUpvoted ? "#c87606" : "currentColor"} strokeWidth="2"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                            <span>{wish.upvotes || 0}</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {!wishesLoading && shownCount === 0 && (
                    <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>
                      {filtersActive
                        ? "No wishes match the selected categories. Try clearing filters."
                        : "No wishes yet. Be the first to make one!"}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Wish submission modal */}
          <WishModal
            open={wishModalOpen}
            onClose={function() { setWishModalOpen(false); }}
            currentUser={currentUser}
            userProfile={userProfile}
            onWishCreated={handleWishCreated}
            openAuthModal={openAuthModal}
          />
        </div>
      );
    }

    // Full Map Component
    function FullMapComponent({ businesses, wishes, mapMode, setMapMode, navigate, onVisibleChange, onUpvote, currentUser, selectedCategories, filterBar, onMakeWish }) {
      const mapRef = useRef(null);
      const mapInstanceRef = useRef(null);
      const businessesRef = useRef(businesses);
      businessesRef.current = businesses;
      const clusterGroup2Ref = useRef(null);
      const wishLayerRef = useRef(null);
      var _selectedCategories = selectedCategories || [];

      // Compute which businesses are inside current map bounds and notify parent
      var computeVisible = useCallback(function() {
        if (!mapInstanceRef.current) return;
        var bounds = mapInstanceRef.current.getBounds();
        var visible = businessesRef.current.filter(function(b) {
          return b.lat && b.lng && bounds.contains([b.lat, b.lng]);
        });
        if (onVisibleChange) onVisibleChange(visible);
      }, [onVisibleChange]);

      // ── Initialize map (once) ──
      useEffect(() => {
        if (!mapRef.current) return;

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = L.map(mapRef.current).setView([1.3521, 103.8198], 14);

          L.tileLayer(
            CARTO_TILE_URL,
            {
              attribution: CARTO_ATTRIBUTION,
              maxZoom: 19
            }
          ).addTo(mapInstanceRef.current);

          mapInstanceRef.current.on("moveend", computeVisible);

          var userLocRef2 = { current: null };
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              function(pos) { userLocRef2.current = { lat: pos.coords.latitude, lng: pos.coords.longitude }; },
              function() {},
              { enableHighAccuracy: true, timeout: 8000 }
            );
          }

          mapInstanceRef.current.on("zoomend", function() {
            if (!clusterGroup2Ref.current) return;
            var cg = clusterGroup2Ref.current;
            var z = mapInstanceRef.current.getZoom();
            if (z < 10) return;
            var attempts = 0;
            function trySpiderfy() {
              if (attempts++ >= 20) return;
              var found = false;
              if (cg._featureGroup && cg._inZoomAnimation === 0) {
                var nearest = null;
                var nearestDist = Infinity;
                var uLoc = userLocRef2.current;
                cg._featureGroup.getLayers().forEach(function(layer) {
                  if (!layer._childCount) return;
                  found = true;
                  if (uLoc) {
                    var ll = layer.getLatLng();
                    var d = (ll.lat - uLoc.lat) * (ll.lat - uLoc.lat) + (ll.lng - uLoc.lng) * (ll.lng - uLoc.lng);
                    if (d < nearestDist) { nearestDist = d; nearest = layer; }
                  } else {
                    if (!nearest) nearest = layer;
                  }
                });
                if (nearest && cg._spiderfied !== nearest) nearest.spiderfy();
              }
              if (!found) setTimeout(trySpiderfy, 50);
            }
            setTimeout(trySpiderfy, 50);
          });
        }
      }, [computeVisible]);

      // ── Shop markers layer ──
      useEffect(function() {
        if (!mapInstanceRef.current || mapMode !== "shops") return;

        // Remove wish layer if present
        if (wishLayerRef.current) {
          mapInstanceRef.current.removeLayer(wishLayerRef.current);
          wishLayerRef.current = null;
        }

        const clusterGroup2 = L.markerClusterGroup({
          maxClusterRadius: 30,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: false,
          animate: true,
          disableClusteringAtZoom: 15,
          iconCreateFunction: function(cluster) {
            var markers = cluster.getAllChildMarkers();
            var count = markers.length;
            var imgSrc = markers[0] && markers[0].options._imgSrc ? markers[0].options._imgSrc : '';
            var html = '<div style="position:relative;border:3px solid #c87606;border-radius:50%;width:40px;height:40px;overflow:hidden;background:#f0ece7;box-shadow:0 2px 8px rgba(200,118,6,0.3);">' +
              (imgSrc ? '<img src="' + imgSrc + '" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0.4;"/>' : '') +
              '<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;color:#fff;text-shadow:0 1px 2px rgba(0,0,0,0.4);">+' + count + '</div>' +
              '</div>';
            return L.divIcon({ html: html, iconSize: [40, 40], iconAnchor: [20, 20], className: '' });
          }
        });

        var validBiz2 = businesses.filter(function(b) {
          if (!b.lat || !b.lng) return false;
          if (_selectedCategories && _selectedCategories.length > 0) {
            if (!b.category || _selectedCategories.indexOf(b.category) === -1) return false;
          }
          return true;
        });
        validBiz2.forEach(function(business) {
          var imgSrc = business.coverPhotoUrl || business.cover_image || "";
          var html = '<div style="position:relative;border:3px solid #c87606;border-radius:50%;width:40px;height:40px;overflow:hidden;background:#f0ece7;">' +
            (imgSrc ? '<img src="' + imgSrc + '" style="width:100%;height:100%;object-fit:cover;" />' : '') +
            '</div>';
          var marker = L.marker([business.lat, business.lng], {
            icon: L.divIcon({ html: html, iconSize: [40, 40], iconAnchor: [20, 20], className: "custom-marker" }),
            _imgSrc: imgSrc
          }).bindPopup(
            '<a class="nooks-popup-card" href="/detail/' + (business.slug || business.id) + '" data-slug="' + (business.slug || business.id) + '" style="display:block;text-align:center;min-width:140px;padding:4px 0;cursor:pointer;color:inherit;text-decoration:none;">' +
              (imgSrc ? '<img src="' + imgSrc + '" style="width:100%;height:100px;object-fit:cover;border-radius:8px;margin-bottom:6px;" />' : '') +
              '<div style="font-weight:600;font-size:13px;margin-bottom:4px;">' + business.name + '</div>' +
              '<div style="display:flex;align-items:center;justify-content:center;gap:4px;font-size:12px;color:#999;"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg><span>' + (business.likesCount || 0) + '</span></div>' +
            '</a>',
            { maxWidth: 180, className: "nooks-map-popup" }
          );
          marker.on("popupopen", function() {
            setTimeout(function() {
              var card = document.querySelector('.nooks-popup-card[data-slug="' + (business.slug || business.id) + '"]');
              if (card && !card.dataset.bound) {
                card.dataset.bound = "1";
                card.addEventListener("click", function(e) { e.preventDefault(); navigate("detail/" + (business.slug || business.id)); });
              }
            }, 50);
          });
          clusterGroup2.addLayer(marker);
        });

        mapInstanceRef.current.addLayer(clusterGroup2);
        clusterGroup2Ref.current = clusterGroup2;

        clusterGroup2.on('clusterclick', function(a) {
          var z = mapInstanceRef.current.getZoom();
          if (z >= 10) {
            a.layer.spiderfy();
          } else {
            mapInstanceRef.current.flyTo(a.layer.getLatLng(), z + 2, { animate: true, duration: 0.4 });
          }
        });

        setTimeout(computeVisible, 100);

        return function() {
          if (mapInstanceRef.current && clusterGroup2) {
            mapInstanceRef.current.removeLayer(clusterGroup2);
          }
          clusterGroup2Ref.current = null;
        };
      }, [businesses, navigate, computeVisible, selectedCategories, mapMode]);

      // ── Warm sepia tint for Wishes mode ──
      useEffect(function() {
        if (!mapInstanceRef.current) return;
        var tilePane = mapInstanceRef.current.getPane("tilePane");
        if (!tilePane) return;
        if (mapMode === "wishes") {
          tilePane.style.filter = "sepia(0.25) hue-rotate(330deg)";
        } else {
          tilePane.style.filter = "";
        }
        return function() {
          if (tilePane) tilePane.style.filter = "";
        };
      }, [mapMode]);

      // ── Wishes bubble layer ──
      useEffect(function() {
        if (!mapInstanceRef.current || mapMode !== "wishes") return;

        // Remove shop cluster if present
        if (clusterGroup2Ref.current) {
          mapInstanceRef.current.removeLayer(clusterGroup2Ref.current);
          clusterGroup2Ref.current = null;
        }
        // Remove old wish layer
        if (wishLayerRef.current) {
          mapInstanceRef.current.removeLayer(wishLayerRef.current);
          wishLayerRef.current = null;
        }

        // ── Wish cluster: thought bubble icon, groups nearby wishes at zoom < 14 ──
        var WISH_CLOUD_PATH = 'M 10,46 C 3,46 1,37 4,29 C 1,18 10,11 20,12 C 20,4 29,0 38,3 C 43,-2 56,-1 60,5 C 66,0 76,3 79,13 C 88,11 95,21 92,31 C 92,42 84,49 74,48 C 69,52 59,52 51,49 C 44,52 33,52 27,48 C 20,49 12,48 10,46 Z';
        var wishGroup = L.markerClusterGroup({
          maxClusterRadius: 80,
          disableClusteringAtZoom: 14,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          spiderfyOnMaxZoom: false,
          animate: true,
          iconCreateFunction: function(cluster) {
            var count = cluster.getChildCount();
            var countStr = count > 99 ? '99+' : String(count);
            var countFontSize = count > 99 ? '14px' : count > 9 ? '17px' : '22px';
            // Visual warmth tiers: back layers warm as count grows
            var frontFill, midFill, backFill, strokeW;
            if (count >= 16) {
              frontFill = '#ffeece'; midFill = '#ffe5bc'; backFill = '#ffd9a0'; strokeW = '3';
            } else if (count >= 6) {
              frontFill = '#fffcf7'; midFill = '#fff8ef'; backFill = '#fff4e3'; strokeW = '2.5';
            } else {
              frontFill = '#fffcf7'; midFill = '#fffcf7'; backFill = '#fff9f2'; strokeW = '2.5';
            }
            var html =
              '<div class="wish-cluster-inner" style="position:relative;width:104px;height:90px;">' +
                // Layer 2 — back, offset up-right
                '<svg style="position:absolute;top:8px;left:0;width:104px;height:54px;transform:translate(5px,-5px);opacity:0.28;pointer-events:none;" viewBox="0 0 104 54" fill="none">' +
                  '<path d="' + WISH_CLOUD_PATH + '" fill="' + backFill + '" stroke="#c87606" stroke-width="' + strokeW + '" stroke-linejoin="round"/>' +
                '</svg>' +
                // Layer 1 — middle
                '<svg style="position:absolute;top:8px;left:0;width:104px;height:54px;transform:translate(3px,-3px);opacity:0.55;pointer-events:none;" viewBox="0 0 104 54" fill="none">' +
                  '<path d="' + WISH_CLOUD_PATH + '" fill="' + midFill + '" stroke="#c87606" stroke-width="' + strokeW + '" stroke-linejoin="round"/>' +
                '</svg>' +
                // Layer 0 — front cloud + dot trail
                '<svg style="position:absolute;top:8px;left:0;width:104px;height:82px;filter:drop-shadow(0 3px 12px rgba(0,0,0,0.13));pointer-events:none;" viewBox="0 0 104 82" fill="none">' +
                  '<path d="' + WISH_CLOUD_PATH + '" fill="' + frontFill + '" stroke="#c87606" stroke-width="' + strokeW + '" stroke-linejoin="round"/>' +
                  '<circle cx="52" cy="59" r="5" fill="' + frontFill + '" stroke="#c87606" stroke-width="' + strokeW + '"/>' +
                  '<circle cx="43" cy="69" r="3.5" fill="' + frontFill + '" stroke="#c87606" stroke-width="2.5"/>' +
                  '<circle cx="35" cy="78" r="2.5" fill="' + frontFill + '" stroke="#c87606" stroke-width="2.5"/>' +
                '</svg>' +
                // Count + label centred in cloud body
                '<div style="position:absolute;top:8px;left:0;width:104px;height:52px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;pointer-events:none;">' +
                  '<span style="font-family:Georgia,serif;font-size:' + countFontSize + ';font-weight:700;color:#c87606;line-height:1;letter-spacing:-0.5px;">' + countStr + '</span>' +
                  '<span style="font-size:9px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:#9a8f88;line-height:1;">wishes</span>' +
                '</div>' +
              '</div>';
            return L.divIcon({
              html: html,
              iconSize: [104, 90],
              iconAnchor: [52, 90],
              className: 'wish-cluster-wrapper'
            });
          }
        });

        (wishes || []).forEach(function(wish) {
          if (!wish.lat || !wish.lng) return;
          var catObj = (window.NOOKS_CATEGORIES || []).find(function(c) { return c.id === wish.category; });
          var catColor = catObj ? catObj.color : "#c87606";
          var catLabel = catObj ? catObj.label : wish.category;
          var upvoteCount = wish.upvotes || 0;

          // ── Comic SVG speech bubble marker with direct upvote ──
          var hasUpvoted = currentUser && wish.upvotedBy && wish.upvotedBy.indexOf(currentUser.uid) !== -1;
          var svgShape = getBubbleSvgForWish(wish.id, '#c87606');
          // Force z-index:0 on SVG background so content text shows above it
          var svgShapeFixed = svgShape.replace('class="wish-svg-bg"', 'class="wish-svg-bg" style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:0;"');

          // Dynamic height: estimate lines from text length so the SVG scales to fit
          // Available text width ≈ 123px (230 - 28 padding - 20 emoji - 5 gap - 50 vote - 4 margin)
          // At 12px font ~17 chars/line, line-height 18px, base 44px (top+bottom padding + row min)
          var wishText = wish.text || '';
          var numLines = Math.max(1, Math.ceil(wishText.length / 17));
          numLines = Math.min(numLines, 5); // cap: very long wishes still readable at 5 lines
          var bubbleH = 44 + numLines * 18;

          var bubbleHtml =
            '<div class="wish-bubble-marker" style="position:relative;width:230px;height:' + bubbleH + 'px;">' +
              svgShapeFixed +
              '<div class="wish-bubble-content" style="position:relative;z-index:2;display:flex;align-items:center;gap:5px;padding:10px 14px 14px 14px;font-size:12px;font-weight:500;color:#4a4440;white-space:normal;line-height:1.4;">' +
                '<span class="wish-bubble-emoji" style="flex-shrink:0;font-size:15px;">' + (catObj ? '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="' + catColor + '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' + catObj.svg + '</svg>' : '💬') + '</span>' +
                '<span class="wish-bubble-text" style="word-break:break-word;overflow-wrap:break-word;">' + escapeHtml(wish.text) + '</span>' +
                '<button class="wish-bubble-vote' + (hasUpvoted ? ' upvoted' : '') + '" data-wish-id="' + wish.id + '" style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border:1.5px solid #c87606;border-radius:10px;background:' + (hasUpvoted ? '#c87606' : '#fff') + ';font-size:10px;font-weight:700;color:' + (hasUpvoted ? '#fff' : '#c87606') + ';cursor:pointer;flex-shrink:0;align-self:flex-start;" title="' + (hasUpvoted ? 'Remove upvote' : 'Upvote this wish') + '">' +
                  '<svg width="10" height="10" viewBox="0 0 24 24" fill="' + (hasUpvoted ? '#fff' : 'none') + '" stroke="' + (hasUpvoted ? '#fff' : '#c87606') + '" stroke-width="2.5"><path d="M12 19V5M5 12l7-7 7 7"/></svg>' +
                  '<span>' + (upvoteCount || 0) + '</span>' +
                '</button>' +
              '</div>' +
            '</div>';

          var marker = L.marker([wish.lat, wish.lng], {
            icon: L.divIcon({
              html: bubbleHtml,
              iconSize: [230, bubbleH],
              iconAnchor: [115, bubbleH],
              className: "wish-marker-wrapper"
            })
          });

          // Speech-bubble popup with full wish text
          var popupHtml =
            '<div class="wish-popup">' +
              '<div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">' +
                '<span style="background:' + catColor + '22;color:' + catColor + ';padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;">' + catLabel + '</span>' +
                '<span style="font-size:11px;color:#aaa;">— ' + escapeHtml(wish.userName || getAnonNameForWish(wish.id)) + '</span>' +
              '</div>' +
              '<p style="font-size:13px;margin:0;color:#333;line-height:1.4;">"' + escapeHtml(wish.text) + '"</p>' +
            '</div>';

          marker.bindPopup(popupHtml, { maxWidth: 220, className: "wish-map-popup" });

          wishGroup.addLayer(marker);
        });

        mapInstanceRef.current.addLayer(wishGroup);
        wishLayerRef.current = wishGroup;

        // ── Scale wish bubbles with zoom level ──
        // IMPORTANT: Apply scale to the INNER .wish-bubble-marker, NOT .wish-marker-wrapper.
        // Leaflet uses transform: translate3d() on .wish-marker-wrapper for positioning.
        // Overwriting that transform with scale() removes the position and hides the marker.
        function updateBubbleScale() {
          var zoom = mapInstanceRef.current.getZoom();
          // At zoom 14+ show full size, scale down at lower zooms, hide below zoom 11
          var scale = zoom >= 14 ? 1 : zoom >= 12 ? 0.7 : zoom >= 11 ? 0.5 : 0;
          var opacity = zoom >= 11 ? 1 : 0;
          var bubbles = mapInstanceRef.current.getContainer().querySelectorAll('.wish-bubble-marker');
          bubbles.forEach(function(el) {
            el.style.transform = 'scale(' + scale + ')';
            el.style.transformOrigin = 'bottom center';
            el.style.opacity = opacity;
            el.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
          });
        }
        updateBubbleScale();
        mapInstanceRef.current.on('zoomend', updateBubbleScale);

        // Event delegation for upvote buttons on bubble markers
        var mapContainer = mapInstanceRef.current.getContainer();
        function handleBubbleVoteClick(e) {
          var btn = e.target.closest('.wish-bubble-vote');
          if (!btn) return;
          e.preventDefault();
          e.stopPropagation();
          var wishId = btn.dataset.wishId;
          if (wishId && onUpvote) onUpvote(wishId);
        }
        mapContainer.addEventListener("click", handleBubbleVoteClick, true);

        return function() {
          mapContainer.removeEventListener("click", handleBubbleVoteClick, true);
          if (mapInstanceRef.current) {
            mapInstanceRef.current.off('zoomend', updateBubbleScale);
            if (wishGroup) mapInstanceRef.current.removeLayer(wishGroup);
          }
          wishLayerRef.current = null;
        };
      }, [wishes, mapMode, currentUser, onUpvote]);

      const userMarkerRef = useRef(null);

      const locateUser = () => {
        if (!mapInstanceRef.current || !navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            mapInstanceRef.current.setView([latitude, longitude], 15);

            if (userMarkerRef.current) {
              mapInstanceRef.current.removeLayer(userMarkerRef.current);
            }
            userMarkerRef.current = L.marker([latitude, longitude], {
              icon: L.divIcon({
                html: '<div style="width:16px;height:16px;background:#4285F4;border:3px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>',
                iconSize: [16, 16],
                className: "user-location-marker"
              })
            }).addTo(mapInstanceRef.current).bindPopup("You are here");
          },
          () => {},
          { enableHighAccuracy: true, timeout: 10000 }
        );
      };

      useEffect(function() {
        if (!mapInstanceRef.current || typeof ResizeObserver === "undefined") return;
        var container = mapRef.current;
        if (!container) return;
        var ro = new ResizeObserver(function() {
          if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
        });
        ro.observe(container);
        return function() { ro.disconnect(); };
      }, []);

      // Auto-locate on first load
      useEffect(() => {
        if (!mapInstanceRef.current || !navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            mapInstanceRef.current.setView([latitude, longitude], 14);
            if (userMarkerRef.current) mapInstanceRef.current.removeLayer(userMarkerRef.current);
            userMarkerRef.current = L.marker([latitude, longitude], {
              icon: L.divIcon({
                html: '<div style="width:16px;height:16px;background:#4285F4;border:3px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>',
                iconSize: [16, 16],
                className: "user-location-marker"
              })
            }).addTo(mapInstanceRef.current).bindPopup("You are here");
          },
          () => {},
          { enableHighAccuracy: true, timeout: 8000 }
        );
      }, [mapInstanceRef.current]);

      return (
        <div className="map-main" style={{ position: "relative" }}>
          <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
          {/* ── Shops / Wishes toggle (inside map) ── */}
          <div className="map-layer-toggle" role="tablist" aria-label="Switch between shops and wishes">
            <button
              role="tab"
              className={"map-toggle-btn" + (mapMode === "shops" ? " active" : "")}
              aria-selected={mapMode === "shops"}
              onClick={function() { setMapMode("shops"); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              Shops
            </button>
            <button
              role="tab"
              className={"map-toggle-btn" + (mapMode === "wishes" ? " active" : "")}
              aria-selected={mapMode === "wishes"}
              onClick={function() { setMapMode("wishes"); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Wishes
            </button>
          </div>
          {filterBar}
          <button className="fullmap-nearme-btn" onClick={locateUser} title="Near me">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M22 12h-4M2 12h4M12 2v4M12 22v-4"/><circle cx="12" cy="12" r="3"/></svg>
            <span>Near me</span>
          </button>
          {/* ── Make a Wish FAB (only in wishes mode) ── */}
          {mapMode === "wishes" && onMakeWish && (
            <button className="wish-fab" onClick={onMakeWish} title="Make a Wish">
              <span style={{ fontSize: "1.1rem" }}>✨</span> Make a Wish
            </button>
          )}
        </div>
      );
    }

    // Detail Tabs Component
    // DetailTabs removed — detail page now uses Firestore-backed layout directly

    // ---------------------------------------------------
    // MY BUSINESS PAGE — Editable owner view
    // ---------------------------------------------------
    // -- Inline: save operator reply to a review ----------------------
    async function saveOperatorReply(reviewId, replyText, operatorUid) {
      const reviewDoc = await db.collection('reviews').doc(reviewId).get();
      if (!reviewDoc.exists) throw new Error('Review not found.');
      const review = reviewDoc.data();
      const bizDoc = await db.collection('businesses').doc(review.bizId).get();
      if (!bizDoc.exists) throw new Error('Business not found.');
      if (bizDoc.data().operatorUid !== operatorUid) throw new Error('Only the business owner can reply.');
      const now = new Date();
      const replyDate = now.toLocaleDateString('en-SG', { month: 'short', year: 'numeric' });
      await db.collection('reviews').doc(reviewId).update({
        reply: replyText,
        replyDate: replyDate,
        replyAt: firebase.firestore.FieldValue.serverTimestamp(),
        replyByUid: operatorUid,
      });
    }

    // =================================================================
    // ─── Item helpers + modals (added 2026-05) ────────────────────────
    // Per-item gallery (up to 5 photos) + extended details:
    //   longDescription, variants[], availability{}, faqs[]
    // Backwards compatible: legacy items with only `imageUrl` keep working.
    // =================================================================

    function getItemPhotos(item) {
      if (!item) return [];
      if (Array.isArray(item.photos) && item.photos.length) {
        return item.photos.filter(function(p) { return p && typeof p === 'string'; });
      }
      return item.imageUrl ? [item.imageUrl] : [];
    }

    function getItemBasePrice(item) {
      if (!item) return 0;
      if (Array.isArray(item.variants) && item.variants.length) {
        var prices = item.variants
          .map(function(v) { return parseFloat(v && v.price) || 0; })
          .filter(function(p) { return p > 0; });
        if (prices.length) return Math.min.apply(null, prices);
      }
      return parseFloat(item.price) || 0;
    }

    function getItemPriceLabel(item) {
      var hasVariants = item && Array.isArray(item.variants) && item.variants.length > 0;
      var price = getItemBasePrice(item);
      return (hasVariants ? 'from S$' : 'S$') + price.toFixed(2);
    }

    function getAvailabilityLabel(av) {
      if (!av) return '';
      var typeLabel = av.type === 'in_stock' ? 'Ready stock'
        : av.type === 'made_to_order' ? 'Made to order'
        : av.type === 'by_request' ? 'By request' : '';
      var lead = parseInt(av.leadTimeDays) || 0;
      if (av.type !== 'in_stock' && lead > 0) {
        typeLabel += ' · ~' + lead + ' day' + (lead === 1 ? '' : 's');
      }
      return typeLabel;
    }

    // ── Item Editor Modal (owner side) ───────────────────────────────
    function ItemEditorModal({ open, initialItem, onSave, onClose, ownerUid, saving }) {
      const emptyAvail = { type: 'in_stock', leadTimeDays: 0, pickup: true, delivery: false, notes: '' };
      const emptyData = function() {
        return {
          name: '', price: '', description: '',
          photos: [],
          longDescription: '',
          variants: [],
          availability: Object.assign({}, emptyAvail),
          faqs: []
        };
      };
      const [data, setData] = useState(emptyData());
      const [uploadingPhoto, setUploadingPhoto] = useState(0);
      const fileInputRef = useRef(null);

      useEffect(function() {
        if (!open) return;
        if (initialItem) {
          setData({
            name: initialItem.name || '',
            price: initialItem.price != null ? String(initialItem.price) : '',
            description: initialItem.description || '',
            photos: getItemPhotos(initialItem),
            longDescription: initialItem.longDescription || '',
            variants: Array.isArray(initialItem.variants) ? initialItem.variants.map(function(v) {
              return { name: v.name || '', price: v.price != null ? String(v.price) : '', note: v.note || '' };
            }) : [],
            availability: Object.assign({}, emptyAvail, initialItem.availability || {}),
            faqs: Array.isArray(initialItem.faqs) ? initialItem.faqs.map(function(f) {
              return { q: f.q || '', a: f.a || '' };
            }) : []
          });
        } else {
          setData(emptyData());
        }
      }, [open, initialItem]);

      if (!open) return null;

      const setField = function(field, val) {
        setData(function(prev) { var n = Object.assign({}, prev); n[field] = val; return n; });
      };
      const setAvail = function(field, val) {
        setData(function(prev) {
          return Object.assign({}, prev, { availability: Object.assign({}, prev.availability, { [field]: val }) });
        });
      };

      const addPhoto = async function(file) {
        if (!file) return;
        if (data.photos.length >= 5) { showToast('Max 5 photos per item.', 'info'); return; }
        setUploadingPhoto(function(n) { return n + 1; });
        try {
          var compressed = await compressImage(file, 800, 0.8);
          var safeName = (file.name || 'photo.jpg').replace(/[^\w.\-]/g, '_');
          var ref = storage.ref('businesses/' + ownerUid + '/items/' + Date.now() + '_' + safeName);
          await ref.put(compressed);
          var url = await ref.getDownloadURL();
          setData(function(prev) {
            return Object.assign({}, prev, { photos: prev.photos.concat([url]).slice(0, 5) });
          });
        } catch (err) {
          console.error('[ItemEditor] photo upload failed', err);
          showToast('Photo upload failed: ' + (err.message || err), 'error');
        } finally {
          setUploadingPhoto(function(n) { return Math.max(0, n - 1); });
        }
      };

      const movePhotoToFront = function(idx) {
        setData(function(prev) {
          if (idx <= 0 || idx >= prev.photos.length) return prev;
          var arr = prev.photos.slice();
          var moved = arr.splice(idx, 1)[0];
          arr.unshift(moved);
          return Object.assign({}, prev, { photos: arr });
        });
      };
      const movePhotoLeft = function(idx) {
        setData(function(prev) {
          if (idx <= 0) return prev;
          var arr = prev.photos.slice();
          var t = arr[idx - 1]; arr[idx - 1] = arr[idx]; arr[idx] = t;
          return Object.assign({}, prev, { photos: arr });
        });
      };
      const movePhotoRight = function(idx) {
        setData(function(prev) {
          if (idx >= prev.photos.length - 1) return prev;
          var arr = prev.photos.slice();
          var t = arr[idx + 1]; arr[idx + 1] = arr[idx]; arr[idx] = t;
          return Object.assign({}, prev, { photos: arr });
        });
      };
      const removePhoto = function(idx) {
        setData(function(prev) {
          var arr = prev.photos.slice();
          arr.splice(idx, 1);
          return Object.assign({}, prev, { photos: arr });
        });
      };

      const addVariant = function() {
        setData(function(prev) { return Object.assign({}, prev, { variants: prev.variants.concat([{ name: '', price: '', note: '' }]) }); });
      };
      const updateVariant = function(i, field, val) {
        setData(function(prev) {
          var arr = prev.variants.slice();
          arr[i] = Object.assign({}, arr[i], { [field]: val });
          return Object.assign({}, prev, { variants: arr });
        });
      };
      const removeVariant = function(i) {
        setData(function(prev) {
          var arr = prev.variants.slice();
          arr.splice(i, 1);
          return Object.assign({}, prev, { variants: arr });
        });
      };

      const addFaq = function() {
        setData(function(prev) { return Object.assign({}, prev, { faqs: prev.faqs.concat([{ q: '', a: '' }]) }); });
      };
      const updateFaq = function(i, field, val) {
        setData(function(prev) {
          var arr = prev.faqs.slice();
          arr[i] = Object.assign({}, arr[i], { [field]: val });
          return Object.assign({}, prev, { faqs: arr });
        });
      };
      const removeFaq = function(i) {
        setData(function(prev) {
          var arr = prev.faqs.slice();
          arr.splice(i, 1);
          return Object.assign({}, prev, { faqs: arr });
        });
      };

      const handleSave = function() {
        if (uploadingPhoto > 0) { showToast('Wait for photos to finish uploading…', 'info'); return; }
        var trimmedName = (data.name || '').trim();
        if (!trimmedName) { showToast('Item name is required', 'error'); return; }
        var price = parseFloat(data.price);
        if (isNaN(price) || price < 0) { showToast('Please enter a valid base price', 'error'); return; }
        var saved = Object.assign({}, initialItem || {}, {
          name: trimmedName,
          price: price,
          description: (data.description || '').trim(),
          photos: data.photos.slice(0, 5),
          imageUrl: data.photos[0] || (initialItem && initialItem.imageUrl) || '',
          longDescription: (data.longDescription || '').trim(),
          variants: data.variants
            .filter(function(v) { return v.name && v.name.trim(); })
            .map(function(v) { return { name: v.name.trim(), price: parseFloat(v.price) || 0, note: (v.note || '').trim() }; }),
          availability: {
            type: data.availability.type || 'in_stock',
            leadTimeDays: parseInt(data.availability.leadTimeDays) || 0,
            pickup: !!data.availability.pickup,
            delivery: !!data.availability.delivery,
            notes: (data.availability.notes || '').trim()
          },
          faqs: data.faqs
            .filter(function(f) { return f.q && f.q.trim(); })
            .map(function(f) { return { q: f.q.trim(), a: (f.a || '').trim() }; })
        });
        // Preserve any existing id; caller assigns one for new items.
        onSave(saved);
      };

      // ── Style tokens (kept inline so this is self-contained) ────
      var labelStyle = { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' };
      var inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border)', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.9rem', background: '#fff', color: 'var(--ink, #1F1A17)', boxSizing: 'border-box' };
      var sectionStyle = { marginBottom: '1.5rem', paddingBottom: '1.25rem', borderBottom: '1px solid var(--border)' };
      var sectionTitleStyle = { fontFamily: 'var(--serif)', fontSize: '1rem', fontWeight: 600, marginBottom: '0.15rem' };
      var sectionHintStyle = { fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.85rem' };

      return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,15,12,0.55)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }} onClick={onClose}>
          <div onClick={function(e) { e.stopPropagation(); }} style={{ width: '100%', maxWidth: '560px', background: '#fff', borderRadius: '18px 18px 0 0', maxHeight: '94vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h2 style={{ fontFamily: 'var(--serif)', fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>
                {initialItem && initialItem.name ? 'Edit · ' + initialItem.name : 'New item'}
              </h2>
              <button onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--muted)', padding: '0.2rem 0.5rem', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>

              {/* ── Photos ── */}
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Photos</div>
                <div style={sectionHintStyle}>First photo is the cover. Up to 5. Tap a photo to make it the cover, or use the arrows to reorder.</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                  {data.photos.map(function(p, i) {
                    return (
                      <div key={p + '-' + i} style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: '10px', overflow: 'hidden', background: '#FCEADE', border: i === 0 ? '2px solid var(--amber)' : '2px solid transparent' }}>
                        <img src={p} alt="" onClick={function() { movePhotoToFront(i); }} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'pointer' }} />
                        {i === 0 && (
                          <div style={{ position: 'absolute', top: '4px', left: '4px', background: 'var(--amber)', color: '#fff', fontSize: '0.6rem', padding: '1px 5px', borderRadius: '4px', fontWeight: 700, letterSpacing: '0.04em' }}>COVER</div>
                        )}
                        <button onClick={function() { removePhoto(i); }} aria-label="Remove" style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', width: '20px', height: '20px', borderRadius: '50%', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1 }}>×</button>
                        {data.photos.length > 1 && (
                          <div style={{ position: 'absolute', bottom: '2px', left: '2px', right: '2px', display: 'flex', justifyContent: 'space-between' }}>
                            <button onClick={function() { movePhotoLeft(i); }} disabled={i === 0} aria-label="Move left" style={{ background: 'rgba(255,255,255,0.85)', border: 'none', width: '18px', height: '18px', borderRadius: '50%', cursor: i === 0 ? 'not-allowed' : 'pointer', fontSize: '0.7rem', lineHeight: 1, opacity: i === 0 ? 0.4 : 1 }}>‹</button>
                            <button onClick={function() { movePhotoRight(i); }} disabled={i === data.photos.length - 1} aria-label="Move right" style={{ background: 'rgba(255,255,255,0.85)', border: 'none', width: '18px', height: '18px', borderRadius: '50%', cursor: i === data.photos.length - 1 ? 'not-allowed' : 'pointer', fontSize: '0.7rem', lineHeight: 1, opacity: i === data.photos.length - 1 ? 0.4 : 1 }}>›</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {Array.from({ length: uploadingPhoto }, function(_, k) {
                    return (
                      <div key={'up-' + k} style={{ aspectRatio: '1 / 1', borderRadius: '10px', background: '#fff5e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="draft-spinner" style={{ width: 18, height: 18, border: '2px solid #e8d9b8', borderTopColor: 'var(--amber)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      </div>
                    );
                  })}
                  {data.photos.length + uploadingPhoto < 5 && (
                    <label style={{ aspectRatio: '1 / 1', border: '2px dashed var(--border)', borderRadius: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', fontSize: '0.7rem', gap: '0.2rem', background: '#FFFCF8' }}>
                      <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>+</span>
                      <span>Add</span>
                      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={function(e) {
                        var file = e.target.files && e.target.files[0];
                        if (file) addPhoto(file);
                        e.target.value = '';
                      }} />
                    </label>
                  )}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: '0.5rem' }}>{data.photos.length} of 5 photos</div>
              </div>

              {/* ── Basics ── */}
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Basics</div>
                <label style={labelStyle}>Item name *</label>
                <input style={Object.assign({}, inputStyle, { marginBottom: '0.6rem' })} value={data.name} onChange={function(e) { setField('name', e.target.value); }} placeholder="e.g. Sourdough loaf" />
                <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.6rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Base price (S$) *</label>
                    <input type="number" step="0.01" min="0" style={inputStyle} value={data.price} onChange={function(e) { setField('price', e.target.value); }} placeholder="12.00" />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>Short tagline</label>
                    <input style={inputStyle} value={data.description} onChange={function(e) { setField('description', e.target.value); }} placeholder="One-line teaser" />
                  </div>
                </div>
              </div>

              {/* ── Long description ── */}
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Long description</div>
                <div style={sectionHintStyle}>Tell the story — ingredients, process, what makes it special. Optional.</div>
                <textarea style={Object.assign({}, inputStyle, { resize: 'vertical', minHeight: '90px', lineHeight: 1.5, fontFamily: 'inherit' })} value={data.longDescription} onChange={function(e) { setField('longDescription', e.target.value); }} placeholder="Slow-fermented for 24 hours…" />
              </div>

              {/* ── Variants ── */}
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Pricing tiers / variants</div>
                <div style={sectionHintStyle}>Optional. Add sizes or packages with their own prices (e.g. small / regular / large).</div>
                {data.variants.length === 0 && (
                  <div style={{ color: 'var(--muted)', fontSize: '0.82rem', fontStyle: 'italic', marginBottom: '0.5rem' }}>No variants — buyers will see only the base price.</div>
                )}
                {data.variants.map(function(v, i) {
                  return (
                    <div key={'v-' + i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', marginBottom: '0.4rem', padding: '0.5rem', background: 'var(--cream)', borderRadius: '8px' }}>
                      <input style={Object.assign({}, inputStyle, { flex: 2 })} placeholder="Name (e.g. Large)" value={v.name} onChange={function(e) { updateVariant(i, 'name', e.target.value); }} />
                      <input type="number" step="0.01" min="0" style={Object.assign({}, inputStyle, { flex: 1, minWidth: '80px' })} placeholder="Price" value={v.price} onChange={function(e) { updateVariant(i, 'price', e.target.value); }} />
                      <input style={Object.assign({}, inputStyle, { flex: 1.5 })} placeholder="Note (opt.)" value={v.note} onChange={function(e) { updateVariant(i, 'note', e.target.value); }} />
                      <button onClick={function() { removeVariant(i); }} aria-label="Remove" style={{ background: 'transparent', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: '1.1rem', padding: '0.3rem 0.4rem', alignSelf: 'center' }}>×</button>
                    </div>
                  );
                })}
                <button onClick={addVariant} style={{ background: 'transparent', color: 'var(--amber)', border: '1px dashed var(--amber)', padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, marginTop: '0.25rem' }}>+ Add variant</button>
              </div>

              {/* ── Availability ── */}
              <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Availability</div>
                <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.6rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Type</label>
                    <select style={inputStyle} value={data.availability.type} onChange={function(e) { setAvail('type', e.target.value); }}>
                      <option value="in_stock">Ready stock</option>
                      <option value="made_to_order">Made to order</option>
                      <option value="by_request">By request</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Lead time (days)</label>
                    <input type="number" min="0" style={inputStyle} value={data.availability.leadTimeDays} onChange={function(e) { setAvail('leadTimeDays', e.target.value); }} placeholder="0" />
                  </div>
                </div>
                <label style={labelStyle}>Fulfillment</label>
                <div style={{ display: 'flex', gap: '0.85rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={data.availability.pickup} onChange={function(e) { setAvail('pickup', e.target.checked); }} style={{ accentColor: 'var(--amber)', width: 16, height: 16 }} />
                    Pickup
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={data.availability.delivery} onChange={function(e) { setAvail('delivery', e.target.checked); }} style={{ accentColor: 'var(--amber)', width: 16, height: 16 }} />
                    Delivery
                  </label>
                </div>
                <label style={labelStyle}>Notes (optional)</label>
                <input style={inputStyle} value={data.availability.notes} onChange={function(e) { setAvail('notes', e.target.value); }} placeholder="e.g. Order by Wed for Sat pickup" />
              </div>

              {/* ── FAQs ── */}
              <div style={Object.assign({}, sectionStyle, { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 })}>
                <div style={sectionTitleStyle}>FAQs</div>
                <div style={sectionHintStyle}>Optional. Answer common questions buyers ask before ordering.</div>
                {data.faqs.length === 0 && (
                  <div style={{ color: 'var(--muted)', fontSize: '0.82rem', fontStyle: 'italic', marginBottom: '0.5rem' }}>No FAQs yet.</div>
                )}
                {data.faqs.map(function(f, i) {
                  return (
                    <div key={'f-' + i} style={{ background: 'var(--cream)', borderRadius: '8px', padding: '0.6rem 0.75rem', marginBottom: '0.4rem' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.4rem' }}>
                        <input style={inputStyle} placeholder="Question" value={f.q} onChange={function(e) { updateFaq(i, 'q', e.target.value); }} />
                        <button onClick={function() { removeFaq(i); }} aria-label="Remove" style={{ background: 'transparent', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: '1.1rem', padding: '0.3rem 0.4rem' }}>×</button>
                      </div>
                      <textarea style={Object.assign({}, inputStyle, { minHeight: '54px', resize: 'vertical', fontFamily: 'inherit' })} placeholder="Answer" value={f.a} onChange={function(e) { updateFaq(i, 'a', e.target.value); }} />
                    </div>
                  );
                })}
                <button onClick={addFaq} style={{ background: 'transparent', color: 'var(--amber)', border: '1px dashed var(--amber)', padding: '0.45rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, marginTop: '0.25rem' }}>+ Add FAQ</button>
              </div>

            </div>

            <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexShrink: 0, background: '#fff' }}>
              <button onClick={onClose} disabled={saving} style={{ background: 'transparent', color: 'var(--mid, var(--muted))', border: '1px solid var(--border)', padding: '0.65rem 1.2rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving || uploadingPhoto > 0} style={{ background: 'var(--amber)', color: '#fff', border: 'none', padding: '0.65rem 1.4rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem', cursor: (saving || uploadingPhoto > 0) ? 'not-allowed' : 'pointer', opacity: (saving || uploadingPhoto > 0) ? 0.6 : 1 }}>
                {saving ? 'Saving…' : uploadingPhoto > 0 ? 'Uploading…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ── Item Detail Modal (public/customer side) ────────────────────
    function ItemDetailModal({ open, item, business, currentUser, db, navigate, onClose, onBook }) {
      const [slideIdx, setSlideIdx] = useState(0);
      const [touchStart, setTouchStart] = useState(null);

      useEffect(function() { if (open) setSlideIdx(0); }, [open, item]);

      if (!open || !item) return null;

      var photos = getItemPhotos(item);
      var hasPhotos = photos.length > 0;
      var hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
      var hasFaqs = Array.isArray(item.faqs) && item.faqs.length > 0;
      var av = item.availability || null;
      var availLabel = getAvailabilityLabel(av);
      var fulfill = av ? [av.pickup ? 'pickup' : null, av.delivery ? 'delivery' : null].filter(Boolean).join(' & ') : '';

      var slide = function(dir) {
        if (!photos.length) return;
        setSlideIdx(function(prev) { return Math.max(0, Math.min(photos.length - 1, prev + dir)); });
      };

      var sendItemMessage = async function(e) {
        if (!currentUser || !business) return;
        if (e && e.target) e.target.disabled = true;
        try {
          var itemMsg = "Hi! I'm interested in: " + item.name + (item.price ? " (" + getItemPriceLabel(item) + ")" : "") + (item.description ? "\n" + item.description : "");
          var existing = await db.collection("conversations")
            .where("participants", "array-contains", currentUser.uid)
            .where("businessId", "==", business.id)
            .get();
          if (existing.docs.length > 0) {
            var convId = existing.docs[0].id;
            await db.collection("conversations").doc(convId).collection("messages").add({
              text: itemMsg,
              senderUid: currentUser.uid,
              senderName: currentUser.displayName || "Customer",
              createdAt: firebase.firestore.FieldValue.serverTimestamp()
            });
            var otherUid = business.ownerId;
            await db.collection("conversations").doc(convId).update({
              lastMessage: itemMsg,
              lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
              [`unreadBy.${otherUid}`]: (existing.docs[0].data().unreadBy?.[otherUid] || 0) + 1
            });
            navigate("chat/" + convId);
          } else {
            var newConv = await db.collection("conversations").add({
              participants: [currentUser.uid, business.ownerId],
              businessId: business.id,
              businessName: business.name,
              otherParticipantName: business.ownerName,
              shopperName: currentUser.displayName || "Customer",
              shopperUid: currentUser.uid,
              lastMessage: itemMsg,
              lastMessageAt: firebase.firestore.FieldValue.serverTimestamp(),
              unreadBy: { [currentUser.uid]: 0, [business.ownerId]: 1 }
            });
            await db.collection("conversations").doc(newConv.id).collection("messages").add({
              text: itemMsg,
              senderUid: currentUser.uid,
              senderName: currentUser.displayName,
              createdAt: firebase.firestore.FieldValue.serverTimestamp()
            });
            navigate("chat/" + newConv.id);
          }
        } catch (err) {
          console.error("[ItemDetail] message send failed", err);
          showToast("Could not send message: " + (err.message || err), "error");
        } finally {
          if (e && e.target) e.target.disabled = false;
          onClose();
        }
      };

      return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,15,12,0.55)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }} onClick={onClose}>
          <div onClick={function(e) { e.stopPropagation(); }} style={{ width: '100%', maxWidth: '560px', background: '#fff', borderRadius: '18px 18px 0 0', maxHeight: '94vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '0.9rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h2 style={{ fontFamily: 'var(--serif)', fontSize: '1rem', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</h2>
              <button onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--muted)', padding: '0.2rem 0.5rem', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
              {/* Carousel */}
              {hasPhotos ? (
                <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', background: '#FCEADE', overflow: 'hidden', borderRadius: '12px', marginBottom: '1rem' }}
                  onTouchStart={function(e) { setTouchStart(e.touches[0].clientX); }}
                  onTouchEnd={function(e) {
                    if (touchStart == null) return;
                    var dx = e.changedTouches[0].clientX - touchStart;
                    if (Math.abs(dx) > 40) slide(dx < 0 ? 1 : -1);
                    setTouchStart(null);
                  }}
                >
                  <div style={{ display: 'flex', transition: 'transform 0.35s cubic-bezier(0.2,0.8,0.3,1)', height: '100%', transform: 'translateX(-' + (slideIdx * 100) + '%)' }}>
                    {photos.map(function(p, i) {
                      return (
                        <div key={p + '-' + i} style={{ flex: '0 0 100%', height: '100%' }}>
                          <img src={p} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        </div>
                      );
                    })}
                  </div>
                  {photos.length > 1 && (
                    <React.Fragment>
                      <button onClick={function() { slide(-1); }} disabled={slideIdx === 0} aria-label="Previous photo" style={{ position: 'absolute', top: '50%', left: '0.5rem', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.92)', border: 'none', width: '36px', height: '36px', borderRadius: '50%', cursor: slideIdx === 0 ? 'not-allowed' : 'pointer', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: 'var(--ink, #1F1A17)', opacity: slideIdx === 0 ? 0.4 : 1 }}>‹</button>
                      <button onClick={function() { slide(1); }} disabled={slideIdx === photos.length - 1} aria-label="Next photo" style={{ position: 'absolute', top: '50%', right: '0.5rem', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.92)', border: 'none', width: '36px', height: '36px', borderRadius: '50%', cursor: slideIdx === photos.length - 1 ? 'not-allowed' : 'pointer', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', color: 'var(--ink, #1F1A17)', opacity: slideIdx === photos.length - 1 ? 0.4 : 1 }}>›</button>
                      <div style={{ position: 'absolute', bottom: '0.6rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '0.3rem' }}>
                        {photos.map(function(_, i) {
                          return (
                            <div key={'d-' + i} style={{ width: i === slideIdx ? '18px' : '7px', height: '7px', borderRadius: '99px', background: i === slideIdx ? '#fff' : 'rgba(255,255,255,0.55)', transition: 'width 0.2s' }} />
                          );
                        })}
                      </div>
                    </React.Fragment>
                  )}
                </div>
              ) : (
                <div style={{ width: '100%', aspectRatio: '4 / 3', background: 'var(--cream)', borderRadius: '12px', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: '0.85rem' }}>No photo</div>
              )}

              <div style={{ fontFamily: 'var(--serif)', fontSize: '1.35rem', fontWeight: 600, marginBottom: '0.2rem', color: 'var(--ink, #1F1A17)' }}>{item.name}</div>
              <div style={{ color: 'var(--amber)', fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.85rem' }}>{getItemPriceLabel(item)}</div>

              {item.longDescription ? (
                <p style={{ color: 'var(--mid, var(--muted))', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: '1.2rem', whiteSpace: 'pre-wrap' }}>{item.longDescription}</p>
              ) : item.description ? (
                <p style={{ color: 'var(--mid, var(--muted))', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: '1.2rem' }}>{item.description}</p>
              ) : null}

              {hasVariants && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontFamily: 'var(--serif)', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>Choose a size</h4>
                  {item.variants.map(function(v, i) {
                    return (
                      <div key={'vd-' + i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.85rem', background: 'var(--cream)', borderRadius: '8px', marginBottom: '0.4rem' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{v.name}</div>
                          {v.note && <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>{v.note}</div>}
                        </div>
                        <div style={{ color: 'var(--amber)', fontWeight: 700, fontSize: '0.92rem' }}>S${(parseFloat(v.price) || 0).toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {av && (availLabel || fulfill || av.notes) && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontFamily: 'var(--serif)', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>When can I get it?</h4>
                  <div style={{ background: '#E8F3EE', borderRadius: '8px', padding: '0.65rem 0.85rem', fontSize: '0.85rem', color: '#1F4A2E', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <div style={{ fontSize: '1rem', flexShrink: 0, lineHeight: 1.4 }}>{av.type === 'in_stock' ? '✓' : '⏱'}</div>
                    <div>
                      <strong style={{ display: 'block', marginBottom: '0.1rem' }}>{availLabel || 'Available'}</strong>
                      {fulfill && <span>Available for {fulfill}.</span>} {av.notes && <span>{av.notes}</span>}
                    </div>
                  </div>
                </div>
              )}

              {hasFaqs && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <h4 style={{ fontFamily: 'var(--serif)', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>Common questions</h4>
                  {item.faqs.map(function(f, i) {
                    return (
                      <div key={'fa-' + i} style={{ borderBottom: i === item.faqs.length - 1 ? 'none' : '1px solid var(--border)', padding: '0.7rem 0' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '0.25rem', color: 'var(--ink, #1F1A17)' }}>{f.q}</div>
                        <div style={{ fontSize: '0.83rem', color: 'var(--mid, var(--muted))', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{f.a}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {currentUser && business && (
              <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.6rem', flexShrink: 0, background: '#fff' }}>
                {onBook && (
                  <button onClick={function() { onBook(item); onClose(); }} style={{ flex: 1, padding: '0.65rem 1rem', background: 'var(--amber)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>Book</button>
                )}
                <button onClick={sendItemMessage} style={{ flex: 1, padding: '0.65rem 1rem', background: '#fff', color: 'var(--amber)', border: '1px solid var(--amber)', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>Message</button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ── Exit Feedback Modal (owner unlist / delete) ──────────────
    // Captures WHY an owner hides or removes their listing, for product
    // insight. Required reason (radio) + optional comment. Includes a
    // "Prefer not to say" option so it's required-but-never-a-hard-wall.
    // Reason sets differ for unlist (often temporary) vs delete (permanent).
    function ExitFeedbackModal({ action, bizName, saving, onCancel, onSubmit }) {
      const [reason, setReason] = useState("");
      const [comment, setComment] = useState("");
      const isDelete = action === "delete";
      const REASONS = isDelete ? [
        { id: "closed", label: "I've closed the business" },
        { id: "not_enough_customers", label: "Not enough customers / leads" },
        { id: "other_platform", label: "Using another platform instead" },
        { id: "too_much_effort", label: "Too much effort to maintain" },
        { id: "privacy", label: "Privacy concerns" },
        { id: "other", label: "Other" },
        { id: "no_say", label: "Prefer not to say" },
      ] : [
        { id: "too_busy", label: "Too busy to keep up right now" },
        { id: "seasonal", label: "Taking a temporary / seasonal break" },
        { id: "out_of_stock", label: "Out of stock / paused orders" },
        { id: "updating", label: "Updating my listing — will relist soon" },
        { id: "not_enough_interest", label: "Not getting enough interest" },
        { id: "other", label: "Other" },
        { id: "no_say", label: "Prefer not to say" },
      ];
      const selected = REASONS.find(r => r.id === reason);
      const title = isDelete ? ("Delete " + (bizName || "your business") + "?") : ("Unlist " + (bizName || "your business") + "?");
      const blurb = isDelete
        ? "This permanently removes your listing and can't be undone. Before you go, help us understand why — it makes nooks better for the next neighbour."
        : "Your listing will be hidden from the public. You can relist anytime. A quick why helps us improve nooks.";
      const accent = isDelete ? "#C62828" : "#FF8F00";

      function handleSubmit() {
        if (!reason) return;
        onSubmit({ reason: reason, reasonLabel: selected ? selected.label : reason, comment: comment.trim() });
      }

      return (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
          onClick={saving ? undefined : onCancel}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", maxWidth: "440px", width: "100%", maxHeight: "90vh", overflowY: "auto", padding: "1.5rem", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.15rem", margin: "0 0 0.5rem", color: accent }}>{title}</h3>
            <p style={{ fontSize: "0.85rem", color: "var(--muted)", margin: "0 0 1rem", lineHeight: 1.5 }}>{blurb}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "1rem" }}>
              {REASONS.map(r => (
                <label
                  key={r.id}
                  style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.6rem 0.75rem", border: "1px solid " + (reason === r.id ? accent : "var(--border)"), borderRadius: "10px", cursor: "pointer", background: reason === r.id ? "#FFF8F2" : "#fff", fontSize: "0.88rem" }}
                >
                  <input type="radio" name="exitReason" checked={reason === r.id} onChange={() => setReason(r.id)} style={{ accentColor: accent }} />
                  {r.label}
                </label>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Anything else you'd like to share? (optional)"
              style={{ width: "100%", boxSizing: "border-box", padding: "0.6rem 0.75rem", border: "1px solid var(--border)", borderRadius: "10px", fontSize: "0.85rem", fontFamily: "inherit", resize: "vertical", marginBottom: "1rem" }}
            />
            <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end" }}>
              <button
                onClick={onCancel}
                disabled={saving}
                style={{ padding: "0.55rem 1.1rem", background: "#fff", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={saving || !reason}
                style={{ padding: "0.55rem 1.1rem", background: accent, color: "#fff", border: "none", borderRadius: "8px", cursor: (saving || !reason) ? "not-allowed" : "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: (saving || !reason) ? 0.5 : 1 }}
              >{saving ? "Saving…" : (isDelete ? "Delete permanently" : "Unlist")}</button>
            </div>
          </div>
        </div>
      );
    }

    function MyBusinessPage({ bizId, userBusinesses, setUserBusinesses, currentUser, userProfile, profilePicUrl, db, storage }) {
      const biz = userBusinesses.find(b => b.id === bizId);
      const [editing, setEditing] = useState(null);
      const [saving, setSaving] = useState(false);
      const [saveMsg, setSaveMsg] = useState("");
      const [editData, setEditData] = useState({});
      const coverInputRef = useRef(null);
      const [bizTab, setBizTab] = useState("store"); // "store" | "analytics" | "reviews"
      const [bizReviews, setBizReviews] = useState([]);
      const [reviewsLoading, setReviewsLoading] = useState(false);
      const [replyingTo, setReplyingTo] = useState(null);
      const [replyText, setReplyText] = useState("");
      const [replySending, setReplySending] = useState(false);
      const [analyticsData, setAnalyticsData] = useState(null);
      const [igMsg, setIgMsg] = useState(null); // { type: "success"|"error", text: string }

      // Per-item editor modal: { mode: 'edit'|'new', index?: number, item: {...} } or null when closed
      const [itemEditor, setItemEditor] = useState(null);
      const [itemEditorSaving, setItemEditorSaving] = useState(false);
      const [exitFeedback, setExitFeedback] = useState(null); // null | "unlist" | "delete"

      // Save handler for ItemEditorModal — replaces (edit) or appends (new) the item
      // in biz.menuItems, then writes the whole array back to Firestore in one update.
      const saveItemFromEditor = async (savedItem) => {
        if (!biz) return;
        setItemEditorSaving(true);
        try {
          const current = Array.isArray(biz.menuItems) ? biz.menuItems.filter(Boolean) : [];
          let nextItems;
          if (itemEditor && itemEditor.mode === 'edit' && typeof itemEditor.index === 'number') {
            nextItems = current.slice();
            nextItems[itemEditor.index] = Object.assign({}, current[itemEditor.index], savedItem);
          } else {
            nextItems = current.concat([savedItem]);
          }
          await db.collection("businesses").doc(bizId).update({ menuItems: nextItems });
          setUserBusinesses(prev => prev.map(b => b.id === bizId ? Object.assign({}, b, { menuItems: nextItems }) : b));
          showToast("Saved!", "success");
          setItemEditor(null);
        } catch (err) {
          console.error("[MyBusiness] saveItemFromEditor failed", err);
          showToast("Couldn't save: " + (err.message || err), "error");
        } finally {
          setItemEditorSaving(false);
        }
      };

      // Handle Instagram OAuth return params (?ig_connected=1 / ?ig_error=...)
      useEffect(function() {
        var params = new URLSearchParams(window.location.search);
        if (params.get("ig_connected") === "1") {
          var igUser = params.get("ig_user") || "";
          setIgMsg({ type: "success", text: igUser ? "Instagram @" + igUser + " connected!" : "Instagram connected!" });
          // Re-fetch the business to get the latest igConnected + socialMedia fields
          db.collection("businesses").doc(bizId).get().then(function(snap) {
            if (snap.exists) {
              setUserBusinesses(function(prev) {
                return prev.map(function(b) { return b.id === bizId ? Object.assign({}, b, snap.data(), { id: bizId }) : b; });
              });
            }
          });
          window.history.replaceState({}, "", window.location.pathname);
        } else if (params.get("ig_error")) {
          var errCode = params.get("ig_error");
          var errText = errCode === "cancelled"
            ? "Instagram connection cancelled."
            : errCode === "not_professional"
            ? "Your Instagram account must be a Professional account (Business or Creator) to connect. You can switch for free in Instagram Settings."
            : errCode === "link_facebook_page"
            ? "Auto-connect couldn\u2019t import your profile \u2014 Instagram is restricting access for this account. You can update your Instagram handle manually below."
            : "Instagram connection failed. Please try again.";
          setIgMsg({ type: "error", text: errText });
          window.history.replaceState({}, "", window.location.pathname);
        }
      }, [bizId]);

      // Fetch analytics data when analytics tab is opened
      useEffect(function() {
        if (bizTab !== "analytics" || !biz) return;
        (async function() {
          try {
            // Fetch fresh business doc for latest viewCount
            var bizDoc = await db.collection("businesses").doc(bizId).get();
            var bizData = bizDoc.exists ? bizDoc.data() : {};

            // Fetch bookings for this business
            var bookSnap = await db.collection("bookings").where("businessId", "==", bizId).get();
            var bookings = bookSnap.docs.map(function(d) { return d.data(); });
            var totalBookings = bookings.length;
            var completedBookings = bookings.filter(function(b) { return b.status === "completed"; }).length;
            var pendingBookings = bookings.filter(function(b) { return b.status === "pending"; }).length;
            var confirmedBookings = bookings.filter(function(b) { return b.status === "confirmed"; }).length;

            // Calculate lifetime earnings from completed bookings
            var lifetimeEarnings = 0;
            bookings.forEach(function(b) {
              if (b.status === "completed" && b.serviceItem && b.serviceItem.price) {
                lifetimeEarnings += parseFloat(b.serviceItem.price) || 0;
              }
            });

            // Rolling 24h — prefer the server-aggregated field. Fall back to
            // summing the last 24 hourly buckets client-side if the aggregator
            // hasn't run yet (first hour after deploy), then to today-so-far.
            var now = new Date();
            var views24h;
            if (typeof bizData.viewCount24h === "number") {
              views24h = bizData.viewCount24h;
            } else if (bizData.viewsByHour) {
              views24h = 0;
              for (var h = 0; h < 24; h++) {
                var hk = new Date(now.getTime() - h * 3600 * 1000).toISOString().slice(0, 13);
                views24h += Number(bizData.viewsByHour[hk] || 0);
              }
            } else {
              var today = now.toISOString().slice(0, 10);
              views24h = (bizData.viewsByDate && bizData.viewsByDate[today]) || 0;
            }

            // Rolling 7d — prefer the server-aggregated field. Fall back to
            // summing viewsByDate for the last 7 calendar days.
            var views7d;
            if (typeof bizData.viewCount7d === "number") {
              views7d = bizData.viewCount7d;
            } else {
              views7d = 0;
              for (var j = 0; j < 7; j++) {
                var dj = new Date(now.getTime() - j * 86400 * 1000).toISOString().slice(0, 10);
                views7d += Number((bizData.viewsByDate || {})[dj] || 0);
              }
            }

            // Get last 7 days of view data (daily chart)
            var last7days = [];
            for (var i = 6; i >= 0; i--) {
              var d = new Date(); d.setDate(d.getDate() - i);
              var key = d.toISOString().slice(0, 10);
              last7days.push({ date: d.toLocaleDateString("en-SG", { weekday: "short" }), views: (bizData.viewsByDate && bizData.viewsByDate[key]) || 0 });
            }

            setAnalyticsData({
              viewCount: bizData.viewCount || 0,
              views24h: views24h,
              views7d: views7d,
              likesCount: bizData.likesCount || 0,
              totalBookings: totalBookings,
              completedBookings: completedBookings,
              pendingBookings: pendingBookings,
              confirmedBookings: confirmedBookings,
              lifetimeEarnings: lifetimeEarnings,
              last7days: last7days
            });
          } catch(err) {
            console.error("Analytics fetch error:", err);
            showToast("Could not load analytics.", "error");
          }
        })();
      }, [bizTab, bizId]);

      if (!biz) return <div className="page active"><div style={{ padding: "2rem", textAlign: "center" }}>Business not found</div></div>;

      const startEdit = (section) => {
        if (section === "description") setEditData({ description: biz.description || "" });
        else if (section === "address") setEditData({ address: biz.address || "" });
        else if (section === "social") setEditData({ socialMedia: { ...(biz.socialMedia || {}) }, defaultOrderMethod: biz.defaultOrderMethod || "" });
        else if (section === "hours") {
          const rawHours = biz.hours || {};
          const DAYS_NORM = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
          const normalizedHours = {};
          DAYS_NORM.forEach(day => {
            const existing = rawHours[day];
            normalizedHours[day] = {
              open:    (existing && existing.open)  || "09:00",
              close:   (existing && existing.close) || "18:00",
              enabled: existing ? (existing.enabled !== undefined ? existing.enabled : true) : false
            };
          });
          setEditData({ hours: normalizedHours });
        }
        else if (section === "products") setEditData({ menuItems: [...(biz.menuItems || [])], newItem: { name: "", description: "", price: "", imageFile: null, imagePreview: null } });
        else if (section === "name_cover") setEditData({ name: biz.name, coverFile: null, coverPreview: null, coverZoom: biz.coverZoom || 1, coverPosX: biz.coverPosX || 50, coverPosY: biz.coverPosY || 50 });
        else if (section === "category") {
          const validIds = CATEGORIES.filter(c => c.id !== "all").map(c => c.id);
          setEditData({ category: validIds.includes(biz.category) ? biz.category : "others" });
        }
        else if (section === "slug") setEditData({ slug: biz.slug || "" });
        else if (section === "hopemaker") setEditData({ ywcaHopemakerClaim: { claimed: !!(biz.ywcaHopemakerClaim && biz.ywcaHopemakerClaim.claimed), cohort: (biz.ywcaHopemakerClaim && biz.ywcaHopemakerClaim.cohort) || "" } });
        setEditing(section);
        setSaveMsg("");
      };

      const handleNotFound = (err) => {
        if (err.code === "not-found" || (err.message && err.message.indexOf("No document to update") !== -1)) {
          showToast("Business listing not found or has been deleted.", "error", 5000);
          setEditing(null);
          setUserBusinesses(prev => prev.filter(b => b.id !== bizId));
          return true;
        }
        return false;
      };

      const saveField = async (field, value, requiresReview = false) => {
        setSaving(true);
        try {
          if (requiresReview) {
            await db.collection("businesses").doc(bizId).update({
              [`pendingChanges.${field}`]: value,
              status: "pending",
              pendingChangeAt: firebase.firestore.FieldValue.serverTimestamp()
            });
            setUserBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, pendingChanges: { ...(b.pendingChanges || {}), [field]: value }, status: "pending" } : b));
            showToast("Change submitted for admin review.", "success");
          } else {
            await db.collection("businesses").doc(bizId).update({ [field]: value });
            setUserBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, [field]: value } : b));
            showToast("Saved!", "success");
          }
          setEditing(null);
        } catch (err) {
          console.error("Save error:", err);
          if (!handleNotFound(err)) {
            showToast("Error: " + err.message, "error");
          }
        }
        setSaving(false);
      };

      const saveMultipleFields = async (updates, requiresReview = false) => {
        setSaving(true);
        try {
          if (requiresReview) {
            const pendingUpdates = {};
            Object.entries(updates).forEach(([k, v]) => { pendingUpdates[`pendingChanges.${k}`] = v; });
            pendingUpdates.status = "pending";
            pendingUpdates.pendingChangeAt = firebase.firestore.FieldValue.serverTimestamp();
            await db.collection("businesses").doc(bizId).update(pendingUpdates);
            setUserBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, pendingChanges: { ...(b.pendingChanges || {}), ...updates }, status: "pending" } : b));
            showToast("Changes submitted for admin review.", "success");
          } else {
            await db.collection("businesses").doc(bizId).update(updates);
            setUserBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, ...updates } : b));
            showToast("Saved!", "success");
          }
          setEditing(null);
        } catch (err) {
          console.error("Save error:", err);
          if (!handleNotFound(err)) {
            showToast("Error: " + err.message, "error");
          }
        }
        setSaving(false);
      };

      const handleSaveNameCover = async () => {
        setSaving(true);
        try {
          const updates = {};
          let needsReview = false;
          if (editData.name !== biz.name) {
            updates.name = editData.name;
            needsReview = true;
          }
          if (editData.coverFile) {
            showToast("Uploading image...", "info", 8000);
            const coverRef = storage.ref(`businesses/${currentUser.uid}/${Date.now()}_cover`);
            await coverRef.put(await cropSquareImage(editData.coverFile, 1200, editData.coverPosX || 50, editData.coverPosY || 50, editData.coverZoom || 1));
            updates.coverPhotoUrl = await coverRef.getDownloadURL();
            needsReview = true;
          }
          // Save cover position and zoom
          if (editData.coverZoom !== undefined) updates.coverZoom = editData.coverZoom;
          if (editData.coverPosX !== undefined) updates.coverPosX = editData.coverPosX;
          if (editData.coverPosY !== undefined) updates.coverPosY = editData.coverPosY;
          if (Object.keys(updates).length > 0) {
            await saveMultipleFields(updates, needsReview);
            // SSoT: If business name changed, sync in all conversations referencing this business
            if (updates.name) {
              try {
                var convSnap = await db.collection("conversations").where("businessId", "==", bizId).get();
                if (!convSnap.empty) {
                  var batch = db.batch();
                  convSnap.docs.forEach(function(doc) { batch.update(doc.ref, { businessName: updates.name }); });
                  await batch.commit();
                }
              } catch (syncErr) { console.error("businessName sync error:", syncErr); }
            }
          } else {
            setEditing(null);
          }
        } catch (err) {
          console.error("Save error:", err);
          if (!handleNotFound(err)) {
            showToast("Error: " + err.message, "error");
          }
        }
        setSaving(false);
      };

      const sectionHeader = (title, section, buttonLabel = "Edit") => (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", fontStyle: "italic" }}>{title}</h2>
          {editing !== section && (
            <button onClick={() => startEdit(section)} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "6px", padding: "0.3rem 0.75rem", fontSize: "0.75rem", cursor: "pointer", color: "var(--amber)", fontWeight: 600 }}>
              {buttonLabel}
            </button>
          )}
        </div>
      );

      // --- Instagram OAuth helpers -----------------------------------------------
      const IG_APP_ID   = "922868064042627";
      const IG_REDIRECT = "https://asia-southeast1-nooks-sg.cloudfunctions.net/igOAuthCallback";

      const connectInstagram = () => {
        if (!currentUser) return;
        const oauthUrl =
          "https://www.instagram.com/oauth/authorize" +
          "?client_id=" + IG_APP_ID +
          "&redirect_uri=" + encodeURIComponent(IG_REDIRECT) +
          "&scope=instagram_business_basic" +
          "&response_type=code" +
          "&state=" + encodeURIComponent(currentUser.uid);
        window.location.href = oauthUrl;
      };

      const disconnectInstagram = async () => {
        try {
          await db.collection("businesses").doc(bizId).update({
            igConnected: false,
            igConnectedAt: null,
            "socialMedia.instagram": "",
          });
          setUserBusinesses(prev => prev.map(b =>
            b.id === bizId
              ? { ...b, igConnected: false, socialMedia: { ...b.socialMedia, instagram: "" } }
              : b
          ));
        } catch (err) {
          console.error("Disconnect IG error:", err);
        }
      };

      const saveCancel = (onSave) => (
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
          <button onClick={onSave} disabled={saving} style={{ padding: "0.5rem 1.25rem", background: "var(--amber)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600, opacity: saving ? 0.5 : 1 }}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={() => setEditing(null)} style={{ padding: "0.5rem 1.25rem", background: "white", border: "1px solid var(--border)", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem" }}>
            Cancel
          </button>
        </div>
      );

      return (
        <div className="page active">
          <div style={{ maxWidth: "600px", margin: "0 auto", padding: "0 1rem" }}>
            {/* Status Banners */}
            {biz.status === "pending" && (
              <div style={{ background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "12px", padding: "1rem", textAlign: "center", marginBottom: "1.5rem" }}>
                <strong>Currently under review...</strong>
                <p style={{ fontSize: "0.8rem", color: "var(--muted)", margin: "0.25rem 0 0" }}>Your listing is being reviewed by the <span className="nooks-brand">nooks</span> team.</p>
              </div>
            )}
            {biz.status === "changes_requested" && (
              <div style={{ background: "#F3E5F5", border: "1px solid #CE93D8", borderRadius: "12px", padding: "1rem", marginBottom: "1.5rem" }}>
                <strong style={{ color: "#6A1B9A" }}>Changes Requested</strong>
                {biz.reviewNote && <p style={{ fontSize: "0.85rem", color: "#4A148C", marginTop: "0.4rem" }}>{biz.reviewNote}</p>}
              </div>
            )}
            {biz.status === "rejected" && (
              <div style={{ background: "#FFEBEE", border: "1px solid #EF9A9A", borderRadius: "12px", padding: "1rem", marginBottom: "1.5rem" }}>
                <strong style={{ color: "#C62828" }}>Listing Rejected</strong>
                {biz.reviewNote && <p style={{ fontSize: "0.85rem", color: "#B71C1C", marginTop: "0.4rem" }}>{biz.reviewNote}</p>}
              </div>
            )}
            {biz.status === "suspended" && (
              <div style={{ background: "#FFF3E0", border: "1px solid #FFB74D", borderRadius: "12px", padding: "1rem", marginBottom: "1.5rem" }}>
                <strong style={{ color: "#E65100" }}>Listing Suspended</strong>
                {biz.reviewNote && <p style={{ fontSize: "0.85rem", color: "#BF360C", marginTop: "0.4rem" }}>Reason: {biz.reviewNote}</p>}
                <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.4rem" }}>Your listing has been suspended and is not visible to the public. Please contact support if you believe this is an error.</p>
              </div>
            )}

            {/* Instagram OAuth return banner */}
            {igMsg && (
              <div style={{ background: igMsg.type === "success" ? "linear-gradient(135deg, #833AB4 0%, #E1306C 100%)" : "#FFEBEE", border: igMsg.type === "error" ? "1px solid #EF9A9A" : "none", borderRadius: "12px", padding: "0.85rem 1.1rem", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", color: igMsg.type === "success" ? "white" : "#C62828" }}>
                <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>{igMsg.text}</span>
                <button onClick={() => setIgMsg(null)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "inherit", fontSize: "1.1rem", lineHeight: 1, padding: "0 0.25rem" }}>×</button>
              </div>
            )}

            {/* Store / Analytics / Reviews Tab Bar */}
            <div style={{ display: "flex", gap: "0", marginBottom: "1.5rem", borderBottom: "2px solid var(--border)" }}>
              {[{key: "store", label: "My Store"}, {key: "reviews", label: "Reviews"}, {key: "analytics", label: "Analytics"}].map(function(t) {
                return (
                  <button key={t.key} onClick={function() { setBizTab(t.key); }}
                    style={{ flex: 1, padding: "0.7rem 0", background: "none", border: "none", borderBottom: bizTab === t.key ? "2px solid var(--amber)" : "2px solid transparent", marginBottom: "-2px", fontWeight: bizTab === t.key ? 700 : 500, color: bizTab === t.key ? "var(--amber)" : "var(--muted)", cursor: "pointer", fontSize: "0.9rem", transition: "all 0.2s" }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* --- Reviews Tab (Operator) --- */}
            {bizTab === "reviews" && (() => {
              // Load reviews when tab opens
              if (!reviewsLoading && bizReviews.length === 0) {
                setReviewsLoading(true);
                db.collection("reviews").where("bizId", "==", bizId).orderBy("createdAt", "desc").get()
                  .then(function(snap) {
                    setBizReviews(snap.docs.map(function(d) { return { id: d.id, ...d.data() }; }));
                    setReviewsLoading(false);
                  })
                  .catch(function(e) { console.warn("Load reviews error:", e); setReviewsLoading(false); });
              }

              const avgRating = biz ? (biz.rating || 0) : 0;
              const reviewCount = biz ? (biz.reviewCount || 0) : 0;

              return React.createElement('div', null,
                // Summary card
                React.createElement('div', { style: { display: "flex", gap: "24px", alignItems: "center", padding: "20px", background: "var(--white)", borderRadius: "16px", border: "1px solid var(--border)", marginBottom: "20px" } },
                  React.createElement('div', { style: { textAlign: "center" } },
                    React.createElement('div', { style: { fontFamily: "var(--serif)", fontSize: "3rem", fontWeight: 700, color: "var(--ink)", lineHeight: 1 } }, avgRating.toFixed(1)),
                    React.createElement('div', { style: { color: "var(--yellow)", fontSize: "1rem", margin: "4px 0" } }, "?".repeat(Math.round(avgRating)) + "?".repeat(5 - Math.round(avgRating))),
                    React.createElement('div', { style: { fontSize: "0.82rem", color: "var(--muted)" } }, reviewCount + " reviews")
                  ),
                  React.createElement('div', { style: { flex: 1, fontSize: "0.85rem", color: "var(--mid)", lineHeight: 1.6 } },
                    "Respond to customer reviews to build trust with your neighbourhood. Replies show publicly under each review."
                  )
                ),

                // Reviews list
                reviewsLoading
                  ? React.createElement('div', { style: { textAlign: "center", padding: "2rem", color: "var(--muted)" } }, "Loading reviews...")
                  : bizReviews.length === 0
                    ? React.createElement('div', { style: { textAlign: "center", padding: "2rem", color: "var(--muted)" } }, "No reviews yet. They'll appear here once customers start reviewing.")
                    : bizReviews.map(function(review) {
                        var rName = review.customerName || "Anonymous";
                        var rDate = review.createdAt ? new Date(review.createdAt.seconds ? review.createdAt.seconds * 1000 : review.createdAt).toLocaleDateString("en-SG", { month: "short", year: "numeric" }) : "";
                        return React.createElement('div', { key: review.id, style: { background: "var(--white)", borderRadius: "16px", padding: "20px", boxShadow: "var(--shadow-xs)", border: "1px solid var(--border)", marginBottom: "12px" } },
                          // Review header
                          React.createElement('div', { style: { display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "12px" } },
                            React.createElement('div', { style: { width: 36, height: 36, borderRadius: 99, background: "var(--coral-light)", color: "var(--coral)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "0.85rem", flexShrink: 0 } }, rName[0]),
                            React.createElement('div', { style: { flex: 1 } },
                              React.createElement('div', { style: { display: "flex", alignItems: "center", gap: "6px" } },
                                React.createElement('span', { style: { fontWeight: 600, fontSize: "0.9rem" } }, rName),
                                React.createElement('span', { style: { color: "var(--yellow)", fontSize: "0.8rem" } }, "★".repeat(review.rating || 0) + "☆".repeat(5 - (review.rating || 0))),
                                React.createElement('span', { style: { fontSize: "0.75rem", color: "var(--muted)", marginLeft: "auto" } }, rDate)
                              ),
                              review.serviceName && React.createElement('span', { style: { display: "inline-block", background: "var(--bg-2)", color: "var(--mid)", fontSize: "0.72rem", fontWeight: 500, padding: "2px 8px", borderRadius: 99, marginTop: "4px" } }, "Booked: " + review.serviceName),
                              React.createElement('p', { style: { fontSize: "0.85rem", color: "var(--mid)", margin: "4px 0 0", lineHeight: 1.5 } }, review.text || "")
                            )
                          ),

                          // Existing reply
                          review.reply
                            ? React.createElement('div', { style: { padding: "12px 16px", background: "var(--amber-light)", borderRadius: "10px", fontSize: "0.82rem", color: "var(--amber-dark)", fontWeight: 500 } },
                                React.createElement('span', { style: { fontWeight: 700 } }, "Your reply: "),
                                review.reply
                              )
                            // Reply form
                            : replyingTo === review.id
                              ? React.createElement('div', { style: { borderTop: "1px solid var(--border)", paddingTop: "12px" } },
                                  React.createElement('p', { style: { fontSize: "0.78rem", fontWeight: 600, color: "var(--mid)", marginBottom: "8px" } }, "Reply as business owner"),
                                  React.createElement('textarea', {
                                    value: replyText,
                                    onChange: function(e) { setReplyText(e.target.value); },
                                    placeholder: "Thank your customer or address their feedback...",
                                    rows: 2,
                                    style: { width: "100%", border: "1.5px solid var(--border)", borderRadius: "10px", padding: "10px 14px", fontSize: "0.82rem", color: "var(--ink)", background: "var(--white)", resize: "none", fontFamily: "var(--font)", outline: "none" }
                                  }),
                                  React.createElement('div', { style: { display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" } },
                                    React.createElement('button', {
                                      onClick: function() { setReplyingTo(null); setReplyText(""); },
                                      style: { padding: "7px 16px", borderRadius: 99, fontSize: "0.78rem", fontWeight: 600, background: "var(--bg-2)", color: "var(--mid)", border: "none", cursor: "pointer" }
                                    }, "Cancel"),
                                    React.createElement('button', {
                                      disabled: !replyText.trim() || replySending,
                                      onClick: async function() {
                                        if (!replyText.trim()) return;
                                        setReplySending(true);
                                        try {
                                          await saveOperatorReply(review.id, replyText.trim(), currentUser.uid);
                                          // Update local state
                                          setBizReviews(function(prev) {
                                            return prev.map(function(r) {
                                              return r.id === review.id ? Object.assign({}, r, { reply: replyText.trim() }) : r;
                                            });
                                          });
                                          setReplyingTo(null);
                                          setReplyText("");
                                          showToast("Reply sent!", "success");
                                        } catch(e) {
                                          showToast("Failed to send reply: " + (e.message || e), "error");
                                        }
                                        setReplySending(false);
                                      },
                                      style: { padding: "7px 16px", borderRadius: 99, fontSize: "0.78rem", fontWeight: 600, background: replyText.trim() ? "var(--coral)" : "var(--border-2)", color: "#fff", border: "none", cursor: replyText.trim() ? "pointer" : "not-allowed" }
                                    }, replySending ? "Sending..." : "Send Reply")
                                  )
                                )
                              : React.createElement('button', {
                                  onClick: function() { setReplyingTo(review.id); setReplyText(""); },
                                  style: { marginTop: "8px", padding: "7px 16px", borderRadius: 99, fontSize: "0.78rem", fontWeight: 600, background: "var(--bg-2)", color: "var(--mid)", border: "none", cursor: "pointer", transition: "all 0.2s" }
                                }, "💬 Reply")
                        );
                      })
              );
            })()}

            {/* --- Analytics Tab --- */}
            {bizTab === "analytics" && (
              <div>
                {!analyticsData ? (
                  <div style={{ textAlign: "center", padding: "3rem 0", color: "var(--muted)" }}>
                    <div className="save-overlay-spinner" style={{ margin: "0 auto 1rem" }}></div>
                    Loading analytics...
                  </div>
                ) : (
                  <div>
                    {/* Stat Cards Grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.5rem" }}>
                      {/* Views — 24h / 7d / all-time */}
                      <div style={{ background: "#FFF8E1", borderRadius: "12px", padding: "1rem" }}>
                        <div style={{ fontSize: "0.75rem", color: "#F57F17", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.4rem" }}>Store Views</div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "#E65100", fontFamily: "var(--serif)", lineHeight: 1.05 }}>{analyticsData.viewCount.toLocaleString()}</div>
                        <div style={{ fontSize: "0.7rem", color: "#F57F17", marginTop: "0.3rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                          <span><strong>{analyticsData.views24h || 0}</strong> last 24h</span>
                          <span><strong>{analyticsData.views7d || 0}</strong> last 7d</span>
                        </div>
                      </div>
                      {/* Likes */}
                      <div style={{ background: "#FCE4EC", borderRadius: "12px", padding: "1rem" }}>
                        <div style={{ fontSize: "0.75rem", color: "#C62828", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.4rem" }}>Favourites</div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "#B71C1C", fontFamily: "var(--serif)" }}>{analyticsData.likesCount}</div>
                        <div style={{ fontSize: "0.75rem", color: "#C62828", marginTop: "0.2rem" }}>users saved your store</div>
                      </div>
                      {/* Bookings */}
                      <div style={{ background: "#E8F5E9", borderRadius: "12px", padding: "1rem" }}>
                        <div style={{ fontSize: "0.75rem", color: "#2E7D32", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.4rem" }}>Bookings</div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "#1B5E20", fontFamily: "var(--serif)" }}>{analyticsData.totalBookings}</div>
                        <div style={{ fontSize: "0.75rem", color: "#2E7D32", marginTop: "0.2rem" }}>
                          {analyticsData.completedBookings} completed
                          {analyticsData.pendingBookings > 0 ? " — " + analyticsData.pendingBookings + " pending" : ""}
                        </div>
                      </div>
                      {/* Earnings */}
                      <div style={{ background: "#E3F2FD", borderRadius: "12px", padding: "1rem" }}>
                        <div style={{ fontSize: "0.75rem", color: "#1565C0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.4rem" }}>Lifetime Earnings</div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "#0D47A1", fontFamily: "var(--serif)" }}>S${analyticsData.lifetimeEarnings.toFixed(2)}</div>
                        <div style={{ fontSize: "0.75rem", color: "#1565C0", marginTop: "0.2rem" }}>from completed bookings</div>
                      </div>
                    </div>

                    {/* 7-Day Views Chart */}
                    <div style={{ background: "var(--card)", borderRadius: "12px", padding: "1rem", border: "1px solid var(--border)" }}>
                      <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.75rem" }}>Views — Last 7 Days</div>
                      <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", height: "100px" }}>
                        {analyticsData.last7days.map(function(day, i) {
                          var maxViews = Math.max.apply(null, analyticsData.last7days.map(function(d) { return d.views; })) || 1;
                          var height = Math.max(4, (day.views / maxViews) * 80);
                          return (
                            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                              <span style={{ fontSize: "0.65rem", color: "var(--muted)", fontWeight: 600 }}>{day.views || ""}</span>
                              <div style={{ width: "100%", height: height + "px", background: day.views > 0 ? "var(--amber)" : "var(--border)", borderRadius: "4px 4px 0 0", transition: "height 0.3s ease" }}></div>
                              <span style={{ fontSize: "0.6rem", color: "var(--muted)" }}>{day.date}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Booking Breakdown */}
                    {analyticsData.totalBookings > 0 && (
                      <div style={{ background: "var(--card)", borderRadius: "12px", padding: "1rem", border: "1px solid var(--border)", marginTop: "0.75rem" }}>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.75rem" }}>Booking Breakdown</div>
                        {[
                          { label: "Completed", count: analyticsData.completedBookings, color: "#2E7D32" },
                          { label: "Confirmed", count: analyticsData.confirmedBookings, color: "#1565C0" },
                          { label: "Pending", count: analyticsData.pendingBookings, color: "#F57F17" }
                        ].filter(function(r) { return r.count > 0; }).map(function(r) {
                          var pct = Math.round((r.count / analyticsData.totalBookings) * 100);
                          return (
                            <div key={r.label} style={{ marginBottom: "0.5rem" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", marginBottom: "0.2rem" }}>
                                <span style={{ color: r.color, fontWeight: 600 }}>{r.label}</span>
                                <span style={{ color: "var(--muted)" }}>{r.count} ({pct}%)</span>
                              </div>
                              <div style={{ height: "6px", background: "var(--border)", borderRadius: "3px", overflow: "hidden" }}>
                                <div style={{ width: pct + "%", height: "100%", background: r.color, borderRadius: "3px", transition: "width 0.5s ease" }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* --- Store Tab (existing content) --- */}
            {bizTab === "store" && <>

            {/* Saving Overlay */}
            {saving && (
              <div className="save-overlay">
                <div className="save-overlay-spinner"></div>
                <span style={{ fontSize: "0.9rem", color: "var(--muted)", fontWeight: 500 }}>Saving...</span>
              </div>
            )}

            {/* Cover Image — editable with drag-to-reposition */}
            <div style={{ borderRadius: "12px", overflow: "hidden", marginBottom: "1.5rem", position: "relative" }}>
              {editing === "name_cover" ? (
                <div style={{ height: "220px", position: "relative", cursor: (editData._repositioning ? "crosshair" : "pointer"), userSelect: "none" }}
                  onClick={(e) => {
                    if (e.target.tagName === "BUTTON") return; // Don't toggle if clicking X button
                    setEditData(function(prev) { return Object.assign({}, prev, { _repositioning: !prev._repositioning }); });
                  }}
                  onMouseMove={(e) => {
                    if (!editData._repositioning) return;
                    var rect = e.currentTarget.getBoundingClientRect();
                    var px = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                    var py = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
                    setEditData(function(prev) { return Object.assign({}, prev, { coverPosX: Math.round(px), coverPosY: Math.round(py) }); });
                  }}
                  onTouchMove={(e) => {
                    if (!editData._repositioning || !e.touches[0]) return;
                    var rect = e.currentTarget.getBoundingClientRect();
                    var px = Math.max(0, Math.min(100, ((e.touches[0].clientX - rect.left) / rect.width) * 100));
                    var py = Math.max(0, Math.min(100, ((e.touches[0].clientY - rect.top) / rect.height) * 100));
                    setEditData(function(prev) { return Object.assign({}, prev, { coverPosX: Math.round(px), coverPosY: Math.round(py) }); });
                  }}>
                  <img src={editData.coverPreview || biz.coverPhotoUrl || ""} alt="Cover"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none",
                      objectPosition: (editData.coverPosX || 50) + "% " + (editData.coverPosY || 50) + "%",
                      transform: "scale(" + (editData.coverZoom || 1) + ")" }} />
                  <div style={{ position: "absolute", bottom: "8px", left: "50%", transform: "translateX(-50%)",
                    background: editData._repositioning ? "var(--amber)" : "rgba(0,0,0,0.5)",
                    color: "white", padding: "0.3rem 0.8rem", borderRadius: "6px", fontSize: "0.7rem", zIndex: 2, fontWeight: 600, pointerEvents: "none" }}>
                    {editData._repositioning ? "Click anywhere to lock position" : "Click to reposition"}
                  </div>
                </div>
              ) : biz.coverPhotoUrl ? (
                <img src={biz.coverPhotoUrl} alt={biz.name} style={{ width: "100%", height: "220px", objectFit: "cover", display: "block",
                  objectPosition: (biz.coverPosX || 50) + "% " + (biz.coverPosY || 50) + "%",
                  transform: "scale(" + (biz.coverZoom || 1) + ")" }} />
              ) : (
                <div style={{ width: "100%", height: "220px", background: "var(--amber-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "var(--muted)" }}>No cover image</span>
                </div>
              )}
              {editing !== "name_cover" && (
                <button onClick={() => startEdit("name_cover")} style={{ position: "absolute", top: "10px", right: "10px", background: "rgba(255,255,255,0.9)", border: "none", borderRadius: "6px", padding: "0.35rem 0.75rem", fontSize: "0.75rem", cursor: "pointer", fontWeight: 600, color: "var(--amber)" }}>
                  Edit Name / Cover
                </button>
              )}
              <input type="file" accept="image/*" ref={coverInputRef} style={{ display: "none" }} onChange={(e) => {
                const f = e.target.files[0];
                if (f) setEditData(prev => ({ ...prev, coverFile: f, coverPreview: URL.createObjectURL(f), coverPosX: 50, coverPosY: 50, coverZoom: 1 }));
              }} />
            </div>

            {/* Name/Cover Edit Mode */}
            {editing === "name_cover" ? (
              <div style={{ marginBottom: "1.5rem" }}>
                <div className="form-group">
                  <label className="form-label">Business Name</label>
                  <input className="form-input" value={editData.name || ""} onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))} />
                </div>
                <button onClick={() => coverInputRef.current?.click()} style={{ padding: "0.5rem 1rem", border: "1px dashed var(--border)", borderRadius: "8px", background: "white", cursor: "pointer", fontSize: "0.85rem", marginTop: "0.5rem", width: "100%" }}>
                  {editData.coverPreview ? "Change Cover Photo" : "Upload New Cover Photo"}
                </button>
                {/* Zoom slider */}
                {(editData.coverPreview || biz.coverPhotoUrl) && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                    <span style={{ fontSize: "0.7rem", color: "var(--muted)" }}>Zoom</span>
                    <input type="range" min="1" max="2" step="0.05" value={editData.coverZoom || 1}
                      onChange={(e) => setEditData(prev => ({ ...prev, coverZoom: parseFloat(e.target.value) }))}
                      style={{ flex: 1, accentColor: "var(--amber)" }} />
                    <span style={{ fontSize: "0.7rem", color: "var(--muted)", minWidth: "30px" }}>{Math.round((editData.coverZoom || 1) * 100)}%</span>
                  </div>
                )}
                <p style={{ fontSize: "0.7rem", color: "#E65100", marginTop: "0.5rem" }}>* Name and cover changes require admin approval</p>
                {saveCancel(handleSaveNameCover)}
              </div>
            ) : (
              <div style={{ marginBottom: "1.5rem" }}>
                <h1 style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", marginBottom: "0.25rem" }}>{biz.name}</h1>
                {editing === "slug" ? (
                  <div style={{ marginTop: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.85rem" }}>
                      <span style={{ color: "var(--muted)" }}>nooks.sg/</span>
                      <input className="form-input" value={editData.slug || ""} onChange={(e) => setEditData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} style={{ fontSize: "0.85rem", padding: "0.3rem 0.5rem", width: "auto", flex: 1 }} />
                    </div>
                    <p style={{ fontSize: "0.7rem", color: "#E65100", marginTop: "0.35rem" }}>* Web address changes require admin approval. Old links will stop working.</p>
                    {saveCancel(() => saveField("slug", editData.slug, true))}
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>nooks.sg/{biz.slug}</p>
                    {biz.status === "approved" && (
                      <button onClick={() => startEdit("slug")} style={{ background: "none", border: "none", color: "var(--amber)", cursor: "pointer", fontSize: "0.75rem", textDecoration: "underline" }}>change</button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Category — editable */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              {sectionHeader("Category", "category")}
              {editing === "category" ? (
                <div>
                  <select className="form-select" value={editData.category || ""} onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}>
                    {CATEGORIES.filter(c => c.id !== "all").map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                  {saveCancel(() => saveField("category", editData.category))}
                </div>
              ) : (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ background: "var(--amber-light)", color: "var(--amber)", padding: "0.3rem 0.75rem", borderRadius: "12px", fontSize: "0.85rem", fontWeight: 600 }}>
                    <Icon name={CATEGORIES.find(c => c.id === biz.category)?.iconName || "others"} size={14} /> {CATEGORIES.find(c => c.id === biz.category)?.label || biz.category}
                  </span>
                </div>
              )}
            </div>

            {/* Description — editable */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              {sectionHeader("About", "description")}
              {editing === "description" ? (
                <div>
                  <textarea className="form-input" rows={4} value={editData.description || ""} onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))} style={{ resize: "vertical" }} />
                  {saveCancel(() => saveField("description", editData.description))}
                </div>
              ) : (
                <p style={{ color: "var(--text)", lineHeight: 1.6 }}>{biz.description || "No description yet"}</p>
              )}
            </div>

            {/* YWCA Hopemaker claim — editable (self-declaration only; the
                verified badge itself is admin-controlled from Approvals). */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              {sectionHeader("YWCA Hopemaker Programme", "hopemaker")}
              {editing === "hopemaker" ? (
                <div>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", fontSize: "0.9rem" }}>
                    <input
                      type="checkbox"
                      checked={!!(editData.ywcaHopemakerClaim && editData.ywcaHopemakerClaim.claimed)}
                      onChange={(e) => setEditData(prev => ({ ...prev, ywcaHopemakerClaim: { ...(prev.ywcaHopemakerClaim || {}), claimed: e.target.checked } }))}
                      style={{ width: "16px", height: "16px", cursor: "pointer", marginTop: "2px", flexShrink: 0 }}
                    />
                    <span>I'm part of YWCA of Singapore's Hopemaker: Women's Business Accelerator programme</span>
                  </label>
                  {editData.ywcaHopemakerClaim && editData.ywcaHopemakerClaim.claimed && (
                    <div className="form-group" style={{ marginTop: "0.75rem", paddingLeft: "24px" }}>
                      <label className="form-label" style={{ fontSize: "0.8rem" }}>Cohort / batch (optional)</label>
                      <input
                        className="form-input"
                        placeholder="e.g. 2026-Q1 or Hopemaker '26 (Jan–Apr)"
                        value={editData.ywcaHopemakerClaim.cohort || ""}
                        onChange={(e) => setEditData(prev => ({ ...prev, ywcaHopemakerClaim: { ...(prev.ywcaHopemakerClaim || {}), cohort: e.target.value } }))}
                      />
                    </div>
                  )}
                  <p style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: "0.5rem" }}>
                    This flags your listing for our team to cross-check with YWCA — it doesn't apply the verified badge on its own.
                  </p>
                  {saveCancel(() => saveField("ywcaHopemakerClaim", {
                    claimed: !!(editData.ywcaHopemakerClaim && editData.ywcaHopemakerClaim.claimed),
                    cohort: (editData.ywcaHopemakerClaim && editData.ywcaHopemakerClaim.claimed) ? (editData.ywcaHopemakerClaim.cohort || null) : null
                  }))}
                </div>
              ) : (
                biz.ywcaHopemakerClaim && biz.ywcaHopemakerClaim.claimed ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    <span style={{ background: "#EEF1FA", color: "#1F2A6F", padding: "0.3rem 0.75rem", borderRadius: "12px", fontSize: "0.85rem", fontWeight: 600 }}>
                      Declared: YWCA Hopemaker{biz.ywcaHopemakerClaim.cohort ? " · " + biz.ywcaHopemakerClaim.cohort : ""}
                    </span>
                    {biz.ywcaHopemaker && biz.ywcaHopemaker.verified ? (
                      <span style={{ fontSize: "0.78rem", color: "#2E7D32", fontWeight: 600 }}>✓ Verified by YWCA</span>
                    ) : (
                      <span style={{ fontSize: "0.78rem", color: "var(--muted)" }}>Pending admin verification</span>
                    )}
                  </div>
                ) : (
                  <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>Not declared. If you completed YWCA's Hopemaker programme, let us know here.</p>
                )
              )}
            </div>

            {/* Location — editable */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              {sectionHeader("Find them here", "address")}
              {editing === "address" ? (
                <div>
                  <input className="form-input" value={editData.address || ""} placeholder="Enter address or postal code"
                    onChange={(e) => {
                      var val = e.target.value;
                      setEditData(prev => ({ ...prev, address: val }));
                      // Auto-geocode via OneMap when 6-digit postal or 5+ chars typed
                      if (val.length >= 5) {
                        clearTimeout(window.__addrDebounce);
                        window.__addrDebounce = setTimeout(function() {
                          fetch("https://www.onemap.gov.sg/api/common/elastic/search?searchVal=" + encodeURIComponent(val.trim()) + "&returnGeom=Y&getAddrDetails=Y")
                            .then(function(r) { return r.json(); })
                            .then(function(data) {
                              if (data && data.results && data.results.length > 0) {
                                var r = data.results[0];
                                var lat = parseFloat(r.LATITUDE);
                                var lng = parseFloat(r.LONGITUDE);
                                var street = (r.BLK_NO && r.ROAD_NAME) ? r.BLK_NO + " " + _toTitleCase(r.ROAD_NAME) : "";
                                var streetOnly = r.ROAD_NAME ? _toTitleCase(r.ROAD_NAME) : "";
                                var postalFromSearch = r.POSTAL ? String(r.POSTAL).replace(/\D/g, "") : "";
                                setEditData(function(prev) { return Object.assign({}, prev, { lat: lat, lng: lng, address: street || prev.address, street: streetOnly, postalCode: postalFromSearch || prev.postalCode }); });
                                // Update mini map if it exists
                                if (window.__editMap && window.__editMarker) {
                                  window.__editMap.setView([lat, lng], 17);
                                  window.__editMarker.setLatLng([lat, lng]);
                                }
                              }
                            }).catch(function() {});
                        }, 600);
                      }
                    }}
                  />
                  {/* Mini map for location refinement */}
                  <div ref={function(el) {
                    if (!el || el._leaflet_init) return;
                    el._leaflet_init = true;
                    var lat = biz.lat || 1.3521;
                    var lng = biz.lng || 103.8198;
                    var map = L.map(el, { scrollWheelZoom: true, zoomControl: true }).setView([lat, lng], 17);
                    L.tileLayer(CARTO_TILE_URL, { maxZoom: 19 }).addTo(map);
                    var marker = L.marker([lat, lng], { draggable: true }).addTo(map);
                    window.__editMap = map;
                    window.__editMarker = marker;
                    marker.on("dragend", function() {
                      var pos = marker.getLatLng();
                      setEditData(function(prev) { return Object.assign({}, prev, { lat: pos.lat, lng: pos.lng }); });
                      fetch("https://www.onemap.gov.sg/api/public/revgeocode?location=" + pos.lat + "," + pos.lng + "&buffer=50&addressType=All")
                        .then(function(r) { return r.json(); })
                        .then(function(data) {
                          if (data && data.GeocodeInfo && data.GeocodeInfo.length > 0) {
                            var info = data.GeocodeInfo[0];
                            var addr = ((info.BLOCK || "") + " " + (info.ROAD || "")).trim();
                            var streetFromDrag = info.ROAD ? _toTitleCase(info.ROAD) : "";
                            var postalFromDrag = info.POSTALCODE ? String(info.POSTALCODE).replace(/\D/g, "") : "";
                            if (addr) setEditData(function(prev) { return Object.assign({}, prev, { address: addr, street: streetFromDrag, postalCode: postalFromDrag || prev.postalCode }); });
                          }
                        }).catch(function() {});
                    });
                    setTimeout(function() { map.invalidateSize(); }, 200);
                  }} style={{ height: "200px", borderRadius: "10px", marginTop: "0.5rem", marginBottom: "0.75rem", border: "1px solid var(--border)" }}></div>
                  <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginBottom: "0.75rem" }}>Drag the pin to refine your exact location</p>
                  {saveCancel(async () => {
                    var updates = { address: editData.address, street: editData.street || "" };
                    if (editData.lat) updates.lat = editData.lat;
                    if (editData.lng) updates.lng = editData.lng;
                    // ROOT FIX: postalCode must always be kept in sync with address/street.
                    // Otherwise the auto-geocode repair job (_autoGeocodePlaceholders) will later
                    // re-derive street/lat/lng FROM a stale postalCode and silently overwrite this edit.
                    if (editData.postalCode) updates.postalCode = editData.postalCode;
                    await saveMultipleFields(updates, false);
                    window.__editMap = null;
                    window.__editMarker = null;
                  })}
                </div>
              ) : (
                <p style={{ fontSize: "0.9rem", color: "var(--muted)" }}>
                  <Icon name="pin" size={14} /> {getDisplayLocation(biz)}
                </p>
              )}
            </div>

            {/* Instagram Connection — OAuth */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", fontStyle: "italic", marginBottom: "0.75rem" }}>Instagram</h2>
              {biz.igConnected ? (
                <div style={{ background: "linear-gradient(135deg, #833AB4 0%, #C13584 50%, #E1306C 100%)", borderRadius: "12px", padding: "1rem 1.25rem", color: "white" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>@{biz.socialMedia?.instagram || "connected"}</div>
                        <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>Instagram connected ✓</div>
                      </div>
                    </div>
                    <button onClick={disconnectInstagram} style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.4)", padding: "0.4rem 0.85rem", borderRadius: "20px", fontSize: "0.8rem", cursor: "pointer" }}>
                      Disconnect
                    </button>
                  </div>
                </div>
              ) : (
                /* IG_CONNECT_DISABLED: Instagram OAuth connector temporarily disabled */
                <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", background: "#F5F5F5", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.6rem 0.75rem", fontSize: "0.78rem", color: "var(--muted)" }}>
                  <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>ℹ️</span>
                  <span>Instagram connection is temporarily unavailable. You can add your handle manually in the Social Media section below.</span>
                </div>
              )}
            </div>

            {/* Social Media — editable */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              {sectionHeader("Social Media", "social")}
              {editing === "social" ? (
                <div>
                  {["instagram", "tiktok", "facebook", "whatsapp", "link"].map(key => (
                    <div key={key} className="form-group" style={{ marginBottom: "0.75rem" }}>
                      <label className="form-label" style={{ textTransform: "capitalize", fontSize: "0.8rem" }}>{key === "link" ? "Website" : key}</label>
                      <input className="form-input" value={editData.socialMedia?.[key] || ""} onChange={(e) => setEditData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, [key]: e.target.value } }))} placeholder={key === "whatsapp" ? "+65--------" : key === "link" ? "https://..." : `@yourbusiness`} />
                    </div>
                  ))}
                  {saveCancel(() => saveMultipleFields({ socialMedia: editData.socialMedia, defaultOrderMethod: editData.defaultOrderMethod }))}
                </div>
              ) : (
                biz.socialMedia && Object.entries(biz.socialMedia).filter(([k,v]) => v).length > 0 ? (
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {Object.entries(biz.socialMedia).filter(([k,v]) => v).map(([key, val]) => (
                      <span key={key} style={{ padding: "0.4rem 0.85rem", border: "1px solid var(--border)", borderRadius: "20px", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <Icon name={key === "whatsapp" ? "phone" : key === "link" ? "share" : "user"} size={14} /> {key === "whatsapp" ? "WhatsApp" : key === "link" ? "Website" : key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                    ))}
                  </div>
                ) : <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No social media added</p>
              )}
            </div>

            {/* The Founder */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", fontStyle: "italic", marginBottom: "0.75rem" }}>The Founder</h2>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "var(--amber-light)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {profilePicUrl ? <img src={profilePicUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <Icon name="user" size={20} color="var(--amber)" />}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>{currentUser?.displayName}</div>
                  <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                    {userProfile?.joinedAt ? `Member since ${new Date(userProfile.joinedAt?.seconds * 1000).toLocaleDateString("en-SG", { month: "short", year: "numeric" })}` : "New member"}
                  </div>
                </div>
              </div>
            </div>

            {/* Operating Hours — editable */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              {sectionHeader("Operating Hours", "hours")}
              {editing === "hours" ? (
                <div>
                  {/* Always Open toggle */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0.75rem", background: "var(--amber-light)", borderRadius: "8px", marginBottom: "0.75rem" }}>
                    <input type="checkbox" id="always-open-toggle"
                      checked={editData.alwaysOpen || false}
                      onChange={(e) => {
                        var isAlways = e.target.checked;
                        setEditData(prev => {
                          var newHours = Object.assign({}, prev.hours);
                          if (isAlways) {
                            Object.keys(newHours).forEach(function(day) { newHours[day] = { open: "00:00", close: "23:59", enabled: true }; });
                          }
                          return Object.assign({}, prev, { hours: newHours, alwaysOpen: isAlways });
                        });
                      }}
                      style={{ width: "18px", height: "18px", accentColor: "var(--amber)" }} />
                    <label htmlFor="always-open-toggle" style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--amber)", cursor: "pointer" }}>Always Open (24/7)</label>
                  </div>
                  {DAYS_ORDER.filter(day => (editData.hours || {})[day]).map(day => { const info = (editData.hours || {})[day]; return (
                    <div key={day} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0", fontSize: "0.85rem" }}>
                      <input type="checkbox" checked={info.enabled} onChange={(e) => {
                        setEditData(prev => ({ ...prev, hours: { ...prev.hours, [day]: { ...prev.hours[day], enabled: e.target.checked } } }));
                      }} />
                      <span style={{ minWidth: "90px", fontWeight: 500 }}>{day}</span>
                      {info.enabled ? (
                        <>
                          <input type="time" value={info.open || "09:00"} onChange={(e) => setEditData(prev => ({ ...prev, hours: { ...prev.hours, [day]: { ...prev.hours[day], open: e.target.value } } }))} style={{ padding: "0.3rem", borderRadius: "6px", border: "1px solid var(--border)", fontSize: "0.8rem" }} />
                          <span>-</span>
                          <input type="time" value={info.close || "18:00"} onChange={(e) => setEditData(prev => ({ ...prev, hours: { ...prev.hours, [day]: { ...prev.hours[day], close: e.target.value } } }))} style={{ padding: "0.3rem", borderRadius: "6px", border: "1px solid var(--border)", fontSize: "0.8rem" }} />
                        </>
                      ) : <span style={{ color: "var(--muted)" }}>Closed</span>}
                    </div>
                  ); })}
                  {saveCancel(() => saveField("hours", editData.hours))}
                </div>
              ) : (
                biz.hours ? (() => {
                  var allAlways = DAYS_ORDER.every(day => biz.hours[day] && biz.hours[day].enabled && biz.hours[day].open === "00:00" && biz.hours[day].close === "23:59");
                  if (allAlways) return <div style={{ padding: "0.5rem 0.75rem", background: "var(--amber-light)", borderRadius: "8px", fontSize: "0.9rem", fontWeight: 600, color: "var(--amber)" }}>Always Open (24/7)</div>;
                  return DAYS_ORDER.filter(day => biz.hours[day]).map(day => {
                    const info = biz.hours[day];
                    return (
                      <div key={day} style={{ display: "flex", justifyContent: "space-between", padding: "0.35rem 0", fontSize: "0.9rem" }}>
                        <span style={{ color: info.enabled ? "var(--text)" : "var(--muted)" }}>{day}</span>
                        <span style={{ color: "var(--muted)" }}>{info.enabled ? (info.open === "00:00" && info.close === "23:59" ? "24 Hours" : `${info.open} - ${info.close}`) : "Closed"}</span>
                      </div>
                    );
                  });
                })() : <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No hours set</p>
              )}
            </div>

            {/* Products & Services — editable */}
            <div style={{ borderTop: "1px solid var(--border)", padding: "1.5rem 0" }}>
              {sectionHeader("Products & Services", "products", "Add/Edit")}
              {editing === "products" ? (
                <div>
                  {/* Existing items */}
                  {editData.menuItems?.map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
                      <label style={{ cursor: "pointer", flexShrink: 0, position: "relative" }} title="Click to upload product photo">
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setEditData(prev => {
                              const updated = [...prev.menuItems];
                              updated[i] = { ...updated[i], imageFile: file, imagePreview: ev.target.result };
                              return { ...prev, menuItems: updated };
                            });
                          };
                          reader.readAsDataURL(file);
                        }} />
                        {(item.imagePreview || item.imageUrl) ? (
                          <img src={item.imagePreview || item.imageUrl} alt={item.name} style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "6px" }} />
                        ) : (
                          <div style={{ width: "48px", height: "48px", background: "var(--cream)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed var(--border)", transition: "border-color 0.2s" }}>
                            <Icon name="photo" size={18} color="var(--muted)" />
                          </div>
                        )}
                      </label>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <input value={item.name || ""} placeholder="Item name"
                          onChange={(e) => setEditData(prev => { var u = [...prev.menuItems]; u[i] = { ...u[i], name: e.target.value }; return { ...prev, menuItems: u }; })}
                          style={{ width: "100%", border: "1px solid var(--border)", borderRadius: "4px", padding: "0.3rem 0.5rem", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.25rem", boxSizing: "border-box" }} />
                        <input value={item.description || ""} placeholder="Description (optional)"
                          onChange={(e) => setEditData(prev => { var u = [...prev.menuItems]; u[i] = { ...u[i], description: e.target.value }; return { ...prev, menuItems: u }; })}
                          style={{ width: "100%", border: "1px solid var(--border)", borderRadius: "4px", padding: "0.25rem 0.5rem", fontSize: "0.75rem", color: "var(--muted)", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.25rem", flexShrink: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                          <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>S$</span>
                          <input type="number" step="0.01" value={item.price || ""} placeholder="0.00"
                            onChange={(e) => setEditData(prev => { var u = [...prev.menuItems]; u[i] = { ...u[i], price: e.target.value }; return { ...prev, menuItems: u }; })}
                            style={{ width: "65px", border: "1px solid var(--border)", borderRadius: "4px", padding: "0.25rem 0.35rem", fontSize: "0.85rem", fontWeight: 600, color: "var(--amber)", textAlign: "right" }} />
                        </div>
                        <button onClick={() => setEditData(prev => ({ ...prev, menuItems: prev.menuItems.filter((_, idx) => idx !== i) }))} style={{ background: "none", border: "none", color: "#C62828", cursor: "pointer", fontSize: "0.7rem" }}>Remove</button>
                      </div>
                    </div>
                  ))}

                  {/* Add new item */}
                  <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--cream)", borderRadius: "8px" }}>
                    <p style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.5rem" }}>Add New Item</p>
                    <input className="form-input" placeholder="Item name" value={editData.newItem?.name || ""} onChange={(e) => setEditData(prev => ({ ...prev, newItem: { ...prev.newItem, name: e.target.value } }))} style={{ marginBottom: "0.5rem" }} />
                    <input className="form-input" placeholder="Description (optional)" value={editData.newItem?.description || ""} onChange={(e) => setEditData(prev => ({ ...prev, newItem: { ...prev.newItem, description: e.target.value } }))} style={{ marginBottom: "0.5rem" }} />
                    <input className="form-input" type="number" step="0.01" placeholder="Price (S$)" value={editData.newItem?.price || ""} onChange={(e) => setEditData(prev => ({ ...prev, newItem: { ...prev.newItem, price: e.target.value } }))} style={{ marginBottom: "0.5rem" }} />
                    {/* Product image upload */}
                    <div style={{ marginBottom: "0.5rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", padding: "0.5rem", border: "1px dashed var(--border)", borderRadius: "6px", fontSize: "0.8rem", color: "var(--muted)" }}>
                        <Icon name="photo" size={16} color="var(--muted)" />
                        {editData.newItem?.imagePreview ? "Change image" : "Add product image (optional)"}
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (ev) => setEditData(prev => ({ ...prev, newItem: { ...prev.newItem, imageFile: file, imagePreview: ev.target.result } }));
                          reader.readAsDataURL(file);
                        }} />
                      </label>
                      {editData.newItem?.imagePreview && (
                        <div style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <img src={editData.newItem.imagePreview} alt="Preview" style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "6px" }} />
                          <button onClick={() => setEditData(prev => ({ ...prev, newItem: { ...prev.newItem, imageFile: null, imagePreview: null } }))} style={{ background: "none", border: "none", color: "#C62828", cursor: "pointer", fontSize: "0.8rem" }}>Remove</button>
                        </div>
                      )}
                    </div>
                    <button onClick={() => {
                      if (!editData.newItem?.name || !editData.newItem?.price) return;
                      setEditData(prev => ({
                        ...prev,
                        menuItems: [...(prev.menuItems || []), { name: prev.newItem.name, description: prev.newItem.description, price: prev.newItem.price, imageFile: prev.newItem.imageFile, imagePreview: prev.newItem.imagePreview }],
                        newItem: { name: "", description: "", price: "", imageFile: null, imagePreview: null }
                      }));
                    }} style={{ padding: "0.4rem 1rem", background: "var(--amber)", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600 }}>
                      + Add Item
                    </button>
                  </div>

                  {saveCancel(async () => {
                    // Upload any new product images before saving.
                    // compressImage now throws on decode/encode failure;
                    // surface a toast per-item so a single bad photo
                    // doesn't kill the whole save with no feedback.
                    const uploadedItems = [];
                    for (const item of editData.menuItems) {
                      const { imageFile, imagePreview, id, ...rest } = item;
                      if (imageFile) {
                        try {
                          const imgRef = storage.ref(`businesses/${currentUser.uid}/products/${Date.now()}_${imageFile.name}`);
                          await imgRef.put(await compressImage(imageFile, 800, 0.8));
                          rest.imageUrl = await imgRef.getDownloadURL();
                        } catch (err) {
                          console.error("[menuItem-upload] failed for", item.name, err);
                          showToast(`"${item.name || "menu item"}" image: ${err.message || "upload failed"}`, "error");
                          // Preserve any previous URL so we don't drop existing imagery on re-save
                          if (item.imageUrl) rest.imageUrl = item.imageUrl;
                        }
                      } else if (item.imageUrl) {
                        rest.imageUrl = item.imageUrl;
                      }
                      uploadedItems.push(rest);
                    }
                    await saveField("menuItems", uploadedItems);
                  })}
                </div>
              ) : (
                biz.menuItems && biz.menuItems.filter(Boolean).length > 0 ? (
                  <React.Fragment>
                    {biz.menuItems.filter(Boolean).map((item, i) => {
                      const itemPhotos = getItemPhotos(item);
                      const photoCount = itemPhotos.length;
                      const variantCount = Array.isArray(item.variants) ? item.variants.length : 0;
                      const faqCount = Array.isArray(item.faqs) ? item.faqs.length : 0;
                      const hasLong = !!(item.longDescription && item.longDescription.length);
                      return (
                        <div
                          key={i}
                          onClick={() => setItemEditor({ mode: 'edit', index: i, item: item })}
                          style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.6rem 0.5rem", borderBottom: "1px solid var(--border)", cursor: "pointer", borderRadius: "6px", transition: "background 0.15s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "#FFF8F2"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          title="Click to edit details, photos, variants, FAQs"
                        >
                          {photoCount > 0 ? (
                            <img src={itemPhotos[0]} alt={item.name} style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "6px", flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: "48px", height: "48px", background: "var(--cream)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <Icon name="photo" size={18} color="var(--muted)" />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>{item.name}</div>
                            {item.description && <div style={{ fontSize: "0.8rem", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.description}</div>}
                            <div style={{ display: "flex", gap: "0.3rem", marginTop: "0.25rem", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem", borderRadius: "99px", background: photoCount > 0 ? "#E8F3EE" : "#F5EDE5", color: photoCount > 0 ? "var(--amber)" : "var(--muted)", fontWeight: 600 }}>{photoCount} photo{photoCount === 1 ? "" : "s"}</span>
                              {variantCount > 0 && <span style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem", borderRadius: "99px", background: "#FCEADE", color: "#C24D3C", fontWeight: 600 }}>{variantCount} variant{variantCount === 1 ? "" : "s"}</span>}
                              {hasLong && <span style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem", borderRadius: "99px", background: "#FCEADE", color: "#C24D3C", fontWeight: 600 }}>long desc</span>}
                              {faqCount > 0 && <span style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem", borderRadius: "99px", background: "#FCEADE", color: "#C24D3C", fontWeight: 600 }}>{faqCount} FAQ{faqCount === 1 ? "" : "s"}</span>}
                            </div>
                          </div>
                          <div style={{ fontWeight: 600, color: "var(--amber)", flexShrink: 0 }}>{getItemPriceLabel(item)}</div>
                          <div style={{ color: "var(--muted)", marginLeft: "0.4rem", flexShrink: 0, fontSize: "1.1rem" }}>›</div>
                        </div>
                      );
                    })}
                    <div style={{ marginTop: "0.75rem" }}>
                      <button onClick={() => setItemEditor({ mode: 'new', item: null })} style={{ background: "var(--amber)", color: "#fff", border: "none", padding: "0.5rem 1rem", borderRadius: "8px", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>+ Add Item</button>
                      <span style={{ marginLeft: "0.6rem", fontSize: "0.72rem", color: "var(--muted)" }}>Tap any item above to edit photos &amp; details</span>
                    </div>
                  </React.Fragment>
                ) : (
                  <div>
                    <p style={{ color: "var(--muted)", fontSize: "0.85rem", marginBottom: "0.6rem" }}>No products or services added yet</p>
                    <button onClick={() => setItemEditor({ mode: 'new', item: null })} style={{ background: "var(--amber)", color: "#fff", border: "none", padding: "0.5rem 1rem", borderRadius: "8px", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>+ Add your first item</button>
                  </div>
                )
              )}
            </div>

            {/* -- Owner Actions: Unlist / Delete -- */}
            <div style={{ borderTop: "2px solid var(--border)", marginTop: "2rem", paddingTop: "1.5rem" }}>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem", color: "var(--muted)" }}>Manage Listing</h3>

              {/* Unlist / Relist */}
              {biz.status === "approved" && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem", background: "#FFF8E1", borderRadius: "10px", marginBottom: "1rem" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>Unlist Business</div>
                    <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>Hide from public view. You can relist anytime.</div>
                  </div>
                  <button
                    onClick={() => setExitFeedback("unlist")}
                    disabled={saving}
                    style={{ padding: "0.5rem 1.25rem", background: "#FF8F00", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: saving ? 0.5 : 1, whiteSpace: "nowrap" }}
                  >
                    Unlist
                  </button>
                </div>
              )}

              {biz.status === "unlisted" && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem", background: "#E8F5E9", borderRadius: "10px", marginBottom: "1rem" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>Business is Unlisted</div>
                    <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>Your listing is hidden. Click to make it live again.</div>
                  </div>
                  <button
                    onClick={async () => {
                      setSaving(true);
                      await db.collection("businesses").doc(bizId).update({ status: "approved", unlistedAt: firebase.firestore.FieldValue.delete() });
                      await db.collection("business_audit_log").add({ businessId: bizId, businessName: biz.name, action: "relisted", performedBy: currentUser.uid, performedByRole: "owner", timestamp: firebase.firestore.FieldValue.serverTimestamp() });
                      setUserBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, status: "approved" } : b));
                      showToast("Business is live again!", "success");
                      setSaving(false);
                    }}
                    disabled={saving}
                    style={{ padding: "0.5rem 1.25rem", background: "#2E7D32", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: saving ? 0.5 : 1, whiteSpace: "nowrap" }}
                  >
                    ♻️ Relist
                  </button>
                </div>
              )}

              {/* Delete */}
              <div style={{ padding: "1rem", border: "1px solid #FFCDD2", borderRadius: "10px", background: "#fff" }}>
                <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "#C62828" }}>Delete Business</div>
                <div style={{ fontSize: "0.8rem", color: "var(--muted)", marginBottom: "0.75rem" }}>Permanently remove this listing. This cannot be undone.</div>
                <button
                  onClick={() => setExitFeedback("delete")}
                  disabled={saving}
                  style={{ padding: "0.5rem 1.25rem", background: "#fff", color: "#C62828", border: "2px solid #C62828", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: saving ? 0.5 : 1 }}
                >
                  🗑️ Delete Permanently
                </button>
              </div>
            </div>

            <div style={{ height: "2rem" }} />

            </>}
          </div>

          {/* ── Per-item editor modal (added 2026-05) ── */}
          {itemEditor && (
            <ItemEditorModal
              open={true}
              initialItem={itemEditor.item}
              ownerUid={currentUser && currentUser.uid}
              saving={itemEditorSaving}
              onClose={() => setItemEditor(null)}
              onSave={saveItemFromEditor}
            />
          )}

          {/* ── Exit feedback modal (unlist / delete) — added 2026-07 ── */}
          {exitFeedback && (
            <ExitFeedbackModal
              action={exitFeedback}
              bizName={biz.name}
              saving={saving}
              onCancel={() => { if (!saving) setExitFeedback(null); }}
              onSubmit={async (feedback) => {
                setSaving(true);
                try {
                  // 1) Record the exit feedback (the whole point — do this first so
                  //    the reason is captured even if a later step fails).
                  await db.collection("exit_feedback").add({
                    businessId: bizId,
                    businessName: biz.name || "",
                    ownerId: currentUser.uid,
                    category: biz.category || null,
                    action: exitFeedback === "delete" ? "deleted" : "unlisted",
                    reason: feedback.reason,
                    reasonLabel: feedback.reasonLabel,
                    comment: feedback.comment || "",
                    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                  });

                  if (exitFeedback === "delete") {
                    // Archive for audit, with the real reason (not a hardcoded string).
                    await db.collection("deleted_businesses").doc(bizId).set({
                      ...biz,
                      deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
                      deletedBy: currentUser.uid,
                      deletedByRole: "owner",
                      deleteReason: feedback.reasonLabel,
                      deleteComment: feedback.comment || "",
                    });
                    await db.collection("business_audit_log").add({ businessId: bizId, businessName: biz.name, action: "deleted", performedBy: currentUser.uid, performedByRole: "owner", timestamp: firebase.firestore.FieldValue.serverTimestamp(), reason: feedback.reasonLabel, comment: feedback.comment || "" });
                    await db.collection("businesses").doc(bizId).delete();
                    setUserBusinesses(prev => prev.filter(b => b.id !== bizId));
                    setExitFeedback(null);
                    setSaving(false);
                    navigate("profile");
                    return;
                  }

                  // Unlist path
                  await db.collection("businesses").doc(bizId).update({ status: "unlisted", unlistedAt: firebase.firestore.FieldValue.serverTimestamp() });
                  await db.collection("business_audit_log").add({ businessId: bizId, businessName: biz.name, action: "unlisted", performedBy: currentUser.uid, performedByRole: "owner", timestamp: firebase.firestore.FieldValue.serverTimestamp(), reason: feedback.reasonLabel, comment: feedback.comment || "" });
                  setUserBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, status: "unlisted" } : b));
                  showToast("Business unlisted.", "success");
                  setExitFeedback(null);
                  setSaving(false);
                } catch (err) {
                  console.error("[ExitFeedback] submit failed:", err);
                  showToast("Something went wrong — please try again.", "error");
                  setSaving(false);
                }
              }}
            />
          )}
        </div>
      );
    }

    // ---------------------------------------------------
    // ADMIN PANEL COMPONENT
    // ---------------------------------------------------
    function AdminPanel({ db, storage, currentUser, navigate }) {
      const [adminTab, setAdminTab] = useState("dashboard");
      const [pendingEventCount, setPendingEventCount] = useState(0);
      const [allUsers, setAllUsers] = useState([]);
      const [allBusinesses, setAllBusinesses] = useState([]);
      const [loadingData, setLoadingData] = useState(true);
      const [selectedBiz, setSelectedBiz] = useState(null);
      const [reviewNote, setReviewNote] = useState("");
      // YWCA Hopemaker badge — admin edit state (resets when selectedBiz changes)
      const [ywcaVerifiedLocal, setYwcaVerifiedLocal] = useState(false);
      const [ywcaCohortLocal, setYwcaCohortLocal] = useState("");
      const [ywcaSaving, setYwcaSaving] = useState(false);
      useEffect(function() {
        if (selectedBiz && selectedBiz.ywcaHopemaker) {
          setYwcaVerifiedLocal(!!selectedBiz.ywcaHopemaker.verified);
          setYwcaCohortLocal(selectedBiz.ywcaHopemaker.cohort || "");
        } else if (selectedBiz && selectedBiz.ywcaHopemakerClaim && selectedBiz.ywcaHopemakerClaim.claimed) {
          // Not yet admin-verified, but the owner self-declared at signup/edit —
          // prefill the cohort field as a time-saver. Verified stays unchecked;
          // admin must still confirm against YWCA's roster.
          setYwcaVerifiedLocal(false);
          setYwcaCohortLocal(selectedBiz.ywcaHopemakerClaim.cohort || "");
        } else {
          setYwcaVerifiedLocal(false);
          setYwcaCohortLocal("");
        }
      }, [selectedBiz ? selectedBiz.id : null]);
      const [adminBizSearch, setAdminBizSearch] = useState("");
      const [ywcaFilterOn, setYwcaFilterOn] = useState(false);
      // Data Health tab state
      const [healthScanDone, setHealthScanDone] = useState(false);
      const [healthScanning, setHealthScanning] = useState(false);
      const [healthIssues, setHealthIssues] = useState([]);
      const [healthFixing, setHealthFixing] = useState(false);
      const [healthFixed, setHealthFixed] = useState({});
      const [adminUserSearch, setAdminUserSearch] = useState("");
      const [adminUserSortDir, setAdminUserSortDir] = useState("desc"); // "desc" = newest first
      const [actionLoading, setActionLoading] = useState(false);
      const [analyticsData, setAnalyticsData] = useState([]);
      const [analyticsPeriod, setAnalyticsPeriod] = useState(14); // days
      const [allBookings, setAllBookings] = useState([]);
      const [allConversations, setAllConversations] = useState([]);
      const [selectedConv, setSelectedConv] = useState(null);
      const [convMessages, setConvMessages] = useState([]);
      const [loadingConvMessages, setLoadingConvMessages] = useState(false);
      const [bookingFilter, setBookingFilter] = useState("all");
      const [expandedBooking, setExpandedBooking] = useState(null);
      const [resolvedUidNames, setResolvedUidNames] = useState({});
      const [allWishes, setAllWishes] = useState([]);
      const [wishRejectReason, setWishRejectReason] = useState("");
      const [wishActionLoading, setWishActionLoading] = useState(null); // wish ID being acted on
      const [wishFilter, setWishFilter] = useState("pending"); // pending | approved | rejected | all
      const [topStoresWindow, setTopStoresWindow] = useState("viewCount24h"); // traffic ranking window
      // Exit Feedback tab (why owners unlist/delete) — lazy-loaded on first open
      const [exitFeedbackData, setExitFeedbackData] = useState(null); // null until first load
      const [exitFeedbackLoading, setExitFeedbackLoading] = useState(false);
      const [feedbackActionFilter, setFeedbackActionFilter] = useState("all"); // all | unlisted | deleted

      // Fetch all data on mount
      useEffect(() => {
        const fetchData = async () => {
          setLoadingData(true);
          try {
            const [usersSnap, bizSnap, bookingsSnap, convsSnap, wishesSnap, pendingEventsSnap] = await Promise.all([
              db.collection("users").get(),
              db.collection("businesses").get(),
              db.collection("bookings").get(),
              db.collection("conversations").orderBy("lastMessageAt", "desc").get(),
              db.collection("wishes").orderBy("createdAt", "desc").get(),
              db.collection("events").where("status", "==", "pending").get()
            ]);
            setAllUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setAllBusinesses(bizSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setAllBookings(bookingsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setAllConversations(convsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setAllWishes(wishesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setPendingEventCount(pendingEventsSnap.size);
          } catch (err) { console.error("Admin fetch error:", err); }
          setLoadingData(false);
        };
        fetchData();
      }, []);

      // Lazy-load exit feedback the first time the Exit Feedback tab is opened.
      useEffect(() => {
        if (adminTab !== "feedback" || exitFeedbackData !== null || exitFeedbackLoading) return;
        setExitFeedbackLoading(true);
        db.collection("exit_feedback").orderBy("createdAt", "desc").get()
          .then(snap => setExitFeedbackData(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
          .catch(err => { console.error("Exit feedback fetch error:", err); setExitFeedbackData([]); })
          .then(() => setExitFeedbackLoading(false));
      }, [adminTab]);

      // Resolve any UIDs not found in allUsers or allBusinesses via Cloud Function (Firebase Auth)
      useEffect(() => {
        if (!allConversations.length || !currentUser) return;
        const userMap = {};
        allUsers.forEach(u => { userMap[u.id] = u; });
        const allUids = new Set();
        allConversations.forEach(conv => (conv.participants || []).forEach(uid => allUids.add(uid)));
        const unresolved = [...allUids].filter(uid => {
          if (userMap[uid]) return false;
          if (allBusinesses.find(b => b.ownerId === uid || b.operatorUid === uid)) return false;
          return true;
        });
        if (!unresolved.length) return;
        currentUser.getIdToken().then(token =>
          fetch("https://asia-southeast1-nooks-sg.cloudfunctions.net/adminResolveUids", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ uids: unresolved }),
          })
          .then(r => r.json())
          .then(data => setResolvedUidNames(prev => ({ ...prev, ...data })))
          .catch(err => console.error("adminResolveUids error:", err))
        );
      }, [allConversations]);

      // Load messages when admin opens a conversation
      useEffect(() => {
        if (!selectedConv) { setConvMessages([]); return; }
        const loadMessages = async () => {
          setLoadingConvMessages(true);
          try {
            const snap = await db.collection("conversations").doc(selectedConv.id)
              .collection("messages").orderBy("createdAt", "asc").get();
            setConvMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          } catch (err) { console.error("Conv messages fetch error:", err); }
          setLoadingConvMessages(false);
        };
        loadMessages();
      }, [selectedConv]);

      // Fetch analytics data
      useEffect(() => {
        const fetchAnalytics = async () => {
          try {
            const dates = [];
            for (let i = analyticsPeriod - 1; i >= 0; i--) {
              const d = new Date();
              d.setDate(d.getDate() - i);
              dates.push(d.toISOString().slice(0, 10));
            }
            const snap = await db.collection("analytics_daily")
              .where("date", ">=", dates[0])
              .where("date", "<=", dates[dates.length - 1])
              .orderBy("date")
              .get();
            const dataMap = {};
            snap.docs.forEach(d => { dataMap[d.data().date] = d.data(); });
            // Fill in missing dates with zeros
            const filled = dates.map(date => dataMap[date] || {
              date, pageViews: 0, uniqueVisitors: 0, totalSessions: 0,
              newVisitors: 0, returningVisitors: 0, sources: {},
              totalDuration: 0, bounces: 0, totalPagesPerSession: 0, engagedSessions: 0
            });
            setAnalyticsData(filled);
          } catch (err) { console.error("Analytics fetch error:", err); }
        };
        fetchAnalytics();
      }, [analyticsPeriod]);

      // Stats
      const totalUsers = allUsers.length;
      const totalBusinesses = allBusinesses.length;
      const pendingCount = allBusinesses.filter(b => b.status === "pending").length;
      const approvedCount = allBusinesses.filter(b => b.status === "approved").length;
      const suspendedCount = allBusinesses.filter(b => b.status === "suspended" || b.status === "inactive").length;
      const draftCount = allBusinesses.filter(b => b.status === "draft").length;
      // Booking stats
      const totalBookingsMade = allBookings.length;
      const totalBookingsConfirmed = allBookings.filter(b => b.status === "confirmed" || b.status === "completed").length;

      // Category breakdown
      const categoryBreakdown = allBusinesses.reduce((acc, b) => {
        const cat = b.category || "uncategorized";
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {});

      // Users joined over time (last 6 months)
      const now = new Date();
      const monthlySignups = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const label = d.toLocaleDateString("en-SG", { month: "short", year: "2-digit" });
        const count = allUsers.filter(u => {
          if (!u.joinedAt) return false;
          const joined = u.joinedAt.seconds ? new Date(u.joinedAt.seconds * 1000) : new Date(u.joinedAt);
          return joined.getMonth() === d.getMonth() && joined.getFullYear() === d.getFullYear();
        }).length;
        return { label, count };
      });

      // Business approval actions
      // Audit log helper
      const logAudit = async (bizId, bizName, action, reason) => {
        await db.collection("business_audit_log").add({
          businessId: bizId,
          businessName: bizName,
          action,
          reason: reason || "",
          performedBy: currentUser.uid,
          performedByRole: "admin",
          timestamp: firebase.firestore.FieldValue.serverTimestamp()
        });
      };

      const handleApprove = async (bizId) => {
        setActionLoading(true);
        const biz = allBusinesses.find(b => b.id === bizId);
        const updateData = {
          status: "approved",
          reviewedAt: firebase.firestore.FieldValue.serverTimestamp(),
          reviewedBy: currentUser.uid
        };
        // Apply pending changes (name/cover photo) if they exist
        const pending = biz?.pendingChanges || {};
        const appliedChanges = {};
        if (pending.name) {
          updateData.name = pending.name;
          appliedChanges.name = pending.name;
        }
        if (pending.coverPhotoUrl) {
          updateData.coverPhotoUrl = pending.coverPhotoUrl;
          appliedChanges.coverPhotoUrl = pending.coverPhotoUrl;
        }
        if (pending.slug) {
          updateData.slug = pending.slug;
          appliedChanges.slug = pending.slug;
        }
        // Clear pendingChanges after applying
        updateData.pendingChanges = firebase.firestore.FieldValue.delete();
        updateData.pendingChangeAt = firebase.firestore.FieldValue.delete();

        await db.collection("businesses").doc(bizId).update(updateData);
        const auditNote = Object.keys(appliedChanges).length > 0
          ? "Approved with pending changes applied: " + Object.keys(appliedChanges).join(", ") + (reviewNote.trim() ? " — " + reviewNote.trim() : "")
          : reviewNote.trim();
        await logAudit(bizId, appliedChanges.name || biz?.name, "approved", auditNote);
        setAllBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, ...appliedChanges, status: "approved", pendingChanges: null, pendingChangeAt: null } : b));
        setSelectedBiz(null);
        setReviewNote("");
        setActionLoading(false);
      };

      const handleRequestChanges = async (bizId) => {
        if (!reviewNote.trim()) { showToast("Please add a note explaining what changes are needed.", "warning"); return; }
        setActionLoading(true);
        const biz = allBusinesses.find(b => b.id === bizId);
        await db.collection("businesses").doc(bizId).update({ status: "changes_requested", reviewNote: reviewNote.trim(), reviewedAt: firebase.firestore.FieldValue.serverTimestamp(), reviewedBy: currentUser.uid });
        await logAudit(bizId, biz?.name, "changes_requested", reviewNote.trim());
        setAllBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, status: "changes_requested", reviewNote: reviewNote.trim() } : b));
        setSelectedBiz(null);
        setReviewNote("");
        setActionLoading(false);
      };

      const handleReject = async (bizId) => {
        if (!reviewNote.trim()) { showToast("Please add a reason for rejection.", "warning"); return; }
        setActionLoading(true);
        const biz = allBusinesses.find(b => b.id === bizId);
        await db.collection("businesses").doc(bizId).update({
          status: "rejected",
          reviewNote: reviewNote.trim(),
          reviewedAt: firebase.firestore.FieldValue.serverTimestamp(),
          reviewedBy: currentUser.uid,
          pendingChanges: firebase.firestore.FieldValue.delete(),
          pendingChangeAt: firebase.firestore.FieldValue.delete()
        });
        await logAudit(bizId, biz?.name, "rejected", reviewNote.trim());
        setAllBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, status: "rejected", reviewNote: reviewNote.trim(), pendingChanges: null } : b));
        setSelectedBiz(null);
        setReviewNote("");
        setActionLoading(false);
      };

      const handleSuspend = async (bizId) => {
        if (!reviewNote.trim()) { showToast("Please add a reason for suspension.", "warning"); return; }
        setActionLoading(true);
        const biz = allBusinesses.find(b => b.id === bizId);
        await db.collection("businesses").doc(bizId).update({ status: "suspended", reviewNote: reviewNote.trim(), suspendedAt: firebase.firestore.FieldValue.serverTimestamp(), reviewedAt: firebase.firestore.FieldValue.serverTimestamp(), reviewedBy: currentUser.uid });
        await logAudit(bizId, biz?.name, "suspended", reviewNote.trim());
        setAllBusinesses(prev => prev.map(b => b.id === bizId ? { ...b, status: "suspended", reviewNote: reviewNote.trim() } : b));
        setSelectedBiz(null);
        setReviewNote("");
        setActionLoading(false);
      };

      const [deleteConfirm, setDeleteConfirm] = useState(null);

      const handleDeleteBusiness = async (bizId) => {
        if (!reviewNote.trim()) { showToast("Please add a reason for deletion.", "warning"); return; }
        if (deleteConfirm !== bizId) {
          setDeleteConfirm(bizId);
          return;
        }
        setActionLoading(true);
        const biz = allBusinesses.find(b => b.id === bizId);
        await db.collection("deleted_businesses").doc(bizId).set({
          ...biz,
          deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
          deletedBy: currentUser.uid,
          deleteReason: reviewNote.trim()
        });
        await logAudit(bizId, biz?.name, "deleted", reviewNote.trim());
        await db.collection("businesses").doc(bizId).delete();
        setAllBusinesses(prev => prev.filter(b => b.id !== bizId));
        setSelectedBiz(null);
        setReviewNote("");
        setDeleteConfirm(null);
        setActionLoading(false);
      };

      // -- User management --
      const [selectedUser, setSelectedUser] = useState(null);
      const [userNote, setUserNote] = useState("");
      const [userActionLoading, setUserActionLoading] = useState(false);
      const [userDeleteConfirm, setUserDeleteConfirm] = useState(null);
      const [cascadeDeleteBiz, setCascadeDeleteBiz] = useState(false);

      const handleSuspendUser = async (userId) => {
        if (!userNote.trim()) { showToast("Please add a reason for suspension.", "warning"); return; }
        setUserActionLoading(true);
        await db.collection("users").doc(userId).update({
          status: "suspended",
          suspendReason: userNote.trim(),
          suspendedAt: firebase.firestore.FieldValue.serverTimestamp(),
          suspendedBy: currentUser.uid
        });
        setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, status: "suspended", suspendReason: userNote.trim() } : u));
        setSelectedUser(prev => prev ? { ...prev, status: "suspended", suspendReason: userNote.trim() } : prev);
        setUserNote("");
        setUserActionLoading(false);
      };

      const handleReinstateUser = async (userId) => {
        setUserActionLoading(true);
        await db.collection("users").doc(userId).update({
          status: "active",
          suspendReason: firebase.firestore.FieldValue.delete(),
          suspendedAt: firebase.firestore.FieldValue.delete(),
          suspendedBy: firebase.firestore.FieldValue.delete()
        });
        setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, status: "active", suspendReason: null } : u));
        setSelectedUser(prev => prev ? { ...prev, status: "active", suspendReason: null } : prev);
        setUserActionLoading(false);
      };

      const handleDeleteUser = async (userId) => {
        if (!userNote.trim()) { showToast("Please add a reason for deletion.", "warning"); return; }
        if (userDeleteConfirm !== userId) { setUserDeleteConfirm(userId); return; }
        setUserActionLoading(true);
        const user = allUsers.find(u => u.id === userId);
        // Archive before deleting
        await db.collection("deleted_users").doc(userId).set({
          ...user,
          deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
          deletedBy: currentUser.uid,
          deleteReason: userNote.trim(),
          cascadeDeletedBusinesses: cascadeDeleteBiz
        });
        // Cascade delete businesses if checked
        if (cascadeDeleteBiz) {
          const userBizzes = allBusinesses.filter(b => b.ownerId === userId);
          for (const biz of userBizzes) {
            await db.collection("deleted_businesses").doc(biz.id).set({
              ...biz,
              deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
              deletedBy: currentUser.uid,
              deleteReason: "Cascade: owner account deleted — " + userNote.trim()
            });
            await db.collection("businesses").doc(biz.id).delete();
          }
          setAllBusinesses(prev => prev.filter(b => b.ownerId !== userId));
        }
        await db.collection("users").doc(userId).delete();
        setAllUsers(prev => prev.filter(u => u.id !== userId));
        setSelectedUser(null);
        setUserNote("");
        setUserDeleteConfirm(null);
        setCascadeDeleteBiz(false);
        setUserActionLoading(false);
      };

      const userStatusBadge = (status) => {
        if (status === "suspended") return <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#C62828", background: "#FFEBEE", padding: "0.2rem 0.5rem", borderRadius: "12px" }}>Suspended</span>;
        return <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "#2E7D32", background: "#E8F5E9", padding: "0.2rem 0.5rem", borderRadius: "12px" }}>Active</span>;
      };

      const statusBadge = (status) => {
        const map = {
          approved: { label: "Live", color: "#2E7D32", bg: "#E8F5E9" },
          pending: { label: "Pending", color: "#E65100", bg: "#FFF3E0" },
          changes_requested: { label: "Changes Requested", color: "#6A1B9A", bg: "#F3E5F5" },
          rejected: { label: "Rejected", color: "#C62828", bg: "#FFEBEE" },
          suspended: { label: "Suspended", color: "#C62828", bg: "#FFEBEE" },
          unlisted: { label: "Unlisted", color: "#757575", bg: "#EEEEEE" },
          inactive: { label: "Inactive", color: "#757575", bg: "#F5F5F5" },
          draft: { label: "Draft", color: "#6B6B6B", bg: "#F5F5F5" }
        };
        const s = map[status] || map.draft;
        return <span style={{ fontSize: "0.7rem", fontWeight: 600, color: s.color, background: s.bg, padding: "0.2rem 0.6rem", borderRadius: "20px" }}>{s.label}</span>;
      };

      const pendingWishCount = allWishes.filter(w => w.status === "pending").length;
      const tabs = [
        { key: "dashboard", label: "Dashboard" },
        { key: "users", label: `Users (${totalUsers})` },
        { key: "businesses", label: `Businesses (${totalBusinesses})` },
        { key: "approvals", label: `Approvals (${pendingCount})` },
        { key: "events", label: `Events${pendingEventCount ? ` (${pendingEventCount})` : ""}` },
        { key: "wishes", label: `Wishes${pendingWishCount ? ` (${pendingWishCount})` : ""}` },
        { key: "feedback", label: "Exit Feedback" },
        { key: "analytics", label: "Analytics" },
        { key: "bookings", label: `Bookings (${allBookings.length})` },
        { key: "conversations", label: `Conversations (${allConversations.length})` },
        { key: "health", label: "🩺 Data Health" }
      ];

      if (loadingData) {
        return (
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <div className="spinner" />
            <p style={{ color: "var(--muted)", marginTop: "1rem" }}>Loading admin data...</p>
          </div>
        );
      }

      return (
        <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "1.5rem 1rem" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.6rem", flexWrap: "wrap" }}>
            <h1 style={{ fontFamily: "var(--serif)", fontSize: "1.6rem", marginBottom: "0.25rem" }}>Admin Panel</h1>
            <span
              title="v1.2.0 — Added Exit Feedback tab: see why owners unlist/delete, aggregated by reason, category and month, with a dated detail table. v1.1.0 — Added Events review tab for This Week submissions."
              style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--amber)", background: "#E8F5E9", padding: "0.15rem 0.5rem", borderRadius: "20px", cursor: "help", whiteSpace: "nowrap" }}
            >v1.2.0 ⓘ</span>
          </div>
          <p style={{ color: "var(--muted)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Manage users, businesses, and site operations</p>

          {/* Tab Navigation */}
          <div style={{ display: "flex", gap: "0.25rem", marginBottom: "2rem", borderBottom: "2px solid var(--border)", overflowX: "auto" }}>
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => { setAdminTab(t.key); setSelectedBiz(null); setSelectedConv(null); setBookingFilter("all"); setExpandedBooking(null); }}
                style={{
                  padding: "0.7rem 1.2rem", background: "none", border: "none", cursor: "pointer",
                  fontSize: "0.85rem", fontWeight: adminTab === t.key ? 600 : 400,
                  color: adminTab === t.key ? "var(--amber)" : "var(--muted)",
                  borderBottom: adminTab === t.key ? "2px solid var(--amber)" : "2px solid transparent",
                  marginBottom: "-2px", whiteSpace: "nowrap"
                }}
              >{t.label}</button>
            ))}
          </div>

          {/* -- EVENTS TAB (This Week review queue) -- */}
          {adminTab === "events" && (
            <AdminEventsPage db={db} currentUser={currentUser} isAdmin={true} navigate={navigate} />
          )}

          {/* -- EXIT FEEDBACK TAB (why owners unlist / delete) -- */}
          {adminTab === "feedback" && (() => {
            if (exitFeedbackLoading || exitFeedbackData === null) {
              return (
                <div style={{ textAlign: "center", padding: "3rem" }}>
                  <div className="spinner" />
                  <p style={{ color: "var(--muted)", marginTop: "1rem" }}>Loading exit feedback…</p>
                </div>
              );
            }
            const all = exitFeedbackData;
            const unlistedCount = all.filter(f => f.action === "unlisted").length;
            const deletedCount = all.filter(f => f.action === "deleted").length;
            const rows = feedbackActionFilter === "all" ? all : all.filter(f => f.action === feedbackActionFilter);

            const fmtDateTime = (ts) => {
              const d = ts && ts.toDate ? ts.toDate() : null;
              return d ? d.toLocaleString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
            };

            // Aggregations respect the active action filter
            const byReason = {}; rows.forEach(f => { const k = f.reasonLabel || f.reason || "—"; byReason[k] = (byReason[k] || 0) + 1; });
            const byCategory = {}; rows.forEach(f => { const k = f.category || "Uncategorised"; byCategory[k] = (byCategory[k] || 0) + 1; });
            const monthMap = {};
            rows.forEach(f => {
              const d = f.createdAt && f.createdAt.toDate ? f.createdAt.toDate() : null;
              if (!d) { monthMap["0000-00"] = monthMap["0000-00"] || { label: "Unknown", count: 0 }; monthMap["0000-00"].count++; return; }
              const key = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0");
              monthMap[key] = monthMap[key] || { label: d.toLocaleDateString("en-SG", { month: "short", year: "numeric" }), count: 0 };
              monthMap[key].count++;
            });
            const months = Object.keys(monthMap).sort().reverse().map(k => monthMap[k]);
            const reasonEntries = Object.entries(byReason).sort((a, b) => b[1] - a[1]);
            const categoryEntries = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);
            const maxReason = Math.max(1, ...reasonEntries.map(e => e[1]));
            const maxCat = Math.max(1, ...categoryEntries.map(e => e[1]));

            const BarList = ({ entries, max, color }) => (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {entries.length === 0 && <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No data yet.</p>}
                {entries.map(([label, count]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                    <div style={{ flex: "0 0 42%", fontSize: "0.82rem", color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
                    <div style={{ flex: 1, background: "#F1ECE4", borderRadius: "6px", height: "18px", position: "relative" }}>
                      <div style={{ width: (count / max * 100) + "%", background: color, height: "100%", borderRadius: "6px", minWidth: "2px" }} />
                    </div>
                    <div style={{ flex: "0 0 28px", textAlign: "right", fontSize: "0.82rem", fontWeight: 600 }}>{count}</div>
                  </div>
                ))}
              </div>
            );

            const actionBadge = (action) => {
              const isDel = action === "deleted";
              return <span style={{ fontSize: "0.7rem", fontWeight: 600, color: isDel ? "#C62828" : "#B26A00", background: isDel ? "#FDECEA" : "#FFF3E0", padding: "0.15rem 0.5rem", borderRadius: "20px", whiteSpace: "nowrap" }}>{isDel ? "Deleted" : "Unlisted"}</span>;
            };

            const filterBtn = (key, label) => (
              <button
                onClick={() => setFeedbackActionFilter(key)}
                style={{ padding: "0.4rem 0.9rem", borderRadius: "20px", border: "1px solid " + (feedbackActionFilter === key ? "var(--amber)" : "var(--border)"), background: feedbackActionFilter === key ? "var(--amber)" : "#fff", color: feedbackActionFilter === key ? "#fff" : "var(--muted)", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
              >{label}</button>
            );

            return (
              <div>
                {/* Summary cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
                  {[
                    { label: "Total feedback", value: all.length, color: "#5B4B8A", bg: "#EEEAF6" },
                    { label: "Unlisted", value: unlistedCount, color: "#B26A00", bg: "#FFF3E0" },
                    { label: "Deleted", value: deletedCount, color: "#C62828", bg: "#FDECEA" },
                  ].map(c => (
                    <div key={c.label} style={{ background: c.bg, borderRadius: "12px", padding: "1rem" }}>
                      <div style={{ fontSize: "1.6rem", fontWeight: 700, color: c.color }}>{c.value}</div>
                      <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{c.label}</div>
                    </div>
                  ))}
                </div>

                {all.length === 0 && (
                  <p style={{ color: "var(--muted)", fontSize: "0.9rem", background: "#FBF8F3", padding: "1rem", borderRadius: "10px" }}>
                    No exit feedback yet. Reasons are captured from v1.3.0 onward — businesses unlisted before that won't have a reason here.
                  </p>
                )}

                {all.length > 0 && (
                  <>
                    {/* Filter */}
                    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                      {filterBtn("all", "All (" + all.length + ")")}
                      {filterBtn("unlisted", "Unlisted (" + unlistedCount + ")")}
                      {filterBtn("deleted", "Deleted (" + deletedCount + ")")}
                    </div>

                    {/* Breakdowns */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.25rem", marginBottom: "1.75rem" }}>
                      <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1rem" }}>
                        <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.9rem", fontFamily: "var(--serif)" }}>By reason</h4>
                        <BarList entries={reasonEntries} max={maxReason} color="#C87606" />
                      </div>
                      <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1rem" }}>
                        <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.9rem", fontFamily: "var(--serif)" }}>By category</h4>
                        <BarList entries={categoryEntries} max={maxCat} color="#5B8A72" />
                      </div>
                      <div style={{ border: "1px solid var(--border)", borderRadius: "12px", padding: "1rem" }}>
                        <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.9rem", fontFamily: "var(--serif)" }}>By month</h4>
                        <BarList entries={months.map(m => [m.label, m.count])} max={Math.max(1, ...months.map(m => m.count))} color="#4A78A8" />
                      </div>
                    </div>

                    {/* Detail table */}
                    <h4 style={{ margin: "0 0 0.75rem", fontSize: "0.9rem", fontFamily: "var(--serif)" }}>Details ({rows.length})</h4>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                        <thead>
                          <tr style={{ textAlign: "left", color: "var(--muted)", borderBottom: "2px solid var(--border)" }}>
                            <th style={{ padding: "0.5rem 0.6rem" }}>When</th>
                            <th style={{ padding: "0.5rem 0.6rem" }}>Business</th>
                            <th style={{ padding: "0.5rem 0.6rem" }}>Action</th>
                            <th style={{ padding: "0.5rem 0.6rem" }}>Reason</th>
                            <th style={{ padding: "0.5rem 0.6rem" }}>Comment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(f => (
                            <tr key={f.id} style={{ borderBottom: "1px solid var(--border)", verticalAlign: "top" }}>
                              <td style={{ padding: "0.5rem 0.6rem", whiteSpace: "nowrap", color: "var(--muted)" }}>{fmtDateTime(f.createdAt)}</td>
                              <td style={{ padding: "0.5rem 0.6rem", fontWeight: 600 }}>{f.businessName || "—"}</td>
                              <td style={{ padding: "0.5rem 0.6rem" }}>{actionBadge(f.action)}</td>
                              <td style={{ padding: "0.5rem 0.6rem" }}>{f.reasonLabel || f.reason || "—"}</td>
                              <td style={{ padding: "0.5rem 0.6rem", color: f.comment ? "var(--text)" : "var(--muted)" }}>{f.comment ? f.comment : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* -- DASHBOARD TAB -- */}
          {adminTab === "dashboard" && (
            <div>
              {/* Stat Cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
                {[
                  { label: "Total Users", value: totalUsers, color: "#1565C0", bg: "#E3F2FD" },
                  { label: "Total Businesses", value: totalBusinesses, color: "#2E7D32", bg: "#E8F5E9" },
                  { label: "Pending Approval", value: pendingCount, color: "#E65100", bg: "#FFF3E0" },
                  { label: "Live", value: approvedCount, color: "#2E7D32", bg: "#E8F5E9" },
                  { label: "Suspended", value: suspendedCount, color: "#C62828", bg: "#FFEBEE" },
                  { label: "Bookings Made", value: totalBookingsMade, color: "#6A1B9A", bg: "#F3E5F5" },
                  { label: "Bookings Confirmed", value: totalBookingsConfirmed, color: "#00695C", bg: "#E0F2F1" }
                ].map((s, i) => (
                  <div key={i} style={{ background: s.bg, borderRadius: "12px", padding: "1.25rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.8rem", fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: "0.75rem", color: s.color, fontWeight: 500, marginTop: "0.25rem" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Category Breakdown */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.5rem" }}>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Category Breakdown</h3>
                {Object.entries(categoryBreakdown).length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                    {Object.entries(categoryBreakdown).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                      <div key={cat} style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <span style={{ fontSize: "0.85rem", fontWeight: 500, textTransform: "capitalize", minWidth: "120px" }}>{cat}</span>
                        <div style={{ flex: 1, height: "8px", background: "var(--border)", borderRadius: "4px", overflow: "hidden" }}>
                          <div style={{ width: `${(count / totalBusinesses) * 100}%`, height: "100%", background: "var(--amber)", borderRadius: "4px" }} />
                        </div>
                        <span style={{ fontSize: "0.8rem", color: "var(--muted)", minWidth: "24px", textAlign: "right" }}>{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No businesses yet</p>
                )}
              </div>

              {/* Monthly Signups */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.5rem" }}>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>User Signups (Last 6 Months)</h3>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem", height: "120px" }}>
                  {monthlySignups.map((m, i) => {
                    const maxCount = Math.max(...monthlySignups.map(x => x.count), 1);
                    return (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                        <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--amber)", marginBottom: "0.3rem" }}>{m.count}</span>
                        <div style={{ flex: 1, display: "flex", alignItems: "flex-end", width: "100%" }}>
                          <div style={{ width: "100%", height: `${Math.max((m.count / maxCount) * 100, 4)}%`, background: "var(--amber)", borderRadius: "4px 4px 0 0", minHeight: "4px" }} />
                        </div>
                        <span style={{ fontSize: "0.65rem", color: "var(--muted)", marginTop: "0.4rem" }}>{m.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Business Status Breakdown */}
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem" }}>
                <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Business Status Overview</h3>
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  {[
                    { label: "Live", count: approvedCount, color: "#2E7D32", bg: "#E8F5E9" },
                    { label: "Pending", count: pendingCount, color: "#E65100", bg: "#FFF3E0" },
                    { label: "Draft", count: draftCount, color: "#6B6B6B", bg: "#F5F5F5" },
                    { label: "Suspended", count: suspendedCount, color: "#C62828", bg: "#FFEBEE" }
                  ].map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 1rem", borderRadius: "8px", background: s.bg }}>
                      <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: s.color }} />
                      <span style={{ fontSize: "0.8rem", fontWeight: 500 }}>{s.label}: {s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* -- USERS TAB -- */}
          {adminTab === "users" && !selectedUser && (
            <div>
              {/* Sign-in summary strip: total users, who has ever signed in, total sign-ins */}
              {(() => {
                const signedInEver = allUsers.filter(u => (u.signInCount || 0) > 0).length;
                const totalSignIns = allUsers.reduce((s, u) => s + (u.signInCount || 0), 0);
                const last24 = allUsers.filter(u => {
                  const t = u.lastSignInAt?.seconds ? u.lastSignInAt.seconds * 1000 : 0;
                  return t && (Date.now() - t) < 24 * 60 * 60 * 1000;
                }).length;
                const cardStyle = { flex: 1, minWidth: 140, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "0.9rem 1rem" };
                const numStyle = { fontSize: "1.6rem", fontWeight: 700, fontFamily: "var(--serif)", color: "#E65100", lineHeight: 1.1 };
                const labelStyle = { fontSize: "0.72rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.03em", marginTop: 4 };
                return (
                  <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                    <div style={cardStyle}><div style={numStyle}>{allUsers.length}</div><div style={labelStyle}>Registered users</div></div>
                    <div style={cardStyle}><div style={numStyle}>{signedInEver}</div><div style={labelStyle}>Signed in ever</div></div>
                    <div style={cardStyle}><div style={numStyle}>{last24}</div><div style={labelStyle}>Signed in (last 24h)</div></div>
                    <div style={cardStyle}><div style={numStyle}>{totalSignIns.toLocaleString()}</div><div style={labelStyle}>Cumulative sign-ins</div></div>
                  </div>
                );
              })()}
              <input type="text" value={adminUserSearch} onChange={function(e) { setAdminUserSearch(e.target.value); }}
                placeholder="Search users by name, email, or phone..."
                style={{ width: "100%", padding: "0.65rem 1rem", border: "1px solid var(--border)", borderRadius: "10px", fontSize: "0.85rem", fontFamily: "var(--sans)", background: "var(--card)", marginBottom: "1rem", boxSizing: "border-box" }} />
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ background: "var(--cream)", borderBottom: "1px solid var(--border)" }}>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>User</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Email / Phone</th>
                      <th onClick={() => setAdminUserSortDir(adminUserSortDir === "desc" ? "asc" : "desc")} style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--amber)", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
                        Joined {adminUserSortDir === "desc" ? "▼" : "▲"}
                      </th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)", whiteSpace: "nowrap" }}>Sign-ins</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)", whiteSpace: "nowrap" }}>Last sign-in</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Status</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.filter(function(user) {
                      if (!adminUserSearch.trim()) return true;
                      var q = adminUserSearch.toLowerCase();
                      var name = (user.displayName || ((user.firstName || "") + " " + (user.lastName || "")).trim() || "").toLowerCase();
                      return name.includes(q) || (user.email || "").toLowerCase().includes(q) || (user.phone || "").toLowerCase().includes(q) || (user.role || "").toLowerCase().includes(q);
                    }).sort(function(a, b) {
                      var aT = a.joinedAt?.seconds || 0;
                      var bT = b.joinedAt?.seconds || 0;
                      return adminUserSortDir === "desc" ? bT - aT : aT - bT;
                    }).map(user => (
                      <tr key={user.id} style={{ borderBottom: "1px solid var(--border)", cursor: "pointer" }} onClick={() => { setSelectedUser(user); setUserNote(""); setUserDeleteConfirm(null); setCascadeDeleteBiz(false); }}>
                        <td style={{ padding: "0.75rem 1rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "var(--amber-light)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                              {user.profilePicUrl ? (
                                <img src={user.profilePicUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--amber)" }}>{(user.displayName || "?")[0]}</span>
                              )}
                            </div>
                            <span style={{ fontWeight: 500 }}>{user.displayName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown"}</span>
                          </div>
                        </td>
                        <td style={{ padding: "0.75rem 1rem", color: "var(--muted)" }}>{user.email || user.phone || "—"}</td>
                        <td style={{ padding: "0.75rem 1rem", color: "var(--muted)", fontSize: "0.8rem" }}>
                          {user.joinedAt?.seconds ? new Date(user.joinedAt.seconds * 1000).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td style={{ padding: "0.75rem 1rem", fontSize: "0.85rem", fontWeight: 600, color: (user.signInCount || 0) > 0 ? "var(--ink)" : "var(--muted)" }}>
                          {user.signInCount || 0}
                        </td>
                        <td style={{ padding: "0.75rem 1rem", color: "var(--muted)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                          {user.lastSignInAt?.seconds ? (() => {
                            const ms = user.lastSignInAt.seconds * 1000;
                            const diff = Date.now() - ms;
                            if (diff < 60 * 1000) return "just now";
                            if (diff < 60 * 60 * 1000) return Math.floor(diff / 60000) + "m ago";
                            if (diff < 24 * 60 * 60 * 1000) return Math.floor(diff / 3600000) + "h ago";
                            if (diff < 7 * 24 * 60 * 60 * 1000) return Math.floor(diff / 86400000) + "d ago";
                            return new Date(ms).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "2-digit" });
                          })() : "—"}
                        </td>
                        <td style={{ padding: "0.75rem 1rem" }}>{userStatusBadge(user.status)}</td>
                        <td style={{ padding: "0.75rem 1rem" }}>
                          <span style={{ fontSize: "0.7rem", fontWeight: 600, padding: "0.2rem 0.5rem", borderRadius: "12px", background: user.role === "admin" ? "#FFEBEE" : "#E8F5E9", color: user.role === "admin" ? "#C62828" : "#2E7D32" }}>
                            {user.role === "admin" ? "Admin" : "User"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* -- USER DETAIL PANEL -- */}
          {adminTab === "users" && selectedUser && (
            <div>
              <button onClick={() => setSelectedUser(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--amber)", fontWeight: 600, marginBottom: "1rem", fontSize: "0.9rem" }}>
                ← Back to Users
              </button>
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem" }}>
                {/* User header */}
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
                  <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "var(--amber-light)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                    {selectedUser.profilePicUrl ? (
                      <img src={selectedUser.profilePicUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <span style={{ fontSize: "1.2rem", fontWeight: 600, color: "var(--amber)" }}>{(selectedUser.displayName || "?")[0]}</span>
                    )}
                  </div>
                  <div>
                    <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", margin: 0 }}>
                      {selectedUser.displayName || `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim() || "Unknown"}
                    </h2>
                    <div style={{ fontSize: "0.85rem", color: "var(--muted)", marginTop: "0.15rem" }}>
                      {selectedUser.email || ""}{selectedUser.email && selectedUser.phone ? " — " : ""}{selectedUser.phone || ""}
                    </div>
                    <div style={{ marginTop: "0.35rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      {userStatusBadge(selectedUser.status)}
                      <span style={{ fontSize: "0.7rem", fontWeight: 600, padding: "0.2rem 0.5rem", borderRadius: "12px", background: selectedUser.role === "admin" ? "#FFEBEE" : "#E8F5E9", color: selectedUser.role === "admin" ? "#C62828" : "#2E7D32" }}>
                        {selectedUser.role === "admin" ? "Admin" : "User"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* User info */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.5rem", fontSize: "0.85rem" }}>
                  <div><span style={{ color: "var(--muted)" }}>Joined:</span> {selectedUser.joinedAt?.seconds ? new Date(selectedUser.joinedAt.seconds * 1000).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—"}</div>
                  <div><span style={{ color: "var(--muted)" }}>Birthday:</span> {selectedUser.birthday || "—"}</div>
                  <div><span style={{ color: "var(--muted)" }}>UID:</span> <span style={{ fontSize: "0.75rem", fontFamily: "monospace" }}>{selectedUser.id}</span></div>
                  <div><span style={{ color: "var(--muted)" }}>Businesses:</span> {allBusinesses.filter(b => b.ownerId === selectedUser.id).length}</div>
                </div>

                {/* User's businesses list */}
                {(() => {
                  const userBizzes = allBusinesses.filter(b => b.ownerId === selectedUser.id);
                  return userBizzes.length > 0 ? (
                    <div style={{ marginBottom: "1.5rem" }}>
                      <h3 style={{ fontSize: "0.9rem", fontWeight: 600, marginBottom: "0.5rem" }}>Owned Businesses</h3>
                      {userBizzes.map(biz => (
                        <div key={biz.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
                          <div style={{ width: "32px", height: "32px", borderRadius: "6px", background: "var(--amber-light)", overflow: "hidden", flexShrink: 0 }}>
                            {biz.coverPhotoUrl ? <img src={biz.coverPhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, fontSize: "0.85rem" }}>{biz.name}</div>
                            <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{biz.category} — nooks.sg/{biz.slug}</div>
                          </div>
                          {statusBadge(biz.status)}
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}

                {/* Suspension banner if suspended */}
                {selectedUser.status === "suspended" && (
                  <div style={{ background: "#FFF3E0", border: "1px solid #FFB74D", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1.5rem", fontSize: "0.85rem" }}>
                    <strong style={{ color: "#E65100" }}>Currently Suspended</strong>
                    {selectedUser.suspendReason && <p style={{ margin: "0.25rem 0 0", color: "#BF360C" }}>Reason: {selectedUser.suspendReason}</p>}
                  </div>
                )}

                {/* Admin actions — only show if not viewing yourself */}
                {selectedUser.id !== currentUser.uid ? (
                  <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1.5rem" }}>
                    <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Admin Actions</h3>

                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                      <label className="form-label" style={{ fontSize: "0.85rem" }}>Reason (required for suspend/delete)</label>
                      <textarea className="form-input" rows={3} placeholder="E.g. 'Spam account', 'Repeated policy violations'..." value={userNote} onChange={(e) => { setUserNote(e.target.value); setUserDeleteConfirm(null); }} style={{ resize: "vertical" }} />
                    </div>

                    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
                      {selectedUser.status === "suspended" ? (
                        <button onClick={() => handleReinstateUser(selectedUser.id)} disabled={userActionLoading}
                          style={{ padding: "0.6rem 1.5rem", background: "#2E7D32", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: userActionLoading ? 0.5 : 1 }}>
                          ? Reinstate User
                        </button>
                      ) : (
                        <button onClick={() => handleSuspendUser(selectedUser.id)} disabled={userActionLoading}
                          style={{ padding: "0.6rem 1.5rem", background: "#FF8F00", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: userActionLoading ? 0.5 : 1 }}>
                          ? Suspend User
                        </button>
                      )}
                    </div>

                    {/* Delete — separated as destructive */}
                    <div style={{ borderTop: "1px solid #FFCDD2", paddingTop: "1rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem", marginBottom: "0.75rem", cursor: "pointer" }}>
                        <input type="checkbox" checked={cascadeDeleteBiz} onChange={(e) => setCascadeDeleteBiz(e.target.checked)} />
                        Also delete all businesses owned by this user ({allBusinesses.filter(b => b.ownerId === selectedUser.id).length} business{allBusinesses.filter(b => b.ownerId === selectedUser.id).length !== 1 ? "es" : ""})
                      </label>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <button onClick={() => handleDeleteUser(selectedUser.id)} disabled={userActionLoading}
                          style={{ padding: "0.6rem 1.5rem", background: userDeleteConfirm === selectedUser.id ? "#B71C1C" : "#fff", color: userDeleteConfirm === selectedUser.id ? "#fff" : "#C62828", border: "2px solid #C62828", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: userActionLoading ? 0.5 : 1 }}>
                          {userDeleteConfirm === selectedUser.id ? "⚠️ Click again to permanently delete" : "🗑️ Delete User"}
                        </button>
                        {userDeleteConfirm === selectedUser.id && (
                          <button onClick={() => setUserDeleteConfirm(null)} style={{ padding: "0.6rem 1rem", background: "none", border: "1px solid var(--border)", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem", color: "var(--muted)" }}>Cancel</button>
                        )}
                      </div>
                      <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.5rem" }}>
                        Deletion is permanent. The user profile will be archived for audit. Reason is required.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem", color: "var(--muted)", fontSize: "0.85rem", fontStyle: "italic" }}>
                    You cannot suspend or delete your own admin account.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* -- BUSINESSES TAB -- */}
          {adminTab === "businesses" && (
            <div>
              {/* Top stores by views — sortable via the small toggle */}
              {(() => {
                const win = topStoresWindow; // 'viewCount24h' | 'viewCount7d' | 'viewCount'
                const top = [...allBusinesses]
                  .map(b => ({ ...b, _metric: Number(b[win] || 0) }))
                  .filter(b => b._metric > 0)
                  .sort((a, b) => b._metric - a._metric)
                  .slice(0, 10);
                const labelFor = { viewCount24h: "last 24h", viewCount7d: "last 7 days", viewCount: "all-time" };
                const btnStyle = (active) => ({
                  padding: "0.35rem 0.75rem", fontSize: "0.75rem", fontWeight: 600,
                  border: "1px solid " + (active ? "#E65100" : "var(--border)"),
                  background: active ? "#FFF3E0" : "var(--card)",
                  color: active ? "#E65100" : "var(--muted)",
                  borderRadius: 999, cursor: "pointer", marginRight: "0.4rem"
                });
                return (
                  <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "1rem", marginBottom: "1rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
                      <div style={{ fontWeight: 600, fontFamily: "var(--serif)", fontSize: "1rem" }}>
                        Top stores by views — {labelFor[win]}
                      </div>
                      <div>
                        <button onClick={() => setTopStoresWindow("viewCount24h")} style={btnStyle(win === "viewCount24h")}>24h</button>
                        <button onClick={() => setTopStoresWindow("viewCount7d")} style={btnStyle(win === "viewCount7d")}>7d</button>
                        <button onClick={() => setTopStoresWindow("viewCount")} style={btnStyle(win === "viewCount")}>All-time</button>
                      </div>
                    </div>
                    {top.length === 0 ? (
                      <div style={{ color: "var(--muted)", fontSize: "0.85rem", fontStyle: "italic" }}>No views recorded in this window yet.</div>
                    ) : (
                      <div>
                        {top.map((b, i) => (
                          <div key={b.id} onClick={() => { setSelectedBiz(b); setAdminTab("approvals"); }}
                               style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0.25rem", borderBottom: i < top.length - 1 ? "1px solid var(--border)" : "none", cursor: "pointer" }}>
                            <div style={{ width: 24, fontWeight: 700, color: "#E65100", fontFamily: "var(--serif)" }}>{i + 1}</div>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: "var(--amber-light)", overflow: "hidden", flexShrink: 0 }}>
                              {b.coverPhotoUrl ? <img src={b.coverPhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</div>
                              <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                                24h {b.viewCount24h || 0} &middot; 7d {b.viewCount7d || 0} &middot; all {b.viewCount || 0}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: "#E65100", fontFamily: "var(--serif)", fontSize: "1.1rem" }}>
                              {b._metric.toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div style={{ marginBottom: "1rem" }}>
                {/* Filter chip row */}
                {(() => {
                  var ywcaCount = allBusinesses.filter(function(b){ return b && b.ywcaHopemaker && b.ywcaHopemaker.verified; }).length;
                  return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.6rem", alignItems: "center" }}>
                      <button
                        onClick={function(){ setYwcaFilterOn(function(v){ return !v; }); }}
                        title={ywcaFilterOn ? "Showing only YWCA Hopemakers — click to clear" : "Show only YWCA Hopemakers"}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "8px",
                          padding: "0.4rem 0.9rem", borderRadius: "999px",
                          border: "1px solid " + (ywcaFilterOn ? "#1F2A6F" : "var(--border)"),
                          background: ywcaFilterOn ? "#1F2A6F" : "#fff",
                          color: ywcaFilterOn ? "#fff" : "#1F2A6F",
                          fontWeight: 600, fontSize: "0.78rem", cursor: "pointer",
                          fontFamily: "var(--sans)", transition: "all 0.15s"
                        }}
                      >
                        <span style={{ display: "inline-block", width: "12px", height: "12px", position: "relative", flexShrink: 0 }}>
                          <span style={{ position: "absolute", left: "-1px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid " + (ywcaFilterOn ? "#fff" : "#5DA8D6") }} />
                          <span style={{ position: "absolute", left: "1.5px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid " + (ywcaFilterOn ? "#fff" : "#1F2A6F") }} />
                          <span style={{ position: "absolute", left: "4.5px", top: 0, width: 0, height: 0, borderLeft: "4.5px solid transparent", borderRight: "4.5px solid transparent", borderTop: "7.5px solid " + (ywcaFilterOn ? "#fff" : "#D6336C") }} />
                        </span>
                        YWCA Hopemakers ({ywcaCount})
                        {ywcaFilterOn && <span style={{ marginLeft: "2px", opacity: 0.85, fontSize: "0.9rem", lineHeight: 1 }}>×</span>}
                      </button>
                      {ywcaFilterOn && (
                        <span style={{ fontSize: "0.72rem", color: "var(--muted)" }}>Filter active</span>
                      )}
                    </div>
                  );
                })()}
                <input
                  type="text"
                  value={adminBizSearch}
                  onChange={function(e) { setAdminBizSearch(e.target.value); }}
                  placeholder="Search businesses by name, category, or owner..."
                  style={{ width: "100%", padding: "0.65rem 1rem", border: "1px solid var(--border)", borderRadius: "10px", fontSize: "0.85rem", fontFamily: "var(--sans)", background: "var(--card)" }}
                />
              </div>
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ background: "var(--cream)", borderBottom: "1px solid var(--border)" }}>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Business</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Category</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Owner</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Status</th>
                      <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: "var(--muted)" }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBusinesses.filter(function(biz) {
                      if (ywcaFilterOn && !(biz.ywcaHopemaker && biz.ywcaHopemaker.verified)) return false;
                      if (!adminBizSearch.trim()) return true;
                      var q = adminBizSearch.toLowerCase();
                      return (biz.name || "").toLowerCase().includes(q) || (biz.category || "").toLowerCase().includes(q) || (biz.ownerName || "").toLowerCase().includes(q) || (biz.slug || "").toLowerCase().includes(q);
                    }).map(biz => (
                      <tr key={biz.id} style={{ borderBottom: "1px solid var(--border)", cursor: "pointer" }} onClick={() => { setSelectedBiz(biz); setAdminTab("approvals"); }}>
                        <td style={{ padding: "0.75rem 1rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "var(--amber-light)", overflow: "hidden", flexShrink: 0 }}>
                              {biz.coverPhotoUrl ? <img src={biz.coverPhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                {biz.name}
                                {biz.ywcaHopemaker && biz.ywcaHopemaker.verified && (
                                  <span title={"YWCA Hopemaker" + (biz.ywcaHopemaker.cohort ? " · " + biz.ywcaHopemaker.cohort : "")} style={{ display: "inline-flex", alignItems: "center", gap: "3px", background: "#fff", border: "1px solid #1F2A6F", color: "#1F2A6F", fontWeight: 700, fontSize: "0.62rem", padding: "1px 6px 1px 4px", borderRadius: "999px", lineHeight: 1.4 }}>
                                    <span style={{ display: "inline-block", width: "8px", height: "8px", position: "relative", flexShrink: 0 }}>
                                      <span style={{ position: "absolute", left: "-1px", top: 0, width: 0, height: 0, borderLeft: "3px solid transparent", borderRight: "3px solid transparent", borderTop: "5px solid #5DA8D6" }} />
                                      <span style={{ position: "absolute", left: "1px", top: 0, width: 0, height: 0, borderLeft: "3px solid transparent", borderRight: "3px solid transparent", borderTop: "5px solid #1F2A6F" }} />
                                      <span style={{ position: "absolute", left: "3px", top: 0, width: 0, height: 0, borderLeft: "3px solid transparent", borderRight: "3px solid transparent", borderTop: "5px solid #D6336C" }} />
                                    </span>
                                    YWCA
                                  </span>
                                )}
                                {biz.ywcaHopemakerClaim && biz.ywcaHopemakerClaim.claimed && !(biz.ywcaHopemaker && biz.ywcaHopemaker.verified) && (
                                  <span title={"Self-declared, unverified" + (biz.ywcaHopemakerClaim.cohort ? " · " + biz.ywcaHopemakerClaim.cohort : "")} style={{ display: "inline-flex", alignItems: "center", background: "#FFF8E1", border: "1px solid #F5C842", color: "#8A6D00", fontWeight: 700, fontSize: "0.6rem", padding: "1px 6px", borderRadius: "999px" }}>
                                    YWCA claim
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>nooks.sg/{biz.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "0.75rem 1rem", textTransform: "capitalize", color: "var(--muted)" }}>{biz.category}</td>
                        <td style={{ padding: "0.75rem 1rem", color: "var(--muted)" }}>{biz.ownerName || "Unknown"}</td>
                        <td style={{ padding: "0.75rem 1rem" }}>{statusBadge(biz.status)}</td>
                        <td style={{ padding: "0.75rem 1rem", color: "var(--muted)", fontSize: "0.8rem" }}>
                          {biz.createdAt?.seconds ? new Date(biz.createdAt.seconds * 1000).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* -- APPROVALS TAB -- */}
          {adminTab === "approvals" && !selectedBiz && (
            <div>
              <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", marginBottom: "1rem" }}>Pending Review ({pendingCount})</h3>
              {allBusinesses.filter(b => b.status === "pending").length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {allBusinesses.filter(b => b.status === "pending").map(biz => (
                    <div key={biz.id} onClick={() => { setSelectedBiz(biz); setReviewNote(""); }} style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                      <div style={{ width: "56px", height: "56px", borderRadius: "10px", overflow: "hidden", flexShrink: 0, background: "var(--amber-light)" }}>
                        {biz.coverPhotoUrl ? <img src={biz.coverPhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, marginBottom: "0.15rem", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                          {biz.name}
                          {biz.ywcaHopemakerClaim && biz.ywcaHopemakerClaim.claimed && !(biz.ywcaHopemaker && biz.ywcaHopemaker.verified) && (
                            <span title="Owner self-declared YWCA Hopemaker — cross-check before verifying" style={{ display: "inline-flex", alignItems: "center", background: "#FFF8E1", border: "1px solid #F5C842", color: "#8A6D00", fontWeight: 700, fontSize: "0.6rem", padding: "1px 6px", borderRadius: "999px" }}>
                              YWCA claim
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{biz.category} — by {biz.ownerName || "Unknown"}</div>
                        <div style={{ fontSize: "0.75rem", color: "var(--light)" }}>
                          {biz.createdAt?.seconds ? `Submitted ${new Date(biz.createdAt.seconds * 1000).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                        </div>
                      </div>
                      <span style={{ fontSize: "0.85rem", color: "var(--amber)", fontWeight: 600 }}>Review →</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "3rem", background: "var(--card)", borderRadius: "12px", border: "1px solid var(--border)" }}>
                  <p style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>✅</p>
                  <p style={{ color: "var(--muted)" }}>All caught up! No pending approvals.</p>
                </div>
              )}

              {/* Recently Reviewed */}
              {allBusinesses.filter(b => ["approved", "rejected", "changes_requested"].includes(b.status)).length > 0 && (
                <div style={{ marginTop: "2rem" }}>
                  <h3 style={{ fontFamily: "var(--serif)", fontSize: "1.1rem", marginBottom: "1rem" }}>Recently Reviewed</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {allBusinesses.filter(b => ["approved", "rejected", "changes_requested"].includes(b.status)).slice(0, 10).map(biz => (
                      <div key={biz.id} onClick={() => { setSelectedBiz(biz); setReviewNote(""); }} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "10px", cursor: "pointer", fontSize: "0.85rem" }}>
                        <span style={{ fontWeight: 500, flex: 1 }}>{biz.name}</span>
                        {statusBadge(biz.status)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* -- APPROVAL DETAIL VIEW -- */}
          {adminTab === "approvals" && selectedBiz && (
            <div>
              <button onClick={() => setSelectedBiz(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", fontSize: "0.85rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                ← Back to list
              </button>

              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}>
                {/* Cover Image */}
                {selectedBiz.coverPhotoUrl && (
                  <div style={{ height: "200px", overflow: "hidden" }}>
                    <img src={selectedBiz.coverPhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                )}

                <div style={{ padding: "1.5rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                    <div>
                      <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.3rem", marginBottom: "0.25rem" }}>{selectedBiz.name}</h2>
                      <p style={{ fontSize: "0.85rem", color: "var(--muted)" }}>nooks.sg/{selectedBiz.slug} — {selectedBiz.category}</p>
                    </div>
                    {statusBadge(selectedBiz.status)}
                  </div>

                  <p style={{ fontSize: "0.9rem", lineHeight: 1.6, marginBottom: "1.25rem", color: "var(--text)" }}>{selectedBiz.description}</p>

                  {/* Details Grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.5rem", fontSize: "0.85rem" }}>
                    <div>
                      <span style={{ fontWeight: 600, display: "block", marginBottom: "0.2rem" }}>Owner</span>
                      <a href="#" onClick={function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        var user = allUsers.find(function(u) { return u.id === selectedBiz.ownerId; });
                        if (user) {
                          setSelectedBiz(null);
                          setSelectedUser(user);
                          setUserNote("");
                          setAdminTab("users");
                        }
                      }} style={{ color: "var(--amber)", textDecoration: "none", cursor: "pointer", fontWeight: 500 }}
                        onMouseEnter={function(e) { e.currentTarget.style.textDecoration = "underline"; }}
                        onMouseLeave={function(e) { e.currentTarget.style.textDecoration = "none"; }}>
                        {selectedBiz.ownerName || "Unknown"}
                      </a>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, display: "block", marginBottom: "0.2rem" }}>Location</span>
                      <span style={{ color: "var(--muted)" }}>{selectedBiz.address || "—"}</span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, display: "block", marginBottom: "0.2rem" }}>Products/Services</span>
                      <span style={{ color: "var(--muted)" }}>{selectedBiz.menuItems?.length || 0} items</span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 600, display: "block", marginBottom: "0.2rem" }}>Submitted</span>
                      <span style={{ color: "var(--muted)" }}>
                        {selectedBiz.createdAt?.seconds ? new Date(selectedBiz.createdAt.seconds * 1000).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </span>
                    </div>
                  </div>

                  {/* Social Media */}
                  {selectedBiz.socialMedia && Object.values(selectedBiz.socialMedia).some(v => v) && (
                    <div style={{ marginBottom: "1.5rem" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.85rem", display: "block", marginBottom: "0.5rem" }}>Social Media</span>
                      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        {Object.entries(selectedBiz.socialMedia).filter(([_, v]) => v).map(([platform, handle]) => (
                          <span key={platform} style={{ fontSize: "0.75rem", padding: "0.3rem 0.7rem", borderRadius: "20px", background: "var(--amber-light)", color: "var(--amber)", fontWeight: 500 }}>
                            {platform}: {handle}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending Changes — show what the owner wants to change */}
                  {selectedBiz.pendingChanges && Object.keys(selectedBiz.pendingChanges).length > 0 && (
                    <div style={{ background: "#FFF3E0", border: "2px solid #FFB74D", borderRadius: "10px", padding: "1rem 1.25rem", marginBottom: "1.5rem" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#E65100", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        📝 Pending Changes Requested
                      </div>
                      {selectedBiz.pendingChanges.name && (
                        <div style={{ marginBottom: "0.75rem" }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Business Name</span>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.25rem" }}>
                            <span style={{ fontSize: "0.9rem", textDecoration: "line-through", color: "#999" }}>{selectedBiz.name}</span>
                            <span style={{ color: "#E65100", fontWeight: 600 }}>→</span>
                            <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "#E65100" }}>{selectedBiz.pendingChanges.name}</span>
                          </div>
                        </div>
                      )}
                      {selectedBiz.pendingChanges.slug && (
                        <div style={{ marginBottom: "0.75rem" }}>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Web Address</span>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.25rem" }}>
                            <span style={{ fontSize: "0.9rem", textDecoration: "line-through", color: "#999" }}>nooks.sg/{selectedBiz.slug}</span>
                            <span style={{ color: "#E65100", fontWeight: 600 }}>→</span>
                            <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "#E65100" }}>nooks.sg/{selectedBiz.pendingChanges.slug}</span>
                          </div>
                        </div>
                      )}
                      {selectedBiz.pendingChanges.coverPhotoUrl && (
                        <div>
                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Cover Photo</span>
                          <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.4rem", alignItems: "center" }}>
                            <div>
                              <div style={{ fontSize: "0.65rem", color: "var(--muted)", marginBottom: "0.2rem" }}>Current</div>
                              <img src={selectedBiz.coverPhotoUrl || ""} alt="Current" style={{ width: "120px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "2px solid #ccc" }} />
                            </div>
                            <span style={{ color: "#E65100", fontWeight: 700, fontSize: "1.2rem" }}>→</span>
                            <div>
                              <div style={{ fontSize: "0.65rem", color: "#E65100", marginBottom: "0.2rem", fontWeight: 600 }}>New</div>
                              <img src={selectedBiz.pendingChanges.coverPhotoUrl} alt="New" style={{ width: "120px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "2px solid #FFB74D" }} />
                            </div>
                          </div>
                        </div>
                      )}
                      {selectedBiz.pendingChangeAt?.seconds && (
                        <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.75rem" }}>
                          Submitted {new Date(selectedBiz.pendingChangeAt.seconds * 1000).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Gallery */}
                  {selectedBiz.galleryUrls?.length > 0 && (
                    <div style={{ marginBottom: "1.5rem" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.85rem", display: "block", marginBottom: "0.5rem" }}>Gallery</span>
                      <div style={{ display: "flex", gap: "0.5rem", overflowX: "auto" }}>
                        {selectedBiz.galleryUrls.map((url, i) => (
                          <img key={i} src={url} alt="" style={{ width: "100px", height: "80px", objectFit: "cover", borderRadius: "8px" }} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Previous Review Note */}
                  {selectedBiz.reviewNote && (
                    <div style={{ background: "#FFF8E1", border: "1px solid #FFD54F", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1.5rem" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.8rem", display: "block", marginBottom: "0.25rem" }}>Previous Review Note</span>
                      <p style={{ fontSize: "0.85rem", color: "#5D4037" }}>{selectedBiz.reviewNote}</p>
                    </div>
                  )}

                  {/* ── Owner's self-declared Hopemaker claim — cross-check prompt ── */}
                  {selectedBiz.ywcaHopemakerClaim && selectedBiz.ywcaHopemakerClaim.claimed && !(selectedBiz.ywcaHopemaker && selectedBiz.ywcaHopemaker.verified) && (
                    <div style={{ background: "#FFF8E1", border: "1px solid #F5C842", borderRadius: "10px", padding: "0.85rem 1rem", marginBottom: "1.5rem" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#8A6D00", marginBottom: "0.25rem" }}>
                        ⚠ Owner self-declared: YWCA Hopemaker{selectedBiz.ywcaHopemakerClaim.cohort ? " · Cohort: " + selectedBiz.ywcaHopemakerClaim.cohort : ""}
                      </div>
                      <p style={{ fontSize: "0.78rem", color: "#5D4E00" }}>
                        Not yet verified. Cross-check with YWCA's roster before applying the badge below.
                      </p>
                    </div>
                  )}

                  {/* ── YWCA Hopemaker partner badge control ── */}
                  <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1.5rem", marginBottom: "1.5rem" }}>
                    <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ display: "inline-block", width: "14px", height: "14px", position: "relative", flexShrink: 0 }}>
                        <span style={{ position: "absolute", left: "-1px", top: 0, width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "9px solid #5DA8D6" }} />
                        <span style={{ position: "absolute", left: "2px", top: 0, width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "9px solid #1F2A6F" }} />
                        <span style={{ position: "absolute", left: "5px", top: 0, width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "9px solid #D6336C" }} />
                      </span>
                      YWCA Hopemaker badge
                    </h3>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.85rem" }}>
                      Verify this business as a graduate of YWCA of Singapore's Hopemaker: Women's Business Accelerator. The badge appears on listings and on the detail page, linked to /hopemakers.
                    </p>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0.75rem", cursor: "pointer", fontSize: "0.9rem" }}>
                      <input
                        type="checkbox"
                        checked={ywcaVerifiedLocal}
                        onChange={function(e) { setYwcaVerifiedLocal(e.target.checked); }}
                        style={{ width: "16px", height: "16px", cursor: "pointer" }}
                      />
                      <span>Verified YWCA Hopemaker</span>
                    </label>
                    <div className="form-group" style={{ marginBottom: "0.85rem" }}>
                      <label className="form-label" style={{ fontSize: "0.8rem" }}>Cohort label (optional)</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. 2026-Q1 or Hopemaker '26 (Jan–Apr)"
                        value={ywcaCohortLocal}
                        onChange={function(e) { setYwcaCohortLocal(e.target.value); }}
                        disabled={!ywcaVerifiedLocal}
                        style={{ fontSize: "0.85rem", padding: "0.5rem 0.75rem" }}
                      />
                    </div>
                    {selectedBiz.ywcaHopemaker && selectedBiz.ywcaHopemaker.verifiedAt && (
                      <p style={{ fontSize: "0.72rem", color: "var(--muted)", marginBottom: "0.75rem" }}>
                        Last verified by {selectedBiz.ywcaHopemaker.verifiedBy || "admin"} on {selectedBiz.ywcaHopemaker.verifiedAt.seconds ? new Date(selectedBiz.ywcaHopemaker.verifiedAt.seconds * 1000).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </p>
                    )}
                    <button
                      disabled={ywcaSaving}
                      onClick={async function() {
                        setYwcaSaving(true);
                        try {
                          var payload = ywcaVerifiedLocal
                            ? {
                                ywcaHopemaker: {
                                  verified: true,
                                  cohort: ywcaCohortLocal || null,
                                  verifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
                                  verifiedBy: (currentUser && currentUser.email) || (currentUser && currentUser.uid) || "admin"
                                }
                              }
                            : { ywcaHopemaker: firebase.firestore.FieldValue.delete() };
                          await db.collection("businesses").doc(selectedBiz.id).update(payload);
                          var nextSelected = Object.assign({}, selectedBiz);
                          if (ywcaVerifiedLocal) {
                            nextSelected.ywcaHopemaker = {
                              verified: true,
                              cohort: ywcaCohortLocal || null,
                              verifiedAt: { seconds: Math.floor(Date.now() / 1000) },
                              verifiedBy: (currentUser && currentUser.email) || "admin"
                            };
                          } else {
                            delete nextSelected.ywcaHopemaker;
                          }
                          setSelectedBiz(nextSelected);
                          showToast(ywcaVerifiedLocal ? "YWCA Hopemaker badge applied" : "YWCA Hopemaker badge removed", "success");
                        } catch (err) {
                          console.error("YWCA badge save error", err);
                          showToast("Failed to save YWCA status: " + (err.message || err), "error");
                        } finally {
                          setYwcaSaving(false);
                        }
                      }}
                      style={{ background: "#1F2A6F", color: "#fff", border: "none", padding: "0.55rem 1.1rem", borderRadius: "999px", fontWeight: 600, fontSize: "0.82rem", cursor: ywcaSaving ? "not-allowed" : "pointer", opacity: ywcaSaving ? 0.6 : 1 }}
                    >
                      {ywcaSaving ? "Saving…" : (selectedBiz.ywcaHopemaker && selectedBiz.ywcaHopemaker.verified ? "Update YWCA badge" : "Apply YWCA badge")}
                    </button>
                  </div>

                  {/* Admin Actions */}
                  <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1.5rem" }}>
                    <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Admin Actions</h3>

                    <div className="form-group" style={{ marginBottom: "1rem" }}>
                      <label className="form-label" style={{ fontSize: "0.85rem" }}>Reason / Note to owner (required for all actions except Approve)</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        placeholder="E.g. 'Fraudulent listing', 'Duplicate business', 'Violates community guidelines...'"
                        value={reviewNote}
                        onChange={(e) => { setReviewNote(e.target.value); setDeleteConfirm(null); }}
                        style={{ resize: "vertical" }}
                      />
                    </div>

                    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                      {selectedBiz.status !== "approved" && (
                        <button onClick={() => handleApprove(selectedBiz.id)} disabled={actionLoading}
                          style={{ padding: "0.6rem 1.5rem", background: "#2E7D32", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: actionLoading ? 0.5 : 1 }}>
                          ? Approve
                        </button>
                      )}
                      <button onClick={() => handleRequestChanges(selectedBiz.id)} disabled={actionLoading}
                        style={{ padding: "0.6rem 1.5rem", background: "#6A1B9A", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: actionLoading ? 0.5 : 1 }}>
                        ? Request Changes
                      </button>
                      {selectedBiz.status !== "rejected" && (
                        <button onClick={() => handleReject(selectedBiz.id)} disabled={actionLoading}
                          style={{ padding: "0.6rem 1.5rem", background: "#C62828", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: actionLoading ? 0.5 : 1 }}>
                          ? Reject
                        </button>
                      )}
                      {selectedBiz.status === "approved" && (
                        <button onClick={() => handleSuspend(selectedBiz.id)} disabled={actionLoading}
                          style={{ padding: "0.6rem 1.5rem", background: "#FF8F00", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: actionLoading ? 0.5 : 1 }}>
                          ? Suspend
                        </button>
                      )}
                    </div>

                    {/* Delete — separated visually as a destructive action */}
                    <div style={{ borderTop: "1px solid #FFCDD2", paddingTop: "1rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        <button onClick={() => handleDeleteBusiness(selectedBiz.id)} disabled={actionLoading}
                          style={{ padding: "0.6rem 1.5rem", background: deleteConfirm === selectedBiz.id ? "#B71C1C" : "#fff", color: deleteConfirm === selectedBiz.id ? "#fff" : "#C62828", border: "2px solid #C62828", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: actionLoading ? 0.5 : 1 }}>
                          {deleteConfirm === selectedBiz.id ? "⚠️ Click again to permanently delete" : "🗑️ Delete Business"}
                        </button>
                        {deleteConfirm === selectedBiz.id && (
                          <button onClick={() => setDeleteConfirm(null)} style={{ padding: "0.6rem 1rem", background: "none", border: "1px solid var(--border)", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem", color: "var(--muted)" }}>
                            Cancel
                          </button>
                        )}
                      </div>
                      <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.5rem" }}>
                        Deletion is permanent. The listing will be archived for audit and removed from the site. Reason is required.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* -- ANALYTICS TAB -- */}
          {adminTab === "analytics" && (() => {
            // Compute aggregate metrics
            const totals = analyticsData.reduce((acc, d) => {
              acc.pageViews += d.pageViews || 0;
              acc.uniqueVisitors += d.uniqueVisitors || 0;
              acc.totalSessions += d.totalSessions || 0;
              acc.newVisitors += d.newVisitors || 0;
              acc.returningVisitors += d.returningVisitors || 0;
              acc.totalDuration += d.totalDuration || 0;
              acc.bounces += d.bounces || 0;
              acc.totalPagesPerSession += d.totalPagesPerSession || 0;
              acc.engagedSessions += d.engagedSessions || 0;
              // Aggregate sources
              if (d.sources) {
                Object.entries(d.sources).forEach(([src, count]) => {
                  acc.sources[src] = (acc.sources[src] || 0) + count;
                });
              }
              return acc;
            }, { pageViews: 0, uniqueVisitors: 0, totalSessions: 0, newVisitors: 0, returningVisitors: 0, totalDuration: 0, bounces: 0, totalPagesPerSession: 0, engagedSessions: 0, sources: {} });

            const avgSessionDuration = totals.totalSessions > 0 ? Math.round(totals.totalDuration / totals.totalSessions) : 0;
            const bounceRate = totals.totalSessions > 0 ? Math.round((totals.bounces / totals.totalSessions) * 100) : 0;
            const pagesPerSession = totals.totalSessions > 0 ? (totals.totalPagesPerSession / totals.totalSessions).toFixed(1) : "0.0";
            const engagementRate = totals.totalSessions > 0 ? Math.round((totals.engagedSessions / totals.totalSessions) * 100) : 0;

            const formatDuration = (secs) => {
              if (secs < 60) return `${secs}s`;
              const m = Math.floor(secs / 60);
              const s = secs % 60;
              return `${m}m ${s}s`;
            };

            // Mini sparkline/bar chart component
            const MiniChart = ({ data, dataKey, color, height = 100 }) => {
              const values = data.map(d => d[dataKey] || 0);
              const maxVal = Math.max(...values, 1);
              const barWidth = Math.max(100 / values.length, 2);
              return (
                <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: `${height}px`, padding: "0 0.25rem" }}>
                  {values.map((v, i) => (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%" }}>
                      <div style={{ flex: 1, display: "flex", alignItems: "flex-end", width: "100%", position: "relative" }}>
                        <div
                          title={`${data[i].date}: ${v}`}
                          style={{
                            width: "100%",
                            height: `${Math.max((v / maxVal) * 100, 3)}%`,
                            background: color,
                            borderRadius: "2px 2px 0 0",
                            minHeight: "2px",
                            opacity: 0.85,
                            transition: "height 0.3s"
                          }}
                        />
                        {data.length <= 14 && v > 0 && (
                          <span style={{
                            position: "absolute",
                            bottom: `calc(${Math.max((v / maxVal) * 100, 3)}% + 2px)`,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            color: color,
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                            lineHeight: 1
                          }}>{v}</span>
                        )}
                      </div>
                      {data.length <= 14 && (
                        <span style={{ fontSize: "0.55rem", color: "var(--light)", marginTop: "3px", transform: "rotate(-45deg)", transformOrigin: "center", whiteSpace: "nowrap" }}>
                          {new Date(data[i].date + "T00:00:00").toLocaleDateString("en-SG", { day: "numeric", month: "short" })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );
            };

            // Donut chart component
            const DonutChart = ({ segments, size = 140 }) => {
              const total = segments.reduce((sum, s) => sum + s.value, 0);
              if (total === 0) return <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>No data yet</p>;
              let cumulative = 0;
              const radius = size / 2 - 10;
              const center = size / 2;
              const strokeWidth = 24;
              const circumference = 2 * Math.PI * radius;

              return (
                <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    {segments.map((seg, i) => {
                      const pct = seg.value / total;
                      const offset = cumulative * circumference;
                      cumulative += pct;
                      return (
                        <circle key={i} cx={center} cy={center} r={radius} fill="none"
                          stroke={seg.color} strokeWidth={strokeWidth}
                          strokeDasharray={`${pct * circumference} ${circumference}`}
                          strokeDashoffset={-offset}
                          transform={`rotate(-90 ${center} ${center})`}
                        />
                      );
                    })}
                    <text x={center} y={center - 6} textAnchor="middle" fontSize="1.2rem" fontWeight="700" fill="var(--text)">{total}</text>
                    <text x={center} y={center + 12} textAnchor="middle" fontSize="0.6rem" fill="var(--muted)">TOTAL</text>
                  </svg>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                    {segments.map((seg, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem" }}>
                        <div style={{ width: "10px", height: "10px", borderRadius: "50%", background: seg.color }} />
                        <span style={{ color: "var(--muted)" }}>{seg.label}</span>
                        <span style={{ fontWeight: 600 }}>{seg.value}</span>
                        <span style={{ color: "var(--light)", fontSize: "0.7rem" }}>({total > 0 ? Math.round((seg.value / total) * 100) : 0}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            };

            const sourceLabels = {
              direct: "Direct",
              organic_search: "Organic Search",
              social: "Social Media",
              referral: "Referral"
            };

            return (
              <div>
                {/* Period Selector */}
                <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
                  {[7, 14, 30].map(p => (
                    <button key={p} onClick={() => setAnalyticsPeriod(p)}
                      style={{
                        padding: "0.4rem 1rem", borderRadius: "20px", border: "1px solid var(--border)",
                        background: analyticsPeriod === p ? "var(--amber)" : "white",
                        color: analyticsPeriod === p ? "white" : "var(--muted)",
                        cursor: "pointer", fontSize: "0.8rem", fontWeight: 500
                      }}>
                      Last {p} days
                    </button>
                  ))}
                </div>

                {/* KPI Cards Row */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.75rem", marginBottom: "2rem" }}>
                  {[
                    { label: "Unique Visitors", value: totals.uniqueVisitors, color: "#1565C0", bg: "#E3F2FD" },
                    { label: "Total Sessions", value: totals.totalSessions, color: "#2E7D32", bg: "#E8F5E9" },
                    { label: "Page Views", value: totals.pageViews, color: "#6A1B9A", bg: "#F3E5F5" },
                    { label: "Engagement Rate", value: `${engagementRate}%`, color: "#00695C", bg: "#E0F2F1" },
                    { label: "Bounce Rate", value: `${bounceRate}%`, color: bounceRate > 70 ? "#C62828" : "#E65100", bg: bounceRate > 70 ? "#FFEBEE" : "#FFF3E0" },
                    { label: "Avg. Session", value: formatDuration(avgSessionDuration), color: "#4527A0", bg: "#EDE7F6" },
                    { label: "Pages / Session", value: pagesPerSession, color: "#00838F", bg: "#E0F7FA" }
                  ].map((kpi, i) => (
                    <div key={i} style={{ background: kpi.bg, borderRadius: "12px", padding: "1rem", textAlign: "center" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                      <div style={{ fontSize: "0.7rem", color: kpi.color, fontWeight: 500, marginTop: "0.2rem" }}>{kpi.label}</div>
                    </div>
                  ))}
                </div>

                {/* Unique Visitors Chart */}
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.25rem" }}>
                  <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Unique Visitors</h3>
                  <MiniChart data={analyticsData} dataKey="uniqueVisitors" color="#1565C0" height={120} />
                </div>

                {/* Sessions Chart */}
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.25rem" }}>
                  <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Sessions (Total Visits)</h3>
                  <MiniChart data={analyticsData} dataKey="totalSessions" color="#2E7D32" height={120} />
                </div>

                {/* Page Views Chart */}
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.25rem" }}>
                  <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Page Views</h3>
                  <MiniChart data={analyticsData} dataKey="pageViews" color="#6A1B9A" height={120} />
                </div>

                {/* Two-column: Traffic Sources + New vs Returning */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", marginBottom: "1.25rem" }}>
                  {/* Traffic Sources */}
                  <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem" }}>
                    <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1.25rem" }}>Traffic Sources</h3>
                    <DonutChart segments={
                      Object.entries(totals.sources).length > 0
                        ? Object.entries(totals.sources).map(([src, count]) => ({
                            label: sourceLabels[src] || src,
                            value: count,
                            color: { direct: "#1565C0", organic_search: "#2E7D32", social: "#E91E63", referral: "#FF9800" }[src] || "#757575"
                          }))
                        : [{ label: "No data yet", value: 0, color: "#E0E0E0" }]
                    } />
                  </div>

                  {/* New vs Returning */}
                  <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem" }}>
                    <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1.25rem" }}>New vs. Returning Visitors</h3>
                    <DonutChart segments={[
                      { label: "New Visitors", value: totals.newVisitors, color: "#00897B" },
                      { label: "Returning Visitors", value: totals.returningVisitors, color: "#5C6BC0" }
                    ]} />
                  </div>
                </div>

                {/* Engagement & Bounce Rate Chart */}
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.25rem" }}>
                  <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "0.5rem" }}>Daily Engagement vs. Bounce</h3>
                  <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginBottom: "1rem" }}>Green = engaged sessions, Red = bounces</p>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: "100px" }}>
                    {analyticsData.map((d, i) => {
                      const engaged = d.engagedSessions || 0;
                      const bounced = d.bounces || 0;
                      const dayTotal = engaged + bounced;
                      const maxDay = Math.max(...analyticsData.map(x => (x.engagedSessions || 0) + (x.bounces || 0)), 1);
                      const engagedPct = dayTotal > 0 ? (engaged / maxDay) * 100 : 0;
                      const bouncedPct = dayTotal > 0 ? (bounced / maxDay) * 100 : 0;
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", position: "relative" }}>
                            {analyticsData.length <= 14 && bounced > 0 && (
                              <span style={{
                                position: "absolute",
                                bottom: `calc(${engagedPct + bouncedPct}% + 2px)`,
                                left: "50%",
                                transform: "translateX(-50%)",
                                fontSize: "0.6rem",
                                fontWeight: 600,
                                color: "#C62828",
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                lineHeight: 1
                              }}>{bounced}</span>
                            )}
                            {analyticsData.length <= 14 && engaged > 0 && (
                              <span style={{
                                position: "absolute",
                                bottom: bounced > 0
                                  ? `calc(${engagedPct}% - 10px)`
                                  : `calc(${engagedPct}% + 2px)`,
                                left: "50%",
                                transform: "translateX(-50%)",
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                color: bounced > 0 ? "#FFFFFF" : "#2E7D32",
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                lineHeight: 1,
                                textShadow: bounced > 0 ? "0 0 2px rgba(0,0,0,0.3)" : "none",
                                zIndex: 1
                              }}>{engaged}</span>
                            )}
                            <div title={`Bounces: ${bounced}`} style={{ width: "100%", height: `${bouncedPct}%`, background: "#EF5350", borderRadius: "2px 2px 0 0", minHeight: bounced > 0 ? "2px" : "0" }} />
                            <div title={`Engaged: ${engaged}`} style={{ width: "100%", height: `${engagedPct}%`, background: "#66BB6A", minHeight: engaged > 0 ? "2px" : "0" }} />
                          </div>
                          {analyticsData.length <= 14 && (
                            <span style={{ fontSize: "0.55rem", color: "var(--light)", marginTop: "3px", textAlign: "center", transform: "rotate(-45deg)", transformOrigin: "center", whiteSpace: "nowrap" }}>
                              {new Date(d.date + "T00:00:00").toLocaleDateString("en-SG", { day: "numeric", month: "short" })}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Daily Average Session Duration */}
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.5rem" }}>
                  <h3 style={{ fontFamily: "var(--serif)", fontSize: "1rem", marginBottom: "1rem" }}>Avg. Session Duration (seconds)</h3>
                  <MiniChart
                    data={analyticsData.map(d => ({
                      ...d,
                      avgDuration: d.totalSessions > 0 ? Math.round(d.totalDuration / d.totalSessions) : 0
                    }))}
                    dataKey="avgDuration"
                    color="#4527A0"
                    height={100}
                  />
                </div>

                {/* GA4 note */}
                <div style={{ marginTop: "1.5rem", padding: "1rem", background: "#E3F2FD", borderRadius: "10px", fontSize: "0.8rem", color: "#1565C0" }}>
                  <strong>Note:</strong> These metrics are tracked via <span className="nooks-brand">nooks</span>' built-in analytics. For deeper insights (demographics, device breakdown, acquisition channels), visit your{" "}
                  <a href="https://analytics.google.com" target="_blank" rel="noopener" style={{ color: "#1565C0", fontWeight: 600 }}>Google Analytics 4 dashboard</a> (ID: G-CBTW0456Z2).
                </div>
              </div>
            );
          })()}

          {/* -- CONVERSATIONS TAB -- */}
          {/* -- BOOKINGS TAB -- */}
          {adminTab === "bookings" && (() => {
            const fmtDate = (ts) => {
              if (!ts) return "—";
              const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
              return d.toLocaleString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
            };
            const STATUS_META = {
              pending:   { label: "Pending",   color: "#E65100", bg: "#FFF3E0" },
              confirmed: { label: "Confirmed", color: "#2E7D32", bg: "#E8F5E9" },
              completed: { label: "Completed", color: "#1565C0", bg: "#E3F2FD" },
              declined:  { label: "Declined",  color: "#C62828", bg: "#FFEBEE" },
              cancelled: { label: "Cancelled", color: "#757575", bg: "#EEEEEE" },
            };
            const sorted = [...allBookings].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            const filtered = bookingFilter === "all" ? sorted : sorted.filter(b => b.status === bookingFilter);
            return (
              <div>
                <p style={{ color: "var(--muted)", fontSize: "0.82rem", marginBottom: "1rem" }}>Full booking log for dispute resolution. All times are Singapore local time.</p>
                {/* Status filter pills */}
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                  {["all", "pending", "confirmed", "completed", "declined", "cancelled"].map(s => {
                    const meta = STATUS_META[s] || { label: "All", color: "var(--amber)", bg: "#E8F5E9" };
                    const count = s === "all" ? allBookings.length : allBookings.filter(b => b.status === s).length;
                    return (
                      <button key={s} onClick={() => { setBookingFilter(s); setExpandedBooking(null); }} style={{
                        padding: "0.35rem 0.9rem", borderRadius: "20px", border: "none", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600,
                        background: bookingFilter === s ? (s === "all" ? "var(--amber)" : meta.bg) : "var(--border)",
                        color: bookingFilter === s ? (s === "all" ? "white" : meta.color) : "var(--muted)"
                      }}>
                        {s === "all" ? "All" : meta.label} ({count})
                      </button>
                    );
                  })}
                </div>
                {filtered.length === 0 ? (
                  <p style={{ color: "var(--muted)", fontSize: "0.85rem", textAlign: "center", padding: "3rem 0" }}>No bookings found.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                    {filtered.map(b => {
                      const meta = STATUS_META[b.status] || STATUS_META.pending;
                      const isOpen = expandedBooking === b.id;
                      return (
                        <div key={b.id} style={{ background: "var(--card)", border: `1px solid ${isOpen ? "var(--amber)" : "var(--border)"}`, borderRadius: "12px", overflow: "hidden" }}>
                          {/* Row */}
                          <div onClick={() => setExpandedBooking(isOpen ? null : b.id)} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "0.75rem", alignItems: "center", padding: "0.9rem 1.1rem", cursor: "pointer" }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{b.businessName || b.bizName || "—"}</div>
                              <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.15rem" }}>Ref: {b.ref || b.id.slice(0,8)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.85rem", fontWeight: 500 }}>{b.customerName || "—"}</div>
                              <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{b.customerEmail || b.customerPhone || "—"}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.82rem" }}>{b.service || b.serviceItem?.name || "—"}</div>
                              <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>{b.date || b.dateISO || "—"} {b.time || b.timeSlot || ""}</div>
                            </div>
                            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: meta.color, background: meta.bg, padding: "0.25rem 0.65rem", borderRadius: "20px", whiteSpace: "nowrap" }}>
                              {meta.label}
                            </span>
                          </div>
                          {/* Expanded detail */}
                          {isOpen && (
                            <div style={{ borderTop: "1px solid var(--border)", padding: "1rem 1.1rem", background: "#FAFAFA", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem 2rem", fontSize: "0.82rem" }}>
                              {[
                                ["Booking Ref", b.ref || b.id],
                                ["Store", b.businessName || b.bizName || "—"],
                                ["Customer Name", b.customerName || "—"],
                                ["Customer Email", b.customerEmail || "—"],
                                ["Customer Phone", b.customerPhone || "—"],
                                ["Service", b.service || b.serviceItem?.name || "—"],
                                ["Price", b.price ? `S$${parseFloat(b.price).toFixed(2)}` : "—"],
                                ["Date", b.date || b.dateISO || "—"],
                                ["Time", b.time || b.timeSlot || "—"],
                                ["Status", `${meta.label}`],
                                ["Submitted", fmtDate(b.createdAt)],
                                ["Notes", b.notes || "None"],
                              ].map(([label, val]) => (
                                <div key={label}>
                                  <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "0.15rem" }}>{label}</div>
                                  <div style={{ fontWeight: label === "Booking Ref" || label === "Status" ? 600 : 400, color: label === "Status" ? meta.color : "inherit", wordBreak: "break-all" }}>{val}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* -- WISHES TAB -- */}
          {adminTab === "wishes" && (() => {
            const CATEGORIES_MAP = {};
            CATEGORIES.forEach(c => { CATEGORIES_MAP[c.id] = c.label; });
            const userMap = {};
            allUsers.forEach(u => { userMap[u.id] = u; });

            const filteredWishes = wishFilter === "all" ? allWishes : allWishes.filter(w => w.status === wishFilter);

            const handleWishApprove = async (wish) => {
              setWishActionLoading(wish.id);
              try {
                await db.collection("wishes").doc(wish.id).update({
                  status: "approved",
                  reviewedAt: firebase.firestore.FieldValue.serverTimestamp(),
                  reviewedBy: currentUser.uid
                });
                setAllWishes(prev => prev.map(w => w.id === wish.id ? { ...w, status: "approved", reviewedBy: currentUser.uid } : w));
              } catch (err) { console.error("Wish approve error:", err); }
              setWishActionLoading(null);
            };

            const handleWishReject = async (wish) => {
              if (!wishRejectReason.trim()) { alert("Please provide a rejection reason."); return; }
              setWishActionLoading(wish.id);
              try {
                await db.collection("wishes").doc(wish.id).update({
                  status: "rejected",
                  rejectReason: wishRejectReason.trim(),
                  reviewedAt: firebase.firestore.FieldValue.serverTimestamp(),
                  reviewedBy: currentUser.uid
                });
                setAllWishes(prev => prev.map(w => w.id === wish.id ? { ...w, status: "rejected", rejectReason: wishRejectReason.trim(), reviewedBy: currentUser.uid } : w));
                setWishRejectReason("");
              } catch (err) { console.error("Wish reject error:", err); }
              setWishActionLoading(null);
            };

            const wishStatusBadge = (status) => {
              const m = { pending: { color: "#c87606", bg: "#c876060f", label: "Pending" }, approved: { color: "#2E7D32", bg: "#2E7D321a", label: "Approved" }, rejected: { color: "#C62828", bg: "#C628281a", label: "Rejected" } };
              const s = m[status] || m.pending;
              return <span style={{ fontSize: "0.7rem", fontWeight: 600, color: s.color, background: s.bg, padding: "0.2rem 0.6rem", borderRadius: "20px" }}>{s.label}</span>;
            };

            return (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                  <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", margin: 0 }}>Neighbourhood Wishes</h2>
                  <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>{filteredWishes.length} wish{filteredWishes.length !== 1 ? "es" : ""}</span>
                </div>

                {/* Filter bar */}
                <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1.25rem", flexWrap: "wrap" }}>
                  {["pending", "approved", "rejected", "all"].map(f => (
                    <button key={f} onClick={() => setWishFilter(f)} style={{
                      padding: "0.4rem 0.9rem", borderRadius: "20px", border: "1px solid " + (wishFilter === f ? "var(--amber)" : "var(--border)"),
                      background: wishFilter === f ? "var(--amber)" : "var(--card)", color: wishFilter === f ? "#fff" : "var(--muted)",
                      fontSize: "0.8rem", fontWeight: 500, cursor: "pointer"
                    }}>
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                      {f !== "all" && ` (${allWishes.filter(w => w.status === f).length})`}
                      {f === "all" && ` (${allWishes.length})`}
                    </button>
                  ))}
                </div>

                {filteredWishes.length === 0 ? (
                  <p style={{ color: "var(--muted)", textAlign: "center", padding: "3rem 0" }}>No {wishFilter === "all" ? "" : wishFilter + " "}wishes yet.</p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {filteredWishes.map(wish => {
                      const user = userMap[wish.uid];
                      const catLabel = CATEGORIES_MAP[wish.category] || wish.category || "—";
                      const createdAt = wish.createdAt ? new Date(wish.createdAt.seconds * 1000).toLocaleString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                      const isLoading = wishActionLoading === wish.id;

                      return (
                        <div key={wish.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1rem 1.25rem" }}>
                          {/* Header row */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", marginBottom: "0.5rem" }}>
                            <div>
                              <span style={{ fontWeight: 600, fontSize: "0.88rem" }}>{wish.userName || "Anonymous"}</span>
                              <span style={{ color: "var(--muted)", fontSize: "0.78rem", marginLeft: "0.5rem" }}>{user ? user.email || user.displayName : wish.uid}</span>
                            </div>
                            {wishStatusBadge(wish.status)}
                          </div>

                          {/* Wish text */}
                          <p style={{ margin: "0.4rem 0 0.6rem", fontSize: "0.95rem", lineHeight: 1.45 }}>{wish.text}</p>

                          {/* Meta row */}
                          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", fontSize: "0.78rem", color: "var(--muted)", marginBottom: "0.75rem" }}>
                            <span>Category: <strong style={{ color: "var(--ink)" }}>{catLabel}</strong></span>
                            <span>Postal: <strong style={{ color: "var(--ink)" }}>{wish.postalCode || "—"}</strong></span>
                            <span>Upvotes: <strong style={{ color: "var(--ink)" }}>{wish.upvotes || 0}</strong></span>
                            <span>{createdAt}</span>
                          </div>

                          {/* Reject reason display (for already-rejected) */}
                          {wish.status === "rejected" && wish.rejectReason && (
                            <div style={{ background: "#FFF3E0", border: "1px solid #FFE0B2", borderRadius: "8px", padding: "0.6rem 0.9rem", marginBottom: "0.75rem", fontSize: "0.82rem" }}>
                              <strong style={{ color: "#E65100" }}>Rejection reason:</strong> {wish.rejectReason}
                            </div>
                          )}

                          {/* Action buttons (only for pending) */}
                          {wish.status === "pending" && (
                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", flexWrap: "wrap" }}>
                              <button
                                onClick={() => handleWishApprove(wish)}
                                disabled={isLoading}
                                style={{ padding: "0.45rem 1.2rem", borderRadius: "8px", border: "none", background: "#2E7D32", color: "#fff", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", opacity: isLoading ? 0.6 : 1 }}
                              >{isLoading ? "..." : "Approve"}</button>

                              <div style={{ flex: 1, minWidth: "200px" }}>
                                <input
                                  type="text"
                                  placeholder="Rejection reason (required)"
                                  value={wishActionLoading === wish.id ? "" : wishRejectReason}
                                  onChange={e => setWishRejectReason(e.target.value)}
                                  style={{ width: "100%", padding: "0.45rem 0.7rem", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "0.82rem", boxSizing: "border-box" }}
                                />
                              </div>

                              <button
                                onClick={() => handleWishReject(wish)}
                                disabled={isLoading}
                                style={{ padding: "0.45rem 1.2rem", borderRadius: "8px", border: "none", background: "#C62828", color: "#fff", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", opacity: isLoading ? 0.6 : 1 }}
                              >{isLoading ? "..." : "Reject"}</button>
                            </div>
                          )}

                          {/* Re-approve button for rejected wishes */}
                          {wish.status === "rejected" && (
                            <button
                              onClick={() => handleWishApprove(wish)}
                              disabled={isLoading}
                              style={{ padding: "0.4rem 1rem", borderRadius: "8px", border: "1px solid #2E7D32", background: "transparent", color: "#2E7D32", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
                            >{isLoading ? "..." : "Re-approve"}</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* -- CONVERSATIONS TAB -- */}
          {adminTab === "conversations" && (() => {
            // Build UID ? user lookup from already-fetched allUsers
            const userMap = {};
            allUsers.forEach(u => { userMap[u.id] = u; });
            // Build bizId ? business lookup from already-fetched allBusinesses
            const bizMap = {};
            allBusinesses.forEach(b => { bizMap[b.id] = b; });

            const resolveParticipants = (conv) => {
              const uids = conv.participants || [];
              return uids.map(uid => {
                // 1. Check Firestore users collection (name/displayName/email)
                const u = userMap[uid];
                if (u) return u.displayName || u.name || u.email || uid.slice(0, 8) + "—";
                // 2. Check business owner match
                const ownedBiz = allBusinesses.find(b => b.ownerId === uid || b.operatorUid === uid);
                if (ownedBiz) return `${ownedBiz.name} (owner)`;
                // 3. Cloud Function resolved name (Auth displayName/email/phone)
                // Use `in` check — value may be a non-null string or "(Deleted Account)"
                if (uid in resolvedUidNames) return resolvedUidNames[uid] || "Unknown User";
                // 4. Last resort (Cloud Function hasn't returned yet)
                return uid.slice(0, 8) + "—";
              }).join(" ↔ ");
            };
            const resolveBiz = (conv) => {
              // Primary: stored bizId field. Fallback: extract from conv ID (format: uid_uid_bizId)
              const bizId = conv.bizId || (() => {
                const parts = (conv.id || "").split("_");
                return parts.length >= 3 ? parts[parts.length - 1] : null;
              })();
              if (!bizId) return null;
              const b = bizMap[bizId];
              return b ? b.name : null;
            };
            const fmtTime = (ts) => {
              if (!ts) return "—";
              const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
              return d.toLocaleString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
            };
            return (
              <div>
                <p style={{ color: "var(--muted)", fontSize: "0.82rem", marginBottom: "1rem" }}>
                  Read-only audit log of all conversations. Participants are resolved by name/email. Click any thread to read messages.
                </p>
                {selectedConv ? (
                  <div>
                    <button onClick={() => setSelectedConv(null)} style={{ background: "none", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.4rem 0.9rem", cursor: "pointer", fontSize: "0.82rem", marginBottom: "1rem", color: "var(--muted)" }}>
                      ← Back to all conversations
                    </button>
                    <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "1.25rem", marginBottom: "1rem" }}>
                      <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.4rem" }}>Participants</div>
                      <div style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: "0.25rem" }}>{resolveParticipants(selectedConv)}</div>
                      {resolveBiz(selectedConv) && (
                        <div style={{ fontSize: "0.82rem", color: "var(--muted)" }}>Store: <strong>{resolveBiz(selectedConv)}</strong></div>
                      )}
                    </div>
                    {loadingConvMessages ? (
                      <div style={{ textAlign: "center", padding: "2rem" }}><div className="spinner" /></div>
                    ) : convMessages.length === 0 ? (
                      <p style={{ color: "var(--muted)", fontSize: "0.85rem", textAlign: "center", padding: "2rem 0" }}>No messages found.</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                        {convMessages.map(msg => {
                          const sender = userMap[msg.senderUid];
                          const senderLabel = msg.senderName || sender?.displayName || sender?.name || sender?.email || msg.senderUid?.slice(0,8) + "—" || "Unknown";
                          return (
                            <div key={msg.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "10px", padding: "0.9rem 1rem" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.35rem" }}>
                                <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--amber)" }}>{senderLabel}</span>
                                <span style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{fmtTime(msg.createdAt)}</span>
                              </div>
                              <p style={{ margin: 0, fontSize: "0.88rem", lineHeight: "1.5", wordBreak: "break-word" }}>{msg.text}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  allConversations.length === 0 ? (
                    <p style={{ color: "var(--muted)", fontSize: "0.85rem", textAlign: "center", padding: "3rem 0" }}>No conversations yet.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                      {allConversations.map(conv => (
                        <div key={conv.id} onClick={() => setSelectedConv(conv)} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "10px", padding: "1rem 1.25rem", cursor: "pointer", transition: "box-shadow 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
                          onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: "0.15rem" }}>{resolveParticipants(conv)}</div>
                              {resolveBiz(conv) && <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginBottom: "0.3rem" }}>Store: {resolveBiz(conv)}</div>}
                              <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: conv.lastMessage ? "normal" : "italic" }}>
                                {conv.lastMessage || "No messages yet"}
                              </p>
                            </div>
                            <div style={{ fontSize: "0.72rem", color: "var(--muted)", whiteSpace: "nowrap", flexShrink: 0, marginTop: "0.1rem" }}>
                              {fmtTime(conv.lastMessageAt)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            );
          })()}

          {/* -- DATA HEALTH TAB -- */}
          {adminTab === "health" && (() => {
            const isMeaningless = (s) => !s || /^singapore\s*\d*$/i.test(s.trim()) || /^\d{5,6}$/.test(s.trim());

            // A street is stale if it's set AND doesn't appear anywhere in the address field
            function isStaleStreet(biz) {
              if (!biz.street || isMeaningless(biz.street)) return false;
              if (!biz.address || isMeaningless(biz.address)) return false;
              // Check if any meaningful word from street appears in address
              const streetWords = biz.street.toLowerCase().split(/\s+/).filter(w => w.length > 3);
              const addrLower = biz.address.toLowerCase();
              return streetWords.every(w => !addrLower.includes(w));
            }

            async function runScan() {
              setHealthScanning(true);
              setHealthScanDone(false);
              setHealthIssues([]);
              setHealthFixed({});
              try {
                const snap = await db.collection("businesses").get();
                const issues = [];
                snap.forEach(doc => {
                  const d = doc.data();
                  if (isStaleStreet(d)) {
                    issues.push({ id: doc.id, name: d.name, slug: d.slug, address: d.address, street: d.street, status: d.status });
                  }
                });
                setHealthIssues(issues);
                setHealthScanDone(true);
              } catch(e) {
                showToast("Scan error: " + e.message, "error");
              }
              setHealthScanning(false);
            }

            async function fixOne(id) {
              try {
                await db.collection("businesses").doc(id).update({ street: "" });
                setHealthFixed(prev => ({ ...prev, [id]: true }));
              } catch(e) {
                showToast("Fix error: " + e.message, "error");
              }
            }

            async function fixAll() {
              setHealthFixing(true);
              const unfixed = healthIssues.filter(b => !healthFixed[b.id]);
              const batch = db.batch();
              unfixed.forEach(b => batch.update(db.collection("businesses").doc(b.id), { street: "" }));
              try {
                await batch.commit();
                const newFixed = {};
                unfixed.forEach(b => { newFixed[b.id] = true; });
                setHealthFixed(prev => ({ ...prev, ...newFixed }));
                showToast(`Fixed ${unfixed.length} records.`, "success");
              } catch(e) {
                showToast("Bulk fix error: " + e.message, "error");
              }
              setHealthFixing(false);
            }

            const unfixedCount = healthIssues.filter(b => !healthFixed[b.id]).length;

            return (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
                  <div>
                    <h2 style={{ fontFamily: "var(--serif)", fontSize: "1.2rem", marginBottom: "0.25rem" }}>Data Health</h2>
                    <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>Detects stale <code>street</code> fields that override the display address — causing listings to show the wrong location.</p>
                  </div>
                  <button onClick={runScan} disabled={healthScanning} style={{ background: "var(--amber)", color: "#fff", border: "none", padding: "0.6rem 1.25rem", borderRadius: "8px", fontWeight: 600, fontSize: "0.85rem", cursor: healthScanning ? "not-allowed" : "pointer", opacity: healthScanning ? 0.6 : 1, whiteSpace: "nowrap" }}>
                    {healthScanning ? "Scanning…" : "Run Scan"}
                  </button>
                </div>

                {!healthScanDone && !healthScanning && (
                  <div style={{ textAlign: "center", padding: "3rem", background: "var(--cream)", borderRadius: "12px", color: "var(--muted)" }}>
                    <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🩺</div>
                    <p>Click <strong>Run Scan</strong> to check all business records for stale location data.</p>
                  </div>
                )}

                {healthScanning && (
                  <div style={{ textAlign: "center", padding: "3rem" }}>
                    <div className="spinner" />
                    <p style={{ color: "var(--muted)", marginTop: "1rem" }}>Scanning all businesses…</p>
                  </div>
                )}

                {healthScanDone && (
                  <div>
                    {healthIssues.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "3rem", background: "#E8F5E9", borderRadius: "12px" }}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>✅</div>
                        <p style={{ fontWeight: 600, color: "#2E7D32" }}>All clear — no stale street fields found.</p>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                          <p style={{ color: "#B71C1C", fontWeight: 600 }}>{unfixedCount} issue{unfixedCount !== 1 ? "s" : ""} found</p>
                          {unfixedCount > 0 && (
                            <button onClick={fixAll} disabled={healthFixing} style={{ background: "#B71C1C", color: "#fff", border: "none", padding: "0.5rem 1.1rem", borderRadius: "8px", fontWeight: 600, fontSize: "0.82rem", cursor: healthFixing ? "not-allowed" : "pointer", opacity: healthFixing ? 0.6 : 1 }}>
                              {healthFixing ? "Fixing…" : `Fix All (${unfixedCount})`}
                            </button>
                          )}
                        </div>
                        <div style={{ border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
                            <thead>
                              <tr style={{ background: "var(--cream)", borderBottom: "1px solid var(--border)" }}>
                                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600 }}>Business</th>
                                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, color: "#B71C1C" }}>Stale street (showing)</th>
                                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600, color: "#2E7D32" }}>Correct address (saved)</th>
                                <th style={{ padding: "0.75rem 1rem", textAlign: "left", fontWeight: 600 }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {healthIssues.map((biz, i) => (
                                <tr key={biz.id} style={{ borderBottom: i < healthIssues.length - 1 ? "1px solid var(--border)" : "none", background: healthFixed[biz.id] ? "#F1F8E9" : "white" }}>
                                  <td style={{ padding: "0.75rem 1rem" }}>
                                    <div style={{ fontWeight: 600 }}>{biz.name}</div>
                                    <div style={{ color: "var(--muted)", fontSize: "0.78rem" }}>{biz.slug} · {biz.status}</div>
                                  </td>
                                  <td style={{ padding: "0.75rem 1rem", color: "#B71C1C" }}>{biz.street}</td>
                                  <td style={{ padding: "0.75rem 1rem", color: "#2E7D32" }}>{biz.address}</td>
                                  <td style={{ padding: "0.75rem 1rem" }}>
                                    {healthFixed[biz.id] ? (
                                      <span style={{ color: "#2E7D32", fontWeight: 600 }}>✅ Fixed</span>
                                    ) : (
                                      <button onClick={() => fixOne(biz.id)} style={{ background: "none", border: "1px solid #B71C1C", color: "#B71C1C", padding: "0.3rem 0.75rem", borderRadius: "6px", fontSize: "0.8rem", cursor: "pointer", fontWeight: 600 }}>Fix</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p style={{ marginTop: "0.75rem", fontSize: "0.78rem", color: "var(--muted)" }}>Fixing clears the <code>street</code> field so the correct <code>address</code> is used for display. No address data is deleted.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      );
    }

    // Wizard Map Step - Interactive map with geocoding
    function WizardMapStep({ formData, setFormData, handlePrev, handleNext }) {
      const mapContainerRef = useRef(null);
      const mapInstanceRef = useRef(null);
      const markerRef = useRef(null);
      const [geocoding, setGeocoding] = useState(false);
      const debounceRef = useRef(null);

      // Initialize map
      useEffect(() => {
        if (!mapContainerRef.current || mapInstanceRef.current) return;
        const map = L.map(mapContainerRef.current).setView([formData.lat, formData.lng], 16);
        L.tileLayer(CARTO_TILE_URL, {
          attribution: CARTO_ATTRIBUTION,
          maxZoom: 19
        }).addTo(map);
        const marker = L.marker([formData.lat, formData.lng], { draggable: true }).addTo(map);
        marker.on("dragend", () => {
          const pos = marker.getLatLng();
          setFormData(prev => ({ ...prev, lat: pos.lat, lng: pos.lng }));
          // Reverse geocode dragged position via OneMap
          fetch("https://www.onemap.gov.sg/api/public/revgeocode?location=" + pos.lat + "," + pos.lng + "&buffer=50&addressType=All")
            .then(r => r.json())
            .then(data => {
              if (data && data.GeocodeInfo && data.GeocodeInfo.length > 0) {
                var info = data.GeocodeInfo[0];
                var addr = (info.BLOCK || "") + " " + (info.ROAD || "");
                if (addr.trim()) setFormData(prev => ({ ...prev, address: addr.trim(), lat: pos.lat, lng: pos.lng }));
              }
            }).catch(() => {});
        });
        mapInstanceRef.current = map;
        markerRef.current = marker;
        // Small delay to fix Leaflet rendering in hidden/animated containers
        setTimeout(() => map.invalidateSize(), 200);
        return () => { map.remove(); mapInstanceRef.current = null; };
      }, []);

      // Geocode when address changes (debounced)
      // Store resolved street address separately so we don't overwrite user's input while typing
      const resolvedAddrRef = useRef("");

      const geocodeAddress = (query) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query || query.length < 3) return;
        debounceRef.current = setTimeout(() => {
          setGeocoding(true);
          fetch("https://www.onemap.gov.sg/api/common/elastic/search?searchVal=" + encodeURIComponent(query.trim()) + "&returnGeom=Y&getAddrDetails=Y")
            .then(r => r.json())
            .then(data => {
              if (data && data.results && data.results.length > 0) {
                var result = data.results[0];
                var newLat = parseFloat(result.LATITUDE);
                var newLng = parseFloat(result.LONGITUDE);
                var streetAddr = "";
                if (result.BLK_NO && result.ROAD_NAME) {
                  streetAddr = result.BLK_NO + " " + _toTitleCase(result.ROAD_NAME);
                } else if (result.ADDRESS) {
                  streetAddr = result.ADDRESS.split("SINGAPORE")[0].trim();
                }
                // Save resolved address for pre-save, but DON'T overwrite the input while typing
                resolvedAddrRef.current = streetAddr;
                // Only update lat/lng + move map pin
                setFormData(prev => ({ ...prev, lat: newLat, lng: newLng }));
                if (mapInstanceRef.current && markerRef.current) {
                  mapInstanceRef.current.setView([newLat, newLng], 17);
                  markerRef.current.setLatLng([newLat, newLng]);
                }
                showToast(streetAddr ? "Found: " + streetAddr : "Location updated", "success", 2500);
              }
              setGeocoding(false);
            }).catch(() => setGeocoding(false));
        }, 800);
      };

      return (
        <div className="wizard-step active">
          <h2 className="wizard-title">Where Are You?</h2>
          <p className="wizard-description">Enter your postal code or address — the map will update automatically. You can also drag the pin.</p>
          <div style={{ borderRadius: "12px", overflow: "hidden", marginBottom: "1.5rem", border: "1px solid var(--border)", position: "relative" }}>
            <div ref={mapContainerRef} style={{ height: "250px", width: "100%" }} />
            {geocoding && (
              <div style={{ position: "absolute", top: "10px", right: "10px", background: "white", padding: "0.3rem 0.8rem", borderRadius: "8px", fontSize: "0.8rem", boxShadow: "0 1px 4px rgba(0,0,0,0.15)", zIndex: 1000 }}>
                Locating...
              </div>
            )}
            {/* Locate Me button */}
            <button type="button" onClick={() => {
              if (!navigator.geolocation) { showToast("Geolocation not supported.", "error"); return; }
              setGeocoding(true);
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  var lat = pos.coords.latitude;
                  var lng = pos.coords.longitude;
                  setFormData(prev => ({ ...prev, lat: lat, lng: lng }));
                  if (mapInstanceRef.current && markerRef.current) {
                    mapInstanceRef.current.setView([lat, lng], 17);
                    markerRef.current.setLatLng([lat, lng]);
                  }
                  // Reverse geocode to get address
                  fetch("https://www.onemap.gov.sg/api/public/revgeocode?location=" + lat + "," + lng + "&buffer=50&addressType=All")
                    .then(r => r.json())
                    .then(data => {
                      if (data && data.GeocodeInfo && data.GeocodeInfo.length > 0) {
                        var info = data.GeocodeInfo[0];
                        var addr = ((info.BLOCK || "") + " " + (info.ROAD || "")).trim();
                        if (addr) setFormData(prev => ({ ...prev, address: addr }));
                      }
                      setGeocoding(false);
                    }).catch(() => setGeocoding(false));
                },
                () => { showToast("Could not get your location.", "error"); setGeocoding(false); },
                { enableHighAccuracy: true, timeout: 10000 }
              );
            }} style={{ position: "absolute", bottom: "10px", right: "10px", background: "white", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.4rem 0.7rem", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, color: "var(--amber)", boxShadow: "0 1px 4px rgba(0,0,0,0.15)", zIndex: 1000, display: "flex", alignItems: "center", gap: "4px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M22 12h-4M2 12h4M12 2v4M12 22v-4"/><circle cx="12" cy="12" r="3"/></svg>
              Locate me
            </button>
          </div>
          <div className="form-group">
            <label className="form-label">Postal Code / Address</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. 331010 or 10 Ang Mo Kio Street"
              value={formData.address}
              onChange={(e) => {
                const val = e.target.value;
                setFormData(prev => ({ ...prev, address: val }));
                geocodeAddress(val);
              }}
            />
            <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.4rem" }}>
              Tip: Enter a 6-digit Singapore postal code for best accuracy
            </p>
          </div>
          <div className="wizard-actions">
            <button className="wizard-prev-btn" onClick={handlePrev}>← Back</button>
            <button className="wizard-next-btn" onClick={handleNext} disabled={!formData.address}>
              Next →
            </button>
          </div>
        </div>
      );
    }

    // Add Business Wizard Component
    function AddBusinessWizard({ onComplete, currentUser, hasAcknowledged, saveAcknowledgement }) {
      const [step, setStep] = useState(1);
      const totalSteps = 8;
      const [ackChecked, setAckChecked] = useState(hasAcknowledged || false);
      const [ackSaving, setAckSaving] = useState(false);
      const [formData, setFormData] = useState({
        name: "",
        slug: "",
        category: "fnb",
        description: "",
        address: "",
        lat: 1.3521,
        lng: 103.8198,
        socialMedia: { instagram: "", tiktok: "", facebook: "", whatsapp: "", link: "" },
        activeSocial: "whatsapp",
        defaultOrderMethod: "",
        // Self-declared YWCA Hopemaker programme affiliation. This is NOT the
        // verified badge — that stays admin-controlled (ywcaHopemaker.verified,
        // set from the Approvals panel). This claim just flags the listing for
        // admin to cross-check against YWCA's roster during approval.
        ywcaHopemakerClaim: { claimed: false, cohort: "" },
        // v2 image-persistence model:
        //   coverSourceUrl  — uncompressed-source uploaded at pick time to
        //                     businesses/{uid}/drafts/cover_source_{ts}
        //                     Crop (posX/posY/zoom) is applied at submit.
        //   galleryUrls     — already-uploaded public URLs; reused on submit.
        //   menuItems[n].imageUrl — same pattern per menu item.
        // These replace the v1 File-in-memory fields (coverPhoto/galleryPhotos
        // /imageFile) that were lost on refresh. See project_listing_drafts_v1.
        coverSourceUrl: "",
        galleryUrls: [],
        menuItems: [],
        hours: {
          Monday: { open: "09:00", close: "18:00", enabled: true },
          Tuesday: { open: "09:00", close: "18:00", enabled: true },
          Wednesday: { open: "09:00", close: "18:00", enabled: true },
          Thursday: { open: "09:00", close: "18:00", enabled: true },
          Friday: { open: "09:00", close: "18:00", enabled: true },
          Saturday: { open: "09:00", close: "18:00", enabled: true },
          Sunday: { open: "09:00", close: "18:00", enabled: false }
        }
      });
      const [newItem, setNewItem] = useState({ name: "", price: "", description: "", imageUrl: "" });
      // Per-item editor modal in the wizard (added 2026-05). null when closed; otherwise
      // { mode: 'edit'|'new', index?: number, item: {...}|null }.
      const [wizardItemEditor, setWizardItemEditor] = useState(null);
      const saveWizardItem = function(savedItem) {
        setFormData(function(prev) {
          var arr = (prev.menuItems || []).slice();
          if (wizardItemEditor && wizardItemEditor.mode === 'edit' && typeof wizardItemEditor.index === 'number') {
            arr[wizardItemEditor.index] = Object.assign({}, arr[wizardItemEditor.index], savedItem);
          } else {
            // assign a stable id like the existing handleAddItem does
            arr.push(Object.assign({ id: Date.now() + '_' + Math.random().toString(36).slice(2, 8) }, savedItem));
          }
          return Object.assign({}, prev, { menuItems: arr });
        });
        setWizardItemEditor(null);
      };
      const coverInputRef = React.useRef(null);
      const galleryInputRef = React.useRef(null);
      // In-memory cover source (compressed Blob from compressImage). Lets the
      // submit handler skip the fetch round-trip to coverSourceUrl when the
      // user is submitting in the same session as the pick — that fetch
      // depends on the storage bucket's CORS allowlist (cors.json) and was
      // the cause of "Failed to submit: Load failed" / "NetworkError when
      // attempting to fetch resource". Null after a draft-resume from a
      // previous session — submit then falls back to fetching the URL.
      const coverSourceFileRef = React.useRef(null);

      // ── Upload-in-progress tracking (v2) ─────────────────────────────
      // Counters of active Storage uploads per slot. The Photos / Products
      // step disables "Next" while any counter > 0, preventing the user
      // from leaving the step with a half-uploaded image.
      const [uploading, setUploading] = useState({ cover: 0, gallery: 0, menu: 0 });
      function bumpUpload(slot, delta) {
        setUploading(function(prev) { var u = Object.assign({}, prev); u[slot] = Math.max(0, (u[slot] || 0) + delta); return u; });
      }

      // Best-effort Storage delete by download URL. Failures are logged but
      // never surfaced to the user — orphaned blobs will be swept by the
      // future cleanup Cloud Function (v3 backlog).
      async function deleteStorageByUrl(url) {
        if (!url || typeof url !== "string" || url.indexOf("http") !== 0) return;
        try { await storage.refFromURL(url).delete(); }
        catch (e) { console.warn("[draft-image] delete failed (ok if already gone):", e.message || e); }
      }

      // Submission state. MUST be declared before the draft-save useEffect
      // below — that effect's dependency array reads `submitting`, and Safari
      // (JavaScriptCore) enforces TDZ strictly on `const` forward references,
      // unmounting the React tree with "Cannot access 'submitting' before
      // initialization" if this is declared later in the component body.
      const [submitting, setSubmitting] = useState(false);

      // ── Draft-save state (hybrid LocalStorage + Firestore) ──────────────
      // Rationale: home-business listers were abandoning long forms after a
      // single submission failure or accidental refresh. We persist all
      // non-image state to both tiers; photos are deferred to v2. Keys:
      //   LS:        nooks:listing-draft:{uid}
      //   Firestore: listingDrafts/{uid}
      const [maxStepVisited, setMaxStepVisited] = useState(1);
      const [draftStatus, setDraftStatus] = useState("idle");           // 'idle'|'saving'|'saved'|'error'
      const [draftLastSavedAt, setDraftLastSavedAt] = useState(null);
      const [draftLoaded, setDraftLoaded] = useState(false);            // flips true after read + user choice
      const [resumePrompt, setResumePrompt] = useState(null);           // {formData, step, clientTimestamp} | null
      const draftInitRef = React.useRef(false);
      const draftLocalTimerRef = React.useRef(null);
      const draftCloudTimerRef = React.useRef(null);

      // Keep the "highest step reached" marker so we only allow click-jumping
      // back to steps the user has already passed through (forward jump is
      // blocked to preserve existing step-by-step validation).
      React.useEffect(function() {
        setMaxStepVisited(function(prev) { return Math.max(prev, step); });
      }, [step]);

      // Reduce formData to a serializable snapshot. Image *URLs* (v2) are
      // included because pick-time uploads mean each photo is already a
      // stable https URL in Firebase Storage at
      // businesses/{uid}/drafts/... — safe to persist and restore.
      function serializeForDraft(fd) {
        return {
          name: fd.name || "",
          slug: fd.slug || "",
          category: fd.category || "fnb",
          description: fd.description || "",
          address: fd.address || "",
          lat: typeof fd.lat === "number" ? fd.lat : 1.3521,
          lng: typeof fd.lng === "number" ? fd.lng : 103.8198,
          street: fd.street || "",
          district: fd.district || "",
          postalCode: fd.postalCode || "",
          socialMedia: Object.assign(
            { instagram: "", tiktok: "", facebook: "", whatsapp: "", link: "" },
            fd.socialMedia || {}
          ),
          activeSocial: fd.activeSocial || "whatsapp",
          defaultOrderMethod: fd.defaultOrderMethod || "",
          ywcaHopemakerClaim: Object.assign({ claimed: false, cohort: "" }, fd.ywcaHopemakerClaim || {}),
          hours: fd.hours || {},
          // v2 image persistence ↓
          coverSourceUrl: fd.coverSourceUrl || "",
          coverPosX: typeof fd.coverPosX === "number" ? fd.coverPosX : 50,
          coverPosY: typeof fd.coverPosY === "number" ? fd.coverPosY : 50,
          coverZoom: typeof fd.coverZoom === "number" ? fd.coverZoom : 1,
          galleryUrls: Array.isArray(fd.galleryUrls) ? fd.galleryUrls.slice() : [],
          menuItems: (fd.menuItems || []).map(function(m) {
            return { id: m.id, name: m.name || "", price: m.price || "", description: m.description || "", imageUrl: m.imageUrl || "" };
          })
        };
      }

      // Does the draft contain anything worth resuming? We don't want to show
      // the "Resume" banner for a completely empty/unchanged form.
      function draftIsMeaningful(fd) {
        if (!fd) return false;
        if ((fd.name || "").trim()) return true;
        if ((fd.description || "").trim()) return true;
        if ((fd.address || "").trim()) return true;
        if ((fd.menuItems || []).length) return true;
        if ((fd.coverSourceUrl || "").trim()) return true;
        if ((fd.galleryUrls || []).length) return true;
        var sm = fd.socialMedia || {};
        for (var k in sm) { if (sm[k] && String(sm[k]).trim()) return true; }
        return false;
      }

      function draftTimeAgo(d) {
        if (!d) return "just now";
        var then = d instanceof Date ? d : new Date(d);
        var secs = Math.floor((new Date() - then) / 1000);
        if (secs < 45) return "just now";
        if (secs < 90) return "a minute ago";
        var mins = Math.floor(secs / 60);
        if (mins < 45) return mins + " minutes ago";
        if (mins < 90) return "an hour ago";
        var hrs = Math.floor(mins / 60);
        if (hrs < 24) return hrs + " hours ago";
        var days = Math.floor(hrs / 24);
        if (days < 2) return "yesterday";
        return days + " days ago";
      }

      // ── Draft LOAD: one-shot on mount ─────────────────────────────────
      // Read both tiers in parallel, take the newer by clientTimestamp.
      // If the result is "meaningful", show the resume banner; otherwise
      // flip draftLoaded so the save effect can begin writing immediately.
      React.useEffect(function() {
        if (!currentUser || draftInitRef.current) return;
        draftInitRef.current = true;

        var uid = currentUser.uid;
        var lsKey = "nooks:listing-draft:" + uid;

        function readLocal() {
          try {
            var raw = localStorage.getItem(lsKey);
            if (!raw) return null;
            var parsed = JSON.parse(raw);
            if (!parsed || !parsed.formData) return null;
            return parsed;
          } catch (e) { return null; }
        }
        async function readCloud() {
          try {
            var snap = await db.collection("listingDrafts").doc(uid).get();
            if (!snap.exists) return null;
            var d = snap.data();
            if (!d || !d.formData) return null;
            return { formData: d.formData, step: d.step || 1, clientTimestamp: d.clientTimestamp || 0 };
          } catch (e) { console.warn("[draft] cloud read failed:", e); return null; }
        }

        (async function() {
          var local = readLocal();
          var cloud = await readCloud();
          var pick = null;
          if (local && cloud) pick = ((local.clientTimestamp || 0) >= (cloud.clientTimestamp || 0)) ? local : cloud;
          else pick = local || cloud;

          if (pick && draftIsMeaningful(pick.formData)) {
            setResumePrompt(pick);
          } else {
            setDraftLoaded(true);
          }
        })();
      }, [currentUser]);

      // User clicks "Resume" — hydrate all fields (including image URLs).
      // Images were uploaded to Storage at pick time, so their URLs survive
      // the refresh. No re-selection needed (v2).
      function applyResume() {
        if (!resumePrompt) return;
        var d = resumePrompt.formData;
        setFormData(function(prev) {
          return Object.assign({}, prev, d, {
            coverSourceUrl: d.coverSourceUrl || "",
            coverPosX: typeof d.coverPosX === "number" ? d.coverPosX : 50,
            coverPosY: typeof d.coverPosY === "number" ? d.coverPosY : 50,
            coverZoom: typeof d.coverZoom === "number" ? d.coverZoom : 1,
            galleryUrls: Array.isArray(d.galleryUrls) ? d.galleryUrls.slice() : [],
            menuItems: (d.menuItems || []).map(function(m) {
              return { id: m.id, name: m.name || "", price: m.price || "", description: m.description || "", imageUrl: m.imageUrl || "" };
            })
          });
        });
        var resumeStep = resumePrompt.step || 1;
        setStep(resumeStep);
        setMaxStepVisited(resumeStep);
        setResumePrompt(null);
        setDraftLoaded(true);
        showToast("Welcome back — your draft is fully restored.", "info");
      }

      // User clicks "Start fresh" — purge both tiers + any Storage remnants.
      async function discardDraft() {
        if (!currentUser) return;
        try { localStorage.removeItem("nooks:listing-draft:" + currentUser.uid); } catch (e) {}
        try { await db.collection("listingDrafts").doc(currentUser.uid).delete(); } catch (e) {
          console.warn("[draft] cloud delete failed:", e);
        }
        setResumePrompt(null);
        setDraftLoaded(true);
      }

      // ── Draft SAVE: debounced (500ms local, 3s cloud) ────────────────
      React.useEffect(function() {
        if (!currentUser || !draftLoaded || submitting) return;
        var uid = currentUser.uid;
        var serialized = serializeForDraft(formData);
        var ts = Date.now();

        // Skip empty-form writes (no name/address/anything) — avoids spurious cloud writes.
        if (!draftIsMeaningful(serialized)) return;

        var payload = { formData: serialized, step: step, clientTimestamp: ts };

        // Local tier — 500ms
        if (draftLocalTimerRef.current) clearTimeout(draftLocalTimerRef.current);
        draftLocalTimerRef.current = setTimeout(function() {
          try { localStorage.setItem("nooks:listing-draft:" + uid, JSON.stringify(payload)); } catch (e) {}
        }, 500);

        // Cloud tier — 3s
        setDraftStatus("saving");
        if (draftCloudTimerRef.current) clearTimeout(draftCloudTimerRef.current);
        draftCloudTimerRef.current = setTimeout(function() {
          db.collection("listingDrafts").doc(uid).set({
            formData: serialized,
            step: step,
            clientTimestamp: ts,
            lastModified: firebase.firestore.FieldValue.serverTimestamp()
          }).then(function() {
            setDraftStatus("saved");
            setDraftLastSavedAt(new Date());
          }).catch(function(e) {
            console.warn("[draft] cloud save failed:", e);
            setDraftStatus("error");
          });
        }, 3000);
      }, [formData, step, currentUser, draftLoaded, submitting]);

      // Called from handleNext on successful submit to purge the draft.
      async function clearDraftAfterSubmit() {
        if (!currentUser) return;
        try { localStorage.removeItem("nooks:listing-draft:" + currentUser.uid); } catch (e) {}
        try { await db.collection("listingDrafts").doc(currentUser.uid).delete(); } catch (e) {
          console.warn("[draft] clear failed:", e);
        }
      }
      // ─────────────────────────────────────────────────────────────────

      // ── v2 image pick handlers: upload-on-pick ────────────────────────
      // Each pick compresses client-side then uploads to
      //   businesses/{uid}/drafts/{slot}_{ts}
      // The returned download URL is stored in formData, so a refresh /
      // resume recovers it without the user re-picking.
      const handleCoverUpload = async (e) => {
        if (!currentUser) { showToast("Please sign in before uploading.", "info"); return; }
        const file = e.target.files[0];
        if (!file) return;
        // Clear the input so picking the same file again re-triggers onChange
        try { e.target.value = ""; } catch (_) {}

        // If a previous cover source exists, best-effort delete so we don't
        // orphan it in Storage (user explicitly chose a replacement).
        const prevUrl = formData.coverSourceUrl;

        bumpUpload("cover", +1);
        try {
          const compressed = await compressImage(file, 1600, 0.9); // uncropped original
          // Keep the compressed Blob in memory so submit can skip the
          // fetch-back-from-storage round-trip in the common (in-session) case.
          coverSourceFileRef.current = compressed;
          const ref = storage.ref("businesses/" + currentUser.uid + "/drafts/cover_source_" + Date.now());
          await ref.put(compressed, { contentType: "image/jpeg" });
          const url = await ref.getDownloadURL();
          setFormData(function(prev) {
            return Object.assign({}, prev, {
              coverSourceUrl: url,
              // reset crop params so user starts from a clean slate on the new image
              coverPosX: 50, coverPosY: 50, coverZoom: 1, _repositioning: false
            });
          });
          if (prevUrl) deleteStorageByUrl(prevUrl);
        } catch (err) {
          console.error("[cover-upload] failed:", err);
          showToast("Cover upload failed — please try again.", "error");
        } finally {
          bumpUpload("cover", -1);
        }
      };

      const handleGalleryUpload = async (e) => {
        if (!currentUser) { showToast("Please sign in before uploading.", "info"); return; }
        const files = Array.from(e.target.files || []);
        try { e.target.value = ""; } catch (_) {}
        if (!files.length) return;

        for (const file of files) {
          bumpUpload("gallery", +1);
          try {
            const compressed = await compressImage(file, 1200, 0.85);
            const ref = storage.ref("businesses/" + currentUser.uid + "/drafts/gallery_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8));
            await ref.put(compressed, { contentType: "image/jpeg" });
            const url = await ref.getDownloadURL();
            setFormData(function(prev) {
              return Object.assign({}, prev, { galleryUrls: (prev.galleryUrls || []).concat([url]) });
            });
          } catch (err) {
            console.error("[gallery-upload] failed:", err);
            showToast("One gallery photo failed to upload.", "error");
          } finally {
            bumpUpload("gallery", -1);
          }
        }
      };

      const removeGalleryPhoto = (index) => {
        var toDelete = null;
        setFormData(function(prev) {
          var arr = (prev.galleryUrls || []).slice();
          if (index >= 0 && index < arr.length) {
            toDelete = arr[index];
            arr.splice(index, 1);
          }
          return Object.assign({}, prev, { galleryUrls: arr });
        });
        // Fire-and-forget: orphan cleanup
        if (toDelete) deleteStorageByUrl(toDelete);
      };

      const handleAddItem = () => {
        if (!newItem.name || !newItem.price) return;
        setFormData(prev => ({
          ...prev,
          menuItems: [...prev.menuItems, { ...newItem, id: Date.now() }]
        }));
        setNewItem({ name: "", price: "", description: "", imageUrl: "" });
      };

      // Upload a menu-item image at pick time. Used by the wizard's "add item"
      // card. Returns the URL via state update on newItem.
      const uploadNewItemImage = async (file) => {
        if (!file || !currentUser) return;
        const prevUrl = newItem.imageUrl;
        bumpUpload("menu", +1);
        try {
          const compressed = await compressImage(file, 800, 0.8);
          const ref = storage.ref("businesses/" + currentUser.uid + "/drafts/menuitem_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8));
          await ref.put(compressed, { contentType: "image/jpeg" });
          const url = await ref.getDownloadURL();
          setNewItem(function(prev) { return Object.assign({}, prev, { imageUrl: url }); });
          if (prevUrl) deleteStorageByUrl(prevUrl);
        } catch (err) {
          console.error("[menuitem-upload] failed:", err);
          showToast("Product image upload failed.", "error");
        } finally {
          bumpUpload("menu", -1);
        }
      };

      const removeItem = (id) => {
        setFormData(prev => ({
          ...prev,
          menuItems: prev.menuItems.filter(item => item.id !== id)
        }));
      };

      const updateHours = (day, field, value) => {
        setFormData(prev => ({
          ...prev,
          hours: { ...prev.hours, [day]: { ...prev.hours[day], [field]: value } }
        }));
      };

      const generateSlug = (name) => {
        return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      };

      const SOCIAL_PLATFORMS = [
        { key: "instagram", label: "Instagram", icon: '<path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>', placeholder: "@yourbusiness" },
        { key: "tiktok", label: "TikTok", icon: '<path d="M16.6 5.82s.51.5 0 0A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z"/>', placeholder: "@yourbusiness" },
        { key: "facebook", label: "Facebook", icon: '<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>', placeholder: "facebook.com/yourbusiness" },
        { key: "whatsapp", label: "WhatsApp", icon: '<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/>', placeholder: "+65--------" },
        { key: "link", label: "Website", icon: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>', placeholder: "https://yourbusiness.com" }
      ];

      // (submitting useState was here — moved up to before the draft-save
      // useEffect that references it; see comment near that declaration.)

      // --- Instagram OAuth for wizard -----------------------------------------
      const [wizardIgConnected, setWizardIgConnected] = useState(false);
      const [wizardIgPic, setWizardIgPic] = useState("");
      const [wizardIgError, setWizardIgError] = useState(null); // null | "cancelled" | "token" | "true" | ...
      // status: "idle" | "loading" | "found" | "not_found"
      const [wizardIgLookup, setWizardIgLookup] = useState({ status: "idle", name: "", profilePic: "" });

      // On mount: check if we've just returned from Instagram OAuth (success or error)
      React.useEffect(function() {
        if (!currentUser) return;
        var params = new URLSearchParams(window.location.search);
        var igConnected = params.get("ig_connected") === "1";
        var igError     = params.get("ig_error");

        // Nothing to handle — not returning from IG OAuth
        if (!igConnected && !igError) return;

        // -- Error case: OAuth failed or user cancelled -----------------------
        if (igError) {
          setWizardIgError(igError);
          setStep(3); // jump to IG step so user sees the error and can retry
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }

        // -- Success case: IG connected ---------------------------------------
        var igUser = params.get("ig_user") || "";
        // Pre-fill the instagram handle
        if (igUser) {
          setFormData(function(prev) {
            return Object.assign({}, prev, { socialMedia: Object.assign({}, prev.socialMedia, { instagram: igUser }) });
          });
          setWizardIgConnected(true);
        }
        // Fetch the profile pic stored by the Cloud Function
        db.collection("users").doc(currentUser.uid).get().then(function(snap) {
          if (snap.exists && snap.data().pendingIgProfilePic) {
            setWizardIgPic(snap.data().pendingIgProfilePic);
          }
        });
        // Jump straight to the business details step so user sees the connected card
        setStep(3);
        window.history.replaceState({}, "", window.location.pathname);
      }, [currentUser]);

      const connectInstagramForWizard = function() {
        if (!currentUser) return;
        setWizardIgError(null); // clear any previous error before re-attempting
        var oauthUrl =
          "https://www.instagram.com/oauth/authorize" +
          "?client_id=922868064042627" +
          "&redirect_uri=" + encodeURIComponent("https://asia-southeast1-nooks-sg.cloudfunctions.net/igOAuthCallback") +
          "&scope=instagram_business_basic" +
          "&response_type=code" +
          "&state=" + encodeURIComponent(currentUser.uid + "|wizard");
        window.location.href = oauthUrl;
      };

      const disconnectWizardInstagram = function() {
        setWizardIgConnected(false);
        setWizardIgPic("");
        setWizardIgLookup({ status: "idle", name: "", profilePic: "" });
        setFormData(function(prev) {
          return Object.assign({}, prev, { socialMedia: Object.assign({}, prev.socialMedia, { instagram: "" }) });
        });
      };

      // Looks up a public Instagram profile by handle using the igProfileLookup Cloud Function.
      // Called on blur of the handle input — no OAuth, no app approval needed.
      const lookupIgHandle = async function(handle) {
        if (!handle || handle.length < 2) return;
        setWizardIgLookup({ status: "loading", name: "", profilePic: "" });
        try {
          const res = await fetch(
            "https://asia-southeast1-nooks-sg.cloudfunctions.net/igProfileLookup?handle=" + encodeURIComponent(handle)
          );
          const data = await res.json();
          if (data.error) {
            setWizardIgLookup({ status: "not_found", name: "", profilePic: "", unverified: false });
          } else {
            // profilePic may be empty if Instagram blocks server-side image fetch — still mark as found.
            // unverified=true means Instagram blocked our scrape; handle is accepted but we couldn't confirm it.
            setWizardIgLookup({ status: "found", name: data.name || handle, profilePic: data.profilePic || "", unverified: data.unverified || false });
          }
        } catch (e) {
          // Network error — don't punish the user, accept the handle unverified
          setWizardIgLookup({ status: "found", name: handle, profilePic: "", unverified: true });
        }
      };
      // ------------------------------------------------------------------------

      const handleNext = async () => {
        if (step < totalSteps) { setStep(step + 1); return; }
        // Final step - save to Firestore
        if (!currentUser) { showToast("Please sign in first.", "info"); return; }
        setSubmitting(true);
        console.log("[submit] start");
        // Mutable working copy so the geocoding block below can merge in
        // resolved lat/lng without trying to reassign the useState const.
        let workingFormData = formData;
        try {
          // ── Cover photo (v2): re-crop from already-uploaded source URL ──
          // At pick time we uploaded the uncompressed source to
          //   businesses/{uid}/drafts/cover_source_{ts}
          // Here we fetch that URL as a Blob, apply the user's crop
          // (posX/posY/zoom), and upload the CROPPED blob to the final
          // businesses/{uid}/{ts}_cover path — the one written to
          // businesses.coverPhotoUrl.
          let coverPhotoUrl = "";
          if (coverSourceFileRef.current || workingFormData.coverSourceUrl) {
            try {
              let srcBlob;
              if (coverSourceFileRef.current) {
                // Fast path (in-session submit): use the in-memory blob from
                // pick time. No network fetch, so this never depends on the
                // storage bucket's CORS allowlist.
                console.log("[submit] cropping cover from in-memory source");
                srcBlob = coverSourceFileRef.current;
              } else {
                // Fallback (draft-resume submit): the in-memory blob is gone
                // because the wizard component remounted on a different
                // session, so re-fetch the source from storage. Requires the
                // bucket's CORS to allow this origin (see cors.json).
                console.log("[submit] re-cropping cover from draft source URL");
                const srcResp = await fetch(workingFormData.coverSourceUrl);
                if (!srcResp.ok) throw new Error("cover fetch " + srcResp.status);
                srcBlob = await srcResp.blob();
              }
              // cropSquareImage expects something with .type starting "image/" —
              // Blob has that. Doesn't use .name, so Blob is fine.
              const cropped = await cropSquareImage(
                srcBlob, 1200,
                workingFormData.coverPosX || 50,
                workingFormData.coverPosY || 50,
                workingFormData.coverZoom || 1
              );
              const coverRef = storage.ref("businesses/" + currentUser.uid + "/" + Date.now() + "_cover");
              await coverRef.put(cropped, { contentType: "image/jpeg" });
              coverPhotoUrl = await coverRef.getDownloadURL();
            } catch (coverErr) {
              console.error("[submit] cover finalize failed:", coverErr);
              showToast("Cover photo failed to finalize — please re-select.", "error");
              throw coverErr;
            }
          } else if (wizardIgConnected && wizardIgPic) {
            // User connected Instagram and we have their profile pic — use it as cover photo
            coverPhotoUrl = wizardIgPic;
          }
          // If IG connected but no pic available, coverPhotoUrl stays "" — acceptable

          // ── Gallery (v2): reuse draft URLs as-is ──
          // These were already uploaded to a publicly-readable Storage path
          // at pick time. No re-upload needed.
          const galleryUrls = Array.isArray(workingFormData.galleryUrls)
            ? workingFormData.galleryUrls.slice()
            : [];
          if (galleryUrls.length) console.log("[submit] reusing " + galleryUrls.length + " draft gallery URLs");

          // ── Menu items (v2): reuse draft imageUrl as-is ──
          const uploadedMenuItems = [];
          if (workingFormData.menuItems.length > 0) console.log("[submit] processing " + workingFormData.menuItems.length + " product items");
          for (const item of workingFormData.menuItems) {
            const { id, ...rest } = item; // imageUrl (if any) is already a Storage URL
            uploadedMenuItems.push(rest);
          }
          // -- Address resolution + coordinate geocoding (single source of truth) ------
          // Strategy:
          //   1. If address is bare postal ? resolve to street via OneMap, capture lat/lng + street
          //   2. If formData already has good coords (from Places autocomplete) ? keep them
          //   3. If coords are still at SG-center placeholder ? force-geocode from postal code
          //   4. Always persist: address, postalCode, street, district, lat, lng
          var finalAddress = workingFormData.address;
          var savedPostalCode = "";
          var streetFromOneMap = "";   // road name captured from OneMap results

          if (/^\d{5,6}$/.test((workingFormData.address || "").trim())) {
            try {
              console.log("[submit] geocoding postal code via OneMap");
              savedPostalCode = workingFormData.address.trim();
              // 8s timeout via AbortController — OneMap occasionally stalls.
              // Without this, a never-resolving fetch hangs the whole submit.
              var geoCtrl = new AbortController();
              var geoTid = setTimeout(function() { geoCtrl.abort(); }, 8000);
              var geoResp = await fetch("https://www.onemap.gov.sg/api/common/elastic/search?searchVal=" + savedPostalCode + "&returnGeom=Y&getAddrDetails=Y", { signal: geoCtrl.signal });
              clearTimeout(geoTid);
              var geoData = await geoResp.json();
              if (geoData && geoData.results && geoData.results.length > 0) {
                var r = geoData.results[0];
                var resolved = (r.BLK_NO && r.ROAD_NAME) ? (r.BLK_NO + " " + _toTitleCase(r.ROAD_NAME)) : "";
                if (resolved && resolved.length > 3) finalAddress = resolved;
                if (r.ROAD_NAME) streetFromOneMap = _toTitleCase(r.ROAD_NAME);
                // Also capture coords if formData still has defaults
                var DEFAULT_SG_LAT = 1.3521, DEFAULT_SG_LNG = 103.8198;
                var oLat = parseFloat(r.LATITUDE), oLng = parseFloat(r.LONGITUDE);
                if (!isNaN(oLat) && !isNaN(oLng)) {
                  var cDefault = Math.abs((workingFormData.lat||DEFAULT_SG_LAT)-DEFAULT_SG_LAT)<0.002 &&
                                 Math.abs((workingFormData.lng||DEFAULT_SG_LNG)-DEFAULT_SG_LNG)<0.002;
                  if (cDefault) workingFormData = Object.assign({}, workingFormData, { lat: oLat, lng: oLng });
                }
              }
            } catch(e) { console.warn("Address resolve failed:", e); }
          }

          // ROOT FIX: If coords are STILL at Singapore default center, force geocode before save
          var DEFAULT_SG_LAT = 1.3521, DEFAULT_SG_LNG = 103.8198;
          var coordsAreDefault = (Math.abs((workingFormData.lat || DEFAULT_SG_LAT) - DEFAULT_SG_LAT) < 0.002 &&
                                  Math.abs((workingFormData.lng || DEFAULT_SG_LNG) - DEFAULT_SG_LNG) < 0.002);
          if (coordsAreDefault) {
            var postalExtract = ((workingFormData.address || "") + " " + savedPostalCode).match(/\b\d{6}\b/);
            var geoQuery = postalExtract ? postalExtract[0] : (workingFormData.address || "").trim();
            if (geoQuery && geoQuery.length >= 5) {
              try {
                console.log("[submit] fallback geocode via OneMap");
                var sgCtrl = new AbortController();
                var sgTid = setTimeout(function() { sgCtrl.abort(); }, 8000);
                var sgResp2 = await fetch("https://www.onemap.gov.sg/api/common/elastic/search?searchVal=" + encodeURIComponent(geoQuery) + "&returnGeom=Y&getAddrDetails=Y", { signal: sgCtrl.signal });
                clearTimeout(sgTid);
                var sgData2 = await sgResp2.json();
                if (sgData2 && sgData2.results && sgData2.results.length > 0) {
                  var sgR = sgData2.results[0];
                  var sgLat = parseFloat(sgR.LATITUDE), sgLng = parseFloat(sgR.LONGITUDE);
                  if (!isNaN(sgLat) && !isNaN(sgLng)) {
                    workingFormData = Object.assign({}, workingFormData, { lat: sgLat, lng: sgLng });
                    if (!savedPostalCode && postalExtract) savedPostalCode = postalExtract[0];
                    if (sgR.ROAD_NAME && !streetFromOneMap) streetFromOneMap = _toTitleCase(sgR.ROAD_NAME);
                  }
                }
              } catch(geoErr) { console.warn("Save-time geocode failed:", geoErr); }
            }
          }

          // Derive street: prefer nooks-address.js capture ? OneMap road name ? extract from address
          var finalStreet = (workingFormData.street && workingFormData.street.trim()) ? workingFormData.street.trim()
            : streetFromOneMap
            ? streetFromOneMap
            : _extractStreetFromAddress(finalAddress);

          // Derive district: use what address autocomplete captured; street (from OneMap) is the real display source
          var cleanPostal = (savedPostalCode || workingFormData.postalCode || "").replace(/\D/g, "");
          var finalDistrict = (workingFormData.district && !/^singapore\s*\d*$/i.test(workingFormData.district.trim()) && !/^\d{5,6}$/.test(workingFormData.district.trim()))
            ? workingFormData.district.trim()
            : '';

          // Save business document
          const bizData = {
            ownerId: currentUser.uid,
            ownerName: currentUser.displayName,
            name: workingFormData.name,
            slug: workingFormData.slug,
            category: workingFormData.category,
            description: workingFormData.description,
            address: finalAddress,
            postalCode: cleanPostal || savedPostalCode || workingFormData.address,
            street: finalStreet,
            district: finalDistrict,
            lat: workingFormData.lat,
            lng: workingFormData.lng,
            socialMedia: workingFormData.socialMedia,
            defaultOrderMethod: workingFormData.defaultOrderMethod,
            coverPhotoUrl,
            galleryUrls,
            menuItems: uploadedMenuItems,
            hours: workingFormData.hours,
            status: "pending",
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
          };

          // Self-declared YWCA Hopemaker claim — separate from the admin-only
          // ywcaHopemaker.verified badge. Written whenever the owner checked
          // the box, so admin can cross-check it during approval review.
          if (workingFormData.ywcaHopemakerClaim && workingFormData.ywcaHopemakerClaim.claimed) {
            bizData.ywcaHopemakerClaim = {
              claimed: true,
              cohort: workingFormData.ywcaHopemakerClaim.cohort || null,
              claimedAt: firebase.firestore.FieldValue.serverTimestamp()
            };
          }

          // If Instagram was connected during the wizard, stamp it on the new listing
          if (wizardIgConnected) {
            bizData.igConnected = true;
            bizData.igConnectedAt = firebase.firestore.FieldValue.serverTimestamp();
            // Use IG profile pic as logo if the user didn't upload a cover photo
            if (wizardIgPic && !coverPhotoUrl) {
              bizData.logoUrl = wizardIgPic;
            }
          }

          console.log("[submit] writing business to Firestore");
          const docRef = await db.collection("businesses").add(bizData);
          console.log("[submit] complete — businessId=" + docRef.id);

          // Purge the draft now that the listing is safely persisted.
          // Failures here are non-fatal: draft doc will just expire via
          // future cleanup; the business is already written.
          await clearDraftAfterSubmit();

          // Clean up the temporary pending IG fields from the user's profile
          if (wizardIgConnected) {
            try {
              await db.collection("users").doc(currentUser.uid).update({
                pendingIgUsername:    firebase.firestore.FieldValue.delete(),
                pendingIgProfilePic:  firebase.firestore.FieldValue.delete(),
                pendingIgConnectedAt: firebase.firestore.FieldValue.delete(),
              });
            } catch(e) { console.warn("Could not clear pendingIg fields:", e); }
          }

          onComplete({ id: docRef.id, ...bizData });
        } catch (error) {
          console.error("[submit] error:", error);
          showToast("Failed to submit: " + error.message, "error");
        } finally {
          // Ensure the button always un-sticks — even on unexpected hangs
          // that slip past our per-step timeout guards.
          setSubmitting(false);
        }
      };

      const handlePrev = () => {
        if (step > 1) setStep(step - 1);
      };

      const progress = (step / totalSteps) * 100;

      return (
        <div className="wizard-container">
          {/* Resume-draft banner: shown if a meaningful draft was found on mount. */}
          {resumePrompt && (
            <div className="draft-resume-banner" role="region" aria-label="Saved draft">
              <div className="drb-text">
                <strong>You have a saved draft</strong>
                <div className="drb-sub">
                  Last edited {draftTimeAgo(resumePrompt.clientTimestamp)} · was on Step {resumePrompt.step || 1} of {totalSteps}
                </div>
              </div>
              <div className="drb-actions">
                <button className="drb-btn-primary" onClick={applyResume}>Resume</button>
                <button className="drb-btn-secondary" onClick={discardDraft}>Start fresh</button>
              </div>
            </div>
          )}

          {/* Click-to-jump stepper: allows jumping back to any previously-visited step. */}
          <div className="wizard-stepper" role="tablist" aria-label="Listing progress">
            {Array.from({ length: totalSteps }, function(_, i) {
              var n = i + 1;
              var reachable = n <= maxStepVisited;
              var active = n === step;
              var done = n < step;
              var cls = "stepper-dot" +
                (active ? " active" : "") +
                (done ? " done" : "") +
                (reachable ? " reachable" : " locked");
              return (
                <button
                  key={n}
                  type="button"
                  className={cls}
                  onClick={reachable && !active ? function() { setStep(n); } : undefined}
                  disabled={!reachable}
                  aria-current={active ? "step" : undefined}
                  title={reachable ? ("Go to Step " + n) : ("Step " + n + " not yet available")}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>

          {/* Step 1 - Welcome + Regulations Acknowledgement */}
          {step === 1 && (
            <div className="wizard-step active">
              <h2 className="wizard-title"><Icon name="shield" size={20} color="var(--amber)" /> Getting Started</h2>
              <p className="wizard-description">
                Before listing on <span className="nooks-brand">nooks</span>, please review Singapore's home-based business regulations.
              </p>

              {hasAcknowledged ? (
                <div style={{ background: "#E8F5E9", border: "1px solid #A5D6A7", borderRadius: "10px", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <Icon name="check" size={18} color="#388E3C" />
                  <span style={{ fontSize: "0.9rem", color: "#2E7D32" }}>You've already acknowledged the regulations.</span>
                </div>
              ) : (
                <>
                  <div style={{ background: "var(--bg)", borderRadius: "10px", padding: "1rem", marginBottom: "0.75rem", fontSize: "0.85rem", lineHeight: "1.6", color: "#444", maxHeight: "220px", overflowY: "auto", border: "1px solid var(--border)" }}>
                    <p style={{ fontWeight: 600, marginBottom: "0.5rem" }}>Key Requirements:</p>
                    <ul style={{ paddingLeft: "1.25rem", margin: 0 }}>
                      <li>Permitted: baking, sewing, hairdressing, beauty (no massage), tuition (max 3), freelance work</li>
                      <li>Run by owner/family/registered tenants only — no hiring non-residents</li>
                      <li>Must not cause noise, smoke, or heavy traffic</li>
                      <li>No commercial signs or banners on property</li>
                      <li>Food businesses: follow SFA guidelines + Food Safety Course Level 1</li>
                      <li>ACRA registration not needed for hobbyist projects; recommended if running for ongoing profit</li>
                      <li>Neighbours can complain to HDB — may be required to stop</li>
                    </ul>
                  </div>
                  <div className="ack-checkbox-row" style={{ margin: "0.75rem 0" }}>
                    <input type="checkbox" id="wizard-ack" checked={ackChecked} onChange={function(e) { setAckChecked(e.target.checked); }} />
                    <label htmlFor="wizard-ack" style={{ fontSize: "0.82rem" }}>I have read and accept Singapore's home-based business regulations. I am responsible for ensuring compliance with HDB, URA, and SFA guidelines.</label>
                  </div>
                </>
              )}

              <a href="/regulations" onClick={function(e) { e.preventDefault(); window.open("/regulations", "_blank"); }} style={{ fontSize: "0.82rem", color: "var(--amber)", display: "block", marginBottom: "1rem" }}>View full regulations →</a>

              <div className="wizard-actions">
                <button className="wizard-next-btn" disabled={!hasAcknowledged && !ackChecked} onClick={async function() {
                  if (!hasAcknowledged && ackChecked) {
                    setAckSaving(true);
                    await saveAcknowledgement();
                    setAckSaving(false);
                  }
                  handleNext();
                }} style={{ width: "100%" }}>
                  {ackSaving ? "Saving..." : "Let's Get Started ?"}
                </button>
              </div>
            </div>
          )}

          {/* Step 2 - Interactive Map */}
          {step === 2 && (
            <WizardMapStep
              formData={formData}
              setFormData={setFormData}
              handlePrev={handlePrev}
              handleNext={handleNext}
            />
          )}

          {/* Step 3: Business Details + Unique URL */}
          {step === 3 && (
            <div className="wizard-step active">
              <h2 className="wizard-title">Create a unique name and description</h2>
              <p className="wizard-description">Tell us about your business</p>
              <div className="form-group">
                <label className="form-label">Business Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="My Awesome Business"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({ ...formData, name, slug: generateSlug(name) });
                  }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  Instagram{" "}
                  <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: "0.85rem" }}>(optional)</span>
                </label>
                {wizardIgConnected ? (
                  /* -- Connected state -- */
                  <div style={{ background: "linear-gradient(135deg, #833AB4 0%, #C13584 50%, #E1306C 100%)", borderRadius: "12px", padding: "0.85rem 1rem", color: "white", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                      {wizardIgPic
                        ? <img src={wizardIgPic} alt="" style={{ width: "38px", height: "38px", borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.5)" }} />
                        : <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                      }
                      <div>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>@{formData.socialMedia.instagram}</div>
                        <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>Connected ?{wizardIgPic ? " — Profile photo will be imported" : ""}</div>
                      </div>
                    </div>
                    <button onClick={disconnectWizardInstagram} style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.4)", padding: "0.35rem 0.75rem", borderRadius: "20px", fontSize: "0.78rem", cursor: "pointer" }}>
                      Disconnect
                    </button>
                  </div>
                ) : (
                  /* -- Not connected -- */
                  <div>
                    {wizardIgError && (
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", background: "#FFF0F0", border: "1px solid #FFCDD2", borderRadius: "8px", padding: "0.6rem 0.75rem", marginBottom: "0.75rem", fontSize: "0.78rem", color: "#B71C1C" }}>
                        <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>⚠️</span>
                        <span>
                          {wizardIgError === "cancelled"
                            ? "Instagram connection was cancelled. Tap below to try again."
                            : wizardIgError === "not_professional"
                            ? (React.createElement(React.Fragment, null, "Your Instagram account must be a ", React.createElement("strong", null, "Professional account"), " (Business or Creator) to connect. ", React.createElement("a", { href: "https://help.instagram.com/502981923235522", target: "_blank", rel: "noopener noreferrer", style: { color: "#B71C1C", textDecoration: "underline" } }, "Switch for free in Instagram settings \u2192")))
                            : wizardIgError === "link_facebook_page"
                            ? (React.createElement(React.Fragment, null, "Auto-connect couldn\u2019t import your profile \u2014 Instagram is restricting access for this account. ", React.createElement("strong", null, "Type your handle manually below"), " to continue."))
                            : "Something went wrong connecting Instagram. Please try again, or type your handle manually below."}
                        </span>
                      </div>
                    )}
                    {/* IG_CONNECT_DISABLED: Instagram OAuth connector temporarily disabled */}
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.6rem" }}>
                      <span style={{ flex: 1, height: "1px", background: "var(--border)" }} />
                      <span style={{ fontSize: "0.75rem", color: "var(--muted)", whiteSpace: "nowrap" }}>type handle manually</span>
                      <span style={{ flex: 1, height: "1px", background: "var(--border)" }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--border)", borderRadius: "8px", overflow: "hidden", background: "white" }}>
                      <span style={{ padding: "0.75rem", background: "var(--bg)", color: "var(--muted)", fontSize: "0.95rem", fontWeight: 600, borderRight: "1px solid var(--border)", whiteSpace: "nowrap" }}>@</span>
                      <input
                        type="text"
                        style={{ flex: 1, border: "none", padding: "0.75rem", fontSize: "0.95rem", outline: "none" }}
                        placeholder="yourbusiness"
                        value={formData.socialMedia.instagram.replace(/^@+/, "")}
                        onChange={(e) => {
                          const val = e.target.value.replace(/^@+/, "").replace(/\s/g, "");
                          setFormData(prev => ({ ...prev, socialMedia: { ...prev.socialMedia, instagram: val } }));
                          setWizardIgLookup({ status: "idle", name: "", profilePic: "" });
                        }}
                        onBlur={(e) => {
                          const val = e.target.value.replace(/^@+/, "").trim();
                          if (val.length >= 2) lookupIgHandle(val);
                        }}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="none"
                        spellCheck="false"
                      />
                    </div>
                    {/* Profile preview after handle lookup */}
                    {wizardIgLookup.status === "loading" && (
                      <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.4rem", padding: "0.4rem 0.5rem" }}>
                        Looking up @{formData.socialMedia.instagram}—
                      </div>
                    )}
                    {wizardIgLookup.status === "found" && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.5rem", padding: "0.6rem 0.75rem", background: "#F3E8FF", border: "1px solid #D8B4FE", borderRadius: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                          {wizardIgLookup.profilePic && (
                            <img src={wizardIgLookup.profilePic} alt="" style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover" }} />
                          )}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>@{formData.socialMedia.instagram}</div>
                            <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>
                              {wizardIgLookup.profilePic ? "Import profile photo as your logo?" : wizardIgLookup.unverified ? "Handle accepted" : "Handle confirmed — link your Instagram"}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setWizardIgConnected(true);
                            setWizardIgPic(wizardIgLookup.profilePic);
                            setWizardIgLookup({ status: "idle", name: "", profilePic: "" });
                          }}
                          style={{ background: "linear-gradient(135deg, #833AB4 0%, #E1306C 100%)", color: "white", border: "none", padding: "0.4rem 0.85rem", borderRadius: "20px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                        >
                          {wizardIgLookup.profilePic ? "Import ?" : "Add ?"}
                        </button>
                      </div>
                    )}
                    {wizardIgLookup.status === "not_found" && formData.socialMedia.instagram.length >= 2 && (
                      <p style={{ fontSize: "0.75rem", color: "#E53935", marginTop: "0.4rem" }}>Couldn't find that profile — double-check the handle.</p>
                    )}
                    <p style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.4rem" }}>No Instagram? No problem — you can skip this and add socials later from your dashboard.</p>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Unique URL</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0", border: "1px solid var(--border)", borderRadius: "8px", overflow: "hidden", background: "white" }}>
                  <span style={{ padding: "0.75rem", background: "var(--bg)", color: "var(--muted)", fontSize: "0.85rem", whiteSpace: "nowrap", borderRight: "1px solid var(--border)" }}>nooks.sg/</span>
                  <input
                    type="text"
                    style={{ flex: 1, border: "none", padding: "0.75rem", fontSize: "0.95rem", outline: "none" }}
                    placeholder="my-awesome-business"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  />
                </div>
                <p style={{ fontSize: "0.75rem", color: "#E65100", marginTop: "0.35rem" }}>⚠ Choose carefully — this web address is permanent and cannot be easily changed later.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {CATEGORIES.filter(c => c.id !== "all").map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Business Description</label>
                <textarea
                  className="form-textarea"
                  placeholder="Describe your products/services!"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              {/* YWCA Hopemaker self-declaration — flags the listing for admin
                  cross-check against YWCA's roster. Does NOT itself grant the
                  verified badge; that stays admin-only. */}
              <div className="form-group" style={{ background: "#F7F8FC", border: "1px solid #D9DFF2", borderRadius: "10px", padding: "1rem" }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", fontSize: "0.9rem", fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={formData.ywcaHopemakerClaim.claimed}
                    onChange={(e) => setFormData(prev => ({ ...prev, ywcaHopemakerClaim: { ...prev.ywcaHopemakerClaim, claimed: e.target.checked } }))}
                    style={{ width: "16px", height: "16px", cursor: "pointer", marginTop: "2px", flexShrink: 0 }}
                  />
                  <span>I'm part of YWCA of Singapore's Hopemaker: Women's Business Accelerator programme</span>
                </label>
                {formData.ywcaHopemakerClaim.claimed && (
                  <div style={{ marginTop: "0.75rem", paddingLeft: "24px" }}>
                    <label className="form-label" style={{ fontSize: "0.8rem" }}>Cohort / batch (optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 2026-Q1 or Hopemaker '26 (Jan–Apr)"
                      value={formData.ywcaHopemakerClaim.cohort}
                      onChange={(e) => setFormData(prev => ({ ...prev, ywcaHopemakerClaim: { ...prev.ywcaHopemakerClaim, cohort: e.target.value } }))}
                      style={{ fontSize: "0.85rem" }}
                    />
                    <p style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: "0.4rem" }}>
                      Our team will verify this with YWCA before your listing shows the Hopemaker badge.
                    </p>
                  </div>
                )}
              </div>

              <div className="wizard-actions">
                <button className="wizard-prev-btn" onClick={handlePrev}>← Back</button>
                <button className="wizard-next-btn" onClick={handleNext} disabled={!formData.name || !formData.description || !formData.slug}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Social Media Profiles */}
          {step === 4 && (
            <div className="wizard-step active">
              <h2 className="wizard-title">Add your social media profiles</h2>
              <p className="wizard-description">Include at least one. This will help your customers contact you.</p>

              <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
                {SOCIAL_PLATFORMS.map((platform) => (
                  <button
                    key={platform.key}
                    onClick={() => setFormData(prev => ({ ...prev, activeSocial: platform.key }))}
                    style={{
                      width: "44px", height: "44px", borderRadius: "50%",
                      border: formData.activeSocial === platform.key ? "2px solid var(--amber)" : "1px solid var(--border)",
                      background: formData.activeSocial === platform.key ? "var(--amber)" : "white",
                      color: formData.activeSocial === platform.key ? "white" : "var(--text)",
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.2s", position: "relative"
                    }}
                    title={platform.label}
                  >
                    <span style={{ display: "inline-flex", width: 20, height: 20 }} dangerouslySetInnerHTML={{ __html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${platform.icon}</svg>` }} />
                    {formData.socialMedia[platform.key] && (
                      <span style={{ position: "absolute", top: "-2px", right: "-2px", width: "10px", height: "10px", borderRadius: "50%", background: "#34A853", border: "2px solid white" }} />
                    )}
                  </button>
                ))}
              </div>

              {SOCIAL_PLATFORMS.filter(p => p.key === formData.activeSocial).map((platform) => (
                <div key={platform.key}>
                  <label className="form-label">{platform.label} {platform.key === "whatsapp" ? "(with country code)" : ""}</label>
                  <div style={{ display: "flex", alignItems: "center", gap: "0", border: "1px solid var(--border)", borderRadius: "8px", overflow: "hidden", background: "white", marginBottom: "1rem" }}>
                    <span style={{ padding: "0.75rem", display: "flex", alignItems: "center", color: "var(--muted)", borderRight: "1px solid var(--border)" }}>
                      <span style={{ display: "inline-flex", width: 18, height: 18 }} dangerouslySetInnerHTML={{ __html: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${platform.icon}</svg>` }} />
                    </span>
                    <input
                      type="text"
                      style={{ flex: 1, border: "none", padding: "0.75rem", fontSize: "0.95rem", outline: "none" }}
                      placeholder={platform.placeholder}
                      value={formData.socialMedia[platform.key]}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        socialMedia: { ...prev.socialMedia, [platform.key]: e.target.value }
                      }))}
                    />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 0", marginBottom: "1rem" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>Use as Default Order Method</div>
                      <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>This will create an "Order Now" button for this profile</div>
                    </div>
                    <button
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        defaultOrderMethod: prev.defaultOrderMethod === platform.key ? "" : platform.key
                      }))}
                      style={{
                        width: "48px", height: "28px", borderRadius: "14px", border: "none", cursor: "pointer",
                        background: formData.defaultOrderMethod === platform.key ? "var(--amber)" : "#ccc",
                        position: "relative", transition: "background 0.2s", flexShrink: 0
                      }}
                    >
                      <span style={{
                        position: "absolute", top: "3px",
                        left: formData.defaultOrderMethod === platform.key ? "23px" : "3px",
                        width: "22px", height: "22px", borderRadius: "50%", background: "white",
                        transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                      }} />
                    </button>
                  </div>
                </div>
              ))}

              {Object.entries(formData.socialMedia).filter(([k, v]) => v).length > 0 && (
                <div style={{ background: "var(--bg)", padding: "0.75rem 1rem", borderRadius: "8px", marginBottom: "1rem" }}>
                  <div style={{ fontSize: "0.8rem", color: "var(--muted)", marginBottom: "0.5rem" }}>Added profiles:</div>
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {Object.entries(formData.socialMedia).filter(([k, v]) => v).map(([key, val]) => (
                      <span key={key} style={{ background: "white", padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.8rem", border: "1px solid var(--border)" }}>
                        {SOCIAL_PLATFORMS.find(p => p.key === key)?.label}: {val}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="wizard-actions">
                <button className="wizard-prev-btn" onClick={handlePrev}>← Back</button>
                <button className="wizard-next-btn" onClick={handleNext} disabled={!Object.values(formData.socialMedia).some(v => v)}>
                  Next →
                </button>
              </div>
              {!Object.values(formData.socialMedia).some(v => v) && (
                <p style={{ color: "#c0392b", fontSize: "0.8rem", textAlign: "center", marginTop: "0.5rem" }}>Add at least one social media profile</p>
              )}
            </div>
          )}

          {/* Step 5: Photos // v3 */}
          {step === 5 && (
            <div className="wizard-step active">
              <h2 className="wizard-title">Photos</h2>
              <p className="wizard-description">Upload your best photos</p>

              <input type="file" accept="image/*" ref={coverInputRef} style={{ display: "none" }} onChange={handleCoverUpload} />
              <input type="file" accept="image/*" multiple ref={galleryInputRef} style={{ display: "none" }} onChange={handleGalleryUpload} />

              {uploading.cover > 0 && !formData.coverSourceUrl ? (
                <div style={{ marginBottom: "1.5rem", textAlign: "center", padding: "2rem", background: "#FFF7EC", border: "2px dashed #d4a84a", borderRadius: "12px" }}>
                  <div className="draft-spinner" style={{ margin: "0 auto 0.6rem", width: 28, height: 28, border: "3px solid #e8d9b8", borderTopColor: "var(--amber)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  <div style={{ fontSize: "0.85rem", color: "var(--muted)" }}>Uploading cover photo…</div>
                </div>
              ) : formData.coverSourceUrl ? (
                <div style={{ marginBottom: "1.5rem" }}>
                  <div style={{ position: "relative", borderRadius: "12px", overflow: "hidden", aspectRatio: "1/1", maxWidth: "260px", margin: "0 auto", cursor: (formData._repositioning ? "crosshair" : "pointer"), userSelect: "none" }}
                    onClick={(e) => {
                      if (e.target.closest('button')) return;
                      setFormData(function(prev) { return Object.assign({}, prev, { _repositioning: !prev._repositioning }); });
                    }}
                    onMouseMove={(e) => {
                      if (!formData._repositioning) return;
                      var rect = e.currentTarget.getBoundingClientRect();
                      var px = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                      var py = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
                      setFormData(function(prev) { return Object.assign({}, prev, { coverPosX: Math.round(px), coverPosY: Math.round(py) }); });
                    }}
                    onTouchMove={(e) => {
                      if (!formData._repositioning || !e.touches[0]) return;
                      var rect = e.currentTarget.getBoundingClientRect();
                      var px = Math.max(0, Math.min(100, ((e.touches[0].clientX - rect.left) / rect.width) * 100));
                      var py = Math.max(0, Math.min(100, ((e.touches[0].clientY - rect.top) / rect.height) * 100));
                      setFormData(function(prev) { return Object.assign({}, prev, { coverPosX: Math.round(px), coverPosY: Math.round(py) }); });
                    }}>
                    <img src={formData.coverSourceUrl} alt="Cover"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none", objectPosition: (formData.coverPosX || 50) + "% " + (formData.coverPosY || 50) + "%", transform: "scale(" + (formData.coverZoom || 1) + ")", transition: "transform 0.1s" }} />
                    <button onClick={(e) => {
                      e.stopPropagation();
                      var toDelete = formData.coverSourceUrl;
                      setFormData(prev => ({ ...prev, coverSourceUrl: "", coverZoom: 1, coverPosX: 50, coverPosY: 50, _repositioning: false }));
                      // Drop the in-memory blob too so submit doesn't try to use a stale source.
                      coverSourceFileRef.current = null;
                      if (toDelete) deleteStorageByUrl(toDelete);
                    }}
                      style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: "50%", width: "28px", height: "28px", cursor: "pointer", fontSize: "1rem", lineHeight: "1", zIndex: 2 }}>×</button>
                    <div style={{ position: "absolute", bottom: "8px", left: "50%", transform: "translateX(-50%)", background: formData._repositioning ? "var(--amber)" : "rgba(0,0,0,0.5)", color: "white", padding: "0.3rem 0.8rem", borderRadius: "6px", fontSize: "0.7rem", zIndex: 2, fontWeight: 600, pointerEvents: "none" }}>
                      {formData._repositioning ? "Click anywhere to lock" : "Click to reposition"}
                    </div>
                  </div>
                  {/* Zoom slider */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem", padding: "0 0.5rem" }}>
                    <span style={{ fontSize: "0.7rem", color: "var(--muted)" }}>Zoom</span>
                    <input type="range" min="1" max="2" step="0.05" value={formData.coverZoom || 1}
                      onChange={(e) => setFormData(prev => ({ ...prev, coverZoom: parseFloat(e.target.value) }))}
                      style={{ flex: 1, accentColor: "var(--amber)" }} />
                    <span style={{ fontSize: "0.7rem", color: "var(--muted)", minWidth: "30px" }}>{Math.round((formData.coverZoom || 1) * 100)}%</span>
                  </div>
                </div>
              ) : (wizardIgConnected && wizardIgPic) ? (
                // IG connected WITH imported pic — cover photo is optional since we have one
                <div style={{ marginBottom: "1.5rem" }}>
                  {wizardIgPic ? (
                    <div style={{ position: "relative", borderRadius: "12px", overflow: "hidden", aspectRatio: "1/1", maxWidth: "260px", margin: "0 auto" }}>
                      <img src={wizardIgPic} alt="Instagram profile" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.5)", color: "white", fontSize: "0.72rem", textAlign: "center", padding: "0.4rem 0.5rem", lineHeight: 1.3 }}>
                        Imported from Instagram — <span style={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => coverInputRef.current.click()}>Replace</span>
                      </div>
                    </div>
                  ) : (
                    <div onClick={() => coverInputRef.current.click()} style={{ background: "#f0faf0", border: "2px dashed var(--amber)", borderRadius: "12px", padding: "1.5rem", textAlign: "center", cursor: "pointer" }}>
                      <div style={{ fontSize: "1.5rem", marginBottom: "0.4rem" }}>📸</div>
                      <div style={{ fontSize: "0.85rem", color: "var(--amber)", fontWeight: 600 }}>Instagram connected</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: "0.25rem" }}>Cover photo optional — or tap to upload your own</div>
                    </div>
                  )}
                </div>
              ) : (
                <div onClick={() => coverInputRef.current.click()} style={{ background: "#FCEADE", padding: "2rem", borderRadius: "12px", textAlign: "center", marginBottom: "1.5rem", cursor: "pointer", border: "2px dashed #d4a84a", transition: "background 0.2s" }}>
                  <Icon name="photo" size={20} /> Upload cover photo <span style={{ color: "#c0392b", fontWeight: 600 }}>*</span>
                </div>
              )}

              <div onClick={() => galleryInputRef.current.click()} style={{ background: "#FCEADE", padding: "2rem", borderRadius: "12px", textAlign: "center", marginBottom: "1rem", cursor: "pointer", border: "2px dashed #d4a84a" }}>
                <Icon name="photo" size={20} /> Add gallery photos
              </div>

              {(formData.galleryUrls.length > 0 || uploading.gallery > 0) && (
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
                  {formData.galleryUrls.map((url, i) => (
                    <div key={url + "-" + i} style={{ position: "relative", width: "80px", height: "80px", borderRadius: "8px", overflow: "hidden" }}>
                      <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button onClick={() => removeGalleryPhoto(i)} style={{ position: "absolute", top: "2px", right: "2px", background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: "50%", width: "20px", height: "20px", cursor: "pointer", fontSize: "0.7rem", lineHeight: "1" }}>×</button>
                    </div>
                  ))}
                  {/* In-flight upload placeholders */}
                  {Array.from({ length: uploading.gallery }, (_, k) => (
                    <div key={"up-" + k} style={{ width: 80, height: 80, borderRadius: 8, background: "#fff5e8", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div className="draft-spinner" style={{ width: 18, height: 18, border: "2px solid #e8d9b8", borderTopColor: "var(--amber)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    </div>
                  ))}
                </div>
              )}

              <div className="wizard-actions">
                <button className="wizard-prev-btn" onClick={handlePrev}>← Back</button>
                <button className="wizard-next-btn" onClick={handleNext} disabled={(!formData.coverSourceUrl && !wizardIgPic) || uploading.cover > 0 || uploading.gallery > 0}>
                  {(uploading.cover > 0 || uploading.gallery > 0) ? "Uploading…" : "Next →"}
                </button>
              </div>
              {!formData.coverSourceUrl && !wizardIgPic && uploading.cover === 0 && (
                <p style={{ color: "#c0392b", fontSize: "0.8rem", textAlign: "center", marginTop: "0.5rem" }}>Cover photo is required</p>
              )}
            </div>
          )}

          {/* Step 6: Products & Services */}
          {step === 6 && (
            <div className="wizard-step active">
              <h2 className="wizard-title">Products & Services</h2>
              <p className="wizard-description">Add your products or services</p>

              {formData.menuItems.length > 0 && (
                <div style={{ marginBottom: "1.5rem" }}>
                  {formData.menuItems.map((item, idx) => {
                    const _wPhotos = getItemPhotos(item);
                    const _wPhotoCount = _wPhotos.length;
                    return (
                    <div key={item.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", background: "white", borderRadius: "8px", marginBottom: "0.5rem", border: "1px solid var(--border)" }}>
                      <div onClick={() => setWizardItemEditor({ mode: 'edit', index: idx, item: item })} style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1, minWidth: 0, cursor: "pointer" }} title="Tap to edit photos &amp; details">
                        {_wPhotoCount > 0 ? (
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <img src={_wPhotos[0]} alt={item.name} style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "6px", display: "block" }} />
                            {_wPhotoCount > 1 && (
                              <div style={{ position: "absolute", bottom: "-3px", right: "-3px", background: "rgba(0,0,0,0.7)", color: "#fff", fontSize: "0.55rem", padding: "1px 4px", borderRadius: "99px", fontWeight: 700 }}>+{_wPhotoCount - 1}</div>
                            )}
                          </div>
                        ) : (
                          <div style={{ width: "44px", height: "44px", background: "var(--cream)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Icon name="photo" size={16} color="var(--muted)" />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600 }}>{item.name}</div>
                          <div style={{ fontSize: "0.85rem", color: "var(--muted)" }}>
                            {getItemPriceLabel(item)}
                            {item.description && ` — ${item.description}`}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => removeItem(item.id)} style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: "1.1rem", padding: "0 0.5rem", flexShrink: 0 }}>×</button>
                    </div>
                    );
                  })}
                  <div style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: "0.4rem", textAlign: "center" }}>Tap any item above to add more photos, variants, FAQs &amp; availability.</div>
                </div>
              )}

              <div className="menu-add-section">
                <div className="form-group">
                  <label className="form-label">Item Name</label>
                  <input type="text" className="form-input" placeholder="e.g. Pet Care" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Price (S$)</label>
                  <input type="number" className="form-input" placeholder="12.50" step="0.01" min="0" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input type="text" className="form-input" placeholder="Short description (optional)" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Product Image (optional)</label>
                  {uploading.menu > 0 && !newItem.imageUrl ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.6rem", border: "1px dashed var(--border)", borderRadius: "8px", fontSize: "0.85rem", color: "var(--muted)", background: "white" }}>
                      <div className="draft-spinner" style={{ width: 16, height: 16, border: "2px solid #e8d9b8", borderTopColor: "var(--amber)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                      Uploading…
                    </div>
                  ) : (
                    <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", padding: "0.6rem", border: "1px dashed var(--border)", borderRadius: "8px", fontSize: "0.85rem", color: "var(--muted)", background: "white" }}>
                      <Icon name="photo" size={16} color="var(--muted)" />
                      {newItem.imageUrl ? "Change image" : "Upload image"}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        uploadNewItemImage(file);
                        e.target.value = "";
                      }} />
                    </label>
                  )}
                  {newItem.imageUrl && (
                    <div style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <img src={newItem.imageUrl} alt="Preview" style={{ width: "48px", height: "48px", objectFit: "cover", borderRadius: "6px" }} />
                      <button onClick={() => {
                        var toDelete = newItem.imageUrl;
                        setNewItem(prev => ({ ...prev, imageUrl: "" }));
                        if (toDelete) deleteStorageByUrl(toDelete);
                      }} style={{ background: "none", border: "none", color: "#C62828", cursor: "pointer", fontSize: "0.8rem" }}>Remove</button>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleAddItem}
                  disabled={!newItem.name || !newItem.price || uploading.menu > 0}
                  style={{ background: (!newItem.name || !newItem.price || uploading.menu > 0) ? "#d4a84a" : "#c87606", color: "white", border: "none", padding: "0.75rem", borderRadius: "8px", width: "100%", cursor: (!newItem.name || !newItem.price || uploading.menu > 0) ? "not-allowed" : "pointer", fontWeight: 600, transition: "background 0.2s" }}
                >
                  + Add Item
                </button>
              </div>
              <div className="wizard-actions">
                <button className="wizard-prev-btn" onClick={handlePrev}>← Back</button>
                <button className="wizard-next-btn" onClick={handleNext}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Step 7: Operating Hours */}
          {step === 7 && (
            <div className="wizard-step active">
              <h2 className="wizard-title">Operating Hours</h2>
              <p className="wizard-description">When are you open?</p>
              {DAYS_ORDER.map((day) => (
                <div key={day} className="form-group">
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.35rem" }}>
                    <input
                      type="checkbox"
                      checked={formData.hours[day]?.enabled ?? true}
                      onChange={(e) => updateHours(day, "enabled", e.target.checked)}
                      style={{ width: "18px", height: "18px", accentColor: "var(--amber)", cursor: "pointer" }}
                    />
                    <label className="form-label" style={{ marginBottom: 0, color: formData.hours[day]?.enabled ? "var(--text)" : "var(--muted)" }}>{day}</label>
                    {!formData.hours[day]?.enabled && <span style={{ fontSize: "0.8rem", color: "var(--muted)", marginLeft: "auto" }}>Closed</span>}
                  </div>
                  {formData.hours[day]?.enabled && (
                    <div style={{ display: "flex", gap: "0.5rem", paddingLeft: "26px" }}>
                      <input
                        type="time"
                        className="form-input"
                        style={{ flex: 1 }}
                        value={formData.hours[day]?.open || "09:00"}
                        onChange={(e) => updateHours(day, "open", e.target.value)}
                      />
                      <span style={{ alignSelf: "center", color: "var(--muted)", fontSize: "0.85rem" }}>to</span>
                      <input
                        type="time"
                        className="form-input"
                        style={{ flex: 1 }}
                        value={formData.hours[day]?.close || "18:00"}
                        onChange={(e) => updateHours(day, "close", e.target.value)}
                      />
                    </div>
                  )}
                </div>
              ))}
              <div className="wizard-actions">
                <button className="wizard-prev-btn" onClick={handlePrev}>← Back</button>
                <button className="wizard-next-btn" onClick={handleNext}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Step 8: Review & Submit */}
          {step === 8 && (
            <div className="wizard-step active">
              <h2 className="wizard-title">Review & Submit</h2>
              <p className="wizard-description">Everything looks good?</p>
              <div style={{ background: "#FCEADE", padding: "1.5rem", borderRadius: "12px", marginBottom: "1.5rem" }}>
                <p><strong>Business:</strong> {formData.name}</p>
                <p><strong>URL:</strong> nooks.sg/{formData.slug}</p>
                <p><strong>Category:</strong> {CATEGORIES.find(c => c.id === formData.category)?.label}</p>
                <p><strong>Address:</strong> {formData.address}</p>
                <p><strong>Description:</strong> {formData.description}</p>
                {formData.coverSourceUrl && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <strong>Cover Photo:</strong>
                    <img src={formData.coverSourceUrl} alt="Cover" style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "8px", marginTop: "0.5rem", objectPosition: (formData.coverPosX || 50) + "% " + (formData.coverPosY || 50) + "%" }} />
                  </div>
                )}
                {Object.entries(formData.socialMedia).filter(([k, v]) => v).length > 0 && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <strong>Social Media:</strong>
                    {Object.entries(formData.socialMedia).filter(([k, v]) => v).map(([key, val]) => (
                      <p key={key} style={{ margin: "0.25rem 0", fontSize: "0.9rem" }}>
                        {SOCIAL_PLATFORMS.find(p => p.key === key)?.label}: {val}
                        {formData.defaultOrderMethod === key && <span style={{ color: "var(--amber)", fontWeight: 600, marginLeft: "0.5rem" }}>(Order Method)</span>}
                      </p>
                    ))}
                  </div>
                )}
                {formData.menuItems.length > 0 && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <strong>Products:</strong> {formData.menuItems.length} item(s)
                  </div>
                )}
              </div>
              <div className="wizard-actions">
                <button className="wizard-prev-btn" onClick={handlePrev}>← Back</button>
                <button className="wizard-next-btn" onClick={handleNext} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit ?"}
                </button>
              </div>
            </div>
          )}

          {/* Autosave indicator — visible once the user has typed enough to trigger a save. */}
          {draftLoaded && !resumePrompt && (draftStatus !== "idle") && (
            <div style={{ textAlign: "center" }}>
              <span className={"draft-save-indicator " + draftStatus} aria-live="polite">
                <span className="dsi-dot"></span>
                {draftStatus === "saving" && "Saving draft..."}
                {draftStatus === "saved"  && ("Draft saved" + (draftLastSavedAt ? (" · " + draftTimeAgo(draftLastSavedAt)) : ""))}
                {draftStatus === "error"  && "Couldn't save to cloud — kept locally"}
              </span>
            </div>
          )}
        </div>
      );
    }

    // Render App with Toast system
    function AppWithToast() {
      return React.createElement(React.Fragment, null,
        React.createElement(App, null),
        React.createElement(ToastContainer, null)
      );
    }
    ReactDOM.createRoot(document.getElementById("root")).render(<AppWithToast />);
